import React, { useState, useRef, useEffect, useMemo } from "react";

const T = {
  navy:"#3D2C3E", navyMid:"#5C5466", sand:"#F5EDE4", sandDark:"#FFFFFF",
  coral:"#E8A87C", coralSoft:"#F0C4A0", mint:"#9CB8A0", mintSoft:"#C0D4C4",
  gold:"#B8A9C9", text:"#3D2C3E", textMid:"#5C5466", textSoft:"#8E8494",
  white:"#FFFFFF", shadow:"rgba(61,44,62,0.08)", danger:"#C97B6B", cardBorder:"#EDE7E0",
};

const DAYS = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
let MEMBERS = [
  { id:"olivier",   name:"Olivier",   color:T.coral, avatar:"O" },
  { id:"stephanie", name:"Stéphanie", color:T.mint,  avatar:"S" },
  { id:"cleo",      name:"Cléo",      color:T.gold,  avatar:"C" },
];
const DEFAULT_MEMBERS = MEMBERS.map(m=>({...m}));
// Palette de couleurs pour les profils ajoutés dynamiquement
const MEMBER_COLOR_PALETTE = ["#E8735A","#5BBFAD","#D4A843","#9B59B6","#3D5A8A","#E07B39","#27AE60","#3498DB"];
function loadMembersFromStorage() {
  try {
    const v = localStorage.getItem("ff_members");
    if (v === null) return DEFAULT_MEMBERS; // jamais initialisé (compat testeurs existants) → membres de démo
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed : DEFAULT_MEMBERS; // clé présente (même vide = reset volontaire) → on la respecte telle quelle
  } catch { return DEFAULT_MEMBERS; }
}
function saveMembersToStorage(members) {
  try { localStorage.setItem("ff_members", JSON.stringify(members)); } catch {}
}
// Génère un id unique (slug) à partir d'un nom, en évitant les doublons
function slugifyMemberId(name, existing) {
  const base = (name||"membre").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9]/g,"") || "membre";
  let id = base, i = 2;
  while (existing.some(m=>m.id===id)) { id = base + i; i++; }
  return id;
}

// Helper: check if activeUser can edit an item
function canEdit(itemMember, activeUser) {
  if (!activeUser) return true; // no user selected = admin mode
  const members = Array.isArray(itemMember) ? itemMember : [itemMember];
  return members.includes(activeUser);
}

function syncMembersFromProfiles(profiles) {
  if (!profiles) return;
  MEMBERS = MEMBERS.map(m => {
    const p = profiles[m.id];
    if (!p) return m;
    return {
      ...m,
      name: p.name || m.name,
      color: p.color || m.color,
      avatar: p.avatar || m.avatar,
    };
  });
}
// ── Thèmes — modifiables par l'utilisateur ─────────────────────
const DEFAULT_THEMES = [
  { id:"work",       label:"Travail",       icon:"💼", color:"#3D5A8A" },
  { id:"health",     label:"Soins",       icon:"💊", color:"#5BBFAD" },
  { id:"sport",      label:"Sport",         icon:"🏃", color:"#E8735A" },
  { id:"school",     label:"Scolarité",     icon:"🎓", color:"#D4A843" },
  { id:"home",       label:"Maison",        icon:"🏠", color:"#8B6F47" },
  { id:"wellness",   label:"Bien-être",     icon:"🧘", color:"#9B59B6" },
  { id:"food",       label:"Alimentation",  icon:"🍽️", color:"#E07B39" },
  { id:"finance",    label:"Finances",      icon:"💰", color:"#27AE60" },
  { id:"transport",  label:"Transport",     icon:"🚗", color:"#2980B9" },
  { id:"social",     label:"Social",        icon:"👥", color:"#E91E63" },
  { id:"leisure",    label:"Loisirs",       icon:"🎯", color:"#FF6B35" },
];

// ── Dates locales — évite le décalage d'un jour causé par toISOString() (conversion UTC) ──
function toLocalISO(d) {
  if (!(d instanceof Date)) d = new Date(d);
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,"0"), day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function todayISO() { return toLocalISO(new Date()); }

function loadThemes() {
  try { const v = localStorage.getItem("ff_themes"); return v ? JSON.parse(v) : DEFAULT_THEMES; } catch { return DEFAULT_THEMES; }
}
function saveThemes(themes) {
  try { localStorage.setItem("ff_themes", JSON.stringify(themes)); } catch {}
}

// ── Plages horaires humeur (matin/soir) — réglables par le parent, communes à toute la famille ──
const DEFAULT_MOOD_SLOTS = { matin: { start:"06:00", end:"12:00" }, soir: { start:"17:00", end:"22:00" } };
function loadMoodSlots() {
  try { const v = localStorage.getItem("ff_mood_slots"); return v ? { ...DEFAULT_MOOD_SLOTS, ...JSON.parse(v) } : DEFAULT_MOOD_SLOTS; } catch { return DEFAULT_MOOD_SLOTS; }
}
function saveMoodSlots(slots) {
  try { localStorage.setItem("ff_mood_slots", JSON.stringify(slots)); } catch {}
}
// Vérifie si une heure (Date ou ISO string) tombe dans la plage [start,end] (format "HH:MM")
function isWithinMoodSlot(slotRange, when) {
  if (!slotRange) return true;
  const d = when instanceof Date ? when : new Date(when);
  const hhmm = d.toTimeString().slice(0,5);
  return hhmm >= slotRange.start && hhmm <= slotRange.end;
}

// Static fallback for TYPE_COLORS (travel + task always fixed)
const TYPE_COLORS = { work:"#3D5A8A", health:"#5BBFAD", sport:"#E8735A", school:"#D4A843", task:"#7B5EA7", travel:"#E07B39", home:"#8B6F47", wellness:"#9B59B6", food:"#E07B39", finance:"#27AE60", transport:"#2980B9", social:"#E91E63", leisure:"#FF6B35", meal:"#E07B39" };
const TYPE_LABELS = { work:"Travail", health:"Soins", sport:"Sport", school:"Scolarité", home:"Maison", wellness:"Bien-être", food:"Alimentation", finance:"Finances", transport:"Transport", social:"Social", leisure:"Loisirs" };
// Matrice d'Eisenhower — urgent x important
const EISENHOWER = [
  { value:1, urgent:true,  important:true,  label:"Urgent & important",     short:"🔴 Urgent & important",     color:"#E74C3C", icon:"🔴", desc:"À traiter maintenant" },
  { value:2, urgent:true,  important:false, label:"Urgent & non important",  short:"🟠 Urgent & non important",  color:"#E07B39", icon:"🟠", desc:"À planifier dans les 48h" },
  { value:3, urgent:false, important:true,  label:"Non urgent & important",  short:"🟡 Non urgent & important",  color:"#D4A843", icon:"🟡", desc:"À planifier" },
  { value:4, urgent:false, important:false, label:"Non urgent & non important", short:"⚪ Non urgent & non important", color:"#8896A5", icon:"⚪", desc:"À questionner — pourquoi c'est sur la to-do ?" },
];
// Keep IMPORTANCE alias for backward compat
const IMPORTANCE = EISENHOWER.map(e=>({...e, label:e.short}));
const importanceOf = (v) => EISENHOWER.find(e=>e.value===v) || EISENHOWER[1];

// Nombre de défis persos d'AUTRES membres qui attendent une action (vote ou validation finale) de userId
function pendingPersonalCount(personalMissions, userId) {
  if (!userId) return 0;
  return personalMissions.filter(m => {
    if (m.creator === userId) return false;
    const hasVoted = (m.votes||{})[userId] !== undefined;
    const hasValidated = (m.validated||[]).includes(userId);
    if (hasValidated) return false;
    if (!hasVoted) return true; // needs to vote
    const otherCount = MEMBERS.filter(mb=>mb.id!==m.creator).length;
    const allVoted = Object.keys(m.votes||{}).length >= otherCount;
    return allVoted; // voted already, waiting on this member's final validation
  }).length;
}

// Source unique de vérité pour le Sas journée ET le badge du bouton "Points importants du jour"
function computeDayPriorities(memberId, { appts=[], tasks=[], claims=[], moods=[], reminders=[], messages=[], personalMissions=[], moodAlerts=[], medications=[], medLogs=[] }) {
  const todayStr = todayISO();
  const apptsToday = appts.filter(a=>a.date===todayStr && (a.member===memberId || (Array.isArray(a.members) && a.members.includes(memberId)) || (!a.member && !a.members)));
  const tasksToday = tasks.filter(t=>!t.done && (t.importance===1||t.importance===2) && t.date===todayStr && (t.member===memberId || (Array.isArray(t.members) && t.members.includes(memberId)) || (!t.member && !t.members)));
  const claimsToday = claims.filter(c=>!c.done && c.memberId===memberId);
  const remindersToday = reminders.filter(r=>!r.done && r.date===todayStr && (r.member===memberId || !r.member));
  const moodToday = moods.filter(m=>m.member===memberId && m.date===todayStr);
  const hasMatin = moodToday.some(m=>m.slot==="matin");
  const unread = messages.filter(m=>m.to===memberId && !m.read).length;
  const pendingPersonal = pendingPersonalCount(personalMissions, memberId);
  const unseenAlerts = moodAlerts.filter(a=>!a.seen && a.member!==memberId).length;
  const medsToday = medications.filter(m=>m.member===memberId && m.active!==false && !medLogs.some(l=>l.medicationId===m.id && l.date===todayStr));
  const count = apptsToday.length + tasksToday.length + claimsToday.length + remindersToday.length + (hasMatin?0:1) + unread + pendingPersonal + unseenAlerts + medsToday.length;
  return { apptsToday, tasksToday, claimsToday, remindersToday, hasMatin, unread, pendingPersonal, unseenAlerts, medsToday, count };
}

function EisenhowerPicker({ value, onChange }) {
  const current = EISENHOWER.find(e=>e.value===value) || EISENHOWER[1];
  return (
    <div>
      <div style={{ fontSize:11, color:T.textSoft, marginBottom:8, fontWeight:700 }}>Priorité — touche un quadrant</div>
      <div style={{ display:"flex", gap:4, marginBottom:2 }}>
        <div style={{ flex:1, fontSize:9, fontWeight:700, color:T.textSoft, textAlign:"center" }}>URGENT</div>
        <div style={{ flex:1, fontSize:9, fontWeight:700, color:T.textSoft, textAlign:"center" }}>PAS URGENT</div>
      </div>
      <div style={{ fontSize:9, fontWeight:700, color:T.textSoft, marginBottom:2 }}>IMPORTANT</div>
      <div style={{ display:"flex", gap:4, marginBottom:4 }}>
        <button onClick={()=>onChange(1)} type="button" style={{ flex:1, padding:"10px 4px", borderRadius:10, border:"2px solid "+(value===1?EISENHOWER[0].color:T.sandDark), background:value===1?EISENHOWER[0].color+"25":"white", cursor:"pointer", textAlign:"center" }}>
          <div style={{ fontSize:24 }}>🔴</div>
          <div style={{ fontSize:10, fontWeight:700, color:value===1?EISENHOWER[0].color:T.textMid, marginTop:2 }}>Maintenant !</div>
        </button>
        <button onClick={()=>onChange(2)} type="button" style={{ flex:1, padding:"10px 4px", borderRadius:10, border:"2px solid "+(value===2?EISENHOWER[1].color:T.sandDark), background:value===2?EISENHOWER[1].color+"25":"white", cursor:"pointer", textAlign:"center" }}>
          <div style={{ fontSize:24 }}>🟡</div>
          <div style={{ fontSize:10, fontWeight:700, color:value===2?EISENHOWER[1].color:T.textMid, marginTop:2 }}>Bientôt</div>
        </button>
      </div>
      <div style={{ fontSize:9, fontWeight:700, color:T.textSoft, marginBottom:2 }}>PAS IMPORTANT</div>
      <div style={{ display:"flex", gap:4, marginBottom:8 }}>
        <button onClick={()=>onChange(3)} type="button" style={{ flex:1, padding:"10px 4px", borderRadius:10, border:"2px solid "+(value===3?EISENHOWER[2].color:T.sandDark), background:value===3?EISENHOWER[2].color+"25":"white", cursor:"pointer", textAlign:"center" }}>
          <div style={{ fontSize:24 }}>🟠</div>
          <div style={{ fontSize:10, fontWeight:700, color:value===3?EISENHOWER[2].color:T.textMid, marginTop:2 }}>Si possible</div>
        </button>
        <button onClick={()=>onChange(4)} type="button" style={{ flex:1, padding:"10px 4px", borderRadius:10, border:"2px solid "+(value===4?EISENHOWER[3].color:T.sandDark), background:value===4?EISENHOWER[3].color+"25":"white", cursor:"pointer", textAlign:"center" }}>
          <div style={{ fontSize:24 }}>⚪</div>
          <div style={{ fontSize:10, fontWeight:700, color:value===4?EISENHOWER[3].color:T.textMid, marginTop:2 }}>Un jour…</div>
        </button>
      </div>
      <div style={{ padding:"8px 12px", background:current.color+"12", borderRadius:8, border:"1px solid "+current.color, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:18 }}>{current.icon}</span>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:current.color }}>{current.label}</div>
          <div style={{ fontSize:11, color:T.textSoft }}>{current.desc}</div>
        </div>
      </div>
    </div>
  );
}

const RECURRENCE_LABELS = { none:"Aucune", weekly:"Hebdomadaire", biweekly:"Bimensuel", monthly:"Mensuel", custom:"Personnalisé" };

const memberOf = (id) => MEMBERS.find(m => m.id === id) || MEMBERS[0];

const cardStyle = (extra={}) => ({ background:T.sandDark, borderRadius:20, border:`1px solid ${T.cardBorder}`, padding:"14px 16px", boxShadow:`0 1px 4px ${T.shadow}`, ...extra });
const inputStyle = { background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:"10px 12px", fontSize:14, color:T.text, outline:"none" };
const btnStyle = (color, extra={}) => ({ background:color, color:T.white, border:"none", borderRadius:999, padding:"9px 16px", fontSize:13, fontWeight:700, cursor:"pointer", ...extra });
const iconBtn = (color="#888") => ({ background:"transparent", border:"none", cursor:"pointer", color, fontSize:15, padding:"2px 5px", borderRadius:4 });

// ── Shared components ──
function Avatar({ member, size=32 }) {
  const m = memberOf(member);
  return <div style={{ width:size, height:size, borderRadius:"50%", background:m.color, color:T.white, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.4, fontWeight:700, flexShrink:0 }}>{m.avatar}</div>;
}

// ── Shared EditModal ───────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(61,44,62,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:24, padding:20, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto", boxShadow:`0 8px 32px ${T.shadow}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:16, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>{title}</div>
          <button onClick={onClose} style={iconBtn(T.textSoft)}>✕</button>
        </div>
        {children}
      </div>


    </div>
  );
}

// ── Recurrence helpers ─────────────────────────────────────────
function expandRecurringAppt(appt) {
  const cancelled = appt.cancelledOccurrences || [];
  if (!appt.recurrence || appt.recurrence === "none") return cancelled.includes(String(appt.id)+"_0") ? [] : [{...appt, occKey: String(appt.id)+"_0"}];
  if (appt.recurrence === "custom") return (appt.customDates||[]).map((date,i) => {
    const occKey = String(appt.id)+"_"+i;
    const extras = (appt.occurrenceExtras||{})[occKey] || [];
    const allMembers = [...new Set([...(normalizeMembers(appt.members||appt.member)), ...extras])];
    return { ...appt, id:appt.id+"_"+i, date, isOccurrence:i>0, occKey, occMembers:allMembers };
  }).filter(o=>!cancelled.includes(o.occKey));
  const results=[], start=new Date(appt.date+"T00:00:00");
  const end = appt.recurrenceEnd ? new Date(appt.recurrenceEnd+"T00:00:00") : new Date(start.getTime()+365*864e5);
  const iw = appt.recurrence==="monthly" ? null : (appt.recurrenceInterval||(appt.recurrence==="biweekly"?2:1));
  let cur=new Date(start), idx=0;
  while (cur<=end && idx<52) {
    const occKey = String(appt.id)+"_"+idx;
    if (!cancelled.includes(occKey)) {
      const extras = (appt.occurrenceExtras||{})[occKey] || [];
      const allMembers = [...new Set([...(normalizeMembers(appt.members||appt.member)), ...extras])];
      results.push({ ...appt, id:occKey, date:toLocalISO(cur), isOccurrence:idx>0, occKey, occMembers:allMembers });
    }
    if (appt.recurrence==="monthly") { cur=new Date(cur); cur.setMonth(cur.getMonth()+1); }
    else cur=new Date(cur.getTime()+iw*7*864e5);
    idx++;
  }
  return results;
}

function expandRecurringTask(task) {
  if (!task.recurrence || task.recurrence === "none" || !task.date) return [task];
  const results = [];
  const start = new Date(task.date + "T00:00:00");
  const end = task.recurrenceEnd ? new Date(task.recurrenceEnd + "T00:00:00") : new Date(start.getTime() + 365*864e5);
  const iw = task.recurrence === "monthly" ? null : (task.recurrenceInterval || (task.recurrence === "biweekly" ? 2 : 1));
  let cur = new Date(start), idx = 0;
  while (cur <= end && idx < 52) {
    results.push({ ...task, id: task.id + (idx > 0 ? "_" + idx : ""), date: toLocalISO(cur), isOccurrence: idx > 0, done: idx > 0 ? false : task.done });
    if (task.recurrence === "monthly") { cur = new Date(cur); cur.setMonth(cur.getMonth() + 1); }
    else cur = new Date(cur.getTime() + iw * 7 * 864e5);
    idx++;
  }
  return results;
}

function expandEventForWeek(ev) {
  if (!ev.recurrence || ev.recurrence==="none") return [ev];
  if (ev.recurrence==="daily") return DAYS.map((_,di) => ({ ...ev, id:ev.id+"_"+di, day:di, isRecurring:true }));
  if (ev.recurrence==="custom") return (ev.customDays||[]).map(d => ({ ...ev, id:ev.id+"_"+d, day:d, isRecurring:true }));
  return [{ ...ev, isRecurring:true }];
}

// ── RecurrenceFields (shared form section) ─────────────────────
// ── TimePicker ─────────────────────────────────────────────────
function TimePicker({ value, onChange, style={} }) {
  const hours   = Array.from({length:24},(_,i)=>String(i).padStart(2,"0"));
  const minutes = ["00","05","10","15","20","25","30","35","40","45","50","55"];
  const [h, m]  = value ? value.split(":") : ["09","00"];

  const update = (newH, newM) => onChange(newH + ":" + newM);

  return (
    <div style={{ display:"flex", gap:4, alignItems:"center", ...style }}>
      <select value={h} onChange={e=>update(e.target.value, m)} style={{ ...inputStyle, flex:1, textAlign:"center" }}>
        {hours.map(hh=><option key={hh} value={hh}>{hh}h</option>)}
      </select>
      <span style={{ color:T.textSoft, fontWeight:700 }}>:</span>
      <select value={minutes.includes(m)?m:"00"} onChange={e=>update(h,e.target.value)} style={{ ...inputStyle, flex:1, textAlign:"center" }}>
        {minutes.map(mm=><option key={mm} value={mm}>{mm}</option>)}
      </select>
    </div>
  );
}


// ── Navigation helpers ─────────────────────────────────────────
function NavButtons({ address }) {
  if (!address || !address.trim()) return null;
  const enc = encodeURIComponent(address);
  return (
    <div style={{ display:"flex", gap:6, marginTop:4 }}>
      <a href={`https://maps.apple.com/?q=${enc}`} target="_blank" rel="noreferrer"
        style={{ display:"flex",alignItems:"center",gap:4,background:"#000",color:"#fff",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,textDecoration:"none" }}>
        🗺 Plans
      </a>
      <a href={`https://ul.waze.com/ul?q=${enc}&navigate=yes`} target="_blank" rel="noreferrer"
        style={{ display:"flex",alignItems:"center",gap:4,background:"#05C8F7",color:"#fff",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,textDecoration:"none" }}>
        🚗 Waze
      </a>
    </div>
  );
}

function RecurrenceFields({ form, setForm, mode="event" }) {
  const toggleDay = (di) => setForm(f => {
    const days = (f.customDays||[]).includes(di) ? f.customDays.filter(d=>d!==di) : [...(f.customDays||[]),di];
    return { ...f, customDays:days };
  });
  return (
    <div style={{ borderTop:`1px solid ${T.sandDark}`, paddingTop:10, marginTop:4, marginBottom:8 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>Récurrence</div>
      <select value={form.recurrence||"none"} onChange={e=>setForm(f=>({...f,recurrence:e.target.value,customDays:[],customDates:[""]}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:8}}>
        <option value="none">Aucune</option>
        <option value="weekly">Hebdomadaire</option>
        <option value="biweekly">Bimensuel (toutes les 2 sem.)</option>
        {mode==="event" && <option value="daily">Tous les jours</option>}
        {mode==="event" && <option value="custom">Jours personnalisés</option>}
        {mode==="appt"  && <option value="monthly">Mensuel</option>}
        {mode==="appt"  && <option value="custom">Dates personnalisées</option>}
      </select>

      {form.recurrence==="custom" && mode==="event" && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:11, color:T.textSoft, marginBottom:6 }}>Sélectionne les jours</div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {DAYS.map((d,di) => (
              <button key={di} onClick={()=>toggleDay(di)} style={{ padding:"5px 10px", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", border:`2px solid ${(form.customDays||[]).includes(di)?T.coral:T.sandDark}`, background:(form.customDays||[]).includes(di)?T.coral:T.sandDark, color:(form.customDays||[]).includes(di)?T.white:T.textMid }}>{d}</button>
            ))}
          </div>
        </div>
      )}

      {form.recurrence==="custom" && mode==="appt" && (
        <div>
          <div style={{ fontSize:11, color:T.textSoft, marginBottom:6 }}>Dates spécifiques</div>
          {(form.customDates||[""]).map((d,i) => (
            <div key={i} style={{ display:"flex", gap:6, marginBottom:6 }}>
              <input type="date" value={d} onChange={e=>{ const nd=[...(form.customDates||[])]; nd[i]=e.target.value; setForm(f=>({...f,customDates:nd})); }} style={{...inputStyle,flex:1}} />
              {(form.customDates||[]).length>1 && <button onClick={()=>setForm(f=>({...f,customDates:f.customDates.filter((_,j)=>j!==i)}))} style={{ background:T.coralSoft,border:"none",borderRadius:6,color:T.white,cursor:"pointer",padding:"0 10px",fontWeight:700 }}>×</button>}
            </div>
          ))}
          <button onClick={()=>setForm(f=>({...f,customDates:[...(f.customDates||[""]),""]})) } style={{ fontSize:12,color:T.mint,background:"transparent",border:`1px dashed ${T.mint}`,borderRadius:6,padding:"5px 12px",cursor:"pointer" }}>+ Ajouter une date</button>
        </div>
      )}

      {(form.recurrence==="weekly"||form.recurrence==="biweekly") && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Intervalle (sem.)</div>
            <input type="number" min={1} max={8} value={form.recurrenceInterval||1} onChange={e=>setForm(f=>({...f,recurrenceInterval:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
          <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Fin (optionnel)</div>
            <input type="date" value={form.recurrenceEnd||""} onChange={e=>setForm(f=>({...f,recurrenceEnd:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
        </div>
      )}
      {form.recurrence==="monthly" && (
        <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Fin de récurrence</div>
          <input type="date" value={form.recurrenceEnd||""} onChange={e=>setForm(f=>({...f,recurrenceEnd:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
      )}
    </div>
  );
}


function TravelTimeField({ value, onChange, address, fiches=[], label="Temps de trajet", autoOnMount=false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const prevAddress = useRef("");

  const getAddress = (addr) => {
    // Try to find full address from fiches if we have a specialist name
    const fiche = fiches.find(f => f.contact?.name?.toLowerCase() === addr?.toLowerCase() || f.title?.toLowerCase() === addr?.toLowerCase());
    return fiche?.contact?.address || addr;
  };

  const autoCalc = async (addr) => {
    const resolved = getAddress(addr || address);
    if (!resolved?.trim()) return;
    setLoading(true); setError("");
    const min = await fetchTravelTime(resolved);
    if (min) onChange(min);
    else setError("Adresse introuvable");
    setLoading(false);
  };

  // Auto-trigger when address changes and no manual value set
  useEffect(() => {
    if (!address?.trim()) return;
    if (address === prevAddress.current) return;
    prevAddress.current = address;
    if (!value || value === 0) {
      autoCalc(address);
    }
  }, [address]);

  return (
    <div>
      <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>{label}</div>
      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
        <input type="number" min={0} max={240} placeholder="min" value={value||""} onChange={e=>onChange(+e.target.value)} style={{...inputStyle, width:70, textAlign:"center"}} />
        <span style={{ fontSize:12, color:T.textSoft }}>min</span>
        {loading
          ? <span style={{ fontSize:11, color:T.mint }}>🚗 calcul…</span>
          : <button onClick={()=>autoCalc(address)} style={{ fontSize:11, background:T.mint+"22", color:T.mint, border:"none", borderRadius:6, padding:"5px 10px", cursor:"pointer", fontWeight:700 }}>🚗 Recalculer</button>
        }
        {error && <span style={{ fontSize:10, color:T.danger }}>{error}</span>}
      </div>
    </div>
  );
}


// ── MemberPicker — multi-select par boutons ────────────────────
// members peut être un string (ancien format) ou un array (nouveau)
function normalizeMembers(m) {
  if (!m) return ["olivier"];
  if (Array.isArray(m)) return m.length > 0 ? m : ["olivier"];
  return [m];
}

function membersLabel(members) {
  const arr = normalizeMembers(members);
  return arr.map(id => memberOf(id).name).join(", ");
}

function MemberPicker({ value, onChange, label="Membre(s)", readOnly=false, activeUser=null }) {
  const selected = normalizeMembers(value);
  const toggle = (id) => {
    if (selected.includes(id)) {
      const next = selected.filter(m => m !== id);
      onChange(next.length > 0 ? next : [id]);
    } else {
      onChange([...selected, id]);
    }
  };
  return (
    <div>
      {label && <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>{label}</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {MEMBERS.map(m => (
          <label key={m.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderRadius:8, border:`1px solid ${selected.includes(m.id)?m.color:T.sandDark}`, background:selected.includes(m.id)?m.color+"12":"transparent", cursor:"pointer" }}>
            <input type="checkbox" checked={selected.includes(m.id)} onChange={()=>toggle(m.id)} style={{ accentColor:m.color, width:16, height:16 }} />
            <span style={{ width:20, height:20, borderRadius:"50%", background:m.color, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:T.white, flexShrink:0 }}>{m.avatar}</span>
            <span style={{ fontSize:13, fontWeight:600, color:selected.includes(m.id)?m.color:T.text }}>{m.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Multi-avatar display
function MemberAvatars({ members, size=26 }) {
  const arr = normalizeMembers(members);
  return (
    <div style={{ display:"flex", gap:-4 }}>
      {arr.map((id,i) => (
        <div key={id} style={{ marginLeft: i>0 ? -8 : 0, zIndex: arr.length-i }}>
          <Avatar member={id} size={size} />
        </div>
      ))}
    </div>
  );
}


// ── Companion modal — add/remove companions on a single occurrence ──
function OccurrenceCompanionModal({ appt, baseAppt, onSave, onClose }) {
  const mainMember = normalizeMembers(baseAppt.members||baseAppt.member)[0];
  const occKey = appt.occKey;
  const currentExtras = (baseAppt.occurrenceExtras||{})[occKey] || [];
  const [extras, setExtras] = useState(currentExtras);

  const toggle = (id) => {
    if (id === mainMember) return; // can't remove main member
    setExtras(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };

  const save = () => {
    const newExtras = { ...(baseAppt.occurrenceExtras||{}), [occKey]: extras };
    onSave({ ...baseAppt, occurrenceExtras: newExtras });
    onClose();
  };

  const allSelected = [mainMember, ...extras];

  return (
    <Modal title={`Accompagnants — ${appt.date}`} onClose={onClose}>
      <div style={{ fontSize:13, color:T.textMid, marginBottom:12 }}>
        <strong>{appt.title}</strong> · {appt.time}
      </div>
      <div style={{ fontSize:11, color:T.textSoft, marginBottom:8 }}>
        Membre principal (toujours présent) :
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14, padding:"8px 12px", background:T.sandDark, borderRadius:8 }}>
        <Avatar member={mainMember} size={28} />
        <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{memberOf(mainMember).name}</span>
        <span style={{ fontSize:11, color:T.textSoft, marginLeft:"auto" }}>Principal</span>
      </div>
      <div style={{ fontSize:11, color:T.textSoft, marginBottom:8 }}>
        Accompagnants pour ce RDV uniquement :
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
        {MEMBERS.filter(m => m.id !== mainMember).map(m => (
          <button key={m.id} onClick={()=>toggle(m.id)} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:20, fontSize:13, fontWeight:700, cursor:"pointer", border:`2px solid ${extras.includes(m.id)?m.color:T.sandDark}`, background:extras.includes(m.id)?m.color:"transparent", color:extras.includes(m.id)?T.white:T.textMid }}>
            <Avatar member={m.id} size={20} />
            {m.name}
            {extras.includes(m.id) && <span style={{fontSize:11}}>✓</span>}
          </button>
        ))}
      </div>
      <div style={{ fontSize:11, color:T.textSoft, marginBottom:12 }}>
        Présents ce jour : {allSelected.map(id=>memberOf(id).name).join(", ")}
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <button onClick={save} style={btnStyle(T.mint, {flex:1})}>Enregistrer</button>
        <button onClick={onClose} style={btnStyle(T.textSoft)}>Annuler</button>
      </div>
    </Modal>
  );
}


// ── ReminderAttacher — ajouter des rappels à un élément ────────
const LINKED_DELAY_OPTIONS = [
  { value:0,      label:"À l'heure exacte" },
  { value:5,      label:"5 min avant" },
  { value:10,     label:"10 min avant" },
  { value:15,     label:"15 min avant" },
  { value:30,     label:"30 min avant" },
  { value:60,     label:"1h avant" },
  { value:120,    label:"2h avant" },
  { value:1440,   label:"1 jour avant" },
  { value:10080,  label:"1 semaine avant" },
];

function ReminderAttacher({ reminders=[], onChange, itemDate, itemTime, itemTitle, members=[], onAddReminder, onDeleteReminder }) {
  const [showAdd, setShowAdd] = useState(false);
  const [delay, setDelay] = useState(15);

  const addLinkedReminder = () => {
    if (!itemDate) return;
    const base = new Date(itemDate+"T"+(itemTime||"09:00")+":00");
    const target = new Date(base.getTime() - delay*60000);
    const td = toLocalISO(target);
    const tt = target.toTimeString().slice(0,5);
    const id = Date.now()+Math.random();
    const label = LINKED_DELAY_OPTIONS.find(o=>o.value===delay)?.label || `${delay} min avant`;
    // Créé tout de suite dans la vraie liste globale des rappels (sinon, en édition, il ne se créait jamais)
    if (onAddReminder) {
      onAddReminder({ id, title: itemTitle||"(sans titre)", notes:"Rappel lié", member: members[0]||"olivier", members: members.length?members:["olivier"], targetDate: td, targetTime: tt, done:false, triggered:false, createdAt:new Date().toISOString() });
    }
    const newR = { id, delay, targetDate:td, targetTime:tt, label };
    onChange([...reminders, newR]);
    setShowAdd(false);
  };

  const removeReminder = (id) => {
    onDeleteReminder && onDeleteReminder(id);
    onChange(reminders.filter(r=>r.id!==id));
  };

  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ fontSize:11, color:T.textSoft, fontWeight:700, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>⏰ Rappels</div>
      {reminders.map(r=>(
        <div key={r.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:T.coral+"12", borderRadius:7, padding:"6px 10px", marginBottom:5 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:14 }}>⏰</span>
            <span style={{ fontSize:12, fontWeight:600, color:T.coral }}>{r.label}</span>
            {r.targetDate && <span style={{ fontSize:11, color:T.textSoft }}>— {r.targetDate} à {r.targetTime}</span>}
          </div>
          <button onClick={()=>removeReminder(r.id)} style={{ background:"transparent", border:"none", cursor:"pointer", color:T.textSoft, fontSize:16 }}>×</button>
        </div>
      ))}
      {!showAdd ? (
        <button onClick={()=>setShowAdd(true)} style={{ fontSize:12, color:T.coral, background:"transparent", border:`1px dashed ${T.coral}`, borderRadius:6, padding:"5px 12px", cursor:"pointer", fontWeight:700 }}>+ Ajouter un rappel</button>
      ) : (
        <div style={{ background:T.sandDark, borderRadius:8, padding:"10px 12px", marginTop:6 }}>
          <div style={{ fontSize:11, color:T.textSoft, marginBottom:6 }}>Rappel</div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <select value={delay} onChange={e=>setDelay(+e.target.value)} style={{...inputStyle, flex:1, fontSize:12}}>
              {LINKED_DELAY_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button onClick={addLinkedReminder} style={btnStyle(T.coral, {padding:"8px 12px", fontSize:12})}>✓</button>
            <button onClick={()=>setShowAdd(false)} style={btnStyle(T.textSoft, {padding:"8px 10px", fontSize:12})}>✕</button>
          </div>
          {!itemDate && <div style={{ fontSize:11, color:T.danger, marginTop:4 }}>Renseignez d'abord une date.</div>}
        </div>
      )}
    </div>
  );
}


// ── ThemePicker — sélecteur de thème avec option d'ajout ───────
function ThemePicker({ value, onChange, themes, onAddTheme, label="Thème" }) {
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newIcon, setNewIcon] = useState("⭐");
  const [newColor, setNewColor] = useState("#888888");

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const id = "custom_" + Date.now();
    onAddTheme({ id, label:newLabel.trim(), icon:newIcon, color:newColor });
    onChange(id);
    setShowNew(false);
    setNewLabel(""); setNewIcon("⭐"); setNewColor("#888888");
  };

  return (
    <div>
      {label && <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>{label}</div>}
      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
        <select value={value||""} onChange={e=>onChange(e.target.value)} style={{...inputStyle, flex:1}}>
          {themes.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
        </select>
        <button onClick={()=>setShowNew(s=>!s)} style={{ fontSize:18, background:"transparent", border:`1px solid ${T.sandDark}`, borderRadius:7, padding:"6px 10px", cursor:"pointer", color:T.coral }} title="Ajouter un thème">+</button>
      </div>
      {showNew && (
        <div style={{ marginTop:8, background:T.sandDark, borderRadius:8, padding:"10px 12px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, marginBottom:8 }}>Nouveau thème</div>
          <div style={{ display:"flex", gap:6, marginBottom:6 }}>
            <input placeholder="Émoji" value={newIcon} onChange={e=>setNewIcon(e.target.value)} style={{...inputStyle, width:52, textAlign:"center", fontSize:18}} maxLength={2} />
            <input placeholder="Nom du thème" value={newLabel} onChange={e=>setNewLabel(e.target.value)} style={{...inputStyle, flex:1}} />
            <input type="color" value={newColor} onChange={e=>setNewColor(e.target.value)} style={{ width:40, height:40, border:"none", borderRadius:7, cursor:"pointer", padding:2 }} />
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={handleAdd} style={btnStyle(T.coral, {flex:1, fontSize:12})}>Créer</button>
            <button onClick={()=>setShowNew(false)} style={btnStyle(T.textSoft, {fontSize:12})}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── INITIAL DATA ───────────────────────────────────────────────
const INITIAL_EVENTS = [];
const INITIAL_TASKS = [];

function fmtTaskDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 864e5);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Demain";
  if (diff === -1) return "Hier";
  if (diff < 0) return "En retard · " + d.toLocaleDateString("fr-FR", { day:"numeric", month:"short" });
  return d.toLocaleDateString("fr-FR", { weekday:"short", day:"numeric", month:"short" });
}
function isOverdue(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);
  return d < today;
}
const INITIAL_APPTS = [];

// ── PLANNING VIEW ──────────────────────────────────────────────
const EMPTY_EV = { day:0, time:"09:00", title:"", member:"olivier", type:"work", recurrence:"none", recurrenceInterval:1, recurrenceEnd:"", customDays:[], travelTime:0, duration:60, linkedReminders:[] };

// ── TaskTimeModal ───────────────────────────────────────────────
function TaskTimeModal({ taskModal, tasks, onUpdateTask, onToggleTask, onClose, onAddReminder }) {
  const [assignTime, setAssignTime] = useState(taskModal.time||"09:00");
  const m = memberOf(taskModal.member);
  const imp = importanceOf(taskModal.importance||2);
  const baseId = typeof taskModal.taskId === "string" ? taskModal.taskId.split("_")[0] : String(taskModal.taskId);
  const baseTask = tasks.find(tk=>String(tk.id)===baseId)||{};

  const saveTime = () => {
    onUpdateTask && onUpdateTask({ ...baseTask, time:assignTime });
    onClose();
  };

  return (
    <Modal title="Tâche" onClose={onClose}>
      <div style={{ marginBottom:12 }}>
        <div style={{ fontSize:18,fontWeight:800,color:T.text,marginBottom:4,textDecoration:taskModal.done?"line-through":"none" }}>{taskModal.title}</div>
        <div style={{ fontSize:13,color:T.textSoft,marginBottom:2 }}>{m.name}</div>
        <div style={{ fontSize:12,color:imp.color,fontWeight:700 }}>{imp.icon} {imp.label}</div>
      </div>
      <div style={{ marginBottom:12 }}>
        <div style={{ fontSize:11,color:T.textSoft,marginBottom:6 }}>{taskModal.noTime?"Assigner une heure":"Modifier l'heure"}</div>
        <div style={{ display:"flex",gap:8,alignItems:"center" }}>
          <TimePicker value={assignTime} onChange={setAssignTime} style={{flex:1}} />
          <button onClick={saveTime} style={btnStyle(TYPE_COLORS.task,{padding:"8px 14px",fontSize:12})}>OK</button>
        </div>
      </div>
      <button onClick={()=>{ onToggleTask(taskModal.taskId); onClose(); }} style={btnStyle(taskModal.done?T.textSoft:T.mint,{width:"100%",marginBottom:8})}>
        {taskModal.done?"↩ Marquer comme à faire":"✓ Marquer comme terminée"}
      </button>
      {onAddReminder && <button onClick={()=>{ onAddReminder({type:"task", item:taskModal, date:taskModal.date, time:taskModal.time}); onClose(); }} style={btnStyle(T.coral,{width:"100%",marginBottom:8})}>⏰ Ajouter un rappel</button>}
      <button onClick={onClose} style={btnStyle(T.sandDark,{width:"100%",color:T.textMid})}>Fermer</button>
    </Modal>
  );
}


// ══════════════════════════════════════════════════════════════
// 🚗 TEMPS DE TRAJET
// ══════════════════════════════════════════════════════════════

// Adresse de départ par défaut (à personnaliser dans les paramètres)
const DEFAULT_HOME = "Rennes, France";

async function fetchTravelTime(destination, origin=DEFAULT_HOME) {
  if (!destination?.trim()) return null;
  try {
    // Geocode destination
    const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(destination+", Rennes, France")}&format=json&limit=1`);
    const geoData = await geoRes.json();
    if (!geoData[0]) return null;
    const { lon: destLon, lat: destLat } = geoData[0];

    // Geocode origin
    const geoOrig = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(origin)}&format=json&limit=1`);
    const geoOrigData = await geoOrig.json();
    if (!geoOrigData[0]) return null;
    const { lon: origLon, lat: origLat } = geoOrigData[0];

    // Get route via OSRM (free, no API key needed)
    const routeRes = await fetch(`https://router.project-osrm.org/route/v1/driving/${origLon},${origLat};${destLon},${destLat}?overview=false`);
    const routeData = await routeRes.json();
    if (routeData.code !== "Ok") return null;
    const durationMin = Math.ceil(routeData.routes[0].duration / 60);
    return durationMin;
  } catch { return null; }
}

function TravelBadge({ minutes, loading }) {
  if (loading) return <span style={{ fontSize:10,color:T.textSoft,background:T.sandDark,borderRadius:4,padding:"2px 6px" }}>🚗 calcul…</span>;
  if (!minutes) return null;
  return <span style={{ fontSize:10,fontWeight:700,color:"#E07B39",background:"#E07B3918",borderRadius:4,padding:"2px 6px" }}>🚗 {minutes} min</span>;
}

// ── Week helpers ───────────────────────────────────────────────
function getMonday(offset=0) {
  const d = new Date();
  const day = d.getDay();
  const diff = (day===0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff + offset*7);
  d.setHours(0,0,0,0);
  return d;
}
function fmtDate(d) {
  return d.toLocaleDateString("fr-FR", { day:"numeric", month:"short" });
}

function PlanningView({ events, onAdd, onUpdate, onDelete, appts=[], fiches=[], onOpenFiche, onUpdateFiche, tasks=[], onToggleTask, onAddTask, onAddAppt, onUpdateTask, onAddCompanion, onSwitchTab, activeUser=null, addReminder, deleteReminder, onSetReminderTarget, themes=DEFAULT_THEMES, onAddTheme, mealEvents=[], claims=[], onCompleteClaim, onScoreUpdate, reminders=[] }) {
  const [showAdd, setShowAdd]   = useState(false);
  const [addType, setAddType]   = useState(null); // null | "event" | "task" | "appt"
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY_EV);
  const [editForm, setEditForm] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState(() => window.innerWidth < 640 ? "list" : "week");
  const [quickTask, setQuickTask] = useState({ title:"", member:"olivier", date:"", time:"", importance:2, recurrence:"none", recurrenceEnd:"", recurrenceInterval:1 });
  const [quickAppt, setQuickAppt] = useState({ date:"", time:"", title:"", specialist:"", member:activeUser||MEMBERS[0].id, notes:"", recurrence:"none", recurrenceEnd:"", recurrenceInterval:1, customDates:[""] });

  const monday = getMonday(weekOffset);
  const sunday = new Date(monday.getTime() + 6*864e5);
  const isThisWeek = weekOffset === 0;
  const weekLabel = isThisWeek ? "Cette semaine"
    : weekOffset === 1 ? "Semaine prochaine"
    : weekOffset === -1 ? "Semaine derniere"
    : `${fmtDate(monday)} - ${fmtDate(sunday)}`;

  const weekDates = DAYS.map((_, di) => new Date(monday.getTime() + di*864e5));
  const todayStr = new Date().toDateString();

  const handleAdd = () => {
    if (!form.title.trim()) return;
    onAdd({ ...form, id:Date.now() });
    setShowAdd(false); setForm(EMPTY_EV);
  };

  const openEdit = (ev) => {
    const base = events.find(e => String(ev.id).startsWith(String(e.id)));
    setEditing(base || ev);
    setEditForm({ ...base||ev });
  };

  const handleSave = () => {
    if (!editForm.title.trim()) return;
    onUpdate(editForm);
    setEditing(null);
  };

  const [filterMembers, setFilterMembers] = useState(()=>activeUser?[activeUser]:[]);
  const [filterType, setFilterType] = useState("all");
  const [filterDay, setFilterDay] = useState(null);
  const [memberMenuOpen, setMemberMenuOpen] = useState(false);

  const toggleMember = (id) => setFilterMembers(prev => prev.includes(id) ? prev.filter(m=>m!==id) : [...prev, id]);

  // Sync with activeUser changes
  useEffect(()=>{ setFilterMembers(activeUser?[activeUser]:[]); }, [activeUser]);
  // Convert RDV appts to planning events for the current week
  const apptEvents = appts.flatMap(a => {
    const occurrences = expandRecurringAppt(a);
    return occurrences.flatMap(occ => {
      const d = new Date(occ.date + "T00:00:00");
      const mondayTime = monday.getTime();
      const diffDays = Math.round((d.getTime() - mondayTime) / 864e5);
      if (diffDays < 0 || diffDays > 6) return [];
      const evs = [{
        id: "appt_" + occ.id,
        day: diffDays,
        time: occ.time,
        title: occ.title,
        member: occ.member,
        members: occ.occMembers || normalizeMembers(occ.members||occ.member),
        type: "health",
        isAppt: true,
        specialist: occ.specialist,
        travelTime: occ.travelTime || a.travelTime || 20,
        occKey: occ.occKey,
      }];
      // Add travel bloc before appt — skip if atHome
      const isAtHome = occ.atHome || a.atHome || (occ.travelTime===0) || (a.travelTime===0);
      const travel = occ.travelTime || a.travelTime || 20;
      if (occ.time && !isAtHome && travel > 0) {
        const [h, m] = occ.time.split(":").map(Number);
        const totalMins = h*60 + m - travel;
        if (totalMins >= 0) {
          const th = String(Math.floor(totalMins/60)).padStart(2,"0");
          const tm = String(totalMins%60).padStart(2,"0");
          evs.unshift({
            id: "travel_" + occ.id,
            day: diffDays,
            time: `${th}:${tm}`,
            title: `🚗 Trajet — ${occ.title}`,
            member: occ.member,
            type: "travel",
            isTravel: true,
            travelTime: travel,
          });
        }
      }
      return evs;
    });
  });

  const [taskModal, setTaskModal] = useState(null);
  const [claimModal, setClaimModal] = useState(null); // task being viewed/checked
  const [apptModal, setApptModal] = useState(null); // appt being viewed from planning

  // Convert tasks with dates to planning events for current week
  const taskEvents = tasks.flatMap(t => {
    if (!t.date) return [];
    const expandedTasks = expandRecurringTask(t);
    return expandedTasks.flatMap(et => {
      const d = new Date(et.date + "T00:00:00");
      const diffDays = Math.round((d.getTime() - monday.getTime()) / 864e5);
      if (diffDays < 0 || diffDays > 6) return [];
      return [{
        id: "task_" + et.id,
        day: diffDays,
        time: et.time || "",
        noTime: !et.time,
        title: et.title,
        member: normalizeMembers(et.members||et.member)[0],
        members: normalizeMembers(et.members||et.member),
        type: "task",
        isTask: true,
        taskId: et.id,
        done: et.done,
        importance: et.importance || 2,
      }];
    });
  });

  // Build claim events (missions taken by members)
  const claimEvents = claims.filter(c=>!c.done).map(c => ({
    id: "claim_" + c.id,
    claimId: c.id,
    day: (() => {
      const claimDate = new Date(c.date+"T00:00:00");
      const diff = Math.round((claimDate - new Date(weekDates[0])) / 864e5);
      return diff;
    })(),
    time: "08:00",
    title: c.icon + " " + c.title,
    member: c.memberId,
    members: [c.memberId],
    type: "home",
    isClaim: true,
    claim: c,
    done: c.done,
  })).filter(c => c.day >= 0 && c.day <= 6);

  // Convert active reminders to planning events for current week
  const reminderEvents = reminders.filter(r=>!r.done).flatMap(r => {
    if (!r.targetDate) return [];
    const d = new Date(r.targetDate + "T00:00:00");
    const diffDays = Math.round((d.getTime() - monday.getTime()) / 864e5);
    if (diffDays < 0 || diffDays > 6) return [];
    return [{
      id: "reminder_" + r.id,
      day: diffDays,
      time: r.targetTime || "",
      title: r.title,
      member: normalizeMembers(r.members||r.member)[0],
      members: normalizeMembers(r.members||r.member),
      type: "reminder",
      isReminder: true,
      reminderId: r.id,
    }];
  });

  const expandedEvents = [
    ...events.flatMap(ev => expandEventForWeek(ev)),
    ...apptEvents,
    ...taskEvents,
    ...(mealEvents||[]),
    ...claimEvents,
    ...reminderEvents,
  ].filter(ev => ev.isDaily && (!ev.members || ev.members.length===0) ? true : filterMembers.length === 0 || normalizeMembers(ev.members||ev.member).some(m => filterMembers.includes(m)))
  .filter(ev => filterType === "all" || ev.type === filterType);

  // Exercices récurrents (fréquence x/semaine) issus des fiches, pour la semaine affichée
  const mondayStr = toLocalISO(monday);
  const sundayStr = toLocalISO(sunday);
  const weeklyExercises = fiches.flatMap(fi =>
    (fi.exercises||[]).filter(ex => ex.frequency > 0 && (filterMembers.length===0 || filterMembers.includes(fi.member))).map(ex => ({
      ...ex, ficheId: fi.id, ficheTitle: fi.title, member: fi.member,
      doneThisWeek: (ex.completions||[]).filter(d => d >= mondayStr && d <= sundayStr).length,
    }))
  );
  const logExerciseCompletion = (ficheId, exId) => {
    const fiche = fiches.find(fi=>fi.id===ficheId);
    if (!fiche || !onUpdateFiche) return;
    const today = todayISO();
    const exercises = fiche.exercises.map(e=>e.id===exId?{...e, completions:[...(e.completions||[]), today]}:e);
    onUpdateFiche({ ...fiche, exercises, updatedAt: today });
  };

  return (
    <div>
      {/* Member filter — multi-select panel */}
      <div style={{ position:"relative", marginBottom:8 }}>
        <button onClick={()=>setMemberMenuOpen(o=>!o)} style={{ display:"flex", alignItems:"center", gap:8, background:T.sandDark, border:"1px solid "+T.sandDark, borderRadius:8, padding:"8px 12px", cursor:"pointer", fontSize:12, fontWeight:600, color:T.text, width:"100%" }}>
          <span style={{ flex:1, textAlign:"left" }}>
            {filterMembers.length===0 ? "👥 Toute la famille" : filterMembers.map(id=>memberOf(id).name).join(", ")}
          </span>
          {filterMembers.length>0 && (
            <span onClick={e=>{e.stopPropagation();setFilterMembers([]);}} style={{ fontSize:11, color:T.textSoft, background:T.sandDark, borderRadius:4, padding:"1px 6px" }}>✕</span>
          )}
          <span style={{ color:T.textSoft }}>{memberMenuOpen?"▲":"▼"}</span>
        </button>
        {memberMenuOpen && (
          <div style={{ position:"absolute", top:"110%", left:0, right:0, background:T.sandDark, borderRadius:10, boxShadow:"0 4px 20px rgba(30,42,58,0.18)", zIndex:100, overflow:"hidden", border:"1px solid "+T.sandDark }}>
            <div style={{ padding:"8px 12px", borderBottom:"1px solid "+T.sandDark }}>
              <button onClick={()=>{setFilterMembers([]); setMemberMenuOpen(false);}} style={{ fontSize:12, color:T.text, background:"transparent", border:"none", cursor:"pointer", fontWeight:700 }}>✓ Toute la famille</button>
            </div>
            {MEMBERS.map(m=>(
              <label key={m.id} onClick={()=>toggleMember(m.id)} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", cursor:"pointer", background:filterMembers.includes(m.id)?m.color+"12":"transparent", borderBottom:"1px solid "+T.sandDark }}>
                <div style={{ width:18, height:18, borderRadius:4, border:"2px solid "+(filterMembers.includes(m.id)?m.color:T.sandDark), background:filterMembers.includes(m.id)?m.color:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {filterMembers.includes(m.id) && <span style={{color:T.white,fontSize:11,fontWeight:900}}>✓</span>}
                </div>
                <div style={{ width:28, height:28, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:T.white }}>{m.avatar}</div>
                <span style={{ fontSize:14, fontWeight:600, color:filterMembers.includes(m.id)?m.color:T.text }}>{m.name}</span>
              </label>
            ))}
            <div style={{ padding:"10px 12px" }}>
              <button onClick={()=>setMemberMenuOpen(false)} style={{ fontSize:12, color:T.coral, background:"transparent", border:"none", cursor:"pointer", fontWeight:700, width:"100%", textAlign:"center" }}>Fermer</button>
            </div>
          </div>
        )}
      </div>

      {/* 🏋️ Exercices récurrents de la semaine (issus des fiches) */}
      {weeklyExercises.length > 0 && (
        <div style={cardStyle({ marginBottom:12, background:T.sandDark })}>
          <div style={{ fontSize:12, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>🏋️ Exercices de la semaine</div>
          {weeklyExercises.map(ex => (
            <div key={ex.ficheId+"_"+ex.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"6px 0", borderBottom:"1px solid "+T.sand }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{ex.title}</div>
                <div style={{ fontSize:11, color:T.textSoft }}>{ex.ficheTitle} · {memberOf(ex.member).name} · {ex.doneThisWeek}/{ex.frequency} cette semaine</div>
              </div>
              <button onClick={()=>logExerciseCompletion(ex.ficheId, ex.id)} disabled={ex.doneThisWeek>=ex.frequency} style={{ ...btnStyle(ex.doneThisWeek>=ex.frequency?T.mint:T.coral,{fontSize:11,padding:"5px 10px",flexShrink:0}), opacity:ex.doneThisWeek>=ex.frequency?0.6:1 }}>
                {ex.doneThisWeek>=ex.frequency ? "✓ Fait" : "+1"}
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>Planning</h2>
        <button onClick={()=>{ setShowAdd(s=>!s); if(!addType) setAddType("event"); }} style={btnStyle(T.coral)}>{showAdd ? "− Fermer" : "+ Ajouter"}</button>
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:T.sandDark, borderRadius:10, border:"1px solid "+T.sandDark, padding:"8px 12px", marginBottom:16 }}>
        <button onClick={()=>setWeekOffset(o=>o-1)} style={{ background:"transparent", border:"none", cursor:"pointer", fontSize:20, color:T.textMid, padding:"0 6px" }}>{"<"}</button>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{weekLabel}</div>
          <div style={{ fontSize:11, color:T.textSoft }}>{fmtDate(monday)} - {fmtDate(sunday)}</div>
        </div>
        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
          {!isThisWeek && <button onClick={()=>setWeekOffset(0)} style={{ fontSize:11, color:T.coral, background:"transparent", border:"1px solid "+T.coral, borderRadius:6, cursor:"pointer", padding:"3px 8px", fontWeight:700 }}>Auj.</button>}
          <button onClick={()=>setWeekOffset(o=>o+1)} style={{ background:"transparent", border:"none", cursor:"pointer", fontSize:20, color:T.textMid, padding:"0 6px" }}>{">"}</button>
        </div>
      </div>

      {/* UNIFIED ADD PANEL — un seul bloc, on choisit le type à l'intérieur */}
      {showAdd && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ display:"flex", gap:6, marginBottom:14 }}>
            <button onClick={()=>setAddType("event")} style={{ flex:1, padding:"9px 6px", borderRadius:8, border:"2px solid "+(addType==="event"?T.text:T.sandDark), background:addType==="event"?T.text+"15":"transparent", color:addType==="event"?T.text:T.textSoft, fontSize:12, fontWeight:700, cursor:"pointer" }}>📅 Événement</button>
            <button onClick={()=>setAddType("task")} style={{ flex:1, padding:"9px 6px", borderRadius:8, border:"2px solid "+(addType==="task"?T.coral:T.sandDark), background:addType==="task"?T.coral+"15":"transparent", color:addType==="task"?T.coral:T.textSoft, fontSize:12, fontWeight:700, cursor:"pointer" }}>✓ Tâche</button>
            <button onClick={()=>setAddType("appt")} style={{ flex:1, padding:"9px 6px", borderRadius:8, border:"2px solid "+(addType==="appt"?T.mint:T.sandDark), background:addType==="appt"?T.mint+"15":"transparent", color:addType==="appt"?T.mint:T.textSoft, fontSize:12, fontWeight:700, cursor:"pointer" }}>🏥 RDV</button>
          </div>
          {addType==="event" && <div style={{ fontSize:11, color:T.textSoft, marginBottom:10, lineHeight:1.4 }}>Une occasion ponctuelle ou récurrente (sortie, activité...) — sans notion de rendez-vous chez un spécialiste.</div>}
          {addType==="task" && <div style={{ fontSize:11, color:T.textSoft, marginBottom:10, lineHeight:1.4 }}>Une action à faire, avec un niveau de priorité (matrice Eisenhower).</div>}
          {addType==="appt" && <div style={{ fontSize:11, color:T.textSoft, marginBottom:10, lineHeight:1.4 }}>Un rendez-vous chez un spécialiste ou un lieu précis — avec trajet, prix, fiche associée.</div>}
        </div>
      )}

      {/* EVENT FORM */}
      {showAdd && addType==="event" && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:12,fontWeight:800,color:T.text,marginBottom:10 }}>📅 Nouvel événement</div>
          {form.recurrence !== "custom" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
              <select value={form.day} onChange={e=>setForm(f=>({...f,day:+e.target.value}))} style={inputStyle}>{DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}</select>
              <TimePicker value={form.time} onChange={v=>setForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
            </div>
          )}
          {form.recurrence === "custom" && <TimePicker value={form.time} onChange={v=>setForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />}
          <input placeholder="Titre" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <MemberPicker value={form.members||form.member} onChange={v=>setForm(f=>({...f,members:v,member:v[0]}))} />
            <ThemePicker value={form.type} onChange={v=>setForm(f=>({...f,type:v}))} themes={themes} onAddTheme={onAddTheme} label="" />
          </div>
          <RecurrenceFields form={form} setForm={setForm} mode="event" />
          <div style={{ marginBottom:10 }}>
<div style={{ display:"flex", gap:8, marginBottom:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Durée</div>
                <select value={form.duration||60} onChange={e=>setForm(f=>({...f,duration:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                  <option value={0}>Non définie</option>
                  {[15,20,30,45,60,90,120,180].map(d=><option key={d} value={d}>{d<60?d+" min":d===60?"1h":d===90?"1h30":d===120?"2h":"3h"}</option>)}
                </select>
              </div>
            </div>
            <TravelTimeField value={form.travelTime||0} onChange={v=>setForm(f=>({...f,travelTime:v}))} address={form.title} fiches={fiches} label="Temps de trajet (optionnel)" />
          </div>
          <ReminderAttacher reminders={form.linkedReminders||[]} onChange={v=>setForm(f=>({...f,linkedReminders:v}))} itemDate={weekDates[form.day] ? toLocalISO(weekDates[form.day]) : undefined} itemTime={form.time} itemTitle={form.title} members={normalizeMembers(form.members||form.member)} onAddReminder={addReminder} onDeleteReminder={deleteReminder} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleAdd} style={btnStyle(T.coral)}>Confirmer</button>
            <button onClick={()=>{setShowAdd(false);setAddType(null);}} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* TASK FORM */}
      {showAdd && addType==="task" && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:12,fontWeight:800,color:T.coral,marginBottom:10 }}>✓ Nouvelle tâche</div>
          <input placeholder="Titre de la tâche" value={quickTask.title} onChange={e=>setQuickTask(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ marginBottom:8 }}>
            <MemberPicker value={quickTask.members||quickTask.member} onChange={v=>setQuickTask(f=>({...f,members:v,member:v[0]}))} />
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div>
              <input type="date" value={quickTask.date} onChange={e=>setQuickTask(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure (optionnel)</div>
            <TimePicker value={quickTask.time} onChange={v=>setQuickTask(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
          </div>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>Priorité</div>
            <select value={quickTask.importance||2} onChange={e=>setQuickTask(f=>({...f,importance:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {EISENHOWER.map(q=><option key={q.value} value={q.value}>{q.icon} {q.label}</option>)}
            </select>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>{
              if (!quickTask.title.trim() || !quickTask.date) return;
              onAddTask({...quickTask, id:Date.now(), done:false});
              setQuickTask({ title:"",member:"olivier",date:"",time:"",importance:2,recurrence:"none",recurrenceEnd:"",recurrenceInterval:1 });
              setShowAdd(false); setAddType(null);
            }} style={btnStyle(T.coral)}>Confirmer</button>
            <button onClick={()=>{setShowAdd(false);setAddType(null);}} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* REMINDER FORM */}
      {showAdd && addType==="reminder" && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:12,fontWeight:800,color:T.coral,marginBottom:10 }}>⏰ Nouveau rappel</div>
          <ReminderForm onAdd={addReminder} onClose={()=>{setShowAdd(false);setAddType(null);}} defaultMember={activeUser||"olivier"} />
        </div>
      )}

      {/* APPT FORM */}
      {showAdd && addType==="appt" && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:12,fontWeight:800,color:T.mint,marginBottom:10 }}>🏥 Nouveau rendez-vous</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div><input type="date" value={quickAppt.date} onChange={e=>setQuickAppt(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
            <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure</div><TimePicker value={quickAppt.time} onChange={v=>setQuickAppt(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} /></div>
          </div>
          <input placeholder="Titre" value={quickAppt.title} onChange={e=>setQuickAppt(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ position:"relative", marginBottom:8 }}>
            <input list="specialists-list-planning" placeholder="Fiche (nom du praticien/lieu)" value={quickAppt.specialist} onChange={e=>setQuickAppt(f=>({...f,specialist:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            <datalist id="specialists-list-planning">
              {fiches.filter(fi=>fi.contact?.name).map(fi=><option key={fi.id} value={fi.contact.name}>{fi.title}</option>)}
            </datalist>
          </div>
          <div style={{ marginBottom:8 }}><MemberPicker value={quickAppt.members||quickAppt.member} onChange={v=>setQuickAppt(f=>({...f,members:v,member:v[0]}))} /></div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>{
              if (!quickAppt.title.trim()||!quickAppt.date) return;
              onAddAppt({...quickAppt, id:Date.now()});
              setQuickAppt({ date:"",time:"",title:"",specialist:"",member:activeUser||MEMBERS[0].id,notes:"",recurrence:"none",recurrenceEnd:"",recurrenceInterval:1,customDates:[""] });
              setShowAdd(false); setAddType(null);
            }} style={btnStyle(T.mint)}>Confirmer</button>
            <button onClick={()=>{setShowAdd(false);setAddType(null);}} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* View toggle + day pills */}
      <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Vue :</span>
        <select value={viewMode} onChange={e=>setViewMode(e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="list">☰ Liste</option>
          <option value="week">📅 Semaine</option>
          <option value="timeline">🕐 Horaire</option>
        </select>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Jour :</span>
        <select value={filterDay===null?"":filterDay} onChange={e=>setFilterDay(e.target.value===""?null:+e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="">Toute la semaine</option>
          {DAYS.map((day,di)=>{
            const colDate = weekDates[di];
            const isToday = colDate.toDateString()===todayStr;
            return <option key={di} value={di}>{day} {colDate.getDate()}{isToday?" ★":""}</option>;
          })}
        </select>
      </div>

      {/* TIMELINE VIEW */}
      {viewMode==="timeline" && (() => {
        const days = filterDay !== null ? [filterDay] : DAYS.map((_,i)=>i);
        const START_HOUR = 7;
        const END_HOUR = 22;
        const SLOT_H = 30; // minutes per slot
        const TOTAL_SLOTS = (END_HOUR - START_HOUR) * 2; // 30min slots
        const SLOT_PX = 32; // px per 30min

        const getTop = (time) => {
          if (!time) return 0;
          const [h, m] = time.split(":").map(Number);
          return ((h - START_HOUR) * 60 + m) / 30 * SLOT_PX;
        };

        const getHeight = (duration=60) => duration / 30 * SLOT_PX;

        return (
          <div style={{ overflowX: days.length > 1 ? "auto" : "visible" }}>
            <div style={{ display:"flex", minWidth: days.length > 1 ? days.length * 120 + 40 : "auto" }}>
              {/* Time axis */}
              <div style={{ width:38, flexShrink:0, paddingTop:28 }}>
                {Array.from({length: END_HOUR - START_HOUR + 1}, (_, i) => (
                  <div key={i} style={{ height: SLOT_PX*2, display:"flex", alignItems:"flex-start", justifyContent:"flex-end", paddingRight:6, boxSizing:"border-box" }}>
                    <span style={{ fontSize:10, color:T.textSoft, fontWeight:600, lineHeight:1 }}>{String(START_HOUR+i).padStart(2,"0")}h</span>
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {days.map(di => {
                const colDate = weekDates[di];
                const isToday = colDate.toDateString() === todayStr;
                const dayEvs = expandedEvents.filter(e => e.day===di && e.time && !e.noTime);
                const now = new Date();
                const nowTop = isToday ? getTop(`${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`) : null;

                return (
                  <div key={di} style={{ flex:1, minWidth: days.length > 1 ? 110 : "auto" }}>
                    {/* Day header */}
                    <div style={{ textAlign:"center", padding:"4px 2px 6px", fontWeight:800, fontSize:12, color:isToday?T.coral:T.navyMid, borderBottom:`2px solid ${isToday?T.coral:T.sandDark}`, marginBottom:0 }}>
                      {DAYS[di]} {colDate.getDate()}
                      {isToday && <span style={{ display:"block", fontSize:9, color:T.coral }}>Auj.</span>}
                    </div>

                    {/* Time grid */}
                    <div style={{ position:"relative", height: TOTAL_SLOTS * SLOT_PX }}>
                      {/* Hour lines */}
                      {Array.from({length: END_HOUR - START_HOUR}, (_, i) => (
                        <React.Fragment key={i}>
                          <div style={{ position:"absolute", left:0, right:0, top: i*SLOT_PX*2, borderTop:`1px solid ${T.sandDark}`, zIndex:1 }} />
                          <div style={{ position:"absolute", left:0, right:0, top: i*SLOT_PX*2+SLOT_PX, borderTop:`1px dashed ${T.sandDark}`, zIndex:1, opacity:0.5 }} />
                        </React.Fragment>
                      ))}

                      {/* Current time indicator */}
                      {nowTop !== null && nowTop >= 0 && nowTop <= TOTAL_SLOTS * SLOT_PX && (
                        <div style={{ position:"absolute", left:0, right:0, top:nowTop, zIndex:10, display:"flex", alignItems:"center" }}>
                          <div style={{ width:7,height:7,borderRadius:"50%",background:T.coral,flexShrink:0 }} />
                          <div style={{ flex:1,height:1.5,background:T.coral }} />
                        </div>
                      )}

                      {/* Events */}
                      {dayEvs.map(ev => {
                        const color = (themes||DEFAULT_THEMES).find(t=>t.id===ev.type)?.color || TYPE_COLORS[ev.type] || T.navyMid;
                        const top = getTop(ev.time);
                        if (top < 0 || top > TOTAL_SLOTS * SLOT_PX) return null;
                        const height = Math.max(SLOT_PX, ev.isTravel ? SLOT_PX : getHeight(60));
                        const m = memberOf(ev.members?.[0]||ev.member);
                        return (
                          <div key={ev.id} onClick={()=>ev.isAppt?setApptModal(ev):ev.isTask?setTaskModal(ev):openEdit(ev)}
                            style={{ position:"absolute", left:2, right:2, top, height: Math.min(height, TOTAL_SLOTS*SLOT_PX-top), background:color+(ev.isTravel?"22":"33"), border:`1.5px solid ${color}`, borderRadius:6, padding:"2px 5px", cursor:"pointer", zIndex:5, overflow:"hidden", boxSizing:"border-box" }}>
                            <div style={{ fontSize:10, fontWeight:700, color, lineHeight:1.3, wordBreak:"break-word" }}>
                              {ev.isTravel ? "🚗" : ""} {ev.title}
                            </div>
                            {height >= SLOT_PX*1.5 && <div style={{ fontSize:9, color, opacity:0.8 }}>{ev.time} · {m.name}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* LIST VIEW */}
      {(viewMode==="list") && (
        <div>
          {DAYS.map((day,di) => {
            if (filterDay !== null && filterDay !== di) return null;
            const colDate = weekDates[di];
            const isToday = colDate.toDateString() === todayStr;
            const dayEvs = expandedEvents.filter(e=>e.day===di).sort((a,b)=>a.time.localeCompare(b.time));
            if (dayEvs.length === 0 && filterDay === null) return null;
            return (
              <div key={di} style={{ marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:800, color:isToday?T.coral:T.navyMid, marginBottom:6, display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:6,height:6,borderRadius:"50%",background:isToday?T.coral:T.navyMid,display:"inline-block" }} />
                  {day} {colDate.toLocaleDateString("fr-FR",{day:"numeric",month:"long"})}
                  {isToday && <span style={{ fontSize:10,background:T.coral,color:T.white,borderRadius:4,padding:"1px 6px" }}>Aujourd'hui</span>}
                </div>
                {(() => {
                  const unscheduled = dayEvs.filter(ev=>ev.isTask && ev.noTime && !ev.done);
                  const scheduled   = dayEvs.filter(ev=>!(ev.isTask && ev.noTime && !ev.done)).sort((a,b)=>a.time.localeCompare(b.time));
                  return <>
                    {/* Unscheduled tasks block */}
                    {unscheduled.length>0 && (
                      <div style={{ background:T.sand, borderRadius:8, padding:"8px 10px", marginBottom:8, border:"1px dashed "+TYPE_COLORS.task }}>
                        <div style={{ fontSize:10,fontWeight:800,color:TYPE_COLORS.task,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6 }}>
                          À dispatcher ({unscheduled.length})
                        </div>
                        {unscheduled.map(ev => {
                          const m = memberOf(ev.member);
                          const imp = importanceOf(ev.importance||2);
                          return (
                            <div key={ev.id} onClick={()=>ev.isTask?setTaskModal(ev):ev.isAppt?setApptModal(ev):openEdit(ev)} style={{ background:T.sandDark, border:"1.5px dashed "+(ev.isDaily || (!ev.member && (!ev.members || ev.members.length===0))?"#D4A843":TYPE_COLORS.task), borderRadius:7, padding:"7px 10px", marginBottom:5, cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
                              <div onClick={e=>{e.stopPropagation();onToggleTask(ev.taskId);}} style={{ width:18,height:18,borderRadius:4,border:"2px solid "+TYPE_COLORS.task,background:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }} />
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:12,fontWeight:600,color:T.text }}>{ev.title}</div>
                                <div style={{ fontSize:10,color:T.textSoft }}>{m.name} · <span style={{color:imp.color}}>{imp.icon}</span></div>
                              </div>
                              <span style={{ fontSize:10,color:TYPE_COLORS.task,fontWeight:700,background:TYPE_COLORS.task+"15",borderRadius:4,padding:"2px 6px" }}>+ heure</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Scheduled events */}
                    {scheduled.map(ev => {
                      const m = memberOf(ev.member);
                      const themeList = arguments[0]?.themes || DEFAULT_THEMES;
  const color = themeList.find(t=>t.id===ev.type)?.color || TYPE_COLORS[ev.type] || T.navyMid;
                      if (ev.isTask) {
                        const imp = importanceOf(ev.importance||2);
                        return (
                          <div key={ev.id} onClick={()=>setTaskModal(ev)} style={{ background:ev.done?"transparent":color+"12", border:"1.5px dashed "+(ev.isDaily || (!ev.member && (!ev.members || ev.members.length===0))?"#D4A843":color), borderRadius:8, padding:"8px 12px", marginBottom:6, cursor:"pointer", display:"flex", alignItems:"center", gap:10, opacity:ev.done?0.5:1 }}>
                            <div onClick={e=>{e.stopPropagation();onToggleTask(ev.taskId);}} style={{ width:20,height:20,borderRadius:4,border:"2px solid "+(ev.done?T.mint:color),background:ev.done?T.mint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                              {ev.done && <span style={{color:T.white,fontSize:11,fontWeight:900}}>✓</span>}
                            </div>
              <div style={{ flex:1, display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13,fontWeight:600,color:ev.isClaim?"#D4A843":T.text,textDecoration:ev.done?"line-through":"none" }}>{ev.title}</div>
                                <div style={{ fontSize:11,color:T.textSoft,marginTop:1 }}>{ev.time} · {m.name} · <span style={{color:imp.color}}>{imp.icon} {imp.label}</span>{ev.isClaim&&<span style={{color:"#D4A843",fontWeight:700}}> · +{ev.claim?.points}pts</span>}</div>
                              </div>
                              {ev.isClaim && !ev.done && (
                                <button onClick={e=>{ e.stopPropagation(); onCompleteClaim&&onCompleteClaim(ev.claim.id); onScoreUpdate&&onScoreUpdate(prev=>{ const cur=prev[ev.claim.memberId]||{points:0,history:[],completedChallenges:[]}; return {...prev,[ev.claim.memberId]:{...cur,points:(cur.points||0)+ev.claim.points,history:[...(cur.history||[]),{title:ev.claim.title,pts:ev.claim.points,importance:2,date:ev.claim.date,isDaily:true}]}}; }); }} style={{ flexShrink:0,background:"#D4A843",border:"none",borderRadius:8,padding:"5px 10px",cursor:"pointer",fontSize:13,fontWeight:800,color:"white" }}>✓</button>
                              )}
                            </div>
                            <span style={{fontSize:11,color:color,fontWeight:700}}>{ev.isClaim?"Mission":"Tâche"}</span>
                          </div>
                        );
                      }
                      if (ev.isReminder) {
                        return (
                          <div key={ev.id} onClick={()=>onSwitchTab&&onSwitchTab("rappels")} style={{ background:"#D4A84315", borderLeft:"3px solid #D4A843", borderRadius:8, padding:"10px 12px", marginBottom:6, cursor:onSwitchTab?"pointer":"default" }}>
                            <div style={{ fontSize:13,fontWeight:700,color:T.text }}>⏰ {ev.title}</div>
                            <div style={{ fontSize:11,color:T.textSoft,marginTop:2 }}>{ev.time && ev.time+" · "}{m.name} · Rappel</div>
                          </div>
                        );
                      }
                      return (
                        <div key={ev.id} onClick={()=>ev.isClaim?setTaskModal(ev):ev.isAppt?setApptModal(ev):openEdit(ev)} style={{ background:ev.isClaim?"#D4A84315":color+"18", borderLeft:`3px solid ${ev.isClaim?"#D4A843":color}`, borderRadius:8, padding:"10px 12px", marginBottom:6, cursor:"pointer" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:13,fontWeight:700,color:ev.isClaim?"#D4A843":T.text }}>{ev.title}</div>
                              <div style={{ fontSize:11,color:T.textSoft,marginTop:2 }}>{ev.time} · {m.name}{ev.isClaim&&<span style={{color:"#D4A843",fontWeight:700}}> · +{ev.claim?.points}pts</span>}{ev.isAppt&&" · RDV"}{ev.duration>0&&<span style={{color:T.textSoft}}> · ⏱{ev.duration<60?ev.duration+"min":ev.duration===60?"1h":ev.duration===90?"1h30":"2h"}</span>}{ev.recurrence&&ev.recurrence!=="none"&&" ↻"}</div>
                            </div>
                            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                              {ev.isClaim && !ev.done && (
                                <button onClick={e=>{ e.stopPropagation(); onCompleteClaim&&onCompleteClaim(ev.claim.id); onScoreUpdate&&onScoreUpdate(prev=>{ const cur=prev[ev.claim.memberId]||{points:0,history:[],completedChallenges:[]}; return {...prev,[ev.claim.memberId]:{...cur,points:(cur.points||0)+ev.claim.points,history:[...(cur.history||[]),{title:ev.claim.title,pts:ev.claim.points,importance:2,date:ev.claim.date,isDaily:true}]}}; }); }} style={{ background:"#D4A843",border:"none",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:13,fontWeight:800,color:"white" }}>✓</button>
                              )}
                              {ev.isAppt && (() => { const f = fiches.find(fi=>fi.member===ev.member&&ev.title.toLowerCase().includes(fi.title.toLowerCase().split(" ")[0])); return f?<span onClick={e=>{e.stopPropagation();onOpenFiche(f);}} style={{fontSize:10,background:T.mint+"33",color:T.mint,borderRadius:3,padding:"1px 5px",cursor:"pointer",fontWeight:700}}>Fiche</span>:null; })()}
                              <MemberAvatars members={ev.members||ev.member} size={24} />
                            </div>
                          </div>
                          {ev.isAppt && (() => { const lf = fiches.find(fi=>fi.member===ev.member&&ev.title.toLowerCase().includes(fi.title.toLowerCase().split(" ")[0])); const addr = lf?.contact?.address||ev.specialist; return addr ? <NavButtons address={addr} /> : null; })()}
                        </div>
                      );
                    })}
                  </>;
                })()}
                {dayEvs.length===0 && <div style={{ fontSize:12,color:T.textSoft,fontStyle:"italic",paddingLeft:12 }}>Rien ce jour</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* WEEK GRID VIEW */}
      {(viewMode==="week") && <div style={{ display:"grid", gridTemplateColumns:filterDay!==null?"1fr":`repeat(7,1fr)`, gap:6 }}>
        {DAYS.map((day,di) => {
          if (filterDay !== null && filterDay !== di) return null;
          const colDate = weekDates[di];
          const isToday = colDate.toDateString() === todayStr;
          const dayEvs = expandedEvents.filter(e=>e.day===di).sort((a,b)=>a.time.localeCompare(b.time));
          return (
            <div key={di} style={{ background:isToday ? T.coral+"11" : T.sandDark, borderRadius:10, border:"1px solid "+(isToday ? T.coral : T.sandDark), padding:filterDay!==null?"12px 14px":"8px 6px", minHeight:120 }}>
              <div style={{ fontSize:filterDay!==null?13:11, fontWeight:700, color:isToday ? T.coral : T.textSoft, marginBottom:2, textAlign:"center", letterSpacing:"0.05em" }}>{day}</div>
              <div style={{ fontSize:filterDay!==null?12:10, color:isToday ? T.coral : T.textSoft, textAlign:"center", marginBottom:6 }}>{colDate.toLocaleDateString("fr-FR",{day:"numeric",month:filterDay!==null?"long":"numeric"})}</div>
              {dayEvs.map(ev => {
                const m = memberOf(ev.member);
                const themeList = arguments[0]?.themes || DEFAULT_THEMES;
  const color = themeList.find(t=>t.id===ev.type)?.color || TYPE_COLORS[ev.type] || T.navyMid;
                if (ev.isTask) {
                  return (
                    <div key={ev.id} onClick={()=>setTaskModal(ev)} style={{ background:ev.done?"transparent":color+"12", border:"1.5px dashed "+(ev.isDaily || (!ev.member && (!ev.members || ev.members.length===0))?"#D4A843":color), borderRadius:4, padding:filterDay!==null?"6px 8px":"3px 5px", marginBottom:filterDay!==null?6:3, cursor:"pointer", opacity:ev.done?0.45:1 }}>
                      <div style={{ fontSize:filterDay!==null?11:9, display:"flex", alignItems:"center", gap:4 }}>
                        <span style={{color:ev.done?T.mint:color,fontWeight:900,fontSize:10}}>{ev.done?"✓":"○"}</span>
                        <span style={{color:T.textSoft}}>{ev.time}</span>
                      </div>
                      <div style={{ fontSize:filterDay!==null?12:10, fontWeight:600, color:T.text, lineHeight:1.2, textDecoration:ev.done?"line-through":"none" }}>{ev.title}</div>
                      <div style={{ fontSize:9, color:m.color, fontWeight:600, marginTop:1 }}>{m.name}</div>
                    </div>
                  );
                }
                if (ev.isReminder) {
                  return (
                    <div key={ev.id} onClick={()=>onSwitchTab&&onSwitchTab("rappels")} style={{ background:"#D4A84318", borderLeft:"3px solid #D4A843", borderRadius:4, padding:filterDay!==null?"8px 10px":"4px 5px", marginBottom:filterDay!==null?8:4, cursor:onSwitchTab?"pointer":"default" }}>
                      <div style={{ fontSize:filterDay!==null?11:9, color:T.textSoft, marginBottom:1 }}>{ev.time}</div>
                      <div style={{ fontSize:filterDay!==null?14:11, fontWeight:600, color:T.text, lineHeight:1.3 }}>⏰ {ev.title}</div>
                      <div style={{ fontSize:filterDay!==null?12:9, color:m.color, fontWeight:600, marginTop:2 }}>{m.name}</div>
                    </div>
                  );
                }
                return (
                  <div key={ev.id} onClick={()=>openEdit(ev)} style={{ background:color+"18", borderLeft:`3px solid ${color}`, borderRadius:4, padding:filterDay!==null?"8px 10px":"4px 5px", marginBottom:filterDay!==null?8:4, cursor:"pointer" }}>
                    <div style={{ fontSize:filterDay!==null?11:9, color:T.textSoft, marginBottom:1, display:"flex", justifyContent:"space-between" }}>
                      <span>{ev.time}</span>
                      {ev.recurrence && ev.recurrence!=="none" && <span style={{ color:T.gold }}>↻</span>}
                    </div>
                    <div style={{ fontSize:filterDay!==null?14:11, fontWeight:600, color:T.text, lineHeight:1.3 }}>{ev.title}</div>
                    <div style={{ fontSize:filterDay!==null?12:9, color:m.color, fontWeight:600, marginTop:2, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <span>{m.name}{ev.isAppt && <span style={{color:T.textSoft,fontWeight:400}}> · RDV</span>}</span>
                      {ev.isAppt && (() => { const f = fiches.find(fi => fi.member===ev.member && ev.title.toLowerCase().includes(fi.title.toLowerCase().split(" ")[0])); return f ? <span onClick={e=>{e.stopPropagation();onOpenFiche(f);}} style={{fontSize:9,background:T.mint+"33",color:T.mint,borderRadius:3,padding:"1px 4px",cursor:"pointer",fontWeight:700}}>Fiche</span> : null; })()}
                    </div>
                    {ev.isAppt && filterDay!==null && (() => { const lf = fiches.find(fi=>fi.member===ev.member&&ev.title.toLowerCase().includes(fi.title.toLowerCase().split(" ")[0])); const addr = lf?.contact?.address||ev.specialist; return addr ? <NavButtons address={addr} /> : null; })()}
                  </div>
                );
              })}
              {dayEvs.length===0 && <div style={{ fontSize:filterDay!==null?13:10, color:T.textSoft, textAlign:"center", marginTop:filterDay!==null?24:16 }}>Rien ce jour</div>}
            </div>
          );
        })}
      </div>}

      {taskModal && !taskModal.isClaim && <TaskTimeModal taskModal={taskModal} tasks={tasks} onUpdateTask={onUpdateTask} onToggleTask={onToggleTask} onClose={()=>setTaskModal(null)} onAddReminder={(target)=>onSetReminderTarget&&onSetReminderTarget(target)} />}
      {taskModal && taskModal.isClaim && (
        <Modal title="🎯 Mission" onClose={()=>setTaskModal(null)}>
          <div style={{ textAlign:"center", padding:"10px 0 20px" }}>
            <div style={{ fontSize:40, marginBottom:8 }}>{taskModal.claim.icon}</div>
            <div style={{ fontSize:18, fontWeight:800, color:T.text, marginBottom:4 }}>{taskModal.claim.title}</div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginBottom:16 }}>
              <Avatar member={taskModal.claim.memberId} size={20} />
              <span style={{ fontSize:13, color:T.textSoft }}>{memberOf(taskModal.claim.memberId).name}</span>
              <span style={{ fontSize:13, fontWeight:800, color:T.gold }}>+{taskModal.claim.points} pts</span>
            </div>
            <button onClick={()=>{
              onCompleteClaim(taskModal.claim.id);
              onScoreUpdate && onScoreUpdate(prev => {
                const cur = prev[taskModal.claim.memberId] || { points:0, history:[], completedChallenges:[] };
                const history = [...(cur.history||[]), { title:taskModal.claim.title, pts:taskModal.claim.points, importance:2, date:taskModal.claim.date, isDaily:true }];
                const newPoints = (cur.points||0) + taskModal.claim.points;
                return {...prev, [taskModal.claim.memberId]: { ...cur, points:newPoints, history }};
              });
              setTaskModal(null);
            }} style={btnStyle(T.gold,{width:"100%",marginBottom:8})}>✓ Mission accomplie !</button>
            <button onClick={()=>setTaskModal(null)} style={btnStyle(T.textSoft,{width:"100%"})}>Fermer</button>
          </div>
        </Modal>
      )}

      {apptModal && (
        <Modal title={apptModal.title} onClose={()=>setApptModal(null)}>
          <div style={{ fontSize:12,color:T.textSoft,marginBottom:14 }}>
            {apptModal.time}{apptModal.specialist ? " · "+apptModal.specialist : ""}
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            {/* Navigate to RDV tab to edit */}
            <button onClick={()=>{ onSwitchTab&&onSwitchTab("appts", apptModal); setApptModal(null); }} style={{ ...btnStyle(T.mint,{textAlign:"left"}), display:"flex", alignItems:"center", gap:8 }}>
              🏥 Voir dans les RDV
            </button>
            {/* Fiche spécialiste liée */}
            {(() => { const lf = fiches.find(fi=>fi.member===apptModal.member&&apptModal.title.toLowerCase().includes(fi.title.toLowerCase().split(" ")[0])); return lf ? (
              <button onClick={()=>{ onSwitchTab&&onSwitchTab("fiches"); setApptModal(null); }} style={{ ...btnStyle(T.gold,{textAlign:"left"}), display:"flex", alignItems:"center", gap:8 }}>
                📋 Voir la fiche — {lf.title}
              </button>
            ) : null; })()}
            {/* Companion for occurrences */}
            {apptModal.occKey && onAddCompanion && (
              <button onClick={()=>{ onAddCompanion(apptModal); setApptModal(null); }} style={{ ...btnStyle(T.navyMid,{textAlign:"left"}), display:"flex", alignItems:"center", gap:8 }}>
                👤 Ajouter un accompagnant pour ce RDV
              </button>
            )}
            {/* Nav buttons */}
            {(() => { const lf = fiches.find(fi=>fi.member===apptModal.member&&apptModal.title.toLowerCase().includes(fi.title.toLowerCase().split(" ")[0])); const addr = lf?.contact?.address||apptModal.specialist; return addr ? (
              <div>
                <div style={{ fontSize:11,color:T.textSoft,marginBottom:6 }}>Itinéraire</div>
                <NavButtons address={addr} />
              </div>
            ) : null; })()}
            <button onClick={()=>setApptModal(null)} style={btnStyle(T.textSoft)}>Fermer</button>
          </div>
        </Modal>
      )}

      {editing && editForm && (
        <Modal title="Modifier l'événement" onClose={()=>setEditing(null)}>
          {editForm.recurrence !== "custom" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
              <select value={editForm.day} onChange={e=>setEditForm(f=>({...f,day:+e.target.value}))} style={inputStyle}>{DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}</select>
              <TimePicker value={editForm.time} onChange={v=>setEditForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
            </div>
          )}
          <input placeholder="Titre" value={editForm.title} onChange={e=>setEditForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <MemberPicker value={editForm.members||editForm.member} onChange={v=>setEditForm(f=>({...f,members:v,member:v[0]}))} />
            <ThemePicker value={editForm.type} onChange={v=>setEditForm(f=>({...f,type:v}))} themes={themes} onAddTheme={onAddTheme} label="" />
          </div>
          <RecurrenceFields form={editForm} setForm={setEditForm} mode="event" />
          <div style={{ display:"flex", gap:8, marginTop:8 }}>
            <button onClick={handleSave} style={btnStyle(T.coral, {flex:1})}>Enregistrer</button>
            <button onClick={()=>{ onDelete(editing.id); setEditing(null); }} style={btnStyle(T.danger)}>Supprimer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── TASKS VIEW ─────────────────────────────────────────────────
function TasksView({ tasks, onToggle, onAdd, onUpdate, onDelete, activeUser=null, themes=DEFAULT_THEMES, onAddTheme, addReminder, deleteReminder }) {
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ title:"", member:"olivier", date:"", time:"", importance:2, recurrence:"none", recurrenceEnd:"", recurrenceInterval:1 });
  const [formError, setFormError] = useState(null); // {title:bool, date:bool}
  const [editing, setEditing]   = useState(null);
  const [editForm, setEditForm] = useState(null);

  const handleAdd = () => {
    const err = { title: !form.title.trim(), date: !form.date };
    if (err.title || err.date) { setFormError(err); return; }
    setFormError(null);
    onAdd({ ...form, id:Date.now(), done:false });
    setShowAdd(false); setForm({ title:"", member:"olivier", date:"", time:"", importance:2, recurrence:"none", recurrenceEnd:"", recurrenceInterval:1 });
  };

  const openEdit = (task) => { setEditing(task); setEditForm({ ...task }); };
  const handleSave = () => { onUpdate(editForm); setEditing(null); };

  const [filterMember, setFilterMember] = useState(()=>activeUser||"all");
  const [filterImportance, setFilterImportance] = useState(0);
  const [filterToday, setFilterToday] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [taskView, setTaskView] = useState("list");

  useEffect(()=>{ setFilterMember(activeUser||"all"); }, [activeUser]);
  const todayStr = todayISO();
  const filtered = tasks.filter(t =>
    (filterMember==="all" || normalizeMembers(t.members||t.member).includes(filterMember)) &&
    (filterImportance===0 || (t.importance||2)===filterImportance) &&
    (!filterToday || t.date===todayStr) &&
    (filterType==="all" || t.type===filterType)
  );
  const sortByDate = (a,b) => {
    // Date d'abord (chronologique), importance en départage
    if (!a.date && !b.date) return (b.importance||2) - (a.importance||2);
    if (!a.date) return 1;
    if (!b.date) return -1;
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return (b.importance||2) - (a.importance||2); // même date → urgence en premier
  };
  const expandedFiltered = filtered.flatMap(t => expandRecurringTask(t));
  const todo = expandedFiltered.filter(t=>!t.done).sort(sortByDate);
  const done = expandedFiltered.filter(t=>t.done).sort(sortByDate);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>Tâches & corvées</h2>
        <button onClick={()=>setShowAdd(s=>!s)} style={btnStyle(T.coral)}>+ Ajouter</button>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Personne :</span>
        <select value={filterMember} onChange={e=>setFilterMember(e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="all">Tous</option>
          {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>
      <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>
        <button onClick={()=>setFilterToday(f=>!f)} style={{ padding:"6px 14px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(filterToday?T.coral:T.sandDark), background:filterToday?T.coral:"transparent", color:filterToday?T.white:T.textMid }}>📅 Aujourd'hui</button>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Thème :</span>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{...inputStyle,fontSize:12,flex:1}}>
          <option value="all">Tous</option>
          {(themes||DEFAULT_THEMES).map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
        </select>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Importance :</span>
        <select value={filterImportance} onChange={e=>setFilterImportance(+e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value={0}>Toutes</option>
          {EISENHOWER.map(q=><option key={q.value} value={q.value}>{q.icon} {q.label}</option>)}
        </select>
      </div>

      {/* Vue toggle */}
      <div style={{ display:"flex", gap:6, marginBottom:16 }}>
        <button onClick={()=>setTaskView("list")} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(taskView==="list"?T.coral:T.sandDark), background:taskView==="list"?T.coral+"22":"transparent", color:taskView==="list"?T.coral:T.textMid }}>☰ Liste</button>
        <button onClick={()=>setTaskView("matrix")} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(taskView==="matrix"?T.coral:T.sandDark), background:taskView==="matrix"?T.coral+"22":"transparent", color:taskView==="matrix"?T.coral:T.textMid }}>⊞ Matrice</button>
      </div>

      {/* Vue liste */}
      {taskView==="list" && (<>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:12, fontWeight:700, color:T.textSoft, marginBottom:8, letterSpacing:"0.06em", textTransform:"uppercase" }}>À faire ({todo.length})</div>
        {todo.map(t=><TaskRow key={t.id} task={t} onToggle={onToggle} onEdit={openEdit} />)}
        {todo.length===0 && <div style={{ color:T.mint, fontWeight:600, fontSize:14 }}>✓ Tout est fait !</div>}
      </div>
      {done.length>0 && (
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:T.textSoft, marginBottom:8, letterSpacing:"0.06em", textTransform:"uppercase" }}>Terminées ({done.length})</div>
          {done.map(t=><TaskRow key={t.id} task={t} onToggle={onToggle} onEdit={openEdit} />)}
        </div>
      )}
      </>)}

      {/* Vue matrice Eisenhower 2×2 */}
      {taskView==="matrix" && (
        <div>
          <div style={{ fontSize:11, color:T.textSoft, marginBottom:12, lineHeight:1.5 }}>
            La matrice Eisenhower classe les tâches selon leur <strong>urgence</strong> et leur <strong>importance</strong>.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {EISENHOWER.map(q=>{
              const qtasks = todo.filter(t=>(t.importance||2)===q.value);
              // Couleurs de texte assombries pour rester lisibles sur le fond pastel du quadrant (ex: doré/gris trop clairs en couleur d'origine)
              const labelColor = { 1:"#C0392B", 2:"#B8651E", 3:"#8A6D1F", 4:"#56606C" }[q.value] || q.color;
              return (
                <div key={q.value} style={{ background:`${q.color}15`, border:`2px solid ${q.color}55`, borderRadius:14, padding:"12px", minHeight:120 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                    <span style={{ fontSize:16 }}>{q.icon}</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:800, color:labelColor }}>{q.label}</div>
                      <div style={{ fontSize:10, color:T.textSoft, marginTop:1 }}>{q.desc}</div>
                    </div>
                  </div>
                  {qtasks.length===0 && <div style={{ fontSize:11, color:T.textSoft, textAlign:"center", marginTop:12 }}>Aucune tâche</div>}
                  {qtasks.map(t=>(
                    <div key={t.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:`1px solid ${T.cardBorder}` }}>
                      <button onClick={()=>onToggle(t.id)} style={{ width:18, height:18, borderRadius:4, border:`2px solid ${labelColor}`, background:"transparent", cursor:"pointer", flexShrink:0 }} />
                      <span style={{ fontSize:12, color:T.text, flex:1, lineHeight:1.3 }} onClick={()=>openEdit(t)}>{t.title}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {done.length>0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:T.textSoft, marginBottom:8, letterSpacing:"0.06em", textTransform:"uppercase" }}>Terminées ({done.length})</div>
              {done.map(t=><TaskRow key={t.id} task={t} onToggle={onToggle} onEdit={openEdit} />)}
            </div>
          )}
        </div>
      )}
      {showAdd && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <input placeholder="Titre de la tâche *" value={form.title} onChange={e=>{ setForm(f=>({...f,title:e.target.value})); if(formError?.title && e.target.value.trim()) setFormError(fe=>({...fe,title:false})); }} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box",border:formError?.title?"2px solid "+T.danger:inputStyle.border}} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
            <MemberPicker value={form.members||form.member} onChange={v=>setForm(f=>({...f,members:v,member:v[0]}))} />
            <div>
              <div style={{ fontSize:11, color:formError?.date?T.danger:T.textSoft, marginBottom:3 }}>Date *</div>
              <input type="date" value={form.date||""} onChange={e=>{ setForm(f=>({...f,date:e.target.value})); if(formError?.date && e.target.value) setFormError(fe=>({...fe,date:false})); }} style={{...inputStyle,width:"100%",boxSizing:"border-box",border:formError?.date?"2px solid "+T.danger:inputStyle.border}} />
            </div>
          </div>
          {formError && (formError.title || formError.date) && (
            <div style={{ fontSize:12, color:T.danger, fontWeight:600, marginBottom:10 }}>
              ⚠️ {formError.title && formError.date ? "Titre et date sont obligatoires." : formError.title ? "Le titre est obligatoire." : "La date est obligatoire."}
            </div>
          )}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Heure (optionnel, 08:00 par défaut)</div>
            <TimePicker value={form.time||""} onChange={v=>setForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
          </div>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>Priorité</div>
            <select value={form.importance||2} onChange={e=>setForm(f=>({...f,importance:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {EISENHOWER.map(q=><option key={q.value} value={q.value}>{q.icon} {q.label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>Durée estimée</div>
            <select value={form.duration||0} onChange={e=>setForm(f=>({...f,duration:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              <option value={0}>Non définie</option>
              {[15,20,30,45,60,90,120,180].map(d=><option key={d} value={d}>{d<60?d+" min":d===60?"1h":d===90?"1h30":d===120?"2h":"3h"}</option>)}
            </select>
          </div>
          <RecurrenceFields form={form} setForm={setForm} mode="appt" />
          <ReminderAttacher reminders={form.linkedReminders||[]} onChange={v=>setForm(f=>({...f,linkedReminders:v}))} itemDate={form.date} itemTime={form.time} itemTitle={form.title} members={normalizeMembers(form.members||form.member)} onAddReminder={addReminder} onDeleteReminder={deleteReminder} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleAdd} style={btnStyle(T.coral)}>Confirmer</button>
            <button onClick={()=>{ setShowAdd(false); setFormError(null); }} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}
      {editing && editForm && (
        <Modal title="Modifier la tâche" onClose={()=>setEditing(null)}>
          <input placeholder="Titre" value={editForm.title} onChange={e=>setEditForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
            <MemberPicker value={editForm.members||editForm.member} onChange={v=>setEditForm(f=>({...f,members:v,member:v[0]}))} />
            <div>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Date</div>
              <input type="date" value={editForm.date||""} onChange={e=>setEditForm(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Heure (optionnel)</div>
            <TimePicker value={editForm.time||""} onChange={v=>setEditForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>Priorité</div>
            <select value={editForm.importance||2} onChange={e=>setEditForm(f=>({...f,importance:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {EISENHOWER.map(q=><option key={q.value} value={q.value}>{q.icon} {q.label}</option>)}
            </select>
          </div>
          <RecurrenceFields form={editForm} setForm={setEditForm} mode="appt" />
          <ReminderAttacher reminders={editForm.linkedReminders||[]} onChange={v=>setEditForm(f=>({...f,linkedReminders:v}))} itemDate={editForm.date} itemTime={editForm.time} itemTitle={editForm.title} members={normalizeMembers(editForm.members||editForm.member)} onAddReminder={addReminder} onDeleteReminder={deleteReminder} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleSave} style={btnStyle(T.coral,{flex:1})}>Enregistrer</button>
            <button onClick={()=>{ onDelete(editing.id); setEditing(null); }} style={btnStyle(T.danger)}>Supprimer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onEdit }) {
  const m = memberOf(task.member);
  const overdue = !task.done && isOverdue(task.date);
  const dateLabel = fmtTaskDate(task.date);
  return (
    <div style={{ ...cardStyle(), display:"flex", alignItems:"center", gap:12, marginBottom:8, padding:"10px 14px", opacity:task.done?0.55:1, borderLeft: overdue ? "3px solid "+T.danger : "3px solid transparent" }}>
      <button onClick={()=>onToggle(task.id)} style={{ width:20,height:20,borderRadius:4,border:"2px solid "+(task.done?T.mint:T.sandDark),background:task.done?T.mint:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
        {task.done && <span style={{ color:T.white,fontSize:11,fontWeight:900 }}>✓</span>}
      </button>
      <div style={{ flex:1 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
          <div style={{ fontSize:14,fontWeight:600,color:T.text,textDecoration:task.done?"line-through":"none" }}>{task.title}</div>
          {!task.done && <span style={{ fontSize:12, color:importanceOf(task.importance||2).color }} title={importanceOf(task.importance||2).label}>{importanceOf(task.importance||2).icon}</span>}
        </div>
        <div style={{ fontSize:11,color:overdue?T.danger:T.textSoft,marginTop:0,fontWeight:overdue?700:400 }}>
          {dateLabel && <span>{dateLabel} · </span>}{task.time && <span style={{color:T.textMid,fontWeight:600}}>{task.time} · </span>}{m.name}{task.recurrence && task.recurrence!=='none' && !task.isOccurrence && <span style={{color:T.gold,marginLeft:4}}>↻</span>}
        </div>
      </div>
      <button onClick={()=>onEdit(task)} style={iconBtn(T.textSoft)} title="Modifier">✎</button>
      <MemberAvatars members={task.members||task.member} size={26} />
    </div>
  );
}

// ── APPOINTMENTS VIEW ──────────────────────────────────────────
const EMPTY_APPT = { date:"", time:"", title:"", specialist:"", member:"cleo", notes:"", recurrence:"none", recurrenceEnd:"", recurrenceInterval:1, customDates:[""], travelTime:20, atHome:false, duration:45, linkedReminders:[] };

function ApptsView({ appts, onAdd, onUpdate, onDelete, fiches=[], onOpenFiche, onTogglePaid, initialEditAppt=null, activeUser=null, themes=DEFAULT_THEMES, onAddTheme, addReminder, deleteReminder }) {
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({...EMPTY_APPT, member: activeUser || MEMBERS[0].id});
  const [editing, setEditing]   = useState(null);
  const [editForm, setEditForm] = useState(null);

  // Auto-open edit modal if navigated from planning
  useEffect(() => {
    if (!initialEditAppt) return;
    // Try direct match first, then match by occKey prefix
    const occId = String(initialEditAppt.id || "");
    const occKey = initialEditAppt.occKey || occId;
    const base = appts.find(a => {
      const aid = String(a.id);
      return aid === occId || occKey.startsWith(aid + "_") || occKey === aid + "_0" || occId.startsWith(aid + "_");
    });
    if (base) { setEditing(base); setEditForm({...base}); }
  }, [initialEditAppt]);

  const handleAdd = () => {
    if (!form.title.trim()||!form.date) return;
    const appt = { ...form, id:Date.now() };
    if (form.recurrence==="custom") { appt.customDates=form.customDates.filter(d=>d.trim()); appt.date=appt.customDates[0]||form.date; }
    onAdd(appt); setShowAdd(false); setForm({...EMPTY_APPT, member: activeUser || MEMBERS[0].id});
  };

  const openEdit = (appt) => {
    const base = appts.find(a => String(appt.id).startsWith(String(a.id)));
    setEditing(base||appt); setEditForm({ ...base||appt });
  };
  const handleSave = () => { onUpdate(editForm); setEditing(null); };

  const [sortBy, setSortBy]             = useState("date");
  const [filterMember, setFilterMember] = useState(()=>activeUser||"all");
  const [filterSpecialist, setFilterSpecialist] = useState("all");
  const [filterPaid, setFilterPaid] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [showAllRdv, setShowAllRdv] = useState(false);

  useEffect(()=>{ setFilterMember(activeUser||"all"); }, [activeUser]);
  const [companionAppt, setCompanionAppt] = useState(null);

  const today = todayISO();
  const allOcc = appts.flatMap(a=>expandRecurringAppt(a));

  // Build unique specialist list from all appts
  const specialists = [...new Set(appts.map(a=>a.specialist).filter(Boolean))].sort((a,b)=>a.localeCompare(b));

  const sortFn = (a,b) => {
    if (sortBy==="specialist") return (a.specialist||"").localeCompare(b.specialist||"");
    if (sortBy==="member")     return a.member.localeCompare(b.member);
    return a.date.localeCompare(b.date);
  };

  const today2 = todayISO();
  const filtered = allOcc.filter(a =>
    (filterMember==="all" || normalizeMembers(a.members||a.member).includes(filterMember)) &&
    (filterSpecialist==="all" || a.specialist===filterSpecialist) &&
    (!filterPaid || (a.date <= today2 && !a.paid)) &&
    (filterType==="all" || a.type===filterType)
  );
  const upcoming = filtered.filter(a=>a.date>=today).sort(sortFn);
  const past     = filtered.filter(a=>a.date<today).sort(sortFn);
  const todayAppts = filtered.filter(a=>a.date===today).sort(sortFn);

  // Group by specialist when sortBy==="specialist"
  const groupBySpecialist = (list) => {
    const groups = {};
    list.forEach(a => { const k = a.specialist||"Sans spécialiste"; (groups[k]=groups[k]||[]).push(a); });
    return Object.entries(groups).sort(([a],[b])=>a.localeCompare(b));
  };

  const recBadge = (appt) => appt.recurrence && appt.recurrence!=="none" ? (
    <span style={{ fontSize:10,background:T.gold+"22",color:T.gold,borderRadius:4,padding:"1px 6px",fontWeight:700,marginLeft:6 }}>{RECURRENCE_LABELS[appt.recurrence]}</span>
  ) : null;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>Rendez-vous spécialistes</h2>
        <button onClick={()=>setShowAdd(s=>!s)} style={btnStyle(T.mint)}>+ Ajouter</button>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:8, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Personne :</span>
        <select value={filterMember} onChange={e=>setFilterMember(e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="all">Tous</option>
          {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {/* Specialist filter */}
      {specialists.length > 0 && (
        <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
          <button onClick={()=>setFilterSpecialist("all")} style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(filterSpecialist==="all"?T.navyMid:T.sandDark), background:filterSpecialist==="all"?T.navyMid:"transparent", color:filterSpecialist==="all"?T.white:T.textMid }}>Tous les spécialistes</button>
          {specialists.map(sp=>(
            <button key={sp} onClick={()=>setFilterSpecialist(filterSpecialist===sp?"all":sp)} style={{ padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(filterSpecialist===sp?T.mint:T.sandDark), background:filterSpecialist===sp?T.mint:"transparent", color:filterSpecialist===sp?T.white:T.textMid }}>🩺 {sp}</button>
          ))}
        </div>
      )}

      {showAdd && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          {/* Rattacher à une fiche — optionnel */}
          {fiches.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>📋 Rattacher à une fiche spécialiste (optionnel)</div>
              <select onChange={e=>{
                const fiche = fiches.find(f=>f.id===+e.target.value);
                if (fiche) setForm(f=>({...f, title:fiche.title, specialist:fiche.contact?.name||fiche.title, member:fiche.member, members:[fiche.member] }));
              }} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                <option value="">-- Aucune fiche --</option>
                {fiches.map(fi=><option key={fi.id} value={fi.id}>{fi.title} ({MEMBERS.find(m=>m.id===fi.member)?.name||fi.member})</option>)}
              </select>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div><input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
            <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure</div><TimePicker value={form.time} onChange={v=>setForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} /></div>
          </div>
          <input placeholder="Titre" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ position:"relative", marginBottom:8 }}>
            <input list="specialists-list" placeholder="Fiche (nom du praticien/lieu)" value={form.specialist} onChange={e=>setForm(f=>({...f,specialist:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            <datalist id="specialists-list">
              {fiches.filter(fi=>fi.contact?.name).map(fi=><option key={fi.id} value={fi.contact.name}>{fi.title}</option>)}
            </datalist>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <MemberPicker value={form.members||form.member} onChange={v=>setForm(f=>({...f,members:v,member:v[0]}))} />
          <div style={{marginBottom:8}}><ThemePicker value={form.type||"health"} onChange={v=>setForm(f=>({...f,type:v}))} themes={themes} onAddTheme={onAddTheme} label="Thème" /></div>
            <input placeholder="Notes" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={inputStyle} />
          </div>
          <div style={{ marginBottom:8 }}>
            <label onClick={()=>setForm(f=>({...f,atHome:!f.atHome,travelTime:f.atHome?20:0}))} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", padding:"8px 10px", background:form.atHome?"rgba(91,191,173,0.15)":T.sandDark, borderRadius:8, border:"1px solid "+(form.atHome?T.mint:T.cardBorder), marginBottom:6 }}>
              <div style={{ width:18, height:18, borderRadius:4, border:"2px solid "+(form.atHome?T.mint:T.cardBorder), background:form.atHome?T.mint:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {form.atHome && <span style={{ color:"white", fontSize:11, fontWeight:900 }}>✓</span>}
              </div>
              <span style={{ fontSize:13, fontWeight:600, color:form.atHome?T.mint:T.textMid }}>🏠 À domicile</span>
            </label>
<div style={{ display:"flex", gap:8, marginBottom:6 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Durée de séance</div>
                <select value={form.duration||45} onChange={e=>setForm(f=>({...f,duration:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                  {[15,20,30,45,60,90,120].map(d=><option key={d} value={d}>{d<60?d+" min":d===60?"1h":d===90?"1h30":"2h"}</option>)}
                </select>
              </div>
            </div>
            {!form.atHome && <TravelTimeField value={form.travelTime} onChange={v=>setForm(f=>({...f,travelTime:v}))} address={form.specialist} fiches={fiches} />}
          </div>
          <RecurrenceFields form={form} setForm={setForm} mode="appt" />
          <ReminderAttacher reminders={form.linkedReminders||[]} onChange={v=>setForm(f=>({...f,linkedReminders:v}))} itemDate={form.date} itemTime={form.time} itemTitle={form.title} members={normalizeMembers(form.members||form.member)} onAddReminder={addReminder} onDeleteReminder={deleteReminder} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleAdd} style={btnStyle(T.mint)}>Confirmer</button>
            <button onClick={()=>setShowAdd(false)} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom:24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div style={{ fontSize:12,fontWeight:700,color:T.textSoft,letterSpacing:"0.06em",textTransform:"uppercase" }}>
            {showAllRdv ? `À venir (${upcoming.length})` : `Aujourd'hui (${todayAppts.length})`}
          </div>
          <button onClick={()=>setShowAllRdv(s=>!s)} style={{ background:"transparent", border:"none", color:T.mint, fontSize:12, fontWeight:700, cursor:"pointer" }}>
            {showAllRdv ? "Réduire" : "Voir tous →"}
          </button>
        </div>
        {!showAllRdv && (
          <>
            {todayAppts.length===0 && <div style={{ color:T.textSoft,fontSize:14 }}>Aucun rendez-vous aujourd'hui.</div>}
            {todayAppts.map(a=><ApptCard key={a.id} appt={a} recBadge={recBadge} onEdit={openEdit} fiches={fiches} onOpenFiche={onOpenFiche} onTogglePaid={onTogglePaid} onAddCompanion={setCompanionAppt} />)}
          </>
        )}
        {showAllRdv && (
          <>
            {upcoming.length===0 && <div style={{ color:T.textSoft,fontSize:14 }}>Aucun rendez-vous à venir.</div>}
            {sortBy==="specialist"
              ? groupBySpecialist(upcoming).map(([specialist, list]) => (
                  <div key={specialist} style={{ marginBottom:16 }}>
                    <div style={{ fontSize:12,fontWeight:800,color:T.mint,marginBottom:6,display:"flex",alignItems:"center",gap:6 }}>
                      <span style={{ width:6,height:6,borderRadius:"50%",background:T.mint,display:"inline-block" }} />{specialist}
                      <span style={{ color:T.textSoft,fontWeight:400 }}>({list.length})</span>
                    </div>
                    {list.map(a=><ApptCard key={a.id} appt={a} recBadge={recBadge} onEdit={openEdit} fiches={fiches} onOpenFiche={onOpenFiche} onTogglePaid={onTogglePaid} onAddCompanion={setCompanionAppt} />)}
                  </div>
                ))
              : upcoming.slice(0,20).map(a=><ApptCard key={a.id} appt={a} recBadge={recBadge} onEdit={openEdit} fiches={fiches} onOpenFiche={onOpenFiche} onTogglePaid={onTogglePaid} onAddCompanion={setCompanionAppt} />)
            }
            {sortBy!=="specialist" && upcoming.length>20 && <div style={{ fontSize:12,color:T.textSoft,marginTop:6 }}>+ {upcoming.length-20} occurrences…</div>}
          </>
        )}
      </div>
      {showAllRdv && past.length>0 && (
        <div style={{ opacity:0.6 }}>
          <div style={{ fontSize:12,fontWeight:700,color:T.textSoft,marginBottom:8,letterSpacing:"0.06em",textTransform:"uppercase" }}>Passés</div>
          {past.slice(-3).map(a=><ApptCard key={a.id} appt={a} recBadge={recBadge} onEdit={openEdit} fiches={fiches} onOpenFiche={onOpenFiche} onTogglePaid={onTogglePaid} onAddCompanion={setCompanionAppt} />)}
        </div>
      )}

      {companionAppt && (
        <OccurrenceCompanionModal
          appt={companionAppt}
          baseAppt={appts.find(a => companionAppt.occKey?.startsWith(String(a.id))) || companionAppt}
          onSave={(updated) => { onUpdate(updated); }}
          onClose={()=>setCompanionAppt(null)}
        />
      )}

      {editing && editForm && (
        <Modal title="Modifier le rendez-vous" onClose={()=>setEditing(null)}>
          {/* Rattacher à une fiche — optionnel */}
          {fiches.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>📋 Rattacher à une fiche spécialiste (optionnel)</div>
              <select onChange={e=>{
                const fiche = fiches.find(f=>f.id===+e.target.value);
                if (fiche) setEditForm(f=>({...f, title:fiche.title, specialist:fiche.contact?.name||fiche.title, member:fiche.member, members:[fiche.member] }));
              }} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                <option value="">-- Aucune fiche --</option>
                {fiches.map(fi=><option key={fi.id} value={fi.id}>{fi.title} ({MEMBERS.find(m=>m.id===fi.member)?.name||fi.member})</option>)}
              </select>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div><input type="date" value={editForm.date} onChange={e=>setEditForm(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
            <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure</div><TimePicker value={editForm.time} onChange={v=>setEditForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} /></div>
          </div>
          <input placeholder="Titre" value={editForm.title} onChange={e=>setEditForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ position:"relative", marginBottom:8 }}>
            <input list="specialists-list-edit" placeholder="Fiche (nom du praticien/lieu)" value={editForm.specialist} onChange={e=>setEditForm(f=>({...f,specialist:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            <datalist id="specialists-list-edit">
              {fiches.filter(fi=>fi.contact?.name).map(fi=><option key={fi.id} value={fi.contact.name}>{fi.title}</option>)}
            </datalist>
          </div>
          <div style={{ marginBottom:8 }}>
            <MemberPicker value={editForm.members||editForm.member} onChange={v=>setEditForm(f=>({...f,members:v,member:v[0]}))} />
          </div>
          <div style={{marginBottom:8}}><ThemePicker value={editForm.type||"health"} onChange={v=>setEditForm(f=>({...f,type:v}))} themes={themes} onAddTheme={onAddTheme} label="Thème" /></div>
          <input placeholder="Notes" value={editForm.notes||""} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ marginBottom:8 }}>
            <label onClick={()=>setEditForm(f=>({...f,atHome:!f.atHome,travelTime:f.atHome?20:0}))} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", padding:"8px 10px", background:editForm.atHome?"rgba(91,191,173,0.15)":T.sandDark, borderRadius:8, border:"1px solid "+(editForm.atHome?T.mint:T.cardBorder), marginBottom:6 }}>
              <div style={{ width:18, height:18, borderRadius:4, border:"2px solid "+(editForm.atHome?T.mint:T.cardBorder), background:editForm.atHome?T.mint:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {editForm.atHome && <span style={{ color:"white", fontSize:11, fontWeight:900 }}>✓</span>}
              </div>
              <span style={{ fontSize:13, fontWeight:600, color:editForm.atHome?T.mint:T.textMid }}>🏠 À domicile</span>
            </label>
            {!editForm.atHome && <TravelTimeField value={editForm.travelTime||0} onChange={v=>setEditForm(f=>({...f,travelTime:v}))} address={editForm.specialist} fiches={fiches} />}
          </div>
          <RecurrenceFields form={editForm} setForm={setEditForm} mode="appt" />
          <ReminderAttacher reminders={editForm.linkedReminders||[]} onChange={v=>setEditForm(f=>({...f,linkedReminders:v}))} itemDate={editForm.date} itemTime={editForm.time} itemTitle={editForm.title} members={normalizeMembers(editForm.members||editForm.member)} onAddReminder={addReminder} onDeleteReminder={deleteReminder} />
          <div style={{ display:"flex", gap:8, marginTop:8 }}>
            <button onClick={handleSave} style={btnStyle(T.mint,{flex:1})}>Enregistrer</button>
            <button onClick={()=>{ onDelete(editing.id); setEditing(null); }} style={btnStyle(T.danger)}>Supprimer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ApptCard({ appt, recBadge, onEdit, onTogglePaid, fiches=[], onOpenFiche, onAddCompanion }) {
  const m = memberOf(appt.member);
  const linkedFiche = fiches.find(f => f.member===appt.member && appt.title.toLowerCase().includes(f.title.toLowerCase().split(" ")[0]));
  const today = todayISO();
  const isPast = appt.date <= today;
  return (
    <div style={{ ...cardStyle(), display:"flex", gap:12, marginBottom:10, padding:"12px 14px", opacity:appt.paid?0.6:1, borderLeft: isPast && !appt.paid ? `3px solid ${T.gold}` : `3px solid transparent` }}>
      {/* Date block */}
      <div onClick={()=>onEdit(appt)} style={{ background:T.mint+"22",borderRadius:8,padding:"8px 10px",textAlign:"center",minWidth:48,flexShrink:0,cursor:"pointer" }}>
        <div style={{ fontSize:16,fontWeight:800,color:T.mint }}>{appt.date.slice(8)}</div>
        <div style={{ fontSize:9,color:T.mint,fontWeight:600 }}>{new Date(appt.date+"T00:00:00").toLocaleDateString("fr-FR",{month:"short"}).toUpperCase()}</div>
      </div>
      {/* Content */}
      <div style={{ flex:1, minWidth:0 }} onClick={()=>onEdit(appt)}>
        <div style={{ display:"flex",alignItems:"center",flexWrap:"wrap",gap:4 }}>
          <span style={{ fontSize:14,fontWeight:700,color:T.text }}>{appt.title}</span>
          {!appt.isOccurrence && recBadge(appt)}
          {linkedFiche && <button onClick={e=>{e.stopPropagation();onOpenFiche&&onOpenFiche(linkedFiche);}} style={{fontSize:10,background:T.mint+"33",color:T.mint,border:"none",borderRadius:4,padding:"1px 6px",cursor:"pointer",fontWeight:700}}>📋 Fiche</button>}
        </div>
        <div style={{ fontSize:11,color:T.textSoft,marginTop:2 }}>{appt.time}{appt.specialist ? ` · ${appt.specialist}` : ""}</div>
        {appt.notes && <div style={{ fontSize:10,color:T.textMid,marginTop:3,fontStyle:"italic" }}>{appt.notes}</div>}
      </div>
      {/* Right side: avatar + paid toggle */}
      <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:6,flexShrink:0 }}>
        <MemberAvatars members={appt.occMembers||appt.members||appt.member} size={26} />
        {appt.isOccurrence && onAddCompanion && (
          <button onClick={e=>{e.stopPropagation();onAddCompanion(appt);}} style={{ fontSize:10,color:T.textSoft,background:T.sandDark,border:"none",borderRadius:5,padding:"2px 6px",cursor:"pointer" }} title="Ajouter un accompagnant">+👤</button>
        )}
        {isPast && !appt.isOccurrence && (
          <button onClick={e=>{e.stopPropagation();onTogglePaid&&onTogglePaid(appt.id);}} style={{ display:"flex",alignItems:"center",gap:3,background:appt.paid?T.mint+"22":T.sandDark,border:`1px solid ${appt.paid?T.mint:T.sandDark}`,borderRadius:6,padding:"3px 7px",cursor:"pointer",fontSize:10,fontWeight:700,color:appt.paid?T.mint:T.textSoft }} title={appt.paid?"Payé":"À payer"}>
            {appt.paid ? "✓ Payé" : "€ À payer"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── CHAT VIEW ──────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════
// 🤖 ÉQUIPE D'ASSISTANTS IA
// ══════════════════════════════════════════════════════════════

const ASSISTANTS = [
  {
    id: "alfred",
    photo: "https://res.cloudinary.com/aaz6nos5/image/upload/Alfred_kctwgt",
    name: "Alfy",
    role: "Assistant du quotidien",
    emoji: "🏠",
    color: "#E8735A",
    gradient: "linear-gradient(135deg, #E8735A, #D4A843)",
    desc: "Je gère ton quotidien dans l'app — RDV, tâches, recettes, planning...",
    available: false,
    system: `Tu es Alfy, l'assistant du quotidien de la famille. Tu aides à gérer le planning, créer des RDV, ajouter des tâches et des recettes. Tu es bienveillant, concis et efficace. Tu réponds toujours en français.`,
  },
  {
    id: "jeanne",
    photo: "https://res.cloudinary.com/aaz6nos5/image/upload/Jeanne_hrdgjh",
    name: "Jeanne",
    role: "Conseillère juridique",
    emoji: "⚖️",
    color: "#5BBFAD",
    gradient: "linear-gradient(135deg, #5BBFAD, #3D5A8A)",
    desc: "Droits, MDPH, démarches administratives, lois sur le handicap...",
    available: false,
    system: `Tu es Jeanne, conseillère juridique spécialisée dans les droits des familles TND en France. Tu maîtrises la loi du 11 février 2005, la MDPH, l'AEEH, les recours CDAPH, la CAF et l'AAH. Tu es précise, factuelle et rassurante. Tu cites les textes applicables quand c'est pertinent. Tu ne fais pas de conseil juridique personnalisé mais expliques les droits et procédures. Tu réponds toujours en français.`,
  },
  {
    id: "hector",
    photo: "https://res.cloudinary.com/aaz6nos5/image/upload/Hector_y6bxhx",
    name: "Hector",
    role: "Expert nutrition",
    emoji: "🥗",
    color: "#27AE60",
    gradient: "linear-gradient(135deg, #27AE60, #5BBFAD)",
    desc: "Alimentation, besoins nutritionnels, conseils adaptés au TND...",
    available: false,
    system: `Tu es Hector, expert en nutrition spécialisé dans les besoins des familles TND. Tu connais les hypersensibilités alimentaires, les besoins spécifiques liés au TDAH, à l'autisme et aux troubles Dys. Tu proposes des conseils pratiques, des recettes adaptées et des menus équilibrés. Tu es pédagogue et bienveillant. Tu réponds toujours en français.`,
  },
  {
    id: "nico",
    photo: "https://res.cloudinary.com/aaz6nos5/image/upload/Nico_pthiqu",
    name: "Nico",
    role: "Coach sportif",
    emoji: "🏃",
    color: "#D4A843",
    gradient: "linear-gradient(135deg, #D4A843, #E07B39)",
    desc: "Plans d'entraînement adaptés, sport et TND, routines physiques...",
    available: false,
    system: `Tu es Nico, coach sportif spécialisé dans l'accompagnement des familles TND. Tu crées des plans d'entraînement adaptés à tous les âges et profils, en tenant compte des spécificités du TDAH, de la dyspraxie et de l'autisme. Tu es motivant, encourageant et toujours adapté au niveau de chacun. Tu réponds toujours en français.`,
  },
  {
    id: "marie",
    photo: "https://res.cloudinary.com/aaz6nos5/image/upload/Marie_njdzpl",
    name: "Marie",
    role: "Codes sociaux",
    emoji: "💬",
    color: "#9B59B6",
    gradient: "linear-gradient(135deg, #9B59B6, #3D5A8A)",
    desc: "Apprendre les interactions sociales en sécurité, pour les enfants...",
    available: false,
    system: `Tu es Marie, spécialiste des codes sociaux pour les enfants TND. Tu aides les enfants à comprendre et pratiquer les interactions sociales dans un espace sécurisé et bienveillant. Tu utilises des exemples concrets, des jeux de rôle et des situations du quotidien. Tu adaptes ton langage à l'âge et au profil de l'enfant. Tu es douce, patiente et encourageante. Tu réponds toujours en français.`,
  },
];

function ChatView({ events, tasks, appts, onAddEvent, onAddTask, onAddAppt, onToggleTask }) {
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [ficheAssistant, setFicheAssistant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  useEffect(()=>{ bottomRef.current?.scrollIntoView({ behavior:"smooth" }); },[messages]);

  const selectAssistant = (assistant) => {
    if (!assistant.available) return;
    setSelectedAssistant(assistant);
    setMessages([{ role:"assistant", text:`Bonjour ! Je suis ${assistant.name}, ${assistant.role.toLowerCase()}. ${assistant.desc} Comment puis-je vous aider ?` }]);
    setInput("");
  };

  const buildContext = (assistant) => assistant.system + `\n\nMembres de la famille : ${MEMBERS.map(m=>m.name).join(", ")}.\nPLANNING: ${JSON.stringify(events)}\nTÂCHES: ${JSON.stringify(tasks)}\nRDV: ${JSON.stringify(appts)}`;
  const parseAction = (t) => { try { const m=t.match(/<action>([\s\S]*?)<\/action>/); return m?JSON.parse(m[1]):null; } catch { return null; } };
  const cleanText  = (t) => t.replace(/<action>[\s\S]*?<\/action>/g,"").trim();

  const send = async () => {
    if (!input.trim()||loading||!selectedAssistant) return;
    const userMsg = { role:"user", text:input };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs); setInput(""); setLoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", { method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ model:"claude-sonnet-4-6", max_tokens:1000, system:buildContext(selectedAssistant), messages:newMsgs.map(m=>({ role:m.role==="assistant"?"assistant":"user", content:m.text })) }) });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "Désolé, je n'ai pas pu répondre.";
      const action = parseAction(raw);
      if (action) {
        if (action.type==="event") onAddEvent({ day:action.day,time:action.time,title:action.title,member:action.member,type:action.eventType||"work",id:Date.now(),recurrence:"none" });
        if (action.type==="task")  onAddTask({ title:action.title,member:action.member,day:action.day,done:false,id:Date.now() });
        if (action.type==="appt")  onAddAppt({ date:action.date,time:action.time,title:action.title,specialist:action.specialist,member:action.member,notes:action.notes||"",id:Date.now(),recurrence:"none" });
      }
      setMessages(m=>[...m,{ role:"assistant", text:cleanText(raw) }]);
    } catch { setMessages(m=>[...m,{ role:"assistant", text:"Une erreur s'est produite. Vérifiez votre connexion." }]); }
    setLoading(false);
  };

  // Écran conversation
  if (selectedAssistant) {
    return (
      <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
        {/* Header assistant */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, padding:"12px 16px", background:`${selectedAssistant.color}18`, borderRadius:14, border:`1px solid ${selectedAssistant.color}33` }}>
          <button onClick={()=>setSelectedAssistant(null)} style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:16, color:T.textSoft, display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
          <div style={{ width:40, height:40, borderRadius:12, overflow:"hidden", background:selectedAssistant.gradient, flexShrink:0 }}>{selectedAssistant.photo ? <img src={selectedAssistant.photo} alt={selectedAssistant.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <span style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:20 }}>{selectedAssistant.emoji}</span>}</div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:T.text }}>{selectedAssistant.name}</div>
            <div style={{ fontSize:11, color:T.textSoft }}>{selectedAssistant.role}</div>
          </div>
          <div style={{ marginLeft:"auto", fontSize:11, color:"#27AE60", fontWeight:700, background:"rgba(39,174,96,0.15)", padding:"3px 8px", borderRadius:6 }}>● En ligne</div>
        </div>

        {/* Messages */}
        <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:10, marginBottom:12, paddingRight:4 }}>
          {messages.map((m,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:m.role==="user"?"flex-end":"flex-start", gap:8, alignItems:"flex-end" }}>
              {m.role==="assistant" && <div style={{ width:28, height:28, borderRadius:8, overflow:"hidden", background:selectedAssistant.gradient, flexShrink:0 }}>{selectedAssistant.photo ? <img src={selectedAssistant.photo} alt={selectedAssistant.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <span style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:14 }}>{selectedAssistant.emoji}</span>}</div>}
              <div style={{ maxWidth:"78%", padding:"10px 14px", borderRadius:14, background:m.role==="user"?selectedAssistant.color:T.sandDark, color:m.role==="user"?"white":T.text, fontSize:14, lineHeight:1.6, borderBottomRightRadius:m.role==="user"?2:14, borderBottomLeftRadius:m.role==="assistant"?2:14, border:m.role==="user"?"none":`1px solid ${T.cardBorder}` }}>{m.text}</div>
            </div>
          ))}
          {loading && (
            <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
              <div style={{ width:28, height:28, borderRadius:8, background:selectedAssistant.gradient, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>{selectedAssistant.emoji}</div>
              <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:"10px 16px" }}>
                <div style={{ display:"flex", gap:4 }}>
                  {[0,1,2].map(i=><div key={i} style={{ width:6, height:6, borderRadius:"50%", background:T.textSoft, animation:`bounce ${0.6+i*0.1}s infinite` }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ display:"flex", gap:8 }}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder={`Écrire à ${selectedAssistant.name}…`} style={{ flex:1, background:T.sandDark, border:`1px solid ${selectedAssistant.color}44`, borderRadius:999, padding:"12px 16px", fontSize:14, color:T.text, outline:"none", fontFamily:"inherit" }} />
          <button onClick={send} disabled={loading} style={{ background:selectedAssistant.gradient, border:"none", borderRadius:999, padding:"12px 18px", cursor:"pointer", fontSize:14, fontWeight:800, color:"white", opacity:loading?0.6:1 }}>→</button>
        </div>
      </div>
    );
  }


  // Écran fiche de transparence
  if (ficheAssistant) {
    const a = ficheAssistant;
    const FICHES_DATA = {
      alfred: {
        sait: ["Votre planning et vos événements de la semaine","Vos tâches et leur niveau de priorité","Vos rendez-vous et spécialistes","Vos recettes et menus","Les profils des membres de votre famille"],
        saitPas: ["Je n'ai pas accès à internet en temps réel","Je ne mémorise pas vos conversations d'une session à l'autre (V1)","Je ne peux pas envoyer d'emails ou de notifications externes"],
        prompt: "Tu es Alfy, l'assistant du quotidien de la famille. Tu aides à gérer le planning, créer des RDV, ajouter des tâches et des recettes. Tu es bienveillant, concis et efficace. Tu réponds toujours en français.",
        sources: [],
        lastUpdate: "02/07/2026",
      },
      jeanne: {
        sait: ["Loi du 11 février 2005 sur le handicap","Fonctionnement de la MDPH et délais légaux","AEEH et ses compléments","Droits scolaires : PAP, PAI, PPRE, AESH","Recours CDAPH","CAF, AAH, PCH","Démarches administratives liées au handicap"],
        saitPas: ["Je ne suis pas avocate et ne remplace pas un conseil juridique personnalisé","Mes connaissances ont une date limite — les lois évoluent","Je ne connais pas les spécificités de votre département","Je ne connais pas votre dossier individuel dans le détail"],
        prompt: "Tu es Jeanne, conseillère juridique spécialisée dans les droits des familles TND en France. Tu maîtrises la loi du 11 février 2005, la MDPH, l'AEEH, les recours CDAPH, la CAF et l'AAH. Tu es précise, factuelle et rassurante.",
        sources: ["legifrance.gouv.fr — textes de loi officiels","mdph.fr — guides et formulaires MDPH","cnsa.fr — Caisse Nationale de Solidarité pour l'Autonomie","service-public.fr — démarches administratives"],
        lastUpdate: "02/07/2026",
      },
      hector: {
        sait: ["Besoins nutritionnels généraux par âge","Hypersensibilités alimentaires fréquentes dans les profils TND","Alimentation et TDAH (oméga-3, magnésium, sucres...)","Alimentation et autisme (sélectivité alimentaire, textures)","Recettes simples et adaptées","Menus équilibrés pour toute la famille"],
        saitPas: ["Je ne suis pas diététicien agréé et ne remplace pas un suivi médical","Je ne connais pas vos allergies spécifiques sauf si vous me les signalez","Je ne prescris aucun complément alimentaire sans avis médical"],
        prompt: "Tu es Hector, expert en nutrition spécialisé dans les besoins des familles TND. Tu connais les hypersensibilités alimentaires, les besoins spécifiques liés au TDAH, à l'autisme et aux troubles Dys. Tu proposes des conseils pratiques, des recettes adaptées et des menus équilibrés.",
        sources: ["anses.fr — Agence nationale sécurité sanitaire alimentation","mangerbouger.fr — recommandations nutritionnelles officielles","Littérature scientifique sur nutrition et TND"],
        lastUpdate: "02/07/2026",
      },
      nico: {
        sait: ["Plans d'entraînement adaptés (débutant à avancé)","Sport et TDAH : bienfaits, activités recommandées","Sport et dyspraxie : activités adaptées, progression douce","Sport et autisme : routines, prévisibilité, activités sensorielles","Exercices de régulation émotionnelle par le corps","Motivation et gamification de l'activité physique"],
        saitPas: ["Je ne suis pas kinésithérapeute et ne remplace pas un suivi médical","Je ne connais pas vos éventuelles contre-indications médicales","Je ne peux pas évaluer votre condition physique réelle à distance"],
        prompt: "Tu es Nico, coach sportif spécialisé dans l'accompagnement des familles TND. Tu crées des plans d'entraînement adaptés à tous les âges et profils, en tenant compte des spécificités du TDAH, de la dyspraxie et de l'autisme. Tu es motivant et encourageant.",
        sources: ["insep.fr — Institut National du Sport","Littérature scientifique sur activité physique et TND","Recommandations OMS sur l'activité physique par âge"],
        lastUpdate: "02/07/2026",
      },
      marie: {
        sait: ["Codes sociaux du quotidien (salutations, conversations, conflits)","Jeux de rôle et mises en situation adaptées","Comprendre les émotions des autres (théorie de l'esprit)","Gestion des situations sociales difficiles (récréation, repas, groupe)","Langage non verbal : expressions, postures, contact visuel","Adaptation au profil de l'enfant (TDAH, TSA, HPI, Dys)"],
        saitPas: ["Je ne remplace pas un suivi en orthophonie ou psychologie","Je ne connais pas les situations spécifiques de votre enfant sauf si vous me les décrivez","Je ne peux pas évaluer le niveau réel de compétences sociales de votre enfant"],
        prompt: "Tu es Marie, spécialiste des codes sociaux pour les enfants TND. Tu aides les enfants à comprendre et pratiquer les interactions sociales dans un espace sécurisé et bienveillant. Tu utilises des exemples concrets, des jeux de rôle et des situations du quotidien.",
        sources: ["Travaux de Simon Baron-Cohen sur la théorie de l'esprit","Programmes d'habiletés sociales validés (PEERS, MAAS)","Recommandations HAS sur l'accompagnement des TSA"],
        lastUpdate: "02/07/2026",
      },
    };
    const fiche = FICHES_DATA[a.id] || {};

    return (
      <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, padding:"12px 16px", background:`${a.color}18`, borderRadius:14, border:`1px solid ${a.color}33` }}>
          <button onClick={()=>setFicheAssistant(null)} style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:16, color:T.textSoft, display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
          <div style={{ width:40, height:40, borderRadius:12, overflow:"hidden", background:a.gradient, flexShrink:0 }}>{a.photo ? <img src={a.photo} alt={a.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <span style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:20 }}>{a.emoji}</span>}</div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:T.text }}>{a.name} — Fiche de transparence</div>
            <div style={{ fontSize:11, color:T.textSoft }}>Mise à jour : {fiche.lastUpdate}</div>
          </div>
        </div>

        {/* Content */}
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Qui je suis */}
          <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:16, padding:"14px" }}>
            <div style={{ fontSize:12, fontWeight:800, color:a.color, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>👤 Qui je suis</div>
            <div style={{ fontSize:13, color:T.text, lineHeight:1.6 }}>{a.desc}</div>
          </div>

          {/* Ce que je sais */}
          <div style={{ background:"rgba(39,174,96,0.08)", borderRadius:12, padding:"14px", border:"1px solid rgba(39,174,96,0.2)" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#27AE60", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>✅ Ce que je sais</div>
            {(fiche.sait||[]).map((s,i)=>(
              <div key={i} style={{ fontSize:13, color:T.textMid, padding:"4px 0", borderBottom:`1px solid ${T.cardBorder}` }}>• {s}</div>
            ))}
          </div>

          {/* Ce que je ne sais pas */}
          <div style={{ background:"rgba(232,115,90,0.08)", borderRadius:12, padding:"14px", border:"1px solid rgba(232,115,90,0.2)" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#E8735A", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>⚠️ Ce que je ne sais pas</div>
            {(fiche.saitPas||[]).map((s,i)=>(
              <div key={i} style={{ fontSize:13, color:T.textMid, padding:"4px 0", borderBottom:`1px solid ${T.cardBorder}` }}>• {s}</div>
            ))}
          </div>

          {/* Mon prompt */}
          <div style={{ background:"rgba(155,89,182,0.08)", borderRadius:12, padding:"14px", border:"1px solid rgba(155,89,182,0.2)" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#9B59B6", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>🔧 Mon prompt système</div>
            <div style={{ fontSize:12, color:T.textSoft, lineHeight:1.6, fontStyle:"italic" }}>{fiche.prompt}</div>
          </div>

          {/* Sources */}
          {(fiche.sources||[]).length > 0 && (
            <div style={{ background:"rgba(212,168,67,0.08)", borderRadius:12, padding:"14px", border:"1px solid rgba(212,168,67,0.2)" }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#D4A843", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>📚 Carnet de sources</div>
              {(fiche.sources||[]).map((s,i)=>(
                <div key={i} style={{ fontSize:13, color:T.textMid, padding:"4px 0" }}>• {s}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Écran sélection — Équipe
  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:22, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text, marginBottom:4 }}>🤖 L'équipe Alfy</div>
        <div style={{ fontSize:13, color:T.textSoft, lineHeight:1.5 }}>Chaque assistant est spécialisé dans un domaine. Choisissez celui dont vous avez besoin.</div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {ASSISTANTS.map(assistant=>(
          <div key={assistant.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"16px", background:`${assistant.color}35`, borderRadius:16, border:`2px solid ${assistant.color}88`, opacity:assistant.available?1:0.85, position:"relative" }}>
            {/* Zone principale cliquable */}
            <div onClick={()=>selectAssistant(assistant)} style={{ display:"flex", alignItems:"center", gap:14, flex:1, cursor:assistant.available?"pointer":"default" }}>
              <div style={{ width:56, height:56, borderRadius:16, overflow:"hidden", flexShrink:0, background:assistant.gradient }}>{assistant.photo ? <img src={assistant.photo} alt={assistant.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>{assistant.emoji}</div>}</div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                  <span style={{ fontSize:16, fontWeight:800, color:T.text }}>{assistant.name}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:assistant.available?"#27AE60":T.textSoft, background:assistant.available?"rgba(39,174,96,0.15)":T.sandDark, padding:"2px 7px", borderRadius:5 }}>{assistant.available?"Disponible":"Bientôt"}</span>
                </div>
                <div style={{ fontSize:12, fontWeight:700, color:assistant.available?assistant.color:T.textSoft, marginBottom:4 }}>{assistant.role}</div>
                <div style={{ fontSize:12, color:T.textSoft, lineHeight:1.4 }}>{assistant.desc}</div>
              </div>
              {assistant.available && <span style={{ fontSize:18, color:T.textSoft }}>›</span>}
            </div>
            {/* Bouton fiche indépendant */}
            <button onClick={()=>setFicheAssistant(assistant)} style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:999, padding:"6px 10px", fontSize:11, fontWeight:700, color:T.textSoft, cursor:"pointer", flexShrink:0 }}>📋 Fiche</button>
          </div>
        ))}
      </div>

      <div style={{ marginTop:20, padding:"14px 16px", background:T.sandDark, borderRadius:16, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ fontSize:12, color:T.textSoft, lineHeight:1.6 }}>
          💡 <strong style={{ color:T.text }}>Alfy V2</strong> — Les assistants spécialisés (Jeanne, Hector, Nico, Marie) seront disponibles dans la prochaine version d'Alfy, avec mémoire personnalisée et apprentissage continu.
        </div>
      </div>
    </div>
  );
}




// ── FICHES DATA ────────────────────────────────────────────────
const FICHE_TYPES = {
  specialist: { label:"Spécialiste", icon:"🩺", color:"#5BBFAD" },
  medication:  { label:"Médicament",  icon:"💊", color:"#E8735A" },
  exercise:    { label:"Exercice",    icon:"🏃", color:"#D4A843" },
  school:      { label:"École/CNED",  icon:"📚", color:"#2C3E52" },
};

const INITIAL_FICHES = [];

const EMPTY_FICHE = {
  type:"", member:"cleo", title:"",
  contact:{ name:"", phone:"", address:"" },
  notes:"", exercises:[], objectives:"", documents:"", updatedAt:"",
};

// ── FICHES VIEW ────────────────────────────────────────────────
function FichesView({ fiches, onAdd, onUpdate, onDelete, appts, onAddAppt, onCancelOccurrence, activeUser=null, themes=DEFAULT_THEMES, onAddTheme }) {
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filterMember, setFilterMember] = useState(()=>activeUser||"all");
  const [filterType, setFilterType] = useState("all");
  const [filterTheme, setFilterTheme] = useState("all");

  useEffect(()=>{ setFilterMember(activeUser||"all"); }, [activeUser]);
  const [newForm, setNewForm] = useState({...EMPTY_FICHE, member: activeUser || MEMBERS[0].id});

  const filtered = fiches.filter(f =>
    (filterMember==="all" || f.member===filterMember) &&
    (filterTheme==="all" || f.theme===filterTheme)
  );

  const handleCreate = () => {
    if (!newForm.title.trim() || !newForm.type) return;
    onAdd({ ...newForm, id:Date.now(), updatedAt:todayISO(), exercises:[] });
    setCreating(false); setNewForm({...EMPTY_FICHE, member: activeUser || MEMBERS[0].id});
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>Fiches de suivi</h2>
        <button onClick={()=>setCreating(s=>!s)} style={btnStyle(T.coral)}>+ Créer</button>
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:8, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Personne :</span>
        <select value={filterMember} onChange={e=>setFilterMember(e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="all">Tous</option>
          {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Thème :</span>
        <select value={filterTheme||"all"} onChange={e=>setFilterTheme(e.target.value)} style={{...inputStyle,fontSize:12,flex:1}}>
          <option value="all">Tous</option>
          {(themes||DEFAULT_THEMES).map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
        </select>
      </div>

      {/* Create form */}
      {creating && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ marginBottom:8 }}>
            <ThemePicker value={newForm.type||(themes||DEFAULT_THEMES)[0]?.id||""} onChange={v=>setNewForm(f=>({...f,type:v}))} themes={themes} onAddTheme={onAddTheme} label="Thème *" />
          </div>
          <div style={{ marginBottom:8 }}>
            <select value={newForm.member} onChange={e=>setNewForm(f=>({...f,member:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <input placeholder="Titre de la fiche *" value={newForm.title} onChange={e=>setNewForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />

          <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", margin:"12px 0 6px" }}>Coordonnées</div>
          <input placeholder="Nom du praticien / cabinet" value={newForm.contact.name} onChange={e=>setNewForm(f=>({...f,contact:{...f.contact,name:e.target.value}}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <input placeholder="Téléphone" value={newForm.contact.phone} onChange={e=>setNewForm(f=>({...f,contact:{...f.contact,phone:e.target.value}}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <input placeholder="Adresse du cabinet" value={newForm.contact.address} onChange={e=>setNewForm(f=>({...f,contact:{...f.contact,address:e.target.value}}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />

          <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", margin:"12px 0 6px" }}>Suivi financier</div>
          <input type="number" placeholder="Prix par séance (€)" value={newForm.sessionPrice||""} onChange={e=>setNewForm(f=>({...f,sessionPrice:+e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />

          <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", margin:"12px 0 6px" }}>Objectifs</div>
          <textarea placeholder="Objectifs thérapeutiques ou scolaires…" value={newForm.objectives} onChange={e=>setNewForm(f=>({...f,objectives:e.target.value}))} rows={2} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box",resize:"vertical",lineHeight:1.5}} />

          <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", margin:"12px 0 6px" }}>Documents / ordonnances</div>
          <textarea placeholder="Copier-coller d'ordonnances, bilans, consignes écrites…" value={newForm.documents} onChange={e=>setNewForm(f=>({...f,documents:e.target.value}))} rows={2} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box",resize:"vertical",lineHeight:1.5,fontSize:12,fontFamily:"monospace"}} />

          <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", margin:"12px 0 6px" }}>Notes</div>
          <textarea placeholder="Notes personnelles…" value={newForm.notes} onChange={e=>setNewForm(f=>({...f,notes:e.target.value}))} rows={2} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box",resize:"vertical",lineHeight:1.5}} />

          <div style={{ display:"flex", gap:8, marginTop:4 }}>
            <button onClick={handleCreate} style={btnStyle(T.coral)}>Créer</button>
            <button onClick={()=>setCreating(false)} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Cards grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
        {filtered.map(fiche => {
          const ft = (themes||DEFAULT_THEMES).find(t=>t.id===fiche.type) || (themes||DEFAULT_THEMES).find(t=>t.id===fiche.theme) || {icon:"📋",label:"Fiche",color:T.textMid};
          const m  = memberOf(fiche.member);
          // Compute payment status
          const today = todayISO();
          const linkedAppts = appts.filter(a => a.title?.toLowerCase().includes(fiche.title?.toLowerCase().split(" ")[0]||"__") && a.member===fiche.member);
          const pastAppts = linkedAppts.flatMap(a => expandRecurringAppt(a)).filter(a => a.date <= today);
          const payments = fiche.payments || [];
          const totalPaid = payments.filter(p=>p.apptId).reduce((s,p)=>s+(p.amount||0),0) + payments.filter(p=>!p.apptId).reduce((s,p)=>s+(p.amount||0),0);
          const sessionPrice = fiche.sessionPrice || 0;
          const totalDue = pastAppts.length * sessionPrice;
          const restant = sessionPrice ? Math.max(0, totalDue - totalPaid) : 0;
          const hasDebt = sessionPrice && restant > 0;

          return (
            <div key={fiche.id} onClick={()=>setSelected(fiche)} style={{ ...cardStyle(), cursor:"pointer", borderTop:`3px solid ${ft.color}`, position:"relative" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:3 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:ft.color, textTransform:"uppercase", letterSpacing:"0.05em" }}>{ft.icon} {ft.label}</span>
                  </div>
                  <div style={{ fontSize:16, fontWeight:800, color:T.text }}>{fiche.title}</div>
                  {fiche.contact.name && <div style={{ fontSize:12, color:T.textSoft, marginTop:2 }}>{fiche.contact.name}</div>}
                </div>
                <Avatar member={fiche.member} size={28} />
              </div>
              {fiche.notes && <div style={{ fontSize:12, color:T.textMid, lineHeight:1.5, marginBottom:8, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{fiche.notes}</div>}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:11, color:T.textSoft }}>{fiche.exercises.length} exercice{fiche.exercises.length!==1?"s":""}</div>
                {hasDebt ? (
                  <div style={{ display:"flex", alignItems:"center", gap:5, background:T.danger+"15", borderRadius:6, padding:"3px 8px" }}>
                    <span style={{ fontSize:10, fontWeight:800, color:T.danger }}>€ {restant.toFixed(2)} à payer</span>
                  </div>
                ) : sessionPrice && totalPaid > 0 ? (
                  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:T.mint }}>✓ À jour</span>
                  </div>
                ) : (
                  <div style={{ fontSize:10, color:T.textSoft }}>Modifié {fiche.updatedAt}</div>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length===0 && <div style={{ color:T.textSoft, fontSize:14, gridColumn:"1/-1" }}>Aucune fiche pour cette sélection.</div>}
      </div>

      {/* Fiche detail modal */}
      {selected && (
        <FicheDetail
          themes={themes} onAddTheme={onAddTheme}
          fiche={selected}
          appts={appts}
          onUpdate={(f)=>{ onUpdate(f); setSelected(f); }}
          onDelete={(id)=>{ onDelete(id); setSelected(null); }}
          onClose={()=>setSelected(null)}
          onAddAppt={onAddAppt}
          onCancelOccurrence={onCancelOccurrence}
        />
      )}
    </div>
  );
}

// ── FICHE DETAIL ───────────────────────────────────────────────
function FicheDetail({ fiche, appts, onUpdate, onDelete, onClose, onAddAppt, onCancelOccurrence, themes=DEFAULT_THEMES, onAddTheme }) {
  const [f, setF] = useState({ ...fiche });
  const [tab, setTab] = useState("info");
  const [newExTitle, setNewExTitle] = useState("");
  const [newExDesc,  setNewExDesc]  = useState("");
  const [newExFreq,  setNewExFreq]  = useState("");
  const [confirmCancelKey, setConfirmCancelKey] = useState(null);
  const [confirmDeleteFiche, setConfirmDeleteFiche] = useState(false);
  const ft = (themes||DEFAULT_THEMES).find(t=>t.id===f.type) || (themes||DEFAULT_THEMES).find(t=>t.id===f.theme) || {icon:"📋",label:"Fiche",color:T.textMid};
  const [showNewRdv, setShowNewRdv] = useState(false);
  const [rdvForm, setRdvForm] = useState({ date:"", time:"", notes:"", recurrence:"none", recurrenceInterval:1, recurrenceEnd:"", customDates:[""] });

  const handleAddRdv = () => {
    if (!rdvForm.date) return;
    const newAppt = {
      id: Date.now(),
      date: rdvForm.date,
      time: rdvForm.time || "09:00",
      title: f.title,
      specialist: f.contact?.name || "",
      member: f.member,
      notes: rdvForm.notes,
      recurrence: rdvForm.recurrence || "none",
      recurrenceInterval: rdvForm.recurrenceInterval || 1,
      recurrenceEnd: rdvForm.recurrenceEnd || "",
      customDates: rdvForm.customDates,
    };
    if (newAppt.recurrence === "custom") {
      newAppt.customDates = (rdvForm.customDates||[]).filter(d=>d.trim());
      newAppt.date = newAppt.customDates[0] || rdvForm.date;
    }
    onAddAppt && onAddAppt(newAppt);
    setRdvForm({ date:"", time:"", notes:"", member: f.member||"olivier", atHome:false, travelTime:20, price:"", recurrence:"none", recurrenceInterval:1, recurrenceEnd:"", customDates:[""] });
    setShowNewRdv(false);
  };

  const save = (updated) => {
    const saved = { ...updated, updatedAt: todayISO() };
    setF(saved); onUpdate(saved);
  };

  const addExercise = () => {
    if (!newExTitle.trim()) return;
    const ex = { id:Date.now(), title:newExTitle, description:newExDesc, frequency: newExFreq?+newExFreq:null, completions:[], done:false };
    save({ ...f, exercises:[...f.exercises, ex] });
    setNewExTitle(""); setNewExDesc(""); setNewExFreq("");
  };

  const toggleEx = (id) => save({ ...f, exercises:f.exercises.map(e=>e.id===id?{...e,done:!e.done}:e) });
  const deleteEx = (id) => save({ ...f, exercises:f.exercises.filter(e=>e.id!==id) });
  const setExFreq = (id, freq) => save({ ...f, exercises:f.exercises.map(e=>e.id===id?{...e,frequency:freq?+freq:null}:e) });

  const today = todayISO();
  const linkedAppts = appts.filter(a => a.title?.toLowerCase().includes(f.title?.toLowerCase().split(" ")[0]||"__") && a.member===f.member);
  const pastAppts = linkedAppts.flatMap(a => expandRecurringAppt(a)).filter(a => a.date <= today);

  const payments = f.payments || [];
  const totalPaid = payments.filter(p=>p.apptId).reduce((s,p)=>s+(p.amount||0),0) + payments.filter(p=>!p.apptId).reduce((s,p)=>s+(p.amount||0),0);
  const [newPay, setNewPay] = useState({ date:"", amount:"", label:"" });

  const addPayment = (extra={}) => {
    if (!newPay.amount && !extra.amount) return;
    const p = { id:Date.now(), date:extra.date||newPay.date||todayISO(), amount:parseFloat(extra.amount||newPay.amount), label:extra.label||newPay.label||"Séance", apptId:extra.apptId||null };
    save({ ...f, payments:[...payments, p] });
    if (!extra.apptId) setNewPay({ date:"", amount:"", label:"" });
  };
  const toggleApptPaid = (appt) => {
    const existing = payments.find(p => p.apptId === appt.id);
    if (existing) {
      save({ ...f, payments: payments.filter(p => p.apptId !== appt.id) });
    } else {
      const p = { id:Date.now(), date:appt.date, amount:f.sessionPrice||0, label:"Séance du "+new Date(appt.date+"T00:00:00").toLocaleDateString("fr-FR",{day:"numeric",month:"long"}), apptId:appt.id };
      save({ ...f, payments:[...payments, p] });
    }
  };
  const deletePayment = (id) => save({ ...f, payments:payments.filter(p=>p.id!==id) });

  const allOccurrences = linkedAppts.flatMap(a => expandRecurringAppt(a)).sort((a,b)=>a.date.localeCompare(b.date));
  const todayStr2 = todayISO();
  const pastOcc   = allOccurrences.filter(a => a.date <= todayStr2);
  const futureOcc = allOccurrences.filter(a => a.date >  todayStr2);
  const paidApptIds = new Set((f.payments||[]).filter(p=>p.apptId).map(p=>p.apptId));
  const unpaidCount = f.sessionPrice ? pastOcc.filter(a => !paidApptIds.has(a.id)).length : 0;

  const DETAIL_TABS = [
    { id:"info",      label:"Infos" },
    { id:"exercises", label:`Exercices (${f.exercises.length})` },
    { id:"notes",     label:"Notes" },
    { id:"rdv_pay",   label:`RDV & Paiements${unpaidCount>0?" ("+unpaidCount+" impayé"+(unpaidCount>1?"s":"")+")":" ("+allOccurrences.length+")"}` },
  ];

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(30,42,58,0.5)", zIndex:1000, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"16px", overflowY:"auto" }}>
      <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:16, width:"100%", maxWidth:600, boxShadow:`0 8px 32px ${T.shadow}`, marginTop:8 }}>
        {/* Header */}
        <div style={{ background:ft.color, borderRadius:"16px 16px 0 0", padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.7)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{ft.icon} {ft.label} · {memberOf(f.member).name}</div>
            <input value={f.title} onChange={e=>setF(x=>({...x,title:e.target.value}))} onBlur={()=>save(f)} style={{ fontSize:20, fontWeight:800, color:T.white, background:"transparent", border:"none", outline:"none", width:"100%", marginTop:4 }} />
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:8, color:T.white, cursor:"pointer", padding:"6px 10px", fontSize:16 }}>✕</button>
        </div>

        {/* Sub-tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${T.sandDark}`, padding:"0 16px" }}>
          {DETAIL_TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{ background:"transparent", border:"none", cursor:"pointer", padding:"10px 12px", fontSize:12, fontWeight:tab===t.id?700:400, color:tab===t.id?ft.color:T.textSoft, borderBottom:tab===t.id?`2px solid ${ft.color}`:"2px solid transparent" }}>{t.label}</button>
          ))}
        </div>

        <div style={{ padding:"16px 20px", maxHeight:"60vh", overflowY:"auto" }}>
          {/* INFO TAB */}
          {tab==="info" && (
            <div>
              <Section title="Coordonnées">
                <FieldRow label="Nom / cabinet" value={f.contact.name} onChange={v=>setF(x=>({...x,contact:{...x.contact,name:v}}))} onBlur={()=>save(f)} />
                <FieldRow label="Téléphone" value={f.contact.phone} onChange={v=>setF(x=>({...x,contact:{...x.contact,phone:v}}))} onBlur={()=>save(f)} />
                <FieldRow label="Adresse" value={f.contact.address} onChange={v=>setF(x=>({...x,contact:{...x.contact,address:v}}))} onBlur={()=>save(f)} />
                <NavButtons address={f.contact.address} />
                <div style={{ marginTop:10 }}><ThemePicker value={f.type||""} onChange={v=>{ setF(x=>({...x,type:v})); save({...f,type:v}); }} themes={themes} onAddTheme={onAddTheme} label="Thème" /></div>
              </Section>
              <Section title="Objectifs">
                <textarea value={f.objectives} onChange={e=>setF(x=>({...x,objectives:e.target.value}))} onBlur={()=>save(f)} rows={3} placeholder="Objectifs thérapeutiques ou scolaires…" style={{ ...inputStyle, width:"100%", boxSizing:"border-box", resize:"vertical", lineHeight:1.5 }} />
              </Section>
              <Section title="Documents / ordonnances">
                <textarea value={f.documents} onChange={e=>setF(x=>({...x,documents:e.target.value}))} onBlur={()=>save(f)} rows={4} placeholder="Copier-coller d'ordonnances, bilans, consignes écrites…" style={{ ...inputStyle, width:"100%", boxSizing:"border-box", resize:"vertical", lineHeight:1.5, fontSize:12, fontFamily:"monospace" }} />
              </Section>
            </div>
          )}

          {/* EXERCISES TAB */}
          {tab==="exercises" && (
            <div>
              {f.exercises.map(ex=>(
                <div key={ex.id} style={{ ...cardStyle(), display:"flex", gap:10, marginBottom:8, padding:"10px 12px", opacity:ex.done?0.6:1, alignItems:"flex-start" }}>
                  <button onClick={()=>toggleEx(ex.id)} style={{ width:20,height:20,borderRadius:4,border:`2px solid ${ex.done?ft.color:T.sandDark}`,background:ex.done?ft.color:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2 }}>
                    {ex.done && <span style={{ color:T.white,fontSize:10,fontWeight:900 }}>✓</span>}
                  </button>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:T.text, textDecoration:ex.done?"line-through":"none" }}>{ex.title}</div>
                    {ex.description && <div style={{ fontSize:12, color:T.textSoft, marginTop:2 }}>{ex.description}</div>}
                    <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:4 }}>
                      <span style={{ fontSize:11, color:T.textSoft }}>Fréquence :</span>
                      <input type="number" min="0" placeholder="—" value={ex.frequency||""} onChange={e=>setExFreq(ex.id, e.target.value)} style={{...inputStyle,width:40,padding:"2px 4px",fontSize:11,textAlign:"center"}} />
                      <span style={{ fontSize:11, color:T.textSoft }}>x/semaine</span>
                    </div>
                  </div>
                  <button onClick={()=>deleteEx(ex.id)} style={{ background:"transparent", border:"none", cursor:"pointer", color:T.textSoft, fontSize:14 }}>×</button>
                </div>
              ))}
              {f.exercises.length===0 && <div style={{ color:T.textSoft, fontSize:13, marginBottom:12 }}>Aucun exercice pour l'instant.</div>}
              <div style={{ background:T.sandDark, borderRadius:10, padding:12, marginTop:8 }}>
                <div style={{ fontSize:12, fontWeight:700, color:T.textSoft, marginBottom:8 }}>Ajouter un exercice</div>
                <input placeholder="Titre de l'exercice" value={newExTitle} onChange={e=>setNewExTitle(e.target.value)} style={{...inputStyle,width:"100%",marginBottom:6,boxSizing:"border-box"}} />
                <input placeholder="Description (optionnel)" value={newExDesc} onChange={e=>setNewExDesc(e.target.value)} style={{...inputStyle,width:"100%",marginBottom:6,boxSizing:"border-box"}} />
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                  <span style={{ fontSize:12, color:T.textSoft }}>Fréquence :</span>
                  <input type="number" min="0" placeholder="Ex: 3" value={newExFreq} onChange={e=>setNewExFreq(e.target.value)} style={{...inputStyle,width:60,padding:"5px 8px",fontSize:12}} />
                  <span style={{ fontSize:12, color:T.textSoft }}>fois/semaine</span>
                </div>
                <button onClick={addExercise} style={btnStyle(ft.color)}>Ajouter</button>
              </div>
            </div>
          )}

          {/* NOTES TAB */}
          {tab==="notes" && (
            <div>
              <Section title="Notes de suivi">
                <textarea value={f.notes} onChange={e=>setF(x=>({...x,notes:e.target.value}))} onBlur={()=>save(f)} rows={10} placeholder="Notes de séance, observations, évolutions…" style={{ ...inputStyle, width:"100%", boxSizing:"border-box", resize:"vertical", lineHeight:1.6 }} />
              </Section>
            </div>
          )}

          {/* RDV & PAIEMENTS — onglet fusionné */}
          {tab==="rdv_pay" && (
            <div>
              {/* Récap financier */}
              <div style={{ marginBottom:14 }}>
                {f.sessionPrice > 0 && (
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:unpaidCount>0?T.danger+"10":T.mint+"10", borderRadius:10, padding:"10px 14px", marginBottom:8, border:"1px solid "+(unpaidCount>0?T.danger+"30":T.mint+"30") }}>
                    <div>
                      <div style={{ fontSize:11,fontWeight:700,color:T.textSoft,textTransform:"uppercase",marginBottom:2 }}>Total payé</div>
                      <div style={{ fontSize:20,fontWeight:800,color:ft.color }}>{totalPaid.toFixed(2)} €</div>
                      <div style={{ fontSize:11,color:T.textSoft }}>{pastOcc.length} séance{pastOcc.length!==1?"s":""} · {f.sessionPrice} €/séance</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:11,fontWeight:700,color:unpaidCount>0?T.danger:T.mint,marginBottom:2 }}>{unpaidCount>0?"Restant dû":"À jour ✓"}</div>
                      {unpaidCount>0 && <div style={{ fontSize:20,fontWeight:800,color:T.danger }}>{(unpaidCount*(f.sessionPrice||0)).toFixed(2)} €</div>}
                    </div>
                  </div>
                )}
                <div style={{ background:T.sandDark,borderRadius:10,padding:"8px 12px",display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ fontSize:12,color:T.textSoft }}>Prix de la séance :</span>
                  <input type="number" placeholder="Ex: 65" value={f.sessionPrice||""} onChange={e=>setF(x=>({...x,sessionPrice:+e.target.value}))} onBlur={()=>save(f)} style={{...inputStyle,width:90,padding:"5px 8px",fontSize:13}} />
                  <span style={{ fontSize:12,color:T.textSoft }}>€</span>
                </div>
              </div>

              {/* Tous les RDV — ordre chronologique */}
              {allOccurrences.length===0 && <div style={{ color:T.textSoft,fontSize:13,marginBottom:12 }}>Aucun RDV lié. Les RDV contenant "{f.title.split(" ")[0]}" apparaîtront ici.</div>}
              {[...allOccurrences].sort((a,b)=>a.date.localeCompare(b.date)).map((a)=>{
                const isPast = a.date <= todayStr2;
                const apptPaid = payments.some(p => p.apptId===a.id);
                return (
                  <div key={a.id} style={{ ...cardStyle(), display:"flex", alignItems:"center", gap:12, marginBottom:6, padding:"10px 12px", opacity:apptPaid?0.6:1, borderLeft:`3px solid ${isPast?(apptPaid?T.mint:T.danger):"transparent"}` }}>
                    <div style={{ background:ft.color+"22",borderRadius:8,padding:"5px 8px",textAlign:"center",minWidth:40,flexShrink:0 }}>
                      <div style={{ fontSize:14,fontWeight:800,color:ft.color }}>{a.date.slice(8)}</div>
                      <div style={{ fontSize:9,color:ft.color,fontWeight:600 }}>{new Date(a.date+"T00:00:00").toLocaleDateString("fr-FR",{month:"short"}).toUpperCase()}</div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13,fontWeight:600,color:T.text,textDecoration:apptPaid?"line-through":"none" }}>{a.title}</div>
                      <div style={{ fontSize:11,color:T.textSoft }}>{a.time}{a.specialist?" · "+a.specialist:""}</div>
                      {f.contact?.address && <NavButtons address={f.contact.address} />}
                    </div>
                    {isPast ? (
                      <button onClick={()=>f.sessionPrice && toggleApptPaid(a)} style={{ display:"flex",alignItems:"center",gap:4,background:apptPaid?T.mint+"22":T.danger+"12",border:`1px solid ${apptPaid?T.mint:T.danger}`,borderRadius:6,padding:"5px 10px",cursor:f.sessionPrice?"pointer":"not-allowed",fontSize:11,fontWeight:700,color:apptPaid?T.mint:T.danger,flexShrink:0 }} title={f.sessionPrice?"":"Configurez d'abord le prix de la séance"}>
                        {apptPaid ? "✓ Payé" : "€ À payer"}
                      </button>
                    ) : (
                      <span style={{ fontSize:10,color:T.textSoft,background:T.sandDark,borderRadius:5,padding:"2px 7px",flexShrink:0 }}>À venir</span>
                    )}
                    {onCancelOccurrence && (
                      confirmCancelKey === a.occKey ? (
                        <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                          <button onClick={()=>{ onCancelOccurrence(a.occKey); setConfirmCancelKey(null); }} style={{ background:T.danger, color:"white", border:"none", borderRadius:6, padding:"5px 8px", fontSize:11, fontWeight:700, cursor:"pointer" }}>Confirmer</button>
                          <button onClick={()=>setConfirmCancelKey(null)} style={{ background:"transparent", border:"1px solid "+T.textSoft, color:T.textSoft, borderRadius:6, padding:"5px 8px", fontSize:11, cursor:"pointer" }}>Annuler</button>
                        </div>
                      ) : (
                        <button onClick={()=>setConfirmCancelKey(a.occKey)} title="Retirer ce RDV" style={{ background:"transparent", border:"none", cursor:"pointer", color:T.textSoft, fontSize:16, flexShrink:0, padding:"0 4px" }}>×</button>
                      )
                    )}
                  </div>
                );
              })}

              {/* Ajouter un paiement */}
              <Section title="Enregistrer un paiement">
                <div style={{ background:T.sandDark,borderRadius:10,padding:12 }}>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
                    <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Montant (€)</div>
                      <input type="number" placeholder="65" value={newPay.amount} onChange={e=>setNewPay(p=>({...p,amount:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
                    </div>
                    <div><div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div>
                      <input type="date" value={newPay.date} onChange={e=>setNewPay(p=>({...p,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
                    </div>
                  </div>
                  <input placeholder="Libellé (ex: Séance bilan)" value={newPay.label} onChange={e=>setNewPay(p=>({...p,label:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
                  <button onClick={addPayment} style={btnStyle(ft.color)}>Enregistrer</button>
                </div>
                {payments.length>0 && (
                  <div style={{ marginTop:12 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:T.textSoft,marginBottom:6 }}>Paiements enregistrés</div>
                    {payments.map(p=>(
                      <div key={p.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid "+T.sandDark }}>
                        <div style={{ fontSize:12,color:T.text }}>{p.amount.toFixed(2)} € · <span style={{ color:T.textSoft }}>{p.label} · {new Date(p.date+"T00:00:00").toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}</span></div>
                        <button onClick={()=>deletePayment(p.id)} style={{ background:"transparent",border:"none",cursor:"pointer",color:T.textSoft,fontSize:14 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
          )}
        </div>

        {/* Footer */}
        {showNewRdv && (
          <div style={{ padding:"12px 20px", borderTop:"1px solid "+T.sandDark, background:T.sandDark }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.text, marginBottom:10 }}>📅 Nouveau RDV — {f.title}</div>
            <div style={{ display:"flex", gap:8, marginBottom:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Date *</div>
                <input type="date" value={rdvForm.date} onChange={e=>setRdvForm(r=>({...r,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Heure</div>
                <TimePicker value={rdvForm.time||"09:00"} onChange={v=>setRdvForm(r=>({...r,time:v}))} />
              </div>
            </div>
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Membre</div>
              <select value={rdvForm.member||f.member} onChange={e=>setRdvForm(r=>({...r,member:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <label onClick={()=>setRdvForm(r=>({...r,atHome:!r.atHome,travelTime:!r.atHome?0:20}))} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", padding:"7px 10px", background:rdvForm.atHome?"rgba(91,191,173,0.15)":T.sandDark, borderRadius:8, border:"1px solid "+(rdvForm.atHome?T.mint:T.cardBorder), marginBottom:8 }}>
              <div style={{ width:16, height:16, borderRadius:4, border:"2px solid "+(rdvForm.atHome?T.mint:T.cardBorder), background:rdvForm.atHome?T.mint:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {rdvForm.atHome && <span style={{ color:"white", fontSize:10, fontWeight:900 }}>✓</span>}
              </div>
              <span style={{ fontSize:12, fontWeight:600, color:rdvForm.atHome?T.mint:T.textMid }}>🏠 À domicile</span>
            </label>
            {!rdvForm.atHome && (
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Temps de trajet (min)</div>
                <select value={rdvForm.travelTime||20} onChange={e=>setRdvForm(r=>({...r,travelTime:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                  {[5,10,15,20,30,45,60,90].map(t=><option key={t} value={t}>{t} min</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Prix de la séance (€)</div>
              <input type="number" placeholder="0" value={rdvForm.price||""} onChange={e=>setRdvForm(r=>({...r,price:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            </div>
            <RecurrenceFields form={rdvForm} setForm={setRdvForm} mode="appt" />
            <input placeholder="Notes (optionnel)" value={rdvForm.notes} onChange={e=>setRdvForm(r=>({...r,notes:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:10,boxSizing:"border-box"}} />
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleAddRdv} style={btnStyle(ft.color,{flex:2})}>Ajouter le RDV</button>
              <button onClick={()=>setShowNewRdv(false)} style={btnStyle(T.textSoft,{flex:1})}>Annuler</button>
            </div>
          </div>
        )}
        <div style={{ padding:"12px 20px", borderTop:"1px solid "+T.sandDark, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <div style={{ fontSize:11, color:T.textSoft }}>Modifié le {f.updatedAt}</div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>{ setShowNewRdv(s=>{ if(!s) setRdvForm(r=>({...r, price: r.price || f.sessionPrice || ""})); return !s; }); }} style={btnStyle(ft.color, { fontSize:12, padding:"6px 12px" })}>+ RDV</button>
            {confirmDeleteFiche ? (
              <>
                <button onClick={()=>onDelete(f.id)} style={{ background:T.danger, color:"white", border:"none", borderRadius:6, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Confirmer ?</button>
                <button onClick={()=>setConfirmDeleteFiche(false)} style={{ background:"transparent", border:"1px solid "+T.textSoft, color:T.textSoft, borderRadius:6, padding:"6px 12px", fontSize:12, cursor:"pointer" }}>Annuler</button>
              </>
            ) : (
              <button onClick={()=>setConfirmDeleteFiche(true)} style={btnStyle(T.danger, { fontSize:12, padding:"6px 12px" })}>Supprimer</button>
            )}
          </div>
        </div>
      </div>


    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>{title}</div>
      {children}
    </div>
  );
}

function FieldRow({ label, value, onChange, onBlur }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
      <div style={{ fontSize:12, color:T.textSoft, minWidth:90 }}>{label}</div>
      <input value={value} onChange={e=>onChange(e.target.value)} onBlur={onBlur} style={{...inputStyle, flex:1, fontSize:13}} />
    </div>
  );
}


// ── Shared styles (kept for reference, now inline above) ───────
const cardStyleDef = (extra={}) => ({ background:T.sandDark,borderRadius:20,border:`1px solid ${T.cardBorder}`,padding:"14px 16px",boxShadow:`0 1px 4px ${T.shadow}`,...extra });
const inputStyleDef = { background:T.sandDark,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"8px 10px",fontSize:13,color:T.text,outline:"none" };
const btnStyleDef = (color,extra={}) => ({ background:color,color:T.white,border:"none",borderRadius:999,padding:"9px 16px",fontSize:13,fontWeight:700,cursor:"pointer",...extra });

// ── PENSE-BÊTE ─────────────────────────────────────────────────
const INITIAL_NOTES = [];

function PenseBete({ notes, onAdd, onUpdate, onDelete, onConvert, fiches=[] }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [convertModal, setConvertModal] = useState(null);
  const [showQuickReminder, setShowQuickReminder] = useState(false);
  const [convertStep, setConvertStep]   = useState(null); // "task" | "appt" | "event"
  const [convertForm, setConvertForm]   = useState({ date:"", time:"", member:"olivier", importance:2 });

  const handleAdd = () => {
    if (!text.trim()) return;
    onAdd({ id:Date.now(), text:text.trim(), createdAt:todayISO(), done:false });
    setText("");
  };

  return (
    <>
      {/* Floating button */}
      <button onClick={()=>setOpen(s=>!s)} style={{ position:"fixed", bottom:24, left:20, zIndex:500, width:52, height:52, borderRadius:"50%", background:T.gold, border:"none", cursor:"pointer", boxShadow:"0 4px 16px rgba(30,42,58,0.25)", fontSize:22, color:T.white, display:"flex", alignItems:"center", justifyContent:"center" }} title="Pense-bête">
        💡
      </button>

      {open && (
        <div style={{ position:"fixed", bottom:88, left:20, zIndex:500, width:300, maxWidth:"calc(100vw - 40px)", background:T.sandDark, borderRadius:16, boxShadow:"0 8px 32px rgba(30,42,58,0.2)", overflow:"hidden" }}>
          <div style={{ background:T.gold, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:14,fontWeight:800,color:T.white }}>💡 Pense-bête</div>
            <button onClick={()=>setOpen(false)} style={{ background:"rgba(255,255,255,0.2)",border:"none",borderRadius:6,color:T.white,cursor:"pointer",padding:"3px 8px",fontSize:14 }}>✕</button>
          </div>
          <div style={{ padding:12, maxHeight:360, overflowY:"auto" }}>
            {/* Add note */}
            <div style={{ marginBottom:12 }}>
              <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Note rapide…" rows={2} style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",marginBottom:6}} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),handleAdd())} />
              <button onClick={handleAdd} style={btnStyle(T.gold,{width:"100%",padding:"7px"})}>Ajouter</button>
            </div>
            {/* Notes list */}
            {notes.length===0 && <div style={{ color:T.textSoft,fontSize:13,textAlign:"center",padding:"8px 0" }}>Aucune note pour l'instant.</div>}
            {notes.map(n=>(
              <div key={n.id} style={{ background:T.sand,borderRadius:10,padding:"10px 12px",marginBottom:8,borderLeft:"3px solid "+T.gold }}>
                <div style={{ fontSize:13,color:T.text,lineHeight:1.5,marginBottom:4,textDecoration:n.done?"line-through":"none",opacity:n.done?0.5:1 }}>{n.text}</div>
                <div style={{ fontSize:10,color:T.textSoft,marginBottom:6 }}>{n.createdAt}</div>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                  <button onClick={()=>setConvertModal({...n,_fiches:fiches})} style={{ fontSize:11,background:T.mint+"22",color:T.mint,border:"none",borderRadius:5,padding:"3px 8px",cursor:"pointer",fontWeight:700 }}>→ Convertir</button>
                  <button onClick={()=>{ onConvert("reminder",n,{}); }} style={{ fontSize:11,background:T.coral+"22",color:T.coral,border:"none",borderRadius:5,padding:"3px 8px",cursor:"pointer",fontWeight:700 }}>⏰ Rappel</button>
                  <button onClick={()=>onUpdate({...n,done:!n.done})} style={{ fontSize:11,background:T.sandDark,color:T.textMid,border:"none",borderRadius:5,padding:"3px 8px",cursor:"pointer" }}>{n.done?"↩ Rouvrir":"✓ Fait"}</button>
                  <button onClick={()=>onDelete(n.id)} style={{ fontSize:11,background:"transparent",color:T.textSoft,border:"none",cursor:"pointer",padding:"3px 4px" }}>×</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Convert modal — step 1: choose type */}
      {convertModal && !convertStep && (
        <Modal title="Convertir en…" onClose={()=>setConvertModal(null)}>
          <div style={{ fontSize:13,color:T.textMid,marginBottom:16,fontStyle:"italic" }}>"{convertModal.text}"</div>
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            <button onClick={()=>setConvertStep("task")}  style={btnStyle(T.coral,{textAlign:"left"})}>✓ Tâche — à faire à une date précise</button>
            <button onClick={()=>setConvertStep("appt")}  style={btnStyle(T.mint,{textAlign:"left"})}>🏥 Rendez-vous — avec un spécialiste</button>
            <button onClick={()=>setConvertStep("event")} style={btnStyle(T.navyMid,{textAlign:"left"})}>📅 Événement — dans le planning</button>
            <button onClick={()=>setConvertModal(null)} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </Modal>
      )}

      {/* Convert modal — step 2: task details */}
      {convertModal && convertStep==="task" && (
        <Modal title="Nouvelle tâche" onClose={()=>{ setConvertModal(null); setConvertStep(null); }}>
          <div style={{ fontSize:13,color:T.textMid,marginBottom:12,fontStyle:"italic" }}>"{convertModal.text}"</div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div>
              <input type="date" value={convertForm.date} onChange={e=>setConvertForm(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",borderColor:convertForm.date?"":T.danger}} />
            </div>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure (optionnel)</div>
              <TimePicker value={convertForm.time} onChange={v=>setConvertForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Assigné à</div>
            <MemberPicker value={convertForm.members||convertForm.member} onChange={v=>setConvertForm(f=>({...f,members:v,member:v[0]}))} />
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:6 }}>Importance</div>
            <div style={{ display:"flex",gap:6 }}>
              {IMPORTANCE.map(imp=>(
                <button key={imp.value} onClick={()=>setConvertForm(f=>({...f,importance:imp.value}))} style={{ flex:1,padding:"5px 4px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",border:"2px solid "+(convertForm.importance===imp.value?imp.color:T.sandDark),background:convertForm.importance===imp.value?imp.color+"22":"transparent",color:convertForm.importance===imp.value?imp.color:T.textSoft,textAlign:"center" }}>
                  <div>{imp.icon}</div><div style={{fontSize:9}}>{imp.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <button onClick={()=>{
              if (!convertForm.date) return;
              onConvert("task", convertModal, convertForm);
              setConvertModal(null); setConvertStep(null);
              setConvertForm({ date:"", time:"", member:"olivier", importance:2 });
            }} style={btnStyle(convertForm.date?T.coral:T.textSoft,{flex:1})} disabled={!convertForm.date}>Créer la tâche</button>
            <button onClick={()=>setConvertStep(null)} style={btnStyle(T.textSoft)}>Retour</button>
          </div>
        </Modal>
      )}

      {/* Convert modal — step 2: appt details */}
      {convertModal && convertStep==="appt" && (
        <Modal title="Nouveau rendez-vous" onClose={()=>{ setConvertModal(null); setConvertStep(null); }}>
          <div style={{ fontSize:13,color:T.textMid,marginBottom:12,fontStyle:"italic" }}>"{convertModal.text}"</div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date</div>
              <input type="date" value={convertForm.date} onChange={e=>setConvertForm(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            </div>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure</div>
              <TimePicker value={convertForm.time||"09:00"} onChange={v=>setConvertForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Spécialiste</div>
            <input list="specialists-pb" placeholder="Nom du spécialiste" value={convertForm.specialist||""} onChange={e=>setConvertForm(f=>({...f,specialist:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            <datalist id="specialists-pb">
              {fiches.filter(fi=>fi.contact?.name).map(fi=><option key={fi.id} value={fi.contact.name}>{fi.title}</option>)}
            </datalist>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Membre</div>
            <MemberPicker value={convertForm.members||convertForm.member} onChange={v=>setConvertForm(f=>({...f,members:v,member:v[0]}))} />
          </div>
          <input placeholder="Notes (optionnel)" value={convertForm.notes||""} onChange={e=>setConvertForm(f=>({...f,notes:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:10,boxSizing:"border-box"}} />
          <div style={{ display:"flex",gap:8 }}>
            <button onClick={()=>{
              onConvert("appt", convertModal, convertForm);
              setConvertModal(null); setConvertStep(null);
              setConvertForm({ date:"", time:"", member:"olivier", importance:2, specialist:"", notes:"" });
            }} style={btnStyle(T.mint,{flex:1})}>Créer le RDV</button>
            <button onClick={()=>setConvertStep(null)} style={btnStyle(T.textSoft)}>Retour</button>
          </div>
        </Modal>
      )}
      {/* Convert modal — step 2: event details */}
      {convertModal && convertStep==="event" && (
        <Modal title="Nouvel événement" onClose={()=>{ setConvertModal(null); setConvertStep(null); }}>
          <div style={{ fontSize:13,color:T.textMid,marginBottom:12,fontStyle:"italic" }}>"{convertModal.text}"</div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Jour</div>
              <select value={convertForm.day||0} onChange={e=>setConvertForm(f=>({...f,day:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
                {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Heure</div>
              <TimePicker value={convertForm.time||"09:00"} onChange={v=>setConvertForm(f=>({...f,time:v}))} style={{width:"100%",boxSizing:"border-box"}} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Membre</div>
            <MemberPicker value={convertForm.members||convertForm.member||"olivier"} onChange={v=>setConvertForm(f=>({...f,members:v,member:v[0]}))} />
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Type</div>
            <select value={convertForm.eventType||"work"} onChange={e=>setConvertForm(f=>({...f,eventType:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {Object.entries(TYPE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <button onClick={()=>{
              onConvert("event", convertModal, convertForm);
              setConvertModal(null); setConvertStep(null);
              setConvertForm({ date:"", time:"", member:"olivier", importance:2, specialist:"", notes:"", day:0, eventType:"work" });
            }} style={btnStyle(T.navyMid,{flex:1})}>Créer l'événement</button>
            <button onClick={()=>setConvertStep(null)} style={btnStyle(T.textSoft)}>Retour</button>
          </div>
        </Modal>
      )}

    </>
  );
}


// ══════════════════════════════════════════════════════════════
// 🍽️  MODULE REPAS
// ══════════════════════════════════════════════════════════════

const MEAL_COLOR = "#E07B39";

// ── Estimation calorique — reconnaissance par mot-clé sur le nom de l'ingrédient (approximatif, jamais exact) ──
const INGREDIENT_KCAL_DB = [
  { keywords:["pâtes","spaghetti","macaroni","tagliatelle","penne","nouilles"], kcal100:360, unitG:null },
  { keywords:["riz"], kcal100:360, unitG:null },
  { keywords:["boeuf","bœuf","steak"], kcal100:250, unitG:null },
  { keywords:["porc","lardons","bacon"], kcal100:280, unitG:null },
  { keywords:["poulet","volaille","dinde"], kcal100:165, unitG:null },
  { keywords:["thon"], kcal100:130, unitG:140 },
  { keywords:["saumon"], kcal100:200, unitG:null },
  { keywords:["poisson blanc","cabillaud","colin","lieu"], kcal100:90, unitG:null },
  { keywords:["jambon"], kcal100:145, unitG:null },
  { keywords:["oeuf","œuf"], kcal100:155, unitG:50 },
  { keywords:["fromage","gruyère","emmental","comté","parmesan"], kcal100:350, unitG:null },
  { keywords:["mozzarella"], kcal100:280, unitG:null },
  { keywords:["yaourt"], kcal100:60, unitG:125 },
  { keywords:["lait"], kcal100:60, unitG:null },
  { keywords:["crème"], kcal100:300, unitG:null },
  { keywords:["beurre"], kcal100:720, unitG:null },
  { keywords:["huile"], kcal100:880, unitG:null },
  { keywords:["farine"], kcal100:360, unitG:null },
  { keywords:["sucre"], kcal100:400, unitG:null },
  { keywords:["pain","baguette"], kcal100:265, unitG:null },
  { keywords:["pomme de terre","pommes de terre"], kcal100:80, unitG:150 },
  { keywords:["tomate"], kcal100:18, unitG:120 },
  { keywords:["oignon"], kcal100:40, unitG:100 },
  { keywords:["ail"], kcal100:150, unitG:5 },
  { keywords:["carotte"], kcal100:40, unitG:80 },
  { keywords:["courgette"], kcal100:17, unitG:200 },
  { keywords:["poivron"], kcal100:30, unitG:150 },
  { keywords:["champignon"], kcal100:25, unitG:null },
  { keywords:["haricot vert","haricots verts"], kcal100:30, unitG:null },
  { keywords:["salade","laitue"], kcal100:15, unitG:200 },
  { keywords:["olive"], kcal100:115, unitG:null },
  { keywords:["citron"], kcal100:30, unitG:100 },
  { keywords:["pomme"], kcal100:52, unitG:150 },
  { keywords:["banane"], kcal100:90, unitG:120 },
  { keywords:["avocat"], kcal100:160, unitG:150 },
];

function normalizeStr(s) {
  return (s||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"");
}

// Convertit une quantité + unité en grammes (approximatif). Retourne null si non convertible.
function ingredientToGrams(ing, dbEntry) {
  const unit = normalizeStr(ing.unit);
  const qty = ing.qty || 0;
  if (unit === "g" || unit === "gr" || unit === "grammes") return qty;
  if (unit === "kg") return qty * 1000;
  if (unit === "ml" || unit === "cl" || unit === "l") {
    const ml = unit === "cl" ? qty*10 : unit === "l" ? qty*1000 : qty;
    return ml; // approximation 1ml ≈ 1g pour la plupart des liquides courants
  }
  // unité non-poids (vide, "gousses", "boîtes", "tranches"...) → poids unitaire connu de l'ingrédient, sinon estimation générique
  const unitWeight = dbEntry?.unitG || 60;
  return qty * unitWeight;
}

// Estime les calories d'une recette à partir de ses ingrédients — TOUJOURS approximatif
function estimateRecipeCalories(recipe) {
  const ingredients = recipe.ingredients || [];
  if (ingredients.length === 0) return null;
  let totalKcal = 0, matched = 0;
  ingredients.forEach(ing => {
    const name = normalizeStr(ing.name);
    const entry = INGREDIENT_KCAL_DB.find(e => e.keywords.some(k => name.includes(normalizeStr(k))));
    if (!entry) return;
    const grams = ingredientToGrams(ing, entry);
    if (!grams) return;
    totalKcal += (grams/100) * entry.kcal100;
    matched++;
  });
  if (matched === 0) return null;
  const portions = recipe.portions || 1;
  return { perServing: Math.round(totalKcal/portions), matched, total: ingredients.length };
}
const MEAL_SLOTS = ["Petit-déj","Midi","Goûter","Soir"];
const MEAL_SLOT_ICONS = { "Petit-déj":"🌅", "Midi":"☀️", "Goûter":"🍎", "Soir":"🌙" };
const RAYON_ORDER = ["Fruits & Légumes","Viandes & Poissons","Produits laitiers","Épicerie","Surgelés","Boissons","Autre"];

const INITIAL_RECIPES = [
  { id:1, title:"Pasta bolognaise", portions:4, prepTime:15, cookTime:30, tags:["pâtes","viande"],
    ingredients:[
      { name:"Pâtes", qty:400, unit:"g", rayon:"Épicerie" },
      { name:"Bœuf haché", qty:300, unit:"g", rayon:"Viandes & Poissons" },
      { name:"Tomates concassées", qty:400, unit:"g", rayon:"Épicerie" },
      { name:"Oignon", qty:1, unit:"", rayon:"Fruits & Légumes" },
      { name:"Ail", qty:2, unit:"gousses", rayon:"Fruits & Légumes" },
    ],
    steps:["Faire revenir l'oignon et l'ail.","Ajouter la viande, faire dorer.","Ajouter les tomates, mijoter 20 min.","Cuire les pâtes et servir."],
    notes:"Peut se congeler."
  },
  { id:2, title:"Salade niçoise", portions:4, prepTime:20, cookTime:10, tags:["salade","poisson"],
    ingredients:[
      { name:"Thon en boîte", qty:2, unit:"boîtes", rayon:"Épicerie" },
      { name:"Œufs", qty:4, unit:"", rayon:"Produits laitiers" },
      { name:"Tomates", qty:3, unit:"", rayon:"Fruits & Légumes" },
      { name:"Haricots verts", qty:200, unit:"g", rayon:"Fruits & Légumes" },
      { name:"Olives noires", qty:50, unit:"g", rayon:"Épicerie" },
    ],
    steps:["Cuire les œufs durs et les haricots.","Couper les tomates.","Assembler la salade et assaisonner."],
    notes:"Idéal en été."
  },
];

const INITIAL_MENU = {}; // { "Lun-Midi": recipeId, "Lun-Soir": recipeId, ... }
const INITIAL_SHOPPING = []; // [{ name, qty, unit, rayon, checked }]

// ── Repas helpers ──────────────────────────────────────────────
function buildShoppingList(menu, recipes) {
  const map = {};
  Object.values(menu).forEach(recipeId => {
    if (!recipeId) return;
    const recipe = recipes.find(r => r.id === recipeId);
    if (!recipe) return;
    recipe.ingredients.forEach(ing => {
      const key = ing.name.toLowerCase();
      if (map[key]) {
        map[key].qty = (parseFloat(map[key].qty)||0) + (parseFloat(ing.qty)||0);
      } else {
        map[key] = { ...ing, checked:false, id: Date.now() + Math.random() };
      }
    });
  });
  return Object.values(map).sort((a,b) => {
    const ra = RAYON_ORDER.indexOf(a.rayon), rb = RAYON_ORDER.indexOf(b.rayon);
    return (ra===-1?99:ra) - (rb===-1?99:rb);
  });
}

// ── REPAS VIEW ─────────────────────────────────────────────────
function getWeekKey(offset=0) {
  const d = new Date();
  const dow = d.getDay()===0?6:d.getDay()-1;
  d.setDate(d.getDate()-dow+offset*7);
  return toLocalISO(d);
}
function RepasView({ recipes, menu, shopping, onUpdateMenu, onUpdateShopping, onAddRecipe, onUpdateRecipe, onDeleteRecipe }) {
  const [tab, setTab] = useState("menu");
  const [weekOffset, setWeekOffset] = useState(0);
  const [editRecipe, setEditRecipe] = useState(null);
  const [showNewRecipe, setShowNewRecipe] = useState(false);
  const [newRecipe, setNewRecipe] = useState({ title:"", portions:4, prepTime:10, cookTime:20, tags:"", ingredients:[], steps:[], notes:"" });
  const [newIngLine, setNewIngLine] = useState({ name:"", qty:"", unit:"", rayon:"Épicerie" });
  const [newStep, setNewStep] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState("");

  const TABS_REPAS = [
    { id:"menu",    label:"Menu semaine", icon:"📅" },
    { id:"courses", label:"Courses",      icon:"🛒" },
    { id:"recettes",label:"Recettes",     icon:"📖" },
  ];

  const weekKey = getWeekKey(weekOffset);
  const currentMenu = menu[weekKey] || {};
  const updateCurrentMenu = (newMenu) => onUpdateMenu({...menu, [weekKey]: newMenu});

  const getWeekLabel = () => {
    if (weekOffset===0) return "Cette semaine";
    if (weekOffset===1) return "Semaine prochaine";
    if (weekOffset===-1) return "Semaine dernière";
    const d = new Date(); const dow=d.getDay()===0?6:d.getDay()-1;
    d.setDate(d.getDate()-dow+weekOffset*7);
    return d.toLocaleDateString("fr-FR",{day:"numeric",month:"long"});
  };

  const generateShopping = () => {
    const list = buildShoppingList(currentMenu, recipes);
    onUpdateShopping(list);
  };

  const askAI = async () => {
    setAiLoading(true); setAiSuggestion("");
    try {
      const emptySlots = [];
      DAYS.forEach(day => MEAL_SLOTS.forEach(slot => { if (!currentMenu[`${day}-${slot}`]) emptySlots.push(`${day}-${slot}`); }));
      if (emptySlots.length === 0) { setAiSuggestion("Tous les créneaux de la semaine sont déjà planifiés."); setAiLoading(false); return; }
      const recipeList = recipes.map(r => `${r.id}: ${r.title}`).join("\n");
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ model:"claude-sonnet-4-6", max_tokens:800,
          messages:[{ role:"user", content:`Tu es un assistant familial. Voici les recettes disponibles (id: titre) :\n${recipeList}\n\nVoici les créneaux de repas à remplir pour la semaine (format "Jour-Créneau") : ${emptySlots.join(", ")}.\nLes petit-déjeuners et goûters sont essentiels pour les personnes TND — privilégie des repas équilibrés et prévisibles.\nPour chaque créneau, choisis l'id de la recette la plus adaptée dans la liste ci-dessus (jamais un titre inventé). Si aucune recette ne convient pour un créneau, mets null.\nRéponds UNIQUEMENT avec un objet JSON valide, sans texte autour, sans balises markdown, au format exact : {"Lun-Petit-déj": 123, "Lun-Midi": null, ...} en couvrant bien tous les créneaux listés.` }]
        })
      });
      const data = await res.json();
      const raw = (data.content?.[0]?.text || "").trim().replace(/^```json\s*|```$/g, "");
      let parsed = {};
      try { parsed = JSON.parse(raw); } catch { setAiSuggestion("Réponse de l'IA illisible, réessaie."); setAiLoading(false); return; }
      const newMenu = { ...currentMenu };
      let filled = 0, skipped = 0;
      Object.entries(parsed).forEach(([key, recipeId]) => {
        if (recipeId && recipes.some(r => r.id === recipeId)) { newMenu[key] = recipeId; filled++; }
        else skipped++;
      });
      updateCurrentMenu(newMenu);
      setAiSuggestion(`✅ ${filled} créneau${filled>1?"x":""} rempli${filled>1?"s":""} automatiquement.${skipped>0?` ${skipped} créneau${skipped>1?"x":""} laissé${skipped>1?"s":""} vide${skipped>1?"s":""} (aucune recette adaptée trouvée).`:""}`);
    } catch { setAiSuggestion("Erreur de connexion."); }
    setAiLoading(false);
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>🍽️ Repas</h2>
      </div>

      {/* Sub-tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:16, borderBottom:`1px solid ${T.sandDark}`, paddingBottom:0 }}>
        {TABS_REPAS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ background:"transparent", border:"none", cursor:"pointer", padding:"8px 14px", fontSize:13, fontWeight:tab===t.id?700:400, color:tab===t.id?MEAL_COLOR:T.textSoft, borderBottom:tab===t.id?`2px solid ${MEAL_COLOR}`:"2px solid transparent" }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── MENU TAB ── */}
      {tab==="menu" && (
        <div>
          {/* Week navigator */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:T.sandDark, borderRadius:10, border:"1px solid "+T.sandDark, padding:"8px 12px", marginBottom:12 }}>
            <button onClick={()=>setWeekOffset(o=>o-1)} style={{ background:"transparent",border:"none",cursor:"pointer",fontSize:20,color:T.textMid,padding:"0 6px" }}>{"<"}</button>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:13,fontWeight:700,color:T.text }}>{getWeekLabel()}</div>
            </div>
            <div style={{ display:"flex",gap:4,alignItems:"center" }}>
              {weekOffset!==0 && <button onClick={()=>setWeekOffset(0)} style={{ fontSize:11,color:MEAL_COLOR,background:"transparent",border:"1px solid "+MEAL_COLOR,borderRadius:6,cursor:"pointer",padding:"3px 8px",fontWeight:700 }}>Auj.</button>}
              <button onClick={()=>setWeekOffset(o=>o+1)} style={{ background:"transparent",border:"none",cursor:"pointer",fontSize:20,color:T.textMid,padding:"0 6px" }}>{">"}</button>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
            <button onClick={generateShopping} style={btnStyle(MEAL_COLOR,{fontSize:12})}>🛒 Générer la liste de courses</button>
            <button onClick={askAI} disabled={aiLoading} style={btnStyle(T.navyMid,{fontSize:12})}>{aiLoading?"...":"🤖 Compléter le menu (IA)"}</button>
          </div>
          {aiSuggestion && (
            <div style={{ ...cardStyle(), marginBottom:14, background:"#FFF8F0", borderLeft:`3px solid ${MEAL_COLOR}` }}>
              <div style={{ fontSize:11,fontWeight:800,color:MEAL_COLOR,marginBottom:6 }}>💡 Suggestion Alfy</div>
              <pre style={{ fontSize:12,color:T.text,whiteSpace:"pre-wrap",margin:0,lineHeight:1.6 }}>{aiSuggestion}</pre>
              <button onClick={()=>setAiSuggestion("")} style={{ fontSize:11,color:T.textSoft,background:"transparent",border:"none",cursor:"pointer",marginTop:8 }}>Fermer</button>
            </div>
          )}
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {DAYS.map(day=>(
              <div key={day} style={{ background:T.sandDark, borderRadius:10, border:`1px solid ${T.cardBorder}`, overflow:"hidden" }}>
                {/* Day header */}
                <div style={{ background:MEAL_COLOR+"18", padding:"6px 12px" }}>
                  <span style={{ fontSize:12, fontWeight:800, color:T.text }}>{day}</span>
                </div>
                {/* Slots */}
                <div style={{ padding:"6px 8px", display:"flex", flexDirection:"column", gap:4 }}>
                  {MEAL_SLOTS.map(slot=>{
                    const key = `${day}-${slot}`;
                    const recipeId = currentMenu[key];
                    const recipe = recipes.find(r=>r.id===recipeId);
                    return (
                      <div key={key} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", background:recipe?MEAL_COLOR+"12":T.sand, borderRadius:7, border:`1px solid ${recipe?MEAL_COLOR:T.sandDark}` }}>
                        {/* Slot icon + label */}
                        <div style={{ fontSize:16, flexShrink:0 }}>{MEAL_SLOT_ICONS[slot]}</div>
                        <div style={{ fontSize:10, fontWeight:700, color:MEAL_COLOR, textTransform:"uppercase", letterSpacing:"0.04em", width:52, flexShrink:0 }}>{slot}</div>
                        {/* Recipe select */}
                        <select value={recipeId||""} onChange={e=>updateCurrentMenu({...currentMenu, [key]: e.target.value==="ext"?"ext": e.target.value ? +e.target.value : null})} style={{ background:"transparent",border:"none",fontSize:12,color:recipe?T.text:T.textSoft,flex:1,cursor:"pointer",outline:"none",minWidth:0 }}>
                          <option value="">— Aucun —</option>
                          {recipes.map(r=><option key={r.id} value={r.id}>{r.title}</option>)}
                          <option value="ext">🍕 Autre</option>
                        </select>
                        {/* Voir la recette */}
                        {recipe && (
                          <button onClick={()=>setEditRecipe(recipe)} title="Voir la recette" style={{ background:"transparent", border:"none", cursor:"pointer", fontSize:14, flexShrink:0, padding:"0 2px" }}>👁️</button>
                        )}
                        {/* Cook select */}
                        {recipe && (
                          <select value={currentMenu[key+"_cook"]||""} onChange={e=>updateCurrentMenu({...currentMenu,[key]:recipeId,[key+"_cook"]:e.target.value})} style={{ background:"transparent",border:"none",fontSize:11,color:T.textSoft,cursor:"pointer",outline:"none",flexShrink:0 }}>
                            <option value="">👨‍🍳</option>
                            {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── COURSES TAB ── */}
      {tab==="courses" && (
        <div>
          {shopping.length===0 ? (
            <div style={{ textAlign:"center", padding:"32px 0", color:T.textSoft }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🛒</div>
              <div style={{ fontSize:14, marginBottom:12 }}>La liste est vide.</div>
              <button onClick={generateShopping} style={btnStyle(MEAL_COLOR)}>Générer depuis le menu</button>
            </div>
          ) : (
            <>
              <div style={{ display:"flex", gap:8, marginBottom:14, justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13,color:T.textSoft }}>{shopping.filter(i=>!i.checked).length} article{shopping.filter(i=>!i.checked).length!==1?"s":""} restant{shopping.filter(i=>!i.checked).length!==1?"s":""}</div>
                <div style={{ display:"flex",gap:6 }}>
                  <button onClick={generateShopping} style={{ ...btnStyle(MEAL_COLOR,{fontSize:11,padding:"6px 10px"}) }}>↻ Regénérer</button>
                  <button onClick={()=>onUpdateShopping(shopping.map(i=>({...i,checked:false})))} style={btnStyle(T.textSoft,{fontSize:11,padding:"6px 10px"})}>Tout décocher</button>
                </div>
              </div>
              {RAYON_ORDER.concat(["Autre"]).map(rayon=>{
                const items = shopping.filter(i=>(i.rayon||"Autre")===rayon);
                if (items.length===0) return null;
                return (
                  <div key={rayon} style={{ marginBottom:16 }}>
                    <div style={{ fontSize:11,fontWeight:800,color:MEAL_COLOR,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6 }}>{rayon}</div>
                    {items.map(item=>(
                      <div key={item.id||item.name} onClick={()=>onUpdateShopping(shopping.map(i=>i.name===item.name?{...i,checked:!i.checked}:i))} style={{ ...cardStyle(), display:"flex",alignItems:"center",gap:10,marginBottom:6,padding:"9px 12px",opacity:item.checked?0.45:1,cursor:"pointer" }}>
                        <div style={{ width:18,height:18,borderRadius:4,border:`2px solid ${item.checked?T.mint:T.sandDark}`,background:item.checked?T.mint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                          {item.checked && <span style={{color:T.white,fontSize:10,fontWeight:900}}>✓</span>}
                        </div>
                        <div style={{ flex:1,fontSize:13,fontWeight:600,color:T.text,textDecoration:item.checked?"line-through":"none" }}>{item.name}</div>
                        <div style={{ fontSize:12,color:T.textSoft }}>{item.qty} {item.unit}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}
          {/* Manual add */}
          <div style={{ ...cardStyle({ background:T.sandDark }), marginTop:8 }}>
            <div style={{ fontSize:11,fontWeight:700,color:T.textSoft,marginBottom:8 }}>Ajouter manuellement</div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 60px 60px 1fr",gap:6,marginBottom:8 }}>
              <input placeholder="Produit" value={newIngLine.name} onChange={e=>setNewIngLine(f=>({...f,name:e.target.value}))} style={{...inputStyle,boxSizing:"border-box"}} />
              <input placeholder="Qté" type="number" value={newIngLine.qty} onChange={e=>setNewIngLine(f=>({...f,qty:e.target.value}))} style={inputStyle} />
              <input placeholder="Unité" value={newIngLine.unit} onChange={e=>setNewIngLine(f=>({...f,unit:e.target.value}))} style={inputStyle} />
              <select value={newIngLine.rayon} onChange={e=>setNewIngLine(f=>({...f,rayon:e.target.value}))} style={inputStyle}>
                {RAYON_ORDER.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button onClick={()=>{
              if (!newIngLine.name.trim()) return;
              onUpdateShopping([...shopping,{...newIngLine,id:Date.now(),checked:false}]);
              setNewIngLine({name:"",qty:"",unit:"",rayon:"Épicerie"});
            }} style={btnStyle(MEAL_COLOR)}>Ajouter</button>
          </div>
        </div>
      )}

      {/* ── RECETTES TAB ── */}
      {tab==="recettes" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:13,color:T.textSoft }}>{recipes.length} recette{recipes.length!==1?"s":""}</div>
            <button onClick={()=>setShowNewRecipe(s=>!s)} style={btnStyle(MEAL_COLOR)}>+ Ajouter</button>
          </div>

          {showNewRecipe && (
            <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
              <div style={{ fontSize:13,fontWeight:800,color:MEAL_COLOR,marginBottom:10 }}>Nouvelle recette</div>
              <input placeholder="Titre" value={newRecipe.title} onChange={e=>setNewRecipe(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8 }}>
                <div><div style={{fontSize:10,color:T.textSoft,marginBottom:2}}>Portions</div><input type="number" value={newRecipe.portions} onChange={e=>setNewRecipe(f=>({...f,portions:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
                <div><div style={{fontSize:10,color:T.textSoft,marginBottom:2}}>Prép (min)</div><input type="number" value={newRecipe.prepTime} onChange={e=>setNewRecipe(f=>({...f,prepTime:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
                <div><div style={{fontSize:10,color:T.textSoft,marginBottom:2}}>Cuisson (min)</div><input type="number" value={newRecipe.cookTime} onChange={e=>setNewRecipe(f=>({...f,cookTime:+e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
              </div>
              <input placeholder="Tags (ex: pâtes, végétarien)" value={newRecipe.tags} onChange={e=>setNewRecipe(f=>({...f,tags:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
              {/* Ingredients */}
              <div style={{fontSize:11,fontWeight:700,color:T.textSoft,marginBottom:6}}>Ingrédients</div>
              {newRecipe.ingredients.map((ing,i)=>(
                <div key={i} style={{display:"flex",gap:4,marginBottom:4,alignItems:"center"}}>
                  <span style={{fontSize:12,flex:1}}>{ing.qty} {ing.unit} {ing.name} <em style={{color:T.textSoft}}>({ing.rayon})</em></span>
                  <button onClick={()=>setNewRecipe(f=>({...f,ingredients:f.ingredients.filter((_,j)=>j!==i)}))} style={{background:"transparent",border:"none",cursor:"pointer",color:T.textSoft}}>×</button>
                </div>
              ))}
              <div style={{display:"grid",gridTemplateColumns:"1fr 50px 60px 1fr auto",gap:4,marginBottom:8}}>
                <input placeholder="Ingrédient" value={newIngLine.name} onChange={e=>setNewIngLine(f=>({...f,name:e.target.value}))} style={inputStyle} />
                <input placeholder="Qté" value={newIngLine.qty} onChange={e=>setNewIngLine(f=>({...f,qty:e.target.value}))} style={inputStyle} />
                <input placeholder="Unité" value={newIngLine.unit} onChange={e=>setNewIngLine(f=>({...f,unit:e.target.value}))} style={inputStyle} />
                <select value={newIngLine.rayon} onChange={e=>setNewIngLine(f=>({...f,rayon:e.target.value}))} style={inputStyle}>{RAYON_ORDER.map(r=><option key={r} value={r}>{r}</option>)}</select>
                <button onClick={()=>{ if(!newIngLine.name.trim())return; setNewRecipe(f=>({...f,ingredients:[...f.ingredients,{...newIngLine}]})); setNewIngLine({name:"",qty:"",unit:"",rayon:"Épicerie"}); }} style={btnStyle(MEAL_COLOR,{padding:"8px 10px",fontSize:12})}>+</button>
              </div>
              {/* Steps */}
              <div style={{fontSize:11,fontWeight:700,color:T.textSoft,marginBottom:6}}>Étapes</div>
              {newRecipe.steps.map((s,i)=>(
                <div key={i} style={{display:"flex",gap:6,marginBottom:4,alignItems:"flex-start"}}>
                  <span style={{fontSize:11,color:MEAL_COLOR,fontWeight:700,minWidth:18}}>{i+1}.</span>
                  <span style={{fontSize:12,flex:1}}>{s}</span>
                  <button onClick={()=>setNewRecipe(f=>({...f,steps:f.steps.filter((_,j)=>j!==i)}))} style={{background:"transparent",border:"none",cursor:"pointer",color:T.textSoft}}>×</button>
                </div>
              ))}
              <div style={{display:"flex",gap:6,marginBottom:10}}>
                <input placeholder="Ajouter une étape…" value={newStep} onChange={e=>setNewStep(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&newStep.trim()){setNewRecipe(f=>({...f,steps:[...f.steps,newStep.trim()]}));setNewStep("");}}} style={{...inputStyle,flex:1}} />
                <button onClick={()=>{ if(!newStep.trim())return; setNewRecipe(f=>({...f,steps:[...f.steps,newStep.trim()]})); setNewStep(""); }} style={btnStyle(MEAL_COLOR,{padding:"8px 10px",fontSize:12})}>+</button>
              </div>
              <textarea placeholder="Notes (allergènes, astuces…)" value={newRecipe.notes} onChange={e=>setNewRecipe(f=>({...f,notes:e.target.value}))} rows={2} style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",marginBottom:10}} />
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{
                  if(!newRecipe.title.trim())return;
                  const tags = typeof newRecipe.tags==="string" ? newRecipe.tags.split(",").map(t=>t.trim()).filter(Boolean) : newRecipe.tags;
                  onAddRecipe({...newRecipe,tags,id:Date.now()});
                  setShowNewRecipe(false);
                  setNewRecipe({title:"",portions:4,prepTime:10,cookTime:20,tags:"",ingredients:[],steps:[],notes:""});
                }} style={btnStyle(MEAL_COLOR)}>Enregistrer</button>
                <button onClick={()=>setShowNewRecipe(false)} style={btnStyle(T.textSoft)}>Annuler</button>
              </div>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
            {recipes.map(recipe=>{
              const cal = estimateRecipeCalories(recipe);
              return (
              <div key={recipe.id} onClick={()=>setEditRecipe(recipe)} style={{...cardStyle(),cursor:"pointer",borderTop:`3px solid ${MEAL_COLOR}`}}>
                <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:4}}>{recipe.title}</div>
                <div style={{fontSize:12,color:T.textSoft,marginBottom:6}}>
                  ⏱ {recipe.prepTime+recipe.cookTime} min · 🍽 {recipe.portions} pers{cal && <> · 🔥 ≈{cal.perServing} kcal/portion</>}
                </div>
                {recipe.tags?.length>0 && (
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {(Array.isArray(recipe.tags)?recipe.tags:recipe.tags.split(",")).map((tag,i)=>(
                      <span key={i} style={{fontSize:10,background:MEAL_COLOR+"22",color:MEAL_COLOR,borderRadius:4,padding:"1px 6px",fontWeight:700}}>{tag.trim()}</span>
                    ))}
                  </div>
                )}
                <div style={{fontSize:11,color:T.textSoft,marginTop:8}}>{recipe.ingredients.length} ingrédient{recipe.ingredients.length!==1?"s":""}</div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recipe detail modal */}
      {editRecipe && (
        <Modal title={editRecipe.title} onClose={()=>setEditRecipe(null)}>
          <div style={{fontSize:12,color:T.textSoft,marginBottom:4}}>
            ⏱ {editRecipe.prepTime+editRecipe.cookTime} min · 🍽 {editRecipe.portions} portions
          </div>
          {(() => { const cal = estimateRecipeCalories(editRecipe); return cal ? (
            <div style={{fontSize:12,color:MEAL_COLOR,fontWeight:700,marginBottom:12}}>
              🔥 ≈{cal.perServing} kcal/portion <span style={{fontSize:10,color:T.textSoft,fontWeight:400}}>(estimation sur {cal.matched}/{cal.total} ingrédients reconnus)</span>
            </div>
          ) : (
            <div style={{fontSize:11,color:T.textSoft,marginBottom:12,fontStyle:"italic"}}>Estimation calorique non disponible pour cette recette</div>
          ); })()}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:800,color:MEAL_COLOR,textTransform:"uppercase",marginBottom:6}}>Ingrédients</div>
            {editRecipe.ingredients.map((ing,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4,paddingBottom:4,borderBottom:`1px solid ${T.sandDark}`}}>
                <span style={{color:T.text,fontWeight:500}}>{ing.name}</span>
                <span style={{color:T.textSoft}}>{ing.qty} {ing.unit}</span>
              </div>
            ))}
          </div>
          {editRecipe.steps?.length>0 && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:800,color:MEAL_COLOR,textTransform:"uppercase",marginBottom:6}}>Préparation</div>
              {editRecipe.steps.map((step,i)=>(
                <div key={i} style={{display:"flex",gap:8,marginBottom:8,fontSize:13,lineHeight:1.5}}>
                  <span style={{color:MEAL_COLOR,fontWeight:800,minWidth:20}}>{i+1}.</span>
                  <span style={{color:T.text}}>{step}</span>
                </div>
              ))}
            </div>
          )}
          {editRecipe.notes && <div style={{fontSize:12,color:T.textMid,fontStyle:"italic",background:T.sand,borderRadius:8,padding:"8px 10px",marginBottom:12}}>{editRecipe.notes}</div>}
          <button onClick={()=>{ onDeleteRecipe(editRecipe.id); setEditRecipe(null); }} style={btnStyle(T.danger,{width:"100%",fontSize:12,padding:"8px"})}>Supprimer la recette</button>
        </Modal>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🔔 SYSTÈME DE RAPPELS
// ══════════════════════════════════════════════════════════════

const DEFAULT_NOTIF_PREFS = {
  rdv: true,           rdv_before: 15,
  tasks: true,         tasks_before: 0,
  events: true,        events_before: 15,
  repas: true,         repas_time: "17:30",
  menu: true,          menu_time: "20:00",
  mood_matin: true,    mood_matin_time: "08:00",
  mood_soir: true,     mood_soir_time: "21:00",
  bandeau_rdv: true,
  bandeau_tasks: true,
  bandeau_events: true,
  bandeau_repas: true,
  bandeau_menu: true,
  bandeau_mood: true,
};

const TIMING_OPTIONS = [0,5,10,15,20,30,45,60,90,120];

function useRappels({ events, tasks, appts, menu, recipes, notifPrefs=DEFAULT_NOTIF_PREFS, moods=[], personalMissions=[], currentUser=null }) {
  const [bandeau, setBandeau] = useState([]);
  const [notifGranted, setNotifGranted] = useState(false);
  const timerRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission === "granted") setNotifGranted(true);
      else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(p => setNotifGranted(p === "granted"));
      }
    }
  }, []);

  const sendNotif = (title, body, icon="🔔") => {
    if (notifGranted) {
      try { new Notification(`${icon} Alfy — ${title}`, { body, icon:"/favicon.ico" }); } catch {}
    }
  };

  // Build daily bandeau on load
  useEffect(() => {
    const now = new Date();
    const todayStr = toLocalISO(now);
    const todayDow = now.getDay() === 0 ? 6 : now.getDay() - 1; // Mon=0
    const items = [];

    // Today's appts
    const todayAppts = appts.flatMap(a => {
      const occ = a.recurrence && a.recurrence !== "none" ? (() => {
        const all = [];
        const start = new Date(a.date + "T00:00:00");
        const end = a.recurrenceEnd ? new Date(a.recurrenceEnd + "T00:00:00") : new Date(start.getTime() + 365*864e5);
        const iw = a.recurrence === "monthly" ? null : (a.recurrenceInterval || (a.recurrence === "biweekly" ? 2 : 1));
        let cur = new Date(start), idx = 0;
        while (cur <= end && idx < 52) {
          all.push(toLocalISO(cur));
          if (a.recurrence === "monthly") { cur = new Date(cur); cur.setMonth(cur.getMonth()+1); }
          else cur = new Date(cur.getTime() + iw*7*864e5);
          idx++;
        }
        return all;
      })() : [a.date];
      return occ.includes(todayStr) ? [a] : [];
    });
    if (notifPrefs.bandeau_rdv!==false) todayAppts.forEach(a => items.push({ type:"rdv", text:`${a.time} — ${a.title} (${a.specialist||"RDV"})`, color:T.mint, icon:"🏥" }));

    // Today's tasks
    const todayTasks = tasks.filter(t => !t.done && t.date === todayStr);
    if (notifPrefs.bandeau_tasks) todayTasks.forEach(t => items.push({ type:"task", text:`${t.time||""}${t.time?" — ":""}${t.title}`, color:TYPE_COLORS.task, icon:"✓" }));

    // Today's events
    const todayEvents = events.filter(e => e.day === todayDow && (!e.recurrence || e.recurrence !== "none" || e.recurrence === "weekly" || e.recurrence === "daily"));
    if (notifPrefs.bandeau_events) todayEvents.forEach(e => items.push({ type:"event", text:`${e.time} — ${e.title}`, color:TYPE_COLORS[e.type]||T.navyMid, icon:"📅" }));


    // Tonight's meal
    const DAYS_FR = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
    const dayKey = `${DAYS_FR[todayDow]}-Soir`;
    const tonightRecipeId = menu[dayKey];
    const tonightRecipe = recipes.find(r => r.id === tonightRecipeId);
    if (notifPrefs.bandeau_repas && tonightRecipe) items.push({ type:"repas", text:`Ce soir : ${tonightRecipe.title} (${tonightRecipe.prepTime+tonightRecipe.cookTime} min)`, color:MEAL_COLOR, icon:"🍽️" });

    // Menu planning reminder (Sunday evening check)
    const isWeekendEvening = (now.getDay() === 0 || now.getDay() === 6) && now.getHours() >= 18;
    const nextWeekHasMenu = Object.keys(menu).length > 0;
    if (notifPrefs.bandeau_menu && isWeekendEvening && !nextWeekHasMenu) {
      items.push({ type:"menu", text:"Pensez à préparer le menu de la semaine prochaine !", color:MEAL_COLOR, icon:"📋" });
    }
    // Mood reminder in bandeau
    const todayMoodEntries = (typeof moods !== "undefined" ? moods : []).filter(m => m.date === todayStr);
    const hasMatin = todayMoodEntries.some(m=>m.slot==="matin");
    const hasSoir  = todayMoodEntries.some(m=>m.slot==="soir");
    const hour = now.getHours();
    if (notifPrefs.bandeau_mood!==false) {
      if (!hasMatin && hour >= 6 && hour < 12) items.push({ type:"mood", text:"Évaluation du matin non faite 🌅", color:"#27AE60", icon:"😊" });
      if (!hasSoir && hour >= 19) items.push({ type:"mood", text:"Évaluation du soir non faite 🌙", color:"#9B59B6", icon:"😊" });
    }

    const pendingPersonal = pendingPersonalCount(personalMissions, currentUser);
    if (pendingPersonal > 0) {
      items.push({ type:"personal_mission", text:`${pendingPersonal} défi${pendingPersonal>1?"s":""} perso à voter/valider ⭐`, color:"#9B59B6", icon:"🗳️", tab:"game" });
    }

    setBandeau(items);
  }, [events, tasks, appts, menu, recipes, moods, notifPrefs, personalMissions, currentUser]);

  // Notification scheduler — checks every minute
  useEffect(() => {
    if (!notifGranted) return;
    const check = () => {
      const now = new Date();
      const hhmm = now.toTimeString().slice(0,5);
      const todayStr = toLocalISO(now);
      const todayDow = now.getDay() === 0 ? 6 : now.getDay() - 1;
      const DAYS_FR = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

      // RDV notifications (15 min before)
      if (notifPrefs.rdv) appts.forEach(a => {
        if (a.date === todayStr && a.time) {
          const [h, m] = a.time.split(":").map(Number);
          const apptMins = h*60+m, nowMins = now.getHours()*60+now.getMinutes();
          const rdvBefore = notifPrefs.rdv_before ?? 15;
          if (rdvBefore > 0 && apptMins - nowMins === rdvBefore) sendNotif(a.title, `Dans ${rdvBefore} min — ${a.specialist||""}`, "🏥");
          if (apptMins - nowMins === 0) sendNotif(a.title, `C'est maintenant !`, "🏥");
        }
      });

      // Task notifications at their time
      if (notifPrefs.tasks) tasks.filter(t=>!t.done && t.date===todayStr && t.time).forEach(t => {
        const taskBefore = notifPrefs.tasks_before ?? 0;
        const [th2,tm2] = t.time.split(":").map(Number);
        const taskMins = th2*60+tm2 - taskBefore;
        const nowMins2 = now.getHours()*60+now.getMinutes();
        if (taskMins === nowMins2) sendNotif(t.title, taskBefore>0?`Dans ${taskBefore} min`:"C'est maintenant !", "✓");
      });

      // Event notifications (15 min before)
      if (notifPrefs.events) events.filter(e => e.day===todayDow && e.time).forEach(e => {
        const [h, m] = e.time.split(":").map(Number);
        const evMins = h*60+m, nowMins = now.getHours()*60+now.getMinutes();
        const evBefore = notifPrefs.events_before ?? 15;
        if (evBefore > 0 && evMins - nowMins === evBefore) sendNotif(e.title, `Dans ${evBefore} min`, "📅");
        if (evMins - nowMins === 0) sendNotif(e.title, `C'est maintenant !`, "📅");
      });

      // Evening meal reminder at 17:30
      if (notifPrefs.repas && hhmm === (notifPrefs.repas_time||"17:30")) {
        const dayKey = `${DAYS_FR[todayDow]}-Soir`;
        const r = recipes.find(rec => rec.id === menu[dayKey]);
        if (r) sendNotif("Repas du soir", `Ce soir : ${r.title} (${r.prepTime+r.cookTime} min de préparation)`, "🍽️");
        else   sendNotif("Repas du soir", "Aucun repas prévu — pensez à préparer quelque chose !", "🍽️");
      }

      // Weekly menu reminder Sunday at 20:00
      if (notifPrefs.menu && now.getDay() === 0 && hhmm === (notifPrefs.menu_time||"20:00")) {
        sendNotif("Menu de la semaine", "N'oubliez pas de préparer le menu de la semaine prochaine !", "📋");
      }
      if (notifPrefs.mood_matin!==false && hhmm === (notifPrefs.mood_matin_time||"08:00")) {
        sendNotif("Évaluation du matin 🌅", "Comment tu vas ce matin ? Prends 1 minute pour évaluer ton humeur.", "😊");
      }
      if (notifPrefs.mood_soir!==false && hhmm === (notifPrefs.mood_soir_time||"21:00")) {
        sendNotif("Évaluation du soir 🌙", "Comment s'est passée ta journée ? Prends 1 minute pour évaluer ton humeur.", "😊");
      }
    };

    timerRef.current = setInterval(check, 60000);
    return () => clearInterval(timerRef.current);
  }, [notifGranted, events, tasks, appts, menu, recipes]);

  return { bandeau, setBandeau, notifGranted };
}

function Bandeau({ count, notifGranted, onRequestNotif, onOpenSas }) {
  return (
    <div style={{ background:T.sandDark, borderBottom:`1px solid ${T.cardBorder}` }}>
      {!notifGranted && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 20px", background:"rgba(156,184,160,0.15)", borderBottom:`1px solid ${T.cardBorder}` }}>
          <span style={{ fontSize:12, color:"#5C8064" }}>🔔 Activer les notifications pour recevoir des rappels même quand Alfy est en arrière-plan</span>
          <button onClick={onRequestNotif} style={{ fontSize:11, background:T.mint, color:T.white, border:"none", borderRadius:999, padding:"4px 10px", cursor:"pointer", fontWeight:700 }}>Activer</button>
        </div>
      )}
      <button onClick={onOpenSas} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", padding:"10px 20px", background:"transparent", border:"none", cursor:"pointer" }}>
        <span style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:14 }}>📋</span>
          <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Points importants du jour</span>
        </span>
        {count > 0 && (
          <span style={{ background:T.coral, color:"white", borderRadius:20, minWidth:20, height:20, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 6px" }}>{count}</span>
        )}
      </button>
    </div>
  );
}


function MoodAlertBanner({ alerts, onDismiss }) {
  const unseen = alerts.filter(a=>!a.seen);
  if (unseen.length === 0) return null;
  const MOOD_EMOJI = ["😔","😕","😐"];
  return (
    <div style={{ background:"#4A2020", borderBottom:"1px solid #E8735A" }}>
      {unseen.map(a=>(
        <div key={a.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 20px", gap:10 }}>
          <span style={{ fontSize:12, color:"#FFD5CC", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:15 }}>{MOOD_EMOJI[a.moodValue-1]||"⚠️"}</span>
            ⚠️ {memberOf(a.member).name} a signalé une humeur difficile ce {a.slot} ({new Date(a.createdAt).toTimeString().slice(0,5)})
          </span>
          <button onClick={()=>onDismiss(a.id)} style={{ fontSize:11, background:"#E8735A", color:"white", border:"none", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontWeight:700, flexShrink:0 }}>Vu</button>
        </div>
      ))}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 📁 MODULE DOCUMENTS
// ══════════════════════════════════════════════════════════════

const DOC_COLOR = "#6B5B95";

const DOC_TYPES = [
  { value:"bilan",       label:"Bilan",          icon:"📋" },
  { value:"ordonnance",  label:"Ordonnance",      icon:"💊" },
  { value:"compte_rendu",label:"Compte-rendu",    icon:"📝" },
  { value:"courrier",    label:"Courrier",         icon:"✉️" },
  { value:"certificat",  label:"Certificat",      icon:"📜" },
  { value:"autre",       label:"Autre",            icon:"📄" },
];

const INITIAL_DOCUMENTS = [];

const docTypeOf = (v) => DOC_TYPES.find(d=>d.value===v) || DOC_TYPES[DOC_TYPES.length-1];

function DocumentsView({ documents, fiches, onAdd, onUpdate, onDelete, activeUser=null }) {
  const [filterMember,   setFilterMember]   = useState(()=>activeUser||"all");

  useEffect(()=>{ setFilterMember(activeUser||"all"); }, [activeUser]);
  const [filterType,     setFilterType]     = useState("all");
  const [filterSpecialist, setFilterSpecialist] = useState("all");
  const [search,         setSearch]         = useState("");
  const [showAdd,        setShowAdd]        = useState(false);
  const [editing,        setEditing]        = useState(null);
  const [form, setForm] = useState({ title:"", type:"bilan", member:activeUser||MEMBERS[0].id, specialist:"", date:"", author:"", url:"", notes:"" });

  // Build specialist list from fiches + existing docs
  const specialists = [...new Set([
    ...fiches.filter(f=>f.contact?.name).map(f=>f.contact.name),
    ...documents.map(d=>d.specialist).filter(Boolean)
  ])].sort();

  const filtered = documents.filter(d =>
    (filterMember==="all" || d.member===filterMember) &&
    (filterType==="all" || d.type===filterType) &&
    (filterSpecialist==="all" || d.specialist===filterSpecialist) &&
    (!search || d.title.toLowerCase().includes(search.toLowerCase()) || d.notes?.toLowerCase().includes(search.toLowerCase()) || d.author?.toLowerCase().includes(search.toLowerCase()))
  ).sort((a,b)=>b.date.localeCompare(a.date));

  const handleAdd = () => {
    if (!form.title.trim()) return;
    onAdd({ ...form, id:Date.now() });
    setShowAdd(false);
    setForm({ title:"", type:"bilan", member:activeUser||MEMBERS[0].id, specialist:"", date:"", author:"", url:"", notes:"" });
  };

  const handleSave = () => {
    onUpdate(editing);
    setEditing(null);
  };

  // Group by member for display
  const grouped = MEMBERS.reduce((acc, m) => {
    const docs = filtered.filter(d=>d.member===m.id);
    if (docs.length > 0) acc[m.id] = docs;
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>📁 Documents</h2>
        <button onClick={()=>setShowAdd(s=>!s)} style={btnStyle(DOC_COLOR)}>+ Ajouter</button>
      </div>

      {/* Search */}
      <input placeholder="🔍 Rechercher un document…" value={search} onChange={e=>setSearch(e.target.value)} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:10}} />

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Personne :</span>
        <select value={filterMember} onChange={e=>setFilterMember(e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="all">Tous</option>
          {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Type :</span>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{...inputStyle,fontSize:12}}>
          <option value="all">Tous</option>
          {DOC_TYPES.map(dt=><option key={dt.value} value={dt.value}>{dt.icon} {dt.label}</option>)}
        </select>
        {specialists.length>0 && <>
          <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Spécialiste :</span>
          <select value={filterSpecialist} onChange={e=>setFilterSpecialist(e.target.value)} style={{...inputStyle,fontSize:12}}>
            <option value="all">Tous</option>
            {specialists.map(sp=><option key={sp} value={sp}>🩺 {sp}</option>)}
          </select>
        </>}
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:13,fontWeight:800,color:DOC_COLOR,marginBottom:10 }}>📁 Nouveau document</div>
          <input placeholder="Titre du document" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={inputStyle}>
              {DOC_TYPES.map(dt=><option key={dt.value} value={dt.value}>{dt.icon} {dt.label}</option>)}
            </select>
            <select value={form.member} onChange={e=>setForm(f=>({...f,member:e.target.value}))} style={inputStyle}>
              {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Date du document</div>
              <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            </div>
            <div>
              <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Auteur / praticien</div>
              <input placeholder="Dr. Martin…" value={form.author} onChange={e=>setForm(f=>({...f,author:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>Spécialiste</div>
            <input list="doc-specialists" placeholder="Cabinet, service…" value={form.specialist} onChange={e=>setForm(f=>({...f,specialist:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            <datalist id="doc-specialists">{specialists.map(sp=><option key={sp} value={sp} />)}</datalist>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:T.textSoft,marginBottom:3 }}>🔗 Lien vers le document (Google Drive, iCloud, Dropbox…)</div>
            <input type="url" placeholder="https://drive.google.com/…" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
          </div>
          <textarea placeholder="Notes (résumé, points clés…)" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",marginBottom:10}} />
          <div style={{ display:"flex",gap:8 }}>
            <button onClick={handleAdd} style={btnStyle(DOC_COLOR)}>Enregistrer</button>
            <button onClick={()=>setShowAdd(false)} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:"flex",gap:8,marginBottom:16,flexWrap:"wrap" }}>
        {DOC_TYPES.map(dt=>{
          const count = documents.filter(d=>d.type===dt.value).length;
          if (count===0) return null;
          return <div key={dt.value} style={{ background:DOC_COLOR+"15",borderRadius:8,padding:"6px 12px",fontSize:12,color:DOC_COLOR,fontWeight:700 }}>{dt.icon} {count} {dt.label}{count>1?"s":""}</div>;
        })}
      </div>

      {/* Document list */}
      {filtered.length===0 && <div style={{ color:T.textSoft,fontSize:14,textAlign:"center",padding:"32px 0" }}>Aucun document trouvé.</div>}
      <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
        {filtered.map(doc=>{
          const dt = docTypeOf(doc.type);
          const m  = memberOf(doc.member);
          const linkedFiche = fiches.find(f=>f.contact?.name===doc.specialist || f.title.toLowerCase()===doc.specialist?.toLowerCase());
          return (
            <div key={doc.id} style={{ ...cardStyle(), borderLeft:`3px solid ${DOC_COLOR}`, padding:"12px 14px" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap" }}>
                    <span style={{ fontSize:11,background:DOC_COLOR+"22",color:DOC_COLOR,borderRadius:4,padding:"1px 7px",fontWeight:700 }}>{dt.icon} {dt.label}</span>
                    <span style={{ fontSize:11,color:m.color,fontWeight:700 }}>{m.name}</span>
                    {doc.date && <span style={{ fontSize:11,color:T.textSoft }}>{new Date(doc.date+"T00:00:00").toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"})}</span>}
                  </div>
                  <div style={{ fontSize:15,fontWeight:700,color:T.text,marginBottom:2 }}>{doc.title}</div>
                  <div style={{ fontSize:12,color:T.textSoft,marginBottom:doc.notes?4:0 }}>
                    {doc.author && <span>{doc.author}</span>}
                    {doc.author && doc.specialist && <span> · </span>}
                    {doc.specialist && <span>{doc.specialist}</span>}
                  </div>
                  {doc.notes && <div style={{ fontSize:12,color:T.textMid,fontStyle:"italic",lineHeight:1.4 }}>{doc.notes}</div>}
                  {/* Action buttons */}
                  <div style={{ display:"flex",gap:6,marginTop:8,flexWrap:"wrap" }}>
                    {doc.url && (
                      <a href={doc.url} target="_blank" rel="noreferrer" style={{ display:"flex",alignItems:"center",gap:4,background:DOC_COLOR,color:T.white,borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,textDecoration:"none" }}>
                        🔗 Ouvrir le document
                      </a>
                    )}
                    {linkedFiche && (
                      <span style={{ fontSize:11,background:T.mint+"22",color:T.mint,borderRadius:5,padding:"3px 8px",fontWeight:700 }}>📋 Fiche liée</span>
                    )}
                  </div>
                </div>
                <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:6,flexShrink:0 }}>
                  <Avatar member={doc.member} size={26} />
                  <button onClick={()=>setEditing({...doc})} style={{ fontSize:11,color:T.textSoft,background:"transparent",border:"none",cursor:"pointer" }}>✎</button>
                  <button onClick={()=>onDelete(doc.id)} style={{ fontSize:14,color:T.textSoft,background:"transparent",border:"none",cursor:"pointer" }}>×</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editing && (
        <Modal title="Modifier le document" onClose={()=>setEditing(null)}>
          <input placeholder="Titre" value={editing.title} onChange={e=>setEditing(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <select value={editing.type} onChange={e=>setEditing(f=>({...f,type:e.target.value}))} style={inputStyle}>{DOC_TYPES.map(dt=><option key={dt.value} value={dt.value}>{dt.icon} {dt.label}</option>)}</select>
            <select value={editing.member} onChange={e=>setEditing(f=>({...f,member:e.target.value}))} style={inputStyle}>{MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}</select>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8 }}>
            <div><div style={{fontSize:11,color:T.textSoft,marginBottom:3}}>Date</div><input type="date" value={editing.date} onChange={e=>setEditing(f=>({...f,date:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
            <div><div style={{fontSize:11,color:T.textSoft,marginBottom:3}}>Auteur</div><input value={editing.author||""} onChange={e=>setEditing(f=>({...f,author:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:11,color:T.textSoft,marginBottom:3}}>Spécialiste</div>
            <input list="doc-specialists-edit" value={editing.specialist||""} onChange={e=>setEditing(f=>({...f,specialist:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
            <datalist id="doc-specialists-edit">{specialists.map(sp=><option key={sp} value={sp} />)}</datalist>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:11,color:T.textSoft,marginBottom:3}}>🔗 Lien</div>
            <input type="url" value={editing.url||""} onChange={e=>setEditing(f=>({...f,url:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} />
          </div>
          <textarea value={editing.notes||""} onChange={e=>setEditing(f=>({...f,notes:e.target.value}))} rows={3} placeholder="Notes…" style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",marginBottom:10}} />
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleSave} style={btnStyle(DOC_COLOR,{flex:1})}>Enregistrer</button>
            <button onClick={()=>{ onDelete(editing.id); setEditing(null); }} style={btnStyle(T.danger)}>Supprimer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ⏰ MODULE RAPPELS
// ══════════════════════════════════════════════════════════════

const INITIAL_REMINDERS = [];

const DELAY_OPTIONS = [
  { value:5,    label:"Dans 5 min" },
  { value:10,   label:"Dans 10 min" },
  { value:15,   label:"Dans 15 min" },
  { value:30,   label:"Dans 30 min" },
  { value:60,   label:"Dans 1h" },
  { value:120,  label:"Dans 2h" },
  { value:180,  label:"Dans 3h" },
  { value:1440, label:"Demain" },
];

function useRemindersAlarm({ reminders, onTrigger, notifGranted }) {
  useEffect(() => {
    const check = () => {
      const now = new Date();
      reminders.forEach(r => {
        if (r.done || r.triggered) return;
        const target = new Date(r.targetDate + "T" + r.targetTime + ":00");
        const diff = Math.round((target - now) / 60000);
        if (diff <= 0 && diff > -2) {
          // Trigger!
          if (notifGranted) {
            try { new Notification(`⏰ ${r.title}`, { body: r.notes || "Rappel Alfy", icon:"/favicon.ico" }); } catch {}
          }
          onTrigger(r.id);
        }
      });
    };
    const t = setInterval(check, 30000);
    check();
    return () => clearInterval(t);
  }, [reminders, notifGranted]);
}

function ReminderForm({ onAdd, onClose, defaultMember="olivier", themes=DEFAULT_THEMES, onAddTheme }) {
  const [mode, setMode] = useState("delay"); // "delay" | "datetime"
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [members, setMembers] = useState(()=>defaultMember?[defaultMember]:["olivier"]);
  const [type, setType] = useState("work");
  const [delay, setDelay] = useState(15);
  const [date, setDate] = useState(todayISO());
  const [repeatYearly, setRepeatYearly] = useState(false);
  const [time, setTime] = useState(() => {
    const d = new Date(); d.setMinutes(d.getMinutes()+15);
    return d.toTimeString().slice(0,5);
  });

  const getTarget = () => {
    if (mode === "delay") {
      const d = new Date(Date.now() + delay*60000);
      return { date: toLocalISO(d), time: d.toTimeString().slice(0,5) };
    }
    return { date, time };
  };

  const handleAdd = () => {
    if (!title.trim()) return;
    const { date: td, time: tt } = getTarget();
    onAdd({ id:Date.now(), title, notes, type, member:members[0], members, targetDate:td, targetTime:tt, done:false, triggered:false, recurrence: (mode==="datetime" && repeatYearly) ? "yearly" : "none", createdAt:new Date().toISOString() });
    onClose && onClose();
  };

  return (
    <div>
      <input placeholder="Titre du rappel" value={title} onChange={e=>setTitle(e.target.value)} style={{...inputStyle,width:"100%",marginBottom:10,boxSizing:"border-box",fontSize:14}} autoFocus />

      {/* Mode selector */}
      <div style={{ display:"flex", gap:6, marginBottom:10 }}>
        <button onClick={()=>setMode("delay")} style={{ flex:1, padding:"8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(mode==="delay"?T.coral:T.sandDark), background:mode==="delay"?T.coral+"15":"transparent", color:mode==="delay"?T.coral:T.textMid }}>⏱ Dans X temps</button>
        <button onClick={()=>setMode("datetime")} style={{ flex:1, padding:"8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(mode==="datetime"?T.coral:T.sandDark), background:mode==="datetime"?T.coral+"15":"transparent", color:mode==="datetime"?T.coral:T.textMid }}>📅 Date & heure</button>
      </div>

      {mode==="delay" ? (
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:11,color:T.textSoft,marginBottom:4 }}>Rappel dans</div>
          <select value={delay} onChange={e=>setDelay(+e.target.value)} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
            {DELAY_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      ) : (
        <div style={{ marginBottom:10 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <div><div style={{fontSize:11,color:T.textSoft,marginBottom:3}}>Date</div><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{...inputStyle,width:"100%",boxSizing:"border-box"}} /></div>
            <div><div style={{fontSize:11,color:T.textSoft,marginBottom:3}}>Heure</div><TimePicker value={time} onChange={setTime} style={{width:"100%",boxSizing:"border-box"}} /></div>
          </div>
          <button type="button" onClick={()=>setRepeatYearly(v=>!v)} style={{ display:"flex", alignItems:"center", gap:8, background:"transparent", border:"none", cursor:"pointer", padding:0 }}>
            <span style={{ width:18, height:18, borderRadius:4, border:`2px solid ${repeatYearly?T.coral:T.sandDark}`, background:repeatYearly?T.coral:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {repeatYearly && <span style={{ color:T.white, fontSize:11, fontWeight:900 }}>✓</span>}
            </span>
            <span style={{ fontSize:12, color:T.textMid }}>🔁 Se répète chaque année (anniversaire...)</span>
          </button>
        </div>
      )}

      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:11, color:T.textSoft, marginBottom:6 }}>Pour (plusieurs possible)</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {MEMBERS.map(m => {
            const selected = members.includes(m.id);
            return (
              <button key={m.id} type="button" onClick={()=>{
                setMembers(prev => prev.includes(m.id)
                  ? prev.length>1 ? prev.filter(x=>x!==m.id) : prev
                  : [...prev, m.id]);
              }} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:20, fontSize:13, fontWeight:700, cursor:"pointer", border:`2px solid ${selected?m.color:T.sandDark}`, background:selected?m.color:"transparent", color:selected?T.white:T.textMid }}>
                <span style={{ width:18,height:18,borderRadius:"50%",background:selected?"rgba(255,255,255,0.35)":m.color,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:T.white,flexShrink:0 }}>{m.avatar}</span>
                {m.name}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom:10 }}>
        <ThemePicker value={type||"work"} onChange={setType} themes={themes} onAddTheme={onAddTheme} label="Thème" />
      </div>
      <textarea placeholder="Notes (optionnel)" value={notes} onChange={e=>setNotes(e.target.value)} rows={2} style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",marginBottom:10}} />

      <div style={{ display:"flex", gap:8 }}>
        <button onClick={handleAdd} style={btnStyle(T.coral,{flex:1})}>⏰ Créer le rappel</button>
        {onClose && <button onClick={onClose} style={btnStyle(T.textSoft)}>Annuler</button>}
      </div>
    </div>
  );
}

// ── MÉDICAMENTS — suivi de prise, saisi librement par l'utilisateur (pas d'info médicale fournie par l'app) ──
function MedicationsView({ medications, medLogs, onAdd, onUpdate, onDelete, onLogTaken, onUnlogTaken, activeUser }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:"", member:activeUser||MEMBERS[0].id, slot:"Matin", time:"08:00", notes:"", active:true });
  const todayStr = todayISO();

  const isTaken = (medId) => medLogs.some(l=>l.medicationId===medId && l.date===todayStr);

  const resetForm = () => { setForm({ name:"", member:activeUser||MEMBERS[0].id, slot:"Matin", time:"08:00", notes:"", active:true }); setShowAdd(false); setEditing(null); };

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editing) onUpdate({ ...editing, ...form });
    else onAdd({ id:Date.now(), ...form });
    resetForm();
  };

  const visible = activeUser ? medications.filter(m=>m.member===activeUser) : medications;
  const grouped = MEMBERS.map(m=>({ member:m, meds: visible.filter(x=>x.member===m.id) })).filter(g=>g.meds.length>0);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>💊 Médicaments</h2>
        <button onClick={()=>{ resetForm(); setShowAdd(true); }} style={btnStyle(T.coral)}>+ Ajouter</button>
      </div>
      <div style={{ fontSize:12, color:T.textSoft, marginBottom:16, lineHeight:1.5 }}>
        Note ici ce qu'il y a à prendre chaque jour — un rappel apparaît dans "Points importants du jour" tant que ce n'est pas coché.
      </div>

      {(showAdd || editing) && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <input placeholder="Nom du médicament / traitement *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <select value={form.member} onChange={e=>setForm(f=>({...f,member:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {MEMBERS.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select value={form.slot} onChange={e=>setForm(f=>({...f,slot:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box"}}>
              {MED_SLOTS.map(s=><option key={s} value={s}>{MED_SLOT_ICONS[s]} {s}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:3 }}>Heure du rappel</div>
            <TimePicker value={form.time} onChange={v=>setForm(f=>({...f,time:v}))} style={{width:130}} />
          </div>
          <input placeholder="Note (optionnel — ex: à prendre avec un repas)" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inputStyle,width:"100%",marginBottom:10,boxSizing:"border-box"}} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleSave} style={btnStyle(T.coral)}>{editing?"Enregistrer":"Ajouter"}</button>
            <button onClick={resetForm} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {grouped.length===0 && !showAdd && (
        <div style={{ textAlign:"center", padding:"40px 0", color:T.textSoft }}>
          <div style={{ fontSize:40, marginBottom:8 }}>💊</div>
          <div style={{ fontSize:14 }}>Aucun médicament suivi pour l'instant.</div>
        </div>
      )}

      {grouped.map(({member, meds})=>(
        <div key={member.id} style={{ marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <Avatar member={member.id} size={22} />
            <span style={{ fontSize:13, fontWeight:800, color:T.text }}>{member.name}</span>
          </div>
          {meds.sort((a,b)=>(a.time||"").localeCompare(b.time||"")).map(med=>{
            const taken = isTaken(med.id);
            return (
              <div key={med.id} style={{ ...cardStyle(), display:"flex", alignItems:"center", gap:12, marginBottom:8, opacity:med.active===false?0.5:1 }}>
                <button onClick={()=>taken?onUnlogTaken(med.id,todayStr):onLogTaken(med.id,member.id,todayStr)} style={{ width:30,height:30,borderRadius:"50%",border:`2px solid ${taken?T.mint:T.sandDark}`,background:taken?T.mint:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  {taken && <span style={{color:"white",fontSize:14,fontWeight:900}}>✓</span>}
                </button>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:T.text, textDecoration:taken?"line-through":"none" }}>{med.name}</div>
                  <div style={{ fontSize:11, color:T.textSoft }}>{MED_SLOT_ICONS[med.slot]} {med.slot} · {med.time}{med.notes && " · "+med.notes}</div>
                </div>
                <button onClick={()=>{ setEditing(med); setForm({name:med.name,member:med.member,slot:med.slot,time:med.time,notes:med.notes||"",active:med.active!==false}); setShowAdd(false); }} style={{ fontSize:12, background:"transparent", border:"none", color:T.textSoft, cursor:"pointer" }}>✏️</button>
                <button onClick={()=>onDelete(med.id)} style={{ fontSize:14, background:"transparent", border:"none", color:T.textSoft, cursor:"pointer" }}>×</button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function RemindersView({ reminders, onAdd, onDelete, onToggle, activeUser, themes=DEFAULT_THEMES, onAddTheme }) {
  const [showAdd, setShowAdd] = useState(false);
  const [filterMember, setFilterMember] = useState(()=>activeUser||"all");

  useEffect(()=>{ setFilterMember(activeUser||"all"); }, [activeUser]);

  const now = new Date();
  const [filterType, setFilterType] = useState("all");
  const filtered = reminders.filter(r =>
    (filterMember==="all" || normalizeMembers(r.members||r.member).includes(filterMember)) &&
    (filterType==="all" || r.type===filterType)
  );
  const pending  = filtered.filter(r => !r.done).sort((a,b)=>(a.targetDate+a.targetTime).localeCompare(b.targetDate+b.targetTime));
  const done     = filtered.filter(r => r.done).sort((a,b)=>b.targetDate.localeCompare(a.targetDate));

  const fmtTarget = (r) => {
    const d = new Date(r.targetDate+"T"+r.targetTime+":00");
    const diffMin = Math.round((d - now) / 60000);
    if (diffMin < 0) return "En retard · "+d.toLocaleDateString("fr-FR",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
    if (diffMin < 60) return `Dans ${diffMin} min`;
    if (diffMin < 1440) return `Aujourd'hui à ${r.targetTime}`;
    return d.toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})+" à "+r.targetTime;
  };

  const isOverdue = (r) => !r.done && new Date(r.targetDate+"T"+r.targetTime+":00") < now;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>⏰ Rappels</h2>
        <button onClick={()=>setShowAdd(s=>!s)} style={btnStyle(T.coral)}>+ Créer</button>
      </div>

      <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
        <button onClick={()=>setFilterMember("all")} style={{ padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",border:"2px solid "+(filterMember==="all"?T.navy:T.sandDark),background:filterMember==="all"?T.navy:"transparent",color:filterMember==="all"?T.white:T.textMid }}>Tous</button>
        {MEMBERS.map(m=>(
          <button key={m.id} onClick={()=>setFilterMember(filterMember===m.id?"all":m.id)} style={{ display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",border:"2px solid "+(filterMember===m.id?m.color:T.sandDark),background:filterMember===m.id?m.color:"transparent",color:filterMember===m.id?T.white:T.textMid }}>
            <span style={{ width:14,height:14,borderRadius:"50%",background:filterMember===m.id?"rgba(255,255,255,0.4)":m.color,display:"inline-block" }} />{m.name}
          </button>
        ))}
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
        <span style={{ fontSize:11, color:T.textSoft, fontWeight:700 }}>Thème :</span>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{...inputStyle,fontSize:12,flex:1}}>
          <option value="all">Tous</option>
          {(themes||DEFAULT_THEMES).map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
        </select>
      </div>

      {showAdd && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:13,fontWeight:800,color:T.coral,marginBottom:10 }}>⏰ Nouveau rappel</div>
          <ReminderForm onAdd={onAdd} onClose={()=>setShowAdd(false)} defaultMember={activeUser||"olivier"} themes={themes} onAddTheme={onAddTheme} />
        </div>
      )}

      {pending.length===0 && !showAdd && (
        <div style={{ textAlign:"center", padding:"32px 0", color:T.textSoft }}>
          <div style={{ fontSize:32, marginBottom:8 }}>⏰</div>
          <div style={{ fontSize:14, marginBottom:12 }}>Aucun rappel en attente</div>
          <button onClick={()=>setShowAdd(true)} style={btnStyle(T.coral)}>Créer un rappel</button>
        </div>
      )}

      {pending.length>0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11,fontWeight:800,color:T.textSoft,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8 }}>En attente ({pending.length})</div>
          {pending.map(r => (
            <div key={r.id} style={{ ...cardStyle(), display:"flex", gap:12, marginBottom:8, padding:"12px 14px", borderLeft:`3px solid ${isOverdue(r)?T.danger:T.coral}` }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14,fontWeight:700,color:T.text,marginBottom:3 }}>{r.title}{r.recurrence==="yearly" && <span style={{ fontSize:10, fontWeight:700, color:T.gold, marginLeft:6 }}>🔁 Annuel</span>}</div>
                <div style={{ fontSize:12,fontWeight:700,color:isOverdue(r)?T.danger:T.coral }}>{fmtTarget(r)}</div>
                {r.notes && <div style={{ fontSize:11,color:T.textSoft,marginTop:3,fontStyle:"italic" }}>{r.notes}</div>}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"center" }}>
                <MemberAvatars members={r.members||r.member} size={24} />
                <button onClick={()=>onToggle(r.id)} title={r.recurrence==="yearly"?"Reporter à l'année prochaine":"Marquer comme fait"} style={{ fontSize:10,background:T.mint+"22",color:T.mint,border:"none",borderRadius:5,padding:"3px 7px",cursor:"pointer",fontWeight:700 }}>{r.recurrence==="yearly"?"✓ +1 an":"✓ Fait"}</button>
                <button onClick={()=>onDelete(r.id)} style={{ fontSize:14,background:"transparent",border:"none",cursor:"pointer",color:T.textSoft }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {done.length>0 && (
        <div style={{ opacity:0.55 }}>
          <div style={{ fontSize:11,fontWeight:800,color:T.textSoft,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8 }}>Terminés ({done.length})</div>
          {done.slice(0,5).map(r => (
            <div key={r.id} style={{ ...cardStyle(), display:"flex", gap:12, marginBottom:6, padding:"10px 14px" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13,fontWeight:600,color:T.text,textDecoration:"line-through" }}>{r.title}</div>
                <div style={{ fontSize:11,color:T.textSoft }}>{r.targetDate} à {r.targetTime}</div>
              </div>
              <button onClick={()=>onDelete(r.id)} style={{ fontSize:14,background:"transparent",border:"none",cursor:"pointer",color:T.textSoft }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 📍 PRÉSENCE — statuts de localisation
// ══════════════════════════════════════════════════════════════

const PRESENCE_STATUSES = [
  { id:"home",      label:"Maison",         icon:"🏠", color:"#5BBFAD" },
  { id:"school",    label:"École / CNED",   icon:"🎓", color:"#D4A843" },
  { id:"work",      label:"Travail",         icon:"💼", color:"#3D5A8A" },
  { id:"appt",      label:"RDV / Spécialiste", icon:"🏥", color:"#E8735A" },
  { id:"driving",   label:"En route",        icon:"🚗", color:"#E07B39" },
  { id:"outside",   label:"Dehors / Sport",  icon:"🏃", color:"#27AE60" },
  { id:"friend",    label:"Chez des amis",   icon:"👥", color:"#9B59B6" },
  { id:"other",     label:"Autre lieu",      icon:"📍", color:"#888888" },
];

function PresenceModal({ presence, member, onUpdate, onClose }) {
  const m = memberOf(member);
  const current = presence[member] || {};
  const [status, setStatus] = useState(current.status || "home");
  const [note, setNote] = useState(current.note || "");

  const save = () => {
    onUpdate({ ...presence, [member]: { status, note, updatedAt: new Date().toLocaleTimeString("fr-FR", {hour:"2-digit", minute:"2-digit"}) } });
    onClose();
  };

  return (
    <Modal title={`📍 Où est ${m.name} ?`} onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
        {PRESENCE_STATUSES.map(s => (
          <button key={s.id} onClick={()=>setStatus(s.id)} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:10, border:`2px solid ${status===s.id?s.color:T.sandDark}`, background:status===s.id?s.color+"18":"transparent", cursor:"pointer", textAlign:"left" }}>
            <span style={{ fontSize:22 }}>{s.icon}</span>
            <span style={{ fontSize:14, fontWeight:status===s.id?700:400, color:status===s.id?s.color:T.text }}>{s.label}</span>
            {status===s.id && <span style={{ marginLeft:"auto", color:s.color, fontWeight:800 }}>✓</span>}
          </button>
        ))}
      </div>
      <input placeholder="Précision (optionnel) — ex: Café Victor Hugo" value={note} onChange={e=>setNote(e.target.value)} style={{...inputStyle, width:"100%", marginBottom:12, boxSizing:"border-box"}} />
      <button onClick={save} style={btnStyle(m.color, {width:"100%"})}>Enregistrer</button>
    </Modal>
  );
}

function PresenceBadge({ presence, member, onClick }) {
  const p = presence[member];
  if (!p) return null;
  const s = PRESENCE_STATUSES.find(x=>x.id===p.status);
  if (!s) return null;
  return (
    <div onClick={onClick} style={{ display:"flex", alignItems:"center", gap:4, background:s.color+"18", borderRadius:6, padding:"2px 7px", cursor:"pointer" }}>
      <span style={{ fontSize:12 }}>{s.icon}</span>
      <span style={{ fontSize:10, fontWeight:700, color:s.color }}>{s.label}</span>
      {p.updatedAt && <span style={{ fontSize:9, color:T.textSoft }}>· {p.updatedAt}</span>}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🌊 Je souffle BIEN-ÊTRE
// ══════════════════════════════════════════════════════════════

const SOS_EMOTIONS = [
  { id:"anxious",    label:"Anxieux",     icon:"😰", color:"#9B59B6" },
  { id:"angry",      label:"En colère",   icon:"😤", color:"#E74C3C" },
  { id:"sad",        label:"Triste",      icon:"😢", color:"#3498DB" },
  { id:"overwhelmed",label:"Débordé",     icon:"😵", color:"#E07B39" },
  { id:"tired",      label:"Épuisé",      icon:"😴", color:"#7F8C8D" },
  { id:"lost",       label:"Perdu",       icon:"😕", color:"#D4A843" },
];

const SOS_EXERCISES = {
  anxious: [
    { title:"Respiration 4-7-8", icon:"🌬️", duration:"3 min",
      steps:["Expire complètement par la bouche.", "Inspire par le nez en comptant jusqu'à 4.", "Retiens ton souffle en comptant jusqu'à 7.", "Expire lentement par la bouche en comptant jusqu'à 8.", "Répète ce cycle 4 fois."] },
    { title:"Ancrage 5-4-3-2-1", icon:"🌍", duration:"5 min",
      steps:["Nomme 5 choses que tu vois autour de toi.", "Nomme 4 choses que tu peux toucher.", "Nomme 3 choses que tu entends.", "Nomme 2 choses que tu peux sentir.", "Nomme 1 chose que tu peux goûter."] },
    { title:"Visualisation apaisante", icon:"🌊", duration:"5 min",
      steps:["Lis toutes les étapes avant de commencer, puis ferme les yeux.", "Tu vas imaginer un endroit où tu te sens bien et en sécurité.", "Dans cet endroit, observe mentalement les couleurs, les sons, les sensations.", "Reste là quelques minutes et profite du calme.", "👉 Ferme les yeux maintenant et vis l'exercice. Ouvre-les quand tu es prêt(e)."] },
  ],
  angry: [
    { title:"Respiration carrée", icon:"⬜", duration:"3 min",
      steps:["Inspire lentement en comptant jusqu'à 4.", "Retiens ton souffle en comptant jusqu'à 4.", "Expire lentement en comptant jusqu'à 4.", "Reste poumons vides en comptant jusqu'à 4.", "Répète 5 fois."] },
    { title:"Mouvements de décharge", icon:"🤸", duration:"3 min",
      steps:["Serre les poings très fort pendant 5 secondes.", "Relâche brusquement et sens la tension partir.", "Lève les épaules vers les oreilles, retiens 5 secondes.", "Laisse tomber les épaules d'un coup.", "Secoue doucement les mains comme pour enlever de l'eau."] },
    { title:"Le STOP émotionnel", icon:"🛑", duration:"2 min",
      steps:["Dis-toi STOP intérieurement.", "Prends 3 grandes respirations lentes.", "Observe ce que tu ressens dans ton corps sans le juger.", "Demande-toi : de quoi ai-je besoin là maintenant ?", "Choisis une action douce pour toi."] },
  ],
  sad: [
    { title:"Auto-compassion", icon:"🤗", duration:"5 min",
      steps:["Pose une main sur ton cœur et sens sa chaleur.", "Reconnais doucement : je souffre en ce moment, c'est normal.", "Rappelle-toi : tout le monde traverse des moments difficiles.", "Dis-toi lentement à voix basse ou mentalement : je prends soin de moi.", "Reste dans cette posture quelques minutes, les yeux ouverts ou fermés."] },
    { title:"Mouvement doux", icon:"🚶", duration:"5 min",
      steps:["Lève-toi et marche lentement dans la pièce.", "Sens le sol sous tes pieds à chaque pas.", "Remarque ce que tu vois autour de toi.", "Balance doucement les bras.", "Après 5 minutes, remarque si tu te sens un peu différent."] },
    { title:"Gratitude du moment", icon:"✨", duration:"3 min",
      steps:["Prends une feuille ou pense simplement.", "Trouve 3 petites choses positives d'aujourd'hui.", "Elles peuvent être très simples (un bon café, la lumière...).", "Prends le temps de les ressentir vraiment.", "Laisse cette douceur être présente avec ta tristesse."] },
  ],
  overwhelmed: [
    { title:"Une chose à la fois", icon:"1️⃣", duration:"5 min",
      steps:["Pose tout ce que tu fais.", "Prends 3 respirations profondes.", "Écris ou pense à UNE seule chose à faire maintenant.", "Ignore tout le reste pour les 15 prochaines minutes.", "Fais juste cette chose, rien d'autre."] },
    { title:"Respiration abdominale", icon:"🫁", duration:"3 min",
      steps:["Pose une main sur le ventre.", "Inspire lentement par le nez, le ventre se gonfle.", "Expire lentement, le ventre se dégonfle.", "Fais ça 10 fois en te concentrant sur le mouvement.", "Sens ton corps se stabiliser."] },
    { title:"Pause sensorielle", icon:"🎧", duration:"5 min",
      steps:["Trouve un endroit calme et installe-toi confortablement.", "Mets des écouteurs ou bouche doucement tes oreilles avec tes mains.", "Lis la suite avant de fermer les yeux : tu vas juste observer ta respiration.", "Tu laisses les pensées passer comme des nuages, sans les suivre.", "👉 Ferme les yeux maintenant et reste ainsi 3-5 minutes."] },
  ],
  tired: [
    { title:"Relaxation musculaire", icon:"💆", duration:"5 min",
      steps:["Assieds-toi ou allonge-toi confortablement. Lis toutes les étapes d'abord.", "Contracte tes pieds très fort pendant 5 secondes… puis relâche brusquement.", "Contracte tes jambes pendant 5 secondes… puis relâche.", "Contracte ton ventre pendant 5 secondes… puis relâche.", "Contracte tes épaules vers les oreilles 5 secondes… relâche et ressens le calme dans tout le corps."] },
    { title:"Micro-pause énergisante", icon:"⚡", duration:"2 min",
      steps:["Lève-toi et étire-toi vers le haut.", "Secoue doucement tout le corps.", "Fais 5 rotations des épaules en arrière.", "Prends 3 respirations profondes.", "Bois un verre d'eau."] },
    { title:"Cohérence cardiaque", icon:"❤️", duration:"5 min",
      steps:["Le principe : inspire 5 sec, expire 5 sec, pendant 5 minutes. Simple et puissant.", "Commence : inspire par le nez en comptant 1-2-3-4-5.", "Expire lentement par la bouche en comptant 1-2-3-4-5.", "Continue ce rythme régulier. Pense à quelqu'un qui te fait du bien.", "Ce rythme lent régule ton système nerveux. Continue autant que tu veux."] },
  ],
  lost: [
    { title:"Retour au corps", icon:"🧘", duration:"3 min",
      steps:["Pose tes pieds bien à plat sur le sol et sens le contact.", "Sens le poids de ton corps sur la chaise ou le sol.", "Regarde autour de toi et nomme 3 choses que tu vois.", "Prends 3 respirations lentes en observant ton ventre se soulever.", "Tu es là, dans ce moment. C'est suffisant."] },
    { title:"La boussole intérieure", icon:"🧭", duration:"5 min",
      steps:["Prends une feuille et écris : Je me sens... (complète honnêtement).", "Écris : Ce dont j'ai besoin là c'est...", "Écris : Une toute petite chose que je peux faire c'est...", "Lis ce que tu as écrit.", "Fais juste cette toute petite chose."] },
    { title:"Appel à un proche", icon:"📞", duration:"Variable",
      steps:["Pense à quelqu'un en qui tu as confiance.", "Envoie-lui un message simple : 'Je me sens un peu perdu, tu as 5 min ?'", "Tu n'as pas besoin d'expliquer tout.", "Juste le contact suffit souvent.", "Si personne n'est dispo, Alfy est là 💙"] },
  ],
};

function SOSModal({ onClose, activeUser }) {
  const [step, setStep] = useState(1); // 1=emotion, 2=exercise
  const [emotion, setEmotion] = useState(null);
  const [exerciseIdx, setExerciseIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [done, setDone] = useState(false);

  const exercises = emotion ? SOS_EXERCISES[emotion.id] || [] : [];
  const exercise = exercises[exerciseIdx];

  const reset = () => { setStep(1); setEmotion(null); setExerciseIdx(0); setPlaying(false); setStepIdx(0); setDone(false); };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(30,42,58,0.7)", display:"flex", alignItems:"flex-end" }}>
      <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:"20px 20px 0 0", width:"100%", maxHeight:"92vh", overflowY:"auto", padding:"24px 20px 40px" }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:22 }}>🌊</div>
            <div style={{ fontSize:18, fontWeight:800, color:T.text }}>Je souffle</div>
          </div>
          <button onClick={onClose} style={{ background:T.sandDark, border:"none", borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:16, color:T.textSoft }}>✕</button>
        </div>

        {/* Step 1 — Choose emotion */}
        {step===1 && (
          <div>
            <div style={{ fontSize:14, color:T.textSoft, marginBottom:16, textAlign:"center" }}>
              {activeUser ? `${memberOf(activeUser).name}, comment` : "Comment"} tu te sens en ce moment ?
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {SOS_EMOTIONS.map(e=>(
                <button key={e.id} onClick={()=>{ setEmotion(e); setStep(2); }} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, padding:"16px 12px", background:e.color+"12", border:`2px solid ${e.color}`, borderRadius:12, cursor:"pointer" }}>
                  <span style={{ fontSize:32 }}>{e.icon}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:e.color }}>{e.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — Exercise */}
        {step===2 && emotion && exercise && !playing && !done && (
          <div>
            <button onClick={reset} style={{ fontSize:12, color:T.textSoft, background:"transparent", border:"none", cursor:"pointer", marginBottom:12 }}>← Retour</button>
            <div style={{ textAlign:"center", marginBottom:16 }}>
              <div style={{ fontSize:32 }}>{emotion.icon}</div>
              <div style={{ fontSize:14, color:T.textSoft }}>Tu te sens <strong style={{color:emotion.color}}>{emotion.label}</strong></div>
              <div style={{ fontSize:13, color:T.textSoft, marginTop:4 }}>Voici ce qui peut t'aider :</div>
            </div>
            {/* Exercise cards */}
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
              {exercises.map((ex,i)=>(
                <button key={i} onClick={()=>{ setExerciseIdx(i); setPlaying(true); setStepIdx(0); }} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:exerciseIdx===i?emotion.color+"18":T.sand, border:`2px solid ${exerciseIdx===i?emotion.color:T.sandDark}`, borderRadius:12, cursor:"pointer", textAlign:"left" }}>
                  <span style={{ fontSize:26, flexShrink:0 }}>{ex.icon}</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{ex.title}</div>
                    <div style={{ fontSize:11, color:T.textSoft }}>⏱ {ex.duration}</div>
                  </div>
                  <span style={{ marginLeft:"auto", fontSize:18, color:emotion.color }}>▶</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 — Guided exercise */}
        {step===2 && playing && exercise && !done && (
          <div>
            <div style={{ textAlign:"center", marginBottom:20 }}>
              <div style={{ fontSize:48 }}>{exercise.icon}</div>
              <div style={{ fontSize:18, fontWeight:800, color:T.text, marginBottom:4 }}>{exercise.title}</div>
              <div style={{ fontSize:12, color:T.textSoft }}>Étape {stepIdx+1} sur {exercise.steps.length}</div>
              {/* Progress bar */}
              <div style={{ height:4, background:T.sandDark, borderRadius:4, margin:"10px 0" }}>
                <div style={{ height:4, background:emotion.color, borderRadius:4, width:`${((stepIdx+1)/exercise.steps.length)*100}%`, transition:"width 0.3s" }} />
              </div>
            </div>
            <div style={{ background:emotion.color+"12", border:`2px solid ${emotion.color}`, borderRadius:14, padding:"20px 18px", marginBottom:20, minHeight:100, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ fontSize:16, color:T.text, lineHeight:1.7, textAlign:"center", fontWeight:500 }}>{exercise.steps[stepIdx]}</div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              {stepIdx > 0 && <button onClick={()=>setStepIdx(i=>i-1)} style={btnStyle(T.textSoft,{flex:1})}>← Précédent</button>}
              {stepIdx < exercise.steps.length-1
                ? <button onClick={()=>setStepIdx(i=>i+1)} style={btnStyle(emotion.color,{flex:2})}>Étape suivante →</button>
                : <button onClick={()=>setDone(true)} style={btnStyle(T.mint,{flex:2})}>✓ Terminé !</button>
              }
            </div>
            <button onClick={()=>{ setPlaying(false); }} style={{ width:"100%", marginTop:10, background:"transparent", border:"none", cursor:"pointer", fontSize:12, color:T.textSoft }}>Choisir un autre exercice</button>
          </div>
        )}

        {/* Done */}
        {done && (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:56, marginBottom:12 }}>🌟</div>
            <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:8 }}>Bravo !</div>
            <div style={{ fontSize:14, color:T.textSoft, lineHeight:1.7, marginBottom:24 }}>
              Tu as pris soin de toi.<br/>C'est quelque chose de précieux.
            </div>
            <button onClick={reset} style={btnStyle(T.coral,{width:"100%",marginBottom:10})}>Faire un autre exercice</button>
            <button onClick={onClose} style={btnStyle(T.mint,{width:"100%"})}>Retourner dans Alfy</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 💬 MESSAGERIE FAMILIALE
// ══════════════════════════════════════════════════════════════

const INITIAL_MESSAGES = [];

function getConvId(a, b) {
  return [a, b].sort().join("_");
}

function MessagingView({ messages, onSend, onMarkRead, activeUser, currentUser="olivier" }) {
  const me = currentUser;
  const [selectedConv, setSelectedConv] = useState(null); // member id
  const [text, setText] = useState("");
  const endRef = useRef(null);

  // Get conversations list
  const convs = MEMBERS.filter(m => m.id !== me).map(m => {
    const convId = getConvId(me, m.id);
    const convMessages = messages.filter(msg => msg.convId === convId);
    const unread = convMessages.filter(msg => msg.to === me && !msg.read).length;
    const last = convMessages[convMessages.length - 1];
    return { member: m, convId, unread, last, messages: convMessages };
  });

  const selectedMessages = selectedConv
    ? messages.filter(msg => msg.convId === getConvId(me, selectedConv))
    : [];

  const selectedMember = selectedConv ? memberOf(selectedConv) : null;

  // Mark as read when opening conv
  useEffect(() => {
    if (selectedConv) {
      onMarkRead(getConvId(me, selectedConv), me);
    }
  }, [selectedConv]);

  // Scroll to bottom
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [selectedMessages.length]);

  const send = () => {
    if (!text.trim() || !selectedConv) return;
    onSend({
      id: Date.now(),
      convId: getConvId(me, selectedConv),
      from: me,
      to: selectedConv,
      text: text.trim(),
      sentAt: new Date().toISOString(),
      read: false,
    });
    setText("");
  };

  const fmtTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" })
      : d.toLocaleDateString("fr-FR", { day:"numeric", month:"short" }) + " " + d.toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" });
  };

  return (
    <div style={{ height:"calc(100vh - 160px)", display:"flex", flexDirection:"column" }}>
      {!selectedConv ? (
        // Conversations list
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>💬 Messages</h2>
          </div>
          {/* Current user switcher */}
          <div style={{ display:"flex", gap:6, marginBottom:16, background:T.sandDark, borderRadius:10, padding:8 }}>
            <span style={{ fontSize:11, color:T.textSoft, alignSelf:"center", marginRight:4 }}>Je suis :</span>
            {MEMBERS.map(m => (
              <button key={m.id} onClick={()=>onMarkRead("switch", m.id)} style={{ flex:1, padding:"6px 8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:`2px solid ${me===m.id?m.color:T.sandDark}`, background:me===m.id?m.color:"transparent", color:me===m.id?T.white:T.textMid }}>
                {m.name}
              </button>
            ))}
          </div>
          {convs.map(conv => (
            <div key={conv.member.id} onClick={()=>setSelectedConv(conv.member.id)}
              style={{ ...cardStyle(), display:"flex", alignItems:"center", gap:12, marginBottom:8, cursor:"pointer", borderLeft:`3px solid ${conv.unread>0?conv.member.color:"transparent"}` }}>
              <div style={{ position:"relative" }}>
                <Avatar member={conv.member.id} size={40} />
                {conv.unread > 0 && (
                  <div style={{ position:"absolute", top:-4, right:-4, background:T.danger, borderRadius:"50%", width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ fontSize:10, fontWeight:800, color:T.white }}>{conv.unread}</span>
                  </div>
                )}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{conv.member.name}</div>
                {conv.last ? (
                  <div style={{ fontSize:12, color:T.textSoft, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {conv.last.from===me?"Moi : ":""}{conv.last.text}
                  </div>
                ) : (
                  <div style={{ fontSize:12, color:T.textSoft, fontStyle:"italic" }}>Aucun message</div>
                )}
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                {conv.last && <div style={{ fontSize:10, color:T.textSoft }}>{fmtTime(conv.last.sentAt)}</div>}
                <span style={{ fontSize:16, color:T.textSoft }}>›</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Conversation view
        <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <button onClick={()=>setSelectedConv(null)} style={{ background:"transparent", border:"none", cursor:"pointer", fontSize:20, color:T.textMid, padding:"0 4px" }}>‹</button>
            <Avatar member={selectedMember.id} size={32} />
            <div style={{ flex:1 }}>
              <div style={{ fontSize:15, fontWeight:800, color:T.text }}>{selectedMember.name}</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", paddingBottom:8 }}>
            {selectedMessages.length === 0 && (
              <div style={{ textAlign:"center", padding:"40px 20px", color:T.textSoft }}>
                <div style={{ fontSize:32, marginBottom:8 }}>💬</div>
                <div style={{ fontSize:13 }}>Commencez la conversation !</div>
              </div>
            )}
            {selectedMessages.map(msg => {
              const isMe = msg.from === me;
              return (
                <div key={msg.id} style={{ display:"flex", justifyContent:isMe?"flex-end":"flex-start", marginBottom:8 }}>
                  {!isMe && <Avatar member={msg.from} size={24} style={{ marginRight:6, flexShrink:0, alignSelf:"flex-end" }} />}
                  <div style={{ maxWidth:"75%" }}>
                    <div style={{ background:isMe?memberOf(me).color:T.sandDark, color:isMe?T.white:T.text, borderRadius:isMe?"16px 16px 4px 16px":"16px 16px 16px 4px", padding:"10px 14px", fontSize:13, lineHeight:1.5, boxShadow:"0 1px 4px rgba(0,0,0,0.08)" }}>
                      {msg.text}
                    </div>
                    <div style={{ fontSize:10, color:T.textSoft, textAlign:isMe?"right":"left", marginTop:3, paddingRight:4 }}>
                      {fmtTime(msg.sentAt)}{isMe && msg.read ? " · Lu" : ""}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", paddingTop:8, borderTop:`1px solid ${T.sandDark}` }}>
            <textarea
              value={text}
              onChange={e=>setText(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter" && !e.shiftKey){ e.preventDefault(); send(); } }}
              placeholder={`Message à ${selectedMember.name}…`}
              rows={1}
              style={{ ...inputStyle, flex:1, resize:"none", maxHeight:100, lineHeight:1.5, overflowY:"auto" }}
            />
            <button onClick={send} disabled={!text.trim()} style={{ ...btnStyle(memberOf(me).color, {padding:"10px 16px", flexShrink:0}), opacity:text.trim()?1:0.4 }}>
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 😊 MODULE HUMEUR
// ══════════════════════════════════════════════════════════════

const INITIAL_MOODS = [];
const INITIAL_MEDICATIONS = []; // { id, member, name, slot, time, notes, active }
const INITIAL_MED_LOGS = [];    // { id, medicationId, member, date, takenAt }
const MED_SLOTS = ["Matin","Midi","Soir","Coucher"];
const MED_SLOT_ICONS = { "Matin":"🌅", "Midi":"☀️", "Soir":"🌙", "Coucher":"😴" };

const MOOD_LEVELS = [
  { value:1, emoji:"😔", label:"Très bas",  color:"#E74C3C" },
  { value:2, emoji:"😕", label:"Bas",        color:"#E07B39" },
  { value:3, emoji:"😐", label:"Neutre",     color:"#D4A843" },
  { value:4, emoji:"🙂", label:"Bien",       color:"#5BBFAD" },
  { value:5, emoji:"😄", label:"Très bien",  color:"#27AE60" },
];

const MOOD_DIMENSIONS = [
  { id:"mood",    label:"Humeur",   icon:"😊" },
  { id:"energy",  label:"Énergie",  icon:"⚡" },
  { id:"anxiety", label:"Anxiété",  icon:"😰", reversed:true },
];

const DIM_EMOJIS = {
  mood:    ["😔","😕","😐","🙂","😄"],
  energy:  ["🪫","😓","😑","⚡","🚀"],
  anxiety: ["😰","😟","😐","😌","😎"],
};
const DIM_LABELS = {
  mood:    ["Très bas","Bas","Neutre","Bien","Très bien"],
  energy:  ["Épuisé","Fatigué","Neutre","Dynamique","Plein d'énergie"],
  anxiety: ["Très anxieux","Anxieux","Neutre","Calme","Très calme"],
};

function MoodPicker({ value, onChange, dimension }) {
  const emojis = DIM_EMOJIS[dimension.id] || DIM_EMOJIS.mood;
  const labels = DIM_LABELS[dimension.id] || DIM_LABELS.mood;
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em" }}>
        {dimension.icon} {dimension.label}
      </div>
      <div style={{ display:"flex", gap:6, justifyContent:"space-between" }}>
        {[1,2,3,4,5].map(v => (
          <button key={v} onClick={()=>onChange(v)} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3, padding:"8px 4px", borderRadius:10, border:`2px solid ${value===v?MOOD_LEVELS[v-1].color:T.sandDark}`, background:value===v?MOOD_LEVELS[v-1].color+"18":"transparent", cursor:"pointer" }}>
            <span style={{ fontSize:22 }}>{emojis[v-1]}</span>
            <span style={{ fontSize:9, fontWeight:600, color:value===v?MOOD_LEVELS[v-1].color:T.textSoft }}>{labels[v-1]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MoodChart({ entries, member }) {
  const last7 = entries.filter(e=>e.member===member).slice(-14);
  if (last7.length === 0) return null;

  const maxVal = 5;
  const chartH = 80;
  const chartW = 280;
  const padL = 24, padR = 8, padT = 8, padB = 20;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;

  const dims = MOOD_DIMENSIONS;
  const dimColors = { mood:"#5BBFAD", energy:"#D4A843", anxiety:"#9B59B6" };

  const points = (dimId) => last7.map((e,i) => {
    const x = padL + (i/(Math.max(last7.length-1,1))) * innerW;
    const val = dimId==="anxiety" ? (6-e[dimId]) : e[dimId];
    const y = padT + innerH - ((val-1)/(maxVal-1)) * innerH;
    return `${x},${y}`;
  }).join(" ");

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.textSoft, textTransform:"uppercase", marginBottom:6 }}>Évolution (7 derniers jours)</div>
      <div style={{ display:"flex", gap:12, marginBottom:4 }}>
        {dims.map(d=>(
          <div key={d.id} style={{ display:"flex", alignItems:"center", gap:4 }}>
            <div style={{ width:12, height:3, background:dimColors[d.id], borderRadius:2 }} />
            <span style={{ fontSize:10, color:T.textSoft }}>{d.icon} {d.label}</span>
          </div>
        ))}
      </div>
      <svg width={chartW} height={chartH} style={{ overflow:"visible" }}>
        {/* Grid lines */}
        {[1,2,3,4,5].map(v=>{
          const y = padT + innerH - ((v-1)/(maxVal-1)) * innerH;
          return <line key={v} x1={padL} y1={y} x2={chartW-padR} y2={y} stroke={T.sandDark} strokeWidth={1} />;
        })}
        {/* Lines */}
        {dims.map(d=>(
          <polyline key={d.id} points={points(d.id)} fill="none" stroke={dimColors[d.id]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {/* Dots */}
        {dims.map(d=>
          last7.map((e,i)=>{
            const x = padL + (i/(Math.max(last7.length-1,1))) * innerW;
            const val = d.id==="anxiety" ? (6-e[d.id]) : e[d.id];
            const y = padT + innerH - ((val-1)/(maxVal-1)) * innerH;
            return <circle key={d.id+i} cx={x} cy={y} r={3} fill={dimColors[d.id]} />;
          })
        )}
        {/* X labels */}
        {last7.map((e,i)=>{
          const x = padL + (i/(Math.max(last7.length-1,1))) * innerW;
          const d = new Date(e.date);
          const label = d.toLocaleDateString("fr-FR",{weekday:"short"}).slice(0,3);
          return <text key={i} x={x} y={chartH-2} fontSize={8} fill={T.textSoft} textAnchor="middle">{label}</text>;
        })}
      </svg>
    </div>
  );
}

function MoodView({ moods, onAdd, activeUser, currentUser }) {
  const owner = currentUser || activeUser;
  const [filterMember, setFilterMember] = useState(()=>owner||"olivier");
  const [showForm, setShowForm] = useState(false);
  const [slot, setSlot] = useState("matin");
  const [form, setForm] = useState({ mood:3, energy:3, anxiety:3, note:"" });

  useEffect(()=>{ setFilterMember(owner||"olivier"); }, [owner]);

  const todayStr = todayISO();
  const todayMoods = moods.filter(m=>m.member===filterMember && m.date===todayStr);
  const hasMatin = todayMoods.some(m=>m.slot==="matin");
  const hasSoir  = todayMoods.some(m=>m.slot==="soir");

  const memberMoods = moods.filter(m=>m.member===filterMember).sort((a,b)=>b.date.localeCompare(a.date)||b.slot.localeCompare(a.slot));

  const save = () => {
    // Empêche les doublons si l'évaluation de ce créneau existe déjà pour aujourd'hui
    if (moods.some(m=>m.member===filterMember && m.date===todayStr && m.slot===slot)) { setShowForm(false); return; }
    onAdd({ id:Date.now(), member:filterMember, date:todayStr, slot, ...form, createdAt:new Date().toISOString() });
    setShowForm(false);
    setForm({ mood:3, energy:3, anxiety:3, note:"" });
  };

  const avgOf = (dim, entries) => {
    if (!entries.length) return null;
    const avg = entries.reduce((s,e)=>s+(e[dim]||3),0)/entries.length;
    return Math.round(avg*10)/10;
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>😊 Humeur</h2>
      </div>

      {/* Member filter — lecture pour tous, écriture pour soi */}
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {MEMBERS.map(m=>(
          <button key={m.id} onClick={()=>setFilterMember(m.id)} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:`2px solid ${filterMember===m.id?m.color:T.sandDark}`, background:filterMember===m.id?m.color:"transparent", color:filterMember===m.id?T.white:T.textMid, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            <Avatar member={m.id} size={18} />{m.name}
          </button>
        ))}
      </div>
      {filterMember !== owner && (
        <div style={{ background:"rgba(212,168,67,0.1)", border:"1px solid rgba(212,168,67,0.3)", borderRadius:10, padding:"8px 12px", marginBottom:12, fontSize:12, color:"#D4A843", fontWeight:600 }}>
          👁️ Profil de {memberOf(filterMember).name} — lecture seule
        </div>
      )}

      {/* Today's check-in */}
      <div style={{ ...cardStyle(), marginBottom:16, background:"linear-gradient(135deg, #1E2A3A, #2C3E50)", color:T.white }}>
        <div style={{ fontSize:13, fontWeight:800, color:T.textMid, marginBottom:10 }}>Aujourd'hui — {memberOf(filterMember).name}</div>
        <div style={{ display:"flex", gap:8 }}>
          {["matin","soir"].map(s => {
            const entry = todayMoods.find(m=>m.slot===s);
            const done = !!entry;
            const isOwner = filterMember === owner;
            return (
              <button key={s} onClick={()=>{ if(!done && isOwner){ setSlot(s); setShowForm(true); } }} style={{ flex:1, padding:"12px 8px", borderRadius:10, border:`2px solid ${done?T.mint:T.cardBorder}`, background:done?T.mint+"18":T.sandDark, cursor:(done||!isOwner)?"default":"pointer", textAlign:"center", opacity:!isOwner&&!done?0.5:1 }}>
                <div style={{ fontSize:18, marginBottom:4 }}>{s==="matin"?"🌅":"🌙"}</div>
                <div style={{ fontSize:11, fontWeight:700, color:T.textMid, textTransform:"uppercase" }}>{s}</div>
                {done ? (
                  <div style={{ marginTop:6 }}>
                    <div style={{ fontSize:22 }}>{MOOD_LEVELS.find(l=>l.value===entry.mood)?.emoji}</div>
                    <div style={{ fontSize:9, color:T.textSoft }}>⚡{entry.energy} 😰{entry.anxiety}</div>
                    <div style={{ fontSize:9, color:entry.inWindow===false?T.coral:T.textSoft, marginTop:2 }}>
                      {entry.createdAt && new Date(entry.createdAt).toTimeString().slice(0,5)}{entry.inWindow===false && " · hors plage"}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize:11, color:T.textSoft, marginTop:6 }}>{isOwner?"+ Évaluer":"—"}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Form — uniquement pour le membre connecté */}
      {showForm && filterMember === owner && (
        <div style={cardStyle({ marginBottom:16, background:T.sandDark })}>
          <div style={{ fontSize:13, fontWeight:800, color:T.text, marginBottom:14 }}>
            {slot==="matin"?"🌅 Matin":"🌙 Soir"} — Comment tu vas {memberOf(filterMember).name} ?
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:16, marginBottom:14 }}>
            {MOOD_DIMENSIONS.map(d=>(
              <MoodPicker key={d.id} value={form[d.id]} onChange={v=>setForm(f=>({...f,[d.id]:v}))} dimension={d} />
            ))}
          </div>
          <textarea placeholder="Un mot sur ta journée ? (optionnel)" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} rows={2} style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",marginBottom:10}} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save} style={btnStyle(T.coral,{flex:1})}>Enregistrer</button>
            <button onClick={()=>setShowForm(false)} style={btnStyle(T.textSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Chart */}
      {memberMoods.length > 1 && <MoodChart entries={memberMoods} member={filterMember} />}

      {/* Stats */}
      {memberMoods.length > 0 && (
        <div style={{ display:"flex", gap:8, marginBottom:16 }}>
          {MOOD_DIMENSIONS.map(d=>{
            const avg = avgOf(d.id, memberMoods.slice(0,14));
            const level = MOOD_LEVELS.find(l=>l.value===Math.round(avg));
            return avg ? (
              <div key={d.id} style={{ flex:1, ...cardStyle(), textAlign:"center", padding:"10px 6px" }}>
                <div style={{ fontSize:10, color:T.textSoft, marginBottom:4 }}>{d.icon} {d.label}</div>
                <div style={{ fontSize:22 }}>{level?.emoji}</div>
                <div style={{ fontSize:11, fontWeight:700, color:level?.color }}>{avg}/5</div>
              </div>
            ) : null;
          })}
        </div>
      )}

      {/* History list */}
      {memberMoods.length > 0 && (
        <div>
          <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Historique</div>
          {memberMoods.slice(0,20).map(entry => {
            const moodLevel = MOOD_LEVELS.find(l=>l.value===entry.mood);
            return (
              <div key={entry.id} style={{ ...cardStyle(), display:"flex", alignItems:"center", gap:12, marginBottom:6, padding:"10px 14px", borderLeft:`3px solid ${moodLevel?.color||T.sandDark}` }}>
                <div style={{ fontSize:24 }}>{moodLevel?.emoji}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:T.text }}>
                    {new Date(entry.date+"T00:00:00").toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})} — {entry.slot==="matin"?"🌅 Matin":"🌙 Soir"}
                  </div>
                  <div style={{ fontSize:11, color:T.textSoft }}>
                    😊 {entry.mood}/5 · ⚡ {entry.energy}/5 · 😰 {entry.anxiety}/5
                  </div>
                  {entry.note && <div style={{ fontSize:11, color:T.textMid, fontStyle:"italic", marginTop:2 }}>{entry.note}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {memberMoods.length===0 && !showForm && (
        <div style={{ textAlign:"center", padding:"32px 0", color:T.textSoft }}>
          <div style={{ fontSize:48, marginBottom:8 }}>😊</div>
          <div style={{ fontSize:14 }}>Commencez à suivre votre humeur avec les cases matin/soir ci-dessus !</div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🎮 MODULE GAMIFICATION
// ══════════════════════════════════════════════════════════════

const INITIAL_SCORES = {
  olivier:   { points:0, history:[] },
  stephanie: { points:0, history:[] },
  cleo:      { points:0, history:[] },
};

const POINTS_BY_IMPORTANCE = { 1:40, 2:30, 3:20, 4:10 };

const LEVELS = [
  { min:0,    max:99,   label:"Débutant",  icon:"🌱", color:"#8896A5" },
  { min:100,  max:299,  label:"En route",  icon:"🌿", color:"#5BBFAD" },
  { min:300,  max:599,  label:"Actif",     icon:"⭐", color:"#D4A843" },
  { min:600,  max:999,  label:"Champion",  icon:"🏆", color:"#E07B39" },
  { min:1000, max:9999, label:"Légende",   icon:"👑", color:"#E74C3C" },
];

const WEEKLY_CHALLENGES = [
  { id:"daily3",     label:"Trio du jour",     desc:"Valider 3 missions en une seule journée",                       points:50,  icon:"🎯", period:"day" },
  { id:"streak3",    label:"3 jours de suite", desc:"Valider au moins 3 missions par jour pendant 3 jours de suite",  points:100, icon:"⚡", period:"week" },
  { id:"week_all",   label:"Semaine active",   desc:"Valider au moins 1 mission chaque jour de la semaine",           points:120, icon:"📅", period:"week" },
  { id:"multitask",  label:"Multitâches",      desc:"Avoir fait chaque mission au moins une fois dans la semaine",    points:150, icon:"🌟", period:"week" },
  { id:"mood_day",    label:"Humeur du jour",   desc:"Valider les 2 évaluations d'humeur dans la journée",              points:30,  icon:"😊", period:"day" },
  { id:"mood_week",   label:"Humeur de la semaine", desc:"Valider toutes les humeurs (matin+soir) chaque jour de la semaine", points:100, icon:"🌈", period:"week" },
];

function getLevel(points) {
  return LEVELS.find(l => points >= l.min && points <= l.max) || LEVELS[0];
}

function getNextLevel(points) {
  const idx = LEVELS.findIndex(l => points >= l.min && points <= l.max);
  return idx < LEVELS.length - 1 ? LEVELS[idx + 1] : null;
}

function checkChallenges(scores, tasks, memberId, missions=[], moods=[]) {
  const todayStr = todayISO();
  const weekStart = (() => { const d=new Date(); d.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1)); return toLocalISO(d); })();
  const memberHistory = scores[memberId]?.history || [];
  const memberMoods = moods.filter(m=>m.member===memberId);

  // Today missions count
  const todayDone = memberHistory.filter(h=>h.date===todayStr && h.isDaily).length;

  // Week: days that have at least 1 mission
  const weekHistory = memberHistory.filter(h=>h.date>=weekStart && h.isDaily);
  const daysWithMission = [...new Set(weekHistory.map(h=>h.date))];

  // Streak: consecutive days with at least 3 missions each
  let streak3 = 0;
  for (let i=0; i<30; i++) {
    const d = new Date(todayStr); d.setDate(d.getDate()-i);
    const ds = toLocalISO(d);
    const cnt = memberHistory.filter(h=>h.date===ds && h.isDaily).length;
    if (cnt >= 3) streak3++;
    else break;
  }

  // Week active: at least 1 mission every day since week start
  const today = new Date(todayStr);
  const weekStartDate = new Date(weekStart);
  const daysSinceMonday = Math.floor((today - weekStartDate) / 864e5) + 1;
  const weekActive = daysWithMission.length >= daysSinceMonday;

  const completed = scores[memberId]?.completedChallenges || [];
  const weekKey = weekStart;
  const dayKey = todayStr;
  const weekCompleted = completed.filter(c=>c.week===weekKey).map(c=>c.id);
  const dayCompleted = completed.filter(c=>c.week===dayKey).map(c=>c.id);

  // Multitask: every active mission validated at least once this week
  const weekMissionTitles = new Set(weekHistory.map(h=>h.title));
  const activeMissions = missions.filter(m=>m.active!==false);
  const allMissionsDone = activeMissions.length > 0 && activeMissions.every(m=>weekMissionTitles.has(m.title));

  // Mood checks — seules les évaluations faites dans la bonne plage horaire comptent
  const todayMatin = memberMoods.some(m=>m.date===todayStr && m.slot==="matin" && m.inWindow!==false);
  const todaySoir  = memberMoods.some(m=>m.date===todayStr && m.slot==="soir" && m.inWindow!==false);
  const todayMoodDone = todayMatin && todaySoir;

  // Week mood: all days since monday have both matin+soir (dans la bonne plage)
  const todayDate = new Date(todayStr);
  let weekMoodComplete = true;
  for (let i=0; i<daysSinceMonday; i++) {
    const d = new Date(weekStart); d.setDate(d.getDate()+i);
    const ds = toLocalISO(d);
    if (ds >= todayStr) break; // don't check future days
    const hasMatin = memberMoods.some(m=>m.date===ds && m.slot==="matin" && m.inWindow!==false);
    const hasSoir  = memberMoods.some(m=>m.date===ds && m.slot==="soir" && m.inWindow!==false);
    if (!hasMatin || !hasSoir) { weekMoodComplete = false; break; }
  }

  const newChallenges = [];
  if (todayDone >= 3 && !dayCompleted.includes("daily3")) newChallenges.push({ id:"daily3", key:dayKey });
  if (streak3 >= 3 && !weekCompleted.includes("streak3")) newChallenges.push({ id:"streak3", key:weekKey });
  if (weekActive && daysSinceMonday >= 7 && !weekCompleted.includes("week_all")) newChallenges.push({ id:"week_all", key:weekKey });
  if (allMissionsDone && !weekCompleted.includes("multitask")) newChallenges.push({ id:"multitask", key:weekKey });
  if (todayMoodDone && !dayCompleted.includes("mood_day")) newChallenges.push({ id:"mood_day", key:dayKey });
  if (weekMoodComplete && daysSinceMonday >= 7 && !weekCompleted.includes("mood_week")) newChallenges.push({ id:"mood_week", key:weekKey });

  const doneMissionsCount = activeMissions.filter(m=>weekMissionTitles.has(m.title)).length;
  return { newChallenges, weekKey, dayKey, streak3, todayDone, daysWithMission: daysWithMission.length, allMissionsDone, totalMissions: activeMissions.length, doneMissions: doneMissionsCount };
}


// ══════════════════════════════════════════════════════════════
// ⭐ MISSIONS PERSO — créées par chaque membre, votées par les autres
// ══════════════════════════════════════════════════════════════

function PersonalMissionsPanel({ personalMissions, addPersonalMission, deletePersonalMission, votePersonalMission, validatePersonalMission, activeUser, onBack, dark=false }) {
  const pmText = dark ? "white" : T.text;
  const pmTextSoft = dark ? "rgba(255,255,255,0.6)" : T.textSoft;
  const pmTextMid = dark ? "rgba(255,255,255,0.8)" : T.textMid;
  const pmCardBg = dark ? "rgba(255,255,255,0.06)" : T.sandDark;
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title:"", desc:"", targetPts:100 });
  const [voteModal, setVoteModal] = useState(null); // { mission, pts }
  const weekStart = (() => { const d=new Date(); d.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1)); d.setHours(0,0,0,0); return toLocalISO(d); })();

  const myMissions = personalMissions.filter(m=>m.creator===activeUser);
  const othersToVote = personalMissions.filter(m=>m.creator!==activeUser && !(m.votes||{})[activeUser] && !(m.validated||[]).includes(activeUser));
  const othersVoted  = personalMissions.filter(m=>m.creator!==activeUser && ((m.votes||{})[activeUser] || (m.validated||[]).includes(activeUser)));

  const weekPoints = personalMissions
    .filter(m=>m.creator===activeUser && (m.validated||[]).length > 0 && m.validatedDate >= weekStart)
    .reduce((s,m)=>s+(m.avgPoints||0), 0);

  const handleAdd = () => {
    if (!form.title.trim()) return;
    addPersonalMission({ id:Date.now(), title:form.title, desc:form.desc, targetPts:form.targetPts, creator:activeUser, createdAt:todayISO(), votes:{}, validated:[], avgPoints:0 });
    setForm({ title:"", desc:"", targetPts:100 }); setShowAdd(false);
  };

  return (
    <div>
      <button onClick={onBack} style={{ background:dark?"rgba(255,255,255,0.08)":T.sandDark, border:dark?"none":`1px solid ${T.cardBorder}`, borderRadius:999, padding:"6px 12px", cursor:"pointer", fontSize:12, color:pmTextSoft, marginBottom:12 }}>← Retour</button>

      {/* Header stats */}
      <div style={{ background:"linear-gradient(135deg,#9B59B6,#3D5A8A)", borderRadius:16, padding:"16px", marginBottom:16, color:"white" }}>
        <div style={{ fontSize:12, fontWeight:700, opacity:0.8, marginBottom:4 }}>⭐ Mes points perso cette semaine</div>
        <div style={{ fontSize:36, fontWeight:900 }}>{weekPoints} <span style={{ fontSize:14, fontWeight:400, opacity:0.7 }}>/ 300 pts max</span></div>
        <div style={{ marginTop:8, background:dark?"rgba(255,255,255,0.15)":T.cardBorder, borderRadius:6, height:6 }}>
          <div style={{ background:"#D4A843", height:6, borderRadius:6, width:`${Math.min(100,Math.round(weekPoints/300*100))}%`, transition:"width 0.5s" }} />
        </div>
        <div style={{ fontSize:11, opacity:0.6, marginTop:4 }}>{Math.max(0,300-weekPoints)} pts restants avant le plafond</div>
      </div>

      {/* Mes missions */}
      <div style={{ fontSize:12, fontWeight:800, color:pmTextSoft, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>Mes défis personnels</div>
      {myMissions.map(m=>{
        const voteCount = Object.keys(m.votes||{}).length;
        const otherCount = MEMBERS.filter(mb=>mb.id!==activeUser).length;
        const allVoted = voteCount >= otherCount;
        const isValidated = (m.validated||[]).length >= otherCount;
        return (
          <div key={m.id} style={{ background:isValidated?"rgba(39,174,96,0.12)":"rgba(155,89,182,0.1)", border:`1px solid ${isValidated?"#27AE60":"rgba(155,89,182,0.3)"}`, borderRadius:14, padding:"14px", marginBottom:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15, fontWeight:800, color:pmText }}>{m.title}</div>
                {m.desc && <div style={{ fontSize:12, color:pmTextSoft, marginTop:3 }}>{m.desc}</div>}
              </div>
              {!isValidated && <button onClick={()=>deletePersonalMission(m.id)} style={{ background:"transparent", border:"none", cursor:"pointer", fontSize:16, color:pmTextSoft }}>✕</button>}
            </div>
            <div style={{ display:"flex", gap:10, marginTop:10, alignItems:"center" }}>
              <div style={{ fontSize:12, color:pmTextSoft }}>🗳️ {voteCount}/{otherCount} votes</div>
              {allVoted && <div style={{ fontSize:13, fontWeight:800, color:"#D4A843" }}>Moyenne : {m.avgPoints} pts</div>}
              {isValidated && <div style={{ fontSize:12, fontWeight:800, color:"#27AE60" }}>✅ Validé — {m.avgPoints} pts gagnés !</div>}
            </div>
          </div>
        );
      })}

      <button onClick={()=>setShowAdd(true)} style={{ width:"100%", padding:"12px", background:"linear-gradient(135deg,#9B59B6,#3D5A8A)", border:"none", borderRadius:12, fontSize:14, fontWeight:800, color:"white", cursor:"pointer", marginBottom:20 }}>
        + Créer un défi personnel
      </button>

      {showAdd && (
        <div style={{ background:pmCardBg, borderRadius:14, padding:"16px", marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:800, color:pmText, marginBottom:12 }}>Nouveau défi personnel</div>
          <input placeholder="Nom du défi (ex: Lire 20 pages)" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:8}} />
          <textarea placeholder="Description (optionnel)" value={form.desc} onChange={e=>setForm(f=>({...f,desc:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",resize:"none",minHeight:60,marginBottom:10,fontFamily:"inherit"}} />
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:pmTextSoft, marginBottom:6 }}>Points demandés (les autres voteront)</div>
            <div style={{ display:"flex", gap:8 }}>
              {[25,50,100,150,200].map(p=>(
                <button key={p} onClick={()=>setForm(f=>({...f,targetPts:p}))} style={{ flex:1, padding:"8px 4px", borderRadius:8, border:`2px solid ${form.targetPts===p?"#9B59B6":pmCardBg}`, background:form.targetPts===p?"rgba(155,89,182,0.2)":"transparent", cursor:"pointer", fontSize:12, fontWeight:700, color:form.targetPts===p?"#9B59B6":pmTextSoft }}>{p}</button>
              ))}
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleAdd} style={btnStyle("#9B59B6")}>Créer</button>
            <button onClick={()=>setShowAdd(false)} style={btnStyle(pmTextSoft)}>Annuler</button>
          </div>
        </div>
      )}

      {/* À voter */}
      {othersToVote.length > 0 && (
        <>
          <div style={{ fontSize:12, fontWeight:800, color:pmTextSoft, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>🗳️ À voter</div>
          {othersToVote.map(m=>{
            const creator = MEMBERS.find(mb=>mb.id===m.creator);
            return (
              <div key={m.id} style={{ background:"rgba(212,168,67,0.08)", border:"1px solid rgba(212,168,67,0.25)", borderRadius:14, padding:"14px", marginBottom:10 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
                  <Avatar member={m.creator} size={28} />
                  <div>
                    <div style={{ fontSize:15, fontWeight:800, color:pmText }}>{m.title}</div>
                    <div style={{ fontSize:11, color:pmTextSoft }}>Par {creator?.name} · Demande {m.targetPts} pts</div>
                  </div>
                </div>
                {m.desc && <div style={{ fontSize:12, color:pmTextSoft, marginBottom:10 }}>{m.desc}</div>}
                <div style={{ fontSize:11, color:pmTextSoft, marginBottom:8 }}>Combien de points mérite ce défi ?</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
                  {[0,25,50,75,100,150,200,300].map(p=>(
                    <button key={p} onClick={()=>setVoteModal({mission:m, pts:p})} style={{ padding:"6px 10px", borderRadius:8, border:`2px solid ${pmCardBg}`, background:"transparent", cursor:"pointer", fontSize:12, fontWeight:700, color:pmTextSoft }}>{p===0?"❌ 0":p+" pts"}</button>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Déjà votés */}
      {othersVoted.length > 0 && (
        <>
          <div style={{ fontSize:12, fontWeight:800, color:pmTextSoft, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10, marginTop:8 }}>✅ Déjà votés</div>
          {othersVoted.map(m=>{
            const creator = MEMBERS.find(mb=>mb.id===m.creator);
            const myVote = (m.votes||{})[activeUser];
            const isValidated = (m.validated||[]).includes(activeUser);
            const voteCount = Object.keys(m.votes||{}).length;
            const otherCount = MEMBERS.filter(mb=>mb.id!==m.creator).length;
            const allVoted = voteCount >= otherCount;
            return (
              <div key={m.id} style={{ background:pmCardBg, border:`1px solid ${dark?"rgba(255,255,255,0.08)":T.cardBorder}`, borderRadius:14, padding:"14px", marginBottom:10 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
                  <Avatar member={m.creator} size={24} />
                  <div style={{ fontSize:14, fontWeight:700, color:pmText }}>{m.title} <span style={{ fontSize:11, color:pmTextSoft }}>— {creator?.name}</span></div>
                </div>
                <div style={{ display:"flex", gap:12, fontSize:12, color:pmTextSoft, flexWrap:"wrap" }}>
                  {myVote !== undefined && <span>Mon vote : <strong style={{ color:pmText }}>{myVote} pts</strong></span>}
                  {allVoted && <span>Moyenne : <strong style={{ color:"#D4A843" }}>{m.avgPoints} pts</strong></span>}
                  {allVoted && !isValidated && (
                    <button onClick={()=>validatePersonalMission(m.id, activeUser)} style={{ background:"#27AE60", border:"none", borderRadius:8, padding:"4px 10px", fontSize:12, fontWeight:800, color:"white", cursor:"pointer" }}>Valider ✓</button>
                  )}
                  {isValidated && <span style={{ color:"#27AE60" }}>✅ Validé</span>}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Vote modal */}
      {voteModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={()=>setVoteModal(null)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:T.sand, borderRadius:20, padding:"20px", width:"100%", maxWidth:340 }}>
            <div style={{ fontSize:16, fontWeight:800, color:pmText, marginBottom:6 }}>Confirmer ton vote</div>
            <div style={{ fontSize:13, color:pmTextSoft, marginBottom:16 }}>Tu attribues <strong style={{ color:"#9B59B6" }}>{voteModal.pts} pts</strong> au défi "{voteModal.mission.title}"</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>{ votePersonalMission(voteModal.mission.id, activeUser, voteModal.pts); setVoteModal(null); }} style={btnStyle("#9B59B6")}>Confirmer</button>
              <button onClick={()=>setVoteModal(null)} style={btnStyle(pmTextSoft)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GameView({ scores, tasks, onScoreUpdate, activeUser, currentUser, missions=[], claims=[], addMission, deleteMission, addClaim, completeClaim, moods=[], personalMissions=[], addPersonalMission, deletePersonalMission, votePersonalMission, validatePersonalMission }) {
  const [selectedMember, setSelectedMember] = useState(currentUser||activeUser||"olivier");
  const [showCelebration, setShowCelebration] = useState(null);
  const [rankingPeriod, setRankingPeriod] = useState("week");
  const [gameSection, setGameSection] = useState(null);

  useEffect(()=>{ const who = currentUser||activeUser; if(who) setSelectedMember(who); }, [currentUser, activeUser]);

  const memberScore = scores[selectedMember] || { points:0, history:[] };
  const points = memberScore.points || 0;
  const monthCutoffStr = (() => { const d=new Date(); d.setDate(1); d.setHours(0,0,0,0); return toLocalISO(d); })();
  const monthPoints = (memberScore.history||[]).filter(h=>h.date>=monthCutoffStr).reduce((s,h)=>s+h.pts,0);
  const level = getLevel(monthPoints);
  const nextLevel = getNextLevel(monthPoints);
  const progress = nextLevel ? Math.round(((monthPoints - level.min) / (nextLevel.min - level.min)) * 100) : 100;

  // Ranking
  const getPeriodPoints = (memberId) => {
    const history = scores[memberId]?.history || [];
    const now = new Date();
    let cutoff;
    if (rankingPeriod === "week") {
      const d = new Date(now);
      d.setDate(d.getDate() - (d.getDay()===0?6:d.getDay()-1));
      d.setHours(0,0,0,0);
      cutoff = toLocalISO(d);
    } else if (rankingPeriod === "month") {
      cutoff = toLocalISO(new Date(now.getFullYear(), now.getMonth(), 1));
    } else {
      cutoff = "2000-01-01";
    }
    return history.filter(h=>h.date>=cutoff).reduce((s,h)=>s+h.pts, 0);
  };

  const ranking = MEMBERS.map(m => ({
    ...m,
    points: scores[m.id]?.points || 0,
    periodPoints: getPeriodPoints(m.id),
    monthPts: (scores[m.id]?.history||[]).filter(h=>h.date>=monthCutoffStr).reduce((s,h)=>s+h.pts,0),
    level: getLevel((scores[m.id]?.history||[]).filter(h=>h.date>=monthCutoffStr).reduce((s,h)=>s+h.pts,0)),
  })).sort((a,b) => b.periodPoints - a.periodPoints);

  // Weekly challenges status
  const weekStart = (() => { const d=new Date(); d.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1)); return toLocalISO(d); })();
  const todayKey2 = todayISO();
  const completedChallenges = [
    ...(scores[selectedMember]?.completedChallenges||[]).filter(c=>c.week===weekStart).map(c=>c.id),
    ...(scores[selectedMember]?.completedChallenges||[]).filter(c=>c.week===todayKey2).map(c=>c.id),
  ];
  const { todayDone, daysWithMission: daysWithMissionCount, streak3, doneMissions, totalMissions } = checkChallenges(scores, tasks, selectedMember, missions, moods||[]);
  const todayMoods = (moods||[]).filter(m=>m.member===selectedMember && m.date===todayISO());
  const moodMatinDone = todayMoods.some(m=>m.slot==="matin");
  const moodSoirDone  = todayMoods.some(m=>m.slot==="soir");
  const challengeProgress = {
    daily3:    todayDone,
    streak3:   streak3,
    week_all:  daysWithMissionCount,
    multitask: doneMissions||0,
    mood_day:  (moodMatinDone?1:0)+(moodSoirDone?1:0),
    mood_week: daysWithMissionCount, // reuse days count as proxy
  };
  const challengeTarget = { daily3:3, streak3:3, week_all:7, multitask: totalMissions||1, mood_day:2, mood_week:7 };

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:22, fontWeight:800, color:T.text, marginBottom:4 }}>🏆 Classement de la semaine</div>
        <div style={{ fontSize:13, color:T.textSoft }}>Qui mène la famille cette semaine ?</div>
      </div>

      {/* 🏆 Classement semaine — BLOC PRINCIPAL */}
      <div style={{ background:`linear-gradient(135deg, #1E2A3A, #2C3E52)`, borderRadius:20, padding:"20px 16px", marginBottom:20, border:"2px solid rgba(212,168,67,0.4)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
          <span style={{ fontSize:20 }}>🏆</span>
          <div style={{ fontSize:14, fontWeight:800, color:"white" }}>Classement — Cette semaine</div>
        </div>
        {ranking.map((m, i) => {
          const medals = ["🥇","🥈","🥉"];
          const isFirst = i === 0;
          return (
            <div key={m.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", marginBottom:8, borderRadius:14, background:isFirst?"rgba(212,168,67,0.2)":T.sandDark, border:`2px solid ${isFirst?"rgba(212,168,67,0.6)":T.cardBorder}` }}>
              <span style={{ fontSize:24, width:32, textAlign:"center" }}>{medals[i]||`${i+1}`}</span>
              <Avatar member={m.id} size={36} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15, fontWeight:800, color:"white" }}>{m.name}</div>
                <div style={{ fontSize:11, color:T.textSoft }}>{m.level?.icon} {m.level?.label}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:22, fontWeight:900, color:isFirst?"#D4A843":"white" }}>{m.periodPoints}</div>
                <div style={{ fontSize:10, color:T.textSoft }}>pts cette semaine</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mon profil — membre connecté uniquement */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, padding:"10px 14px", background:T.sandDark, borderRadius:12 }}>
        <Avatar member={selectedMember} size={36} />
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:T.text }}>{memberOf(selectedMember).name}</div>
          <div style={{ fontSize:11, color:T.textSoft }}>Mon profil</div>
        </div>
      </div>

      {/* Score card */}
      <div style={{ background:`linear-gradient(135deg, ${level.color}, ${level.color}cc)`, borderRadius:16, padding:"20px 16px", marginBottom:16, color:T.white }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:32 }}>{level.icon}</div>
            <div style={{ fontSize:18, fontWeight:800 }}>{level.label}</div>
            <div style={{ fontSize:13, opacity:0.8 }}>{memberOf(selectedMember).name}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:36, fontWeight:900 }}>{monthPoints}</div>
            <div style={{ fontSize:12, opacity:0.8 }}>pts ce mois</div>
          </div>
        </div>
        {nextLevel && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, opacity:0.8, marginBottom:4 }}>
              <span>Prochain niveau : {nextLevel.icon} {nextLevel.label}</span>
              <span>{nextLevel.min - points} pts restants</span>
            </div>
            <div style={{ height:8, background:T.cardBorder, borderRadius:4 }}>
              <div style={{ height:8, background:T.white, borderRadius:4, width:`${progress}%`, transition:"width 0.5s" }} />
            </div>
          </div>
        )}
      </div>

      {/* Weekly challenges — bouton dépliable */}
      {(() => {
        const [showBonus, setShowBonus] = React.useState(false);
        const doneCount = WEEKLY_CHALLENGES.filter(ch=>completedChallenges.includes(ch.id)).length;
        const totalBonus = WEEKLY_CHALLENGES.reduce((s,ch)=>s+ch.points,0);
        return (
          <div style={{ marginBottom:16 }}>
            <button onClick={()=>setShowBonus(b=>!b)} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", background:T.sandDark, border:`1px solid ${T.sandDark}`, borderRadius:12, cursor:"pointer", textAlign:"left" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:18 }}>⚡</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:800, color:T.text }}>Bonus de la semaine</div>
                  <div style={{ fontSize:11, color:T.textSoft }}>{doneCount}/{WEEKLY_CHALLENGES.length} complétés · jusqu'à +{totalBonus} pts</div>
                </div>
              </div>
              <span style={{ fontSize:16, color:T.textSoft, transition:"transform 0.2s", display:"inline-block", transform:showBonus?"rotate(180deg)":"rotate(0deg)" }}>▾</span>
            </button>
            {showBonus && (
              <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:8 }}>
                {WEEKLY_CHALLENGES.map(ch=>{
                  const done = completedChallenges.includes(ch.id);
                  const prog = challengeProgress[ch.id] || 0;
                  const target = challengeTarget[ch.id];
                  const pct = Math.min(100, Math.round((prog/target)*100));
                  return (
                    <div key={ch.id} style={{ ...cardStyle(), opacity:done?0.6:1, borderLeft:`3px solid ${done?T.mint:T.coral}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:24 }}>{ch.icon}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{ch.label}</div>
                            <div style={{ fontSize:12, fontWeight:800, color:done?T.mint:T.coral }}>+{ch.points} pts</div>
                          </div>
                          <div style={{ fontSize:11, color:T.textSoft, marginBottom:done?0:6 }}>{ch.desc}</div>
                          {!done && (
                            <div>
                              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.textSoft, marginBottom:3 }}>
                                <span>{prog} / {target}</span>
                                <span>{pct}%</span>
                              </div>
                              <div style={{ height:5, background:T.sandDark, borderRadius:3 }}>
                                <div style={{ height:5, background:T.coral, borderRadius:3, width:`${pct}%`, transition:"width 0.3s" }} />
                              </div>
                            </div>
                          )}
                          {done && <div style={{ fontSize:11, color:T.mint, fontWeight:700 }}>✓ Complété !</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Vignettes + sections missions */}
      {!gameSection && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:20, borderTop:"1px solid "+T.sandDark, paddingTop:16 }}>
          <button onClick={()=>setGameSection("famille")} style={{ background:`linear-gradient(135deg,#E8735A,#D4A843)`, border:"none", borderRadius:16, padding:"20px 14px", cursor:"pointer", textAlign:"center", color:"white" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>👨‍👩‍👧</div>
            <div style={{ fontSize:14, fontWeight:800 }}>Missions de la famille</div>
            <div style={{ fontSize:11, opacity:0.85, marginTop:4 }}>Missions collectives pour toute la famille</div>
          </button>
          <button onClick={()=>setGameSection("perso")} style={{ background:`linear-gradient(135deg,#9B59B6,#3D5A8A)`, border:"none", borderRadius:16, padding:"20px 14px", cursor:"pointer", textAlign:"center", color:"white", position:"relative" }}>
            {pendingPersonalCount(personalMissions, selectedMember) > 0 && (
              <span style={{ position:"absolute", top:8, right:8, background:"#E74C3C", color:"white", borderRadius:20, minWidth:20, height:20, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 5px" }}>{pendingPersonalCount(personalMissions, selectedMember)}</span>
            )}
            <div style={{ fontSize:32, marginBottom:8 }}>⭐</div>
            <div style={{ fontSize:14, fontWeight:800 }}>Missions perso</div>
            <div style={{ fontSize:11, opacity:0.85, marginTop:4 }}>Crée tes défis, gagne jusqu'à 300 pts/semaine</div>
          </button>
        </div>
      )}

      {gameSection==="famille" && (
        <div style={{ marginTop:20, borderTop:"1px solid "+T.sandDark, paddingTop:16 }}>
          <button onClick={()=>setGameSection(null)} style={{ background:"rgba(0,0,0,0.06)", border:"none", borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:12, color:T.textSoft, marginBottom:12 }}>← Retour</button>
          <MissionsPanel missions={missions} claims={claims} onAddMission={addMission} onUpdateMission={()=>{}} onDeleteMission={deleteMission} onClaim={addClaim} onCompleteClaim={completeClaim} scores={scores} onScoreUpdate={onScoreUpdate} activeUser={selectedMember} />
        </div>
      )}

      {gameSection==="perso" && (
        <div style={{ marginTop:20, borderTop:"1px solid "+T.sandDark, paddingTop:16 }}>
          <PersonalMissionsPanel
            personalMissions={personalMissions}
            addPersonalMission={addPersonalMission}
            deletePersonalMission={deletePersonalMission}
            votePersonalMission={votePersonalMission}
            validatePersonalMission={validatePersonalMission}
            activeUser={selectedMember}
            onBack={()=>setGameSection(null)}
          />
        </div>
      )}

      {/* Recent history */}
      {(memberScore.history||[]).length > 0 && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:12, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Derniers points gagnés</div>
          {[...(memberScore.history||[])].reverse().slice(0,5).map((h,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid "+T.sandDark }}>
              <span style={{ fontSize:14 }}>{EISENHOWER.find(e=>e.value===h.importance)?.icon||"⭐"}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:600, color:T.text }}>{h.title}</div>
                <div style={{ fontSize:10, color:T.textSoft }}>{new Date(h.date+"T00:00:00").toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})}</div>
              </div>
              <div style={{ fontSize:13, fontWeight:800, color:T.coral }}>+{h.pts} pts</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🎯 MISSIONS QUOTIDIENNES
// ══════════════════════════════════════════════════════════════

const INITIAL_MISSIONS = [
  { id:1, title:"Faire la vaisselle",     icon:"🍽️", points:20, active:true, days:[0,1,2,3,4,5,6], duration:15, desc:"Rincer les assiettes, mettre en machine ou laver à la main. Ne pas oublier les verres et les casseroles." },
  { id:2, title:"Passer l'aspirateur",    icon:"🧹", points:25, active:true, days:[0,2,4],           duration:20, desc:"Aspirer le salon, les chambres et le couloir. Penser à déplacer les chaises pour atteindre les coins." },
  { id:3, title:"Préparer le repas",      icon:"🍳", points:30, active:true, days:[0,1,2,3,4,5,6], duration:30, desc:"Préparer un repas équilibré pour la famille. Vérifier le menu prévu dans l'app Repas." },
  { id:4, title:"Faire les courses",      icon:"🛒", points:35, active:true, days:[1,4],              duration:45, desc:"Suivre la liste de courses dans l'app. Vérifier les dates de péremption et les promotions." },
  { id:5, title:"Sortir les poubelles",   icon:"🗑️", points:15, active:true, days:[0,3],              duration:5,  desc:"Sortir les sacs poubelle et les déposer dans les conteneurs. Remettre des sacs vides." },
  { id:6, title:"Ranger le salon",        icon:"🛋️", points:20, active:true, days:[0,1,2,3,4,5,6], duration:15, desc:"Ramasser ce qui traîne, plier les plaids, ranger la télécommande et remettre les coussins en place." },
];

// A "claim" = mission taken by a member for a specific date
const INITIAL_CLAIMS = [];

function MissionsPanel({ missions, claims, onAddMission, onUpdateMission, onDeleteMission, onClaim, onCompleteClaim, scores, onScoreUpdate, activeUser }) {
  const [showAdd, setShowAdd] = useState(false);
  const [mTitle, setMTitle] = useState("");
  const [mIcon, setMIcon]   = useState("⭐");
  const [mPoints, setMPoints] = useState(20);
  const [mDays, setMDays]   = useState([0,1,2,3,4,5,6]);
  const [mDesc, setMDesc]   = useState("");
  const [mDuration, setMDuration] = useState(15);
  const [selectedMission, setSelectedMission] = useState(null);
  const todayStr = todayISO();

  const todayClaims = claims.filter(c => c.date === todayStr);

  const handleAdd = () => {
    if (!mTitle.trim()) return;
    onAddMission({ title:mTitle, icon:mIcon, points:mPoints, days:mDays, desc:mDesc, duration:mDuration, id:Date.now(), active:true });
    setMTitle(""); setMIcon("⭐"); setMPoints(20); setMDays([0,1,2,3,4,5,6]); setMDesc(""); setMDuration(15);
    setShowAdd(false);
  };

  const handleClaim = (mission, memberId) => {
    // Check not already claimed by this member today
    const alreadyClaimed = todayClaims.some(c => c.missionId===mission.id && c.memberId===memberId);
    if (alreadyClaimed) return;
    onClaim({ id:Date.now(), missionId:mission.id, memberId, date:todayStr, done:false, points:mission.points, title:mission.title, icon:mission.icon });
  };

  const handleComplete = (claim) => {
    if (claim.done) return;
    onCompleteClaim(claim.id);
    // Award points via onScoreUpdate
    onScoreUpdate(prev => {
      const cur = prev[claim.memberId] || { points:0, history:[], completedChallenges:[] };
      const history = [...(cur.history||[]), { title:claim.title, pts:claim.points, importance:2, date:todayStr, isDaily:true }];
      const newPoints = (cur.points||0) + claim.points;
      const tempScores = {...prev, [claim.memberId]: {...cur, points:newPoints, history}};
      const { newChallenges, weekKey, dayKey } = checkChallenges(tempScores, [], claim.memberId, missions, []);
      const bonusPts = newChallenges.reduce((s,ch)=> s + (WEEKLY_CHALLENGES.find(c=>c.id===ch.id)?.points||0), 0);
      const completedChallenges = [...(cur.completedChallenges||[]), ...newChallenges.map(ch=>({id:ch.id,week:ch.key}))];
      return {...prev, [claim.memberId]: { ...cur, points:newPoints+bonusPts, history, completedChallenges }};
    });
  };

  return (
    <div>
      {/* Today's claims */}
      {todayClaims.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Missions prises aujourd'hui</div>
          {todayClaims.map(claim => (
            <div key={claim.id} style={{ ...cardStyle(), display:"flex", alignItems:"center", gap:10, marginBottom:6, opacity:claim.done?0.6:1, borderLeft:`3px solid ${claim.done?T.mint:T.gold}` }}>
              <span style={{ fontSize:22 }}>{claim.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{claim.title}</div>
                <div style={{ fontSize:11, color:T.textSoft, display:"flex", alignItems:"center", gap:6 }}>
                  <Avatar member={claim.memberId} size={16} />
                  {memberOf(claim.memberId).name} · <span style={{ color:T.gold, fontWeight:700 }}>+{claim.points} pts</span>
                </div>
              </div>
              {!claim.done
                ? <button onClick={()=>handleComplete(claim)} style={btnStyle(T.mint,{fontSize:12,padding:"6px 12px"})}>✓ Fait !</button>
                : <span style={{ fontSize:20 }}>✅</span>
              }
            </div>
          ))}
        </div>
      )}

      {/* Mission list */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div style={{ fontSize:12, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em" }}>Missions disponibles</div>
        <button type="button" onClick={()=>setShowAdd(s=>!s)} style={btnStyle(T.gold,{fontSize:12,padding:"5px 12px"})}>+ Créer</button>
      </div>

      {/* Mission detail modal */}
      {selectedMission && (
        <Modal title={`${selectedMission.icon} ${selectedMission.title}`} onClose={()=>setSelectedMission(null)}>
          <div style={{ textAlign:"center", marginBottom:16 }}>
            <span style={{ fontSize:52 }}>{selectedMission.icon}</span>
          </div>
          {/* Points & duration */}
          <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:16 }}>
            <div style={{ padding:"6px 14px", background:"rgba(212,168,67,0.15)", border:"1px solid #D4A843", borderRadius:20 }}>
              <span style={{ fontSize:13, fontWeight:800, color:"#D4A843" }}>+{selectedMission.points} pts</span>
            </div>
            {selectedMission.duration && (
              <div style={{ padding:"6px 14px", background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:20 }}>
                <span style={{ fontSize:13, fontWeight:700, color:T.textMid }}>⏱ {selectedMission.duration} min</span>
              </div>
            )}
          </div>
          {/* Description */}
          {selectedMission.desc && (
            <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Description</div>
              <div style={{ fontSize:13, color:T.textMid, lineHeight:1.6 }}>{selectedMission.desc}</div>
            </div>
          )}
          {/* Days */}
          {selectedMission.days && selectedMission.days.length < 7 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Jours</div>
              <div style={{ display:"flex", gap:4 }}>
                {["L","M","M","J","V","S","D"].map((d,i)=>(
                  <div key={i} style={{ flex:1, textAlign:"center", padding:"5px 2px", borderRadius:7, background:selectedMission.days.includes(i)?"#D4A84333":T.sandDark, border:`1px solid ${selectedMission.days.includes(i)?"#D4A843":T.cardBorder}`, fontSize:11, fontWeight:700, color:selectedMission.days.includes(i)?"#D4A843":T.textSoft }}>{d}</div>
                ))}
              </div>
            </div>
          )}
          <button onClick={()=>setSelectedMission(null)} style={btnStyle(T.gold,{width:"100%"})}>Fermer</button>
        </Modal>
      )}

      {showAdd && (
        <Modal title="🎯 Nouvelle mission" onClose={()=>setShowAdd(false)}>
          <div style={{ display:"flex", gap:6, marginBottom:10 }}>
            <input value={mIcon} onChange={e=>setMIcon(e.target.value)} style={{...inputStyle,width:48,textAlign:"center",fontSize:18}} maxLength={2} />
            <input placeholder="Nom de la mission" value={mTitle} onChange={e=>setMTitle(e.target.value)} style={{...inputStyle,flex:1}} autoFocus />
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
            <span style={{ fontSize:12, color:T.textSoft }}>Points :</span>
            <select value={mPoints} onChange={e=>setMPoints(+e.target.value)} style={{...inputStyle,flex:1}}>
              {[10,15,20,25,30,35,40,50].map(p=><option key={p} value={p}>{p} pts</option>)}
            </select>
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:5 }}>Jours</div>
            <div style={{ display:"flex", gap:4 }}>
              {["L","M","M","J","V","S","D"].map((d,i)=>{
                const active = mDays.includes(i);
                return (
                  <button key={i} type="button" onClick={()=>setMDays(prev => prev.includes(i) ? prev.filter(x=>x!==i) : [...prev,i].sort())}
                    style={{ flex:1, padding:"6px 2px", borderRadius:7, border:"2px solid "+(active?T.gold:T.sandDark), background:active?T.gold+"22":"transparent", cursor:"pointer", fontSize:11, fontWeight:700, color:active?T.gold:T.textSoft }}>
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
          <textarea placeholder="Description (conseils, étapes...)" value={mDesc} onChange={e=>setMDesc(e.target.value)} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:8,minHeight:60,resize:"vertical"}} />
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12 }}>
            <span style={{ fontSize:12, color:T.textSoft, flexShrink:0 }}>⏱ Durée estimée :</span>
            <select value={mDuration} onChange={e=>setMDuration(+e.target.value)} style={{...inputStyle,flex:1}}>
              {[5,10,15,20,30,45,60].map(d=><option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <button type="button" onClick={handleAdd} style={btnStyle(T.gold,{width:"100%"})}>✓ Créer la mission</button>
        </Modal>
      )}

      {missions.filter(m=> m.active !== false).map(mission => {
        const todayClaim = todayClaims.find(c=>c.missionId===mission.id);
        const isTakenToday = !!todayClaim;

        // If claimed today, show as taken (not available)
        if (isTakenToday) {
          const claimer = memberOf(todayClaim.memberId);
          return (
            <div key={mission.id} style={{ ...cardStyle(), marginBottom:8, borderLeft:`3px solid ${todayClaim.done?T.mint:T.gold}`, opacity:0.7 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <button onClick={(e)=>{e.stopPropagation();setSelectedMission(mission);}} style={{ fontSize:28, background:"transparent", border:"none", cursor:"pointer", padding:"4px", borderRadius:8, flexShrink:0, filter:"grayscale(0.3)" }}>{mission.icon}</button>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{mission.title}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <Avatar member={todayClaim.memberId} size={16} />
                    <span style={{ fontSize:11, color:T.textSoft }}>{claimer.name} · <span style={{color:T.gold,fontWeight:700}}>+{mission.points} pts</span></span>
                    {todayClaim.done && <span style={{ fontSize:11, color:T.mint, fontWeight:700 }}>✅ Fait !</span>}
                  </div>
                </div>
                <button onClick={()=>onDeleteMission(mission.id)} style={{ fontSize:10, background:"transparent", border:"none", color:T.textSoft, cursor:"pointer" }}>×</button>
              </div>
            </div>
          );
        }

        return (
          <div key={mission.id} style={{ ...cardStyle(), marginBottom:8, borderLeft:`3px solid ${T.gold}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <button onClick={(e)=>{e.stopPropagation();setSelectedMission(mission);}} style={{ fontSize:28, background:"transparent", border:"none", cursor:"pointer", padding:"4px", borderRadius:8, flexShrink:0 }}>{mission.icon}</button>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:700, color:T.text, cursor:"pointer" }} onClick={(e)=>{e.stopPropagation();setSelectedMission(mission);}}>{mission.title}</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:T.gold }}>+{mission.points} pts</span>
                  {mission.days && mission.days.length<7 && (
                    <span style={{ fontSize:10, color:T.textSoft }}>· {["L","M","M","J","V","S","D"].filter((_,i)=>mission.days.includes(i)).join(" ")}</span>
                  )}
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
                <div style={{ display:"flex", gap:4 }}>
                  {MEMBERS.map(m=>(
                    <button key={m.id} onClick={()=>handleClaim(mission, m.id)}
                      style={{ padding:"6px 8px", borderRadius:8, border:`2px solid ${m.color}`, background:m.color+"12", cursor:"pointer", fontSize:11, fontWeight:700, color:m.color }}>
                      {m.avatar}
                    </button>
                  ))}
                </div>
                <button onClick={()=>onDeleteMission(mission.id)} style={{ fontSize:10, background:"transparent", border:"none", color:T.textSoft, cursor:"pointer" }}>Supprimer</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🏠 PAGE D'ACCUEIL
// ══════════════════════════════════════════════════════════════

function HomeView({ onNavigate, tasks, appts, events, reminders, moods, scores, messages, activeUser, currentUser, medications=[], medLogs=[] }) {
  const todayStr = todayISO();
  const todayDow = new Date().getDay()===0?6:new Date().getDay()-1;
  const me = currentUser || activeUser || "olivier";

  // Quick stats
  const todayTasks = tasks.filter(t=>!t.done && t.date===todayStr);
  const todayAppts = appts.filter(a=>a.date===todayStr);
  const todayEvents = events.filter(e=>e.day===todayDow);
  const pendingReminders = reminders.filter(r=>!r.done);
  const unreadMessages = messages.filter(m=>m.to===me && !m.read).length;
  const monthCutoff = new Date(); monthCutoff.setDate(1);
  const monthPts = (scores[me]?.history||[]).filter(h=>h.date>=toLocalISO(monthCutoff)).reduce((s,h)=>s+h.pts,0);
  const todayMood = moods.filter(m=>m.member===me && m.date===todayStr);
  const pendingMeds = medications.filter(m=>m.member===me && m.active!==false && !medLogs.some(l=>l.medicationId===m.id && l.date===todayStr));

  const SECTIONS = [
    {
      title:"Quotidien",
      items:[
        { id:"planning", icon:"📅", label:"Planning",    desc:`${todayEvents.length+todayAppts.length} événement${todayEvents.length+todayAppts.length>1?"s":""} aujourd'hui`, color:"#3D5A8A" },
        { id:"tasks",    icon:"✓",  label:"Tâches",      desc:`${todayTasks.length} tâche${todayTasks.length>1?"s":""} à faire`, color:"#7B5EA7" },
        { id:"appts",    icon:"🏥", label:"RDV",         desc:`${todayAppts.length} RDV aujourd'hui`, color:"#5BBFAD" },
        { id:"rappels",  icon:"⏰", label:"Rappels",     desc:`${pendingReminders.length} en attente`, color:"#E07B39" },
      ]
    },
    {
      title:"Organisation",
      items:[
        { id:"repas",  icon:"🍽️", label:"Repas",        desc:"Menus & recettes",       color:"#E8735A" },
        { id:"fiches", icon:"📋", label:"Fiches",        desc:"Spécialistes & suivi",   color:"#D4A843" },
        { id:"docs",   icon:"📁", label:"Documents",     desc:"Fichiers & ordonnances", color:"#8B6F47" },
      ]
    },
    {
      title:"Bien-être & famille",
      items:[
        { id:"humeur",   icon:"😊", label:"Humeur",      desc: todayMood.length>0 ? "Évalué aujourd'hui ✓" : "Pas encore évalué", color:"#27AE60" },
        { id:"medicaments", icon:"💊", label:"Médicaments", desc: pendingMeds.length>0 ? `${pendingMeds.length} à prendre` : "Tout est pris ✓", color:"#D4A843" },
        { id:"game",     icon:"🏆", label:"Défis & Classement", desc:`${monthPts} pts ce mois`, color:"#D4A843" },
        { id:"messages", icon:"✉️", label:"Messages",    desc:unreadMessages>0?`${unreadMessages} non lu${unreadMessages>1?"s":""}`:  "Messagerie famille", color:"#E91E63" },
        { id:"medias",   icon:"🎙️", label:"Médias",      desc:"Webinaires, podcasts, articles", color:"#E8735A" },
        { id:"chat",     icon:"💬", label:"Assistant",   desc:"Alfy répond à tout",   color:"#5BBFAD" },
      ]
    }
  ];

  return (
    <div>
      {/* Welcome header */}
      <div style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:20, padding:"20px 16px", marginBottom:20, color:T.text, boxShadow:`0 1px 4px ${T.shadow}` }}>
        <div style={{ fontSize:13, color:T.textSoft, marginBottom:4 }}>
          {new Date().toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}
        </div>
        <div style={{ fontSize:22, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text, marginBottom:12 }}>
          Bonjour {memberOf(me).name} 👋
        </div>
        {/* Quick stats row */}
        <div style={{ display:"flex", gap:8 }}>
          {[
            { v:todayTasks.length,   l:"tâches",    c:T.coral },
            { v:todayAppts.length,   l:"RDV",       c:T.mint },
            { v:pendingReminders.length, l:"rappels", c:"#D4A843" },
            { v:monthPts,            l:"pts mois",  c:"#9B59B6" },
          ].map((s,i)=>(
            <div key={i} style={{ flex:1, background:s.c+"15", border:`1px solid ${s.c}33`, borderRadius:10, padding:"8px 4px", textAlign:"center" }}>
              <div style={{ fontSize:18, fontWeight:900, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:9, color:T.textMid, marginTop:1 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 🏆 Classement rapide — accueil */}
      {(() => {
        const weekStart = (() => { const d=new Date(); d.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1)); d.setHours(0,0,0,0); return toLocalISO(d); })();
        const weekRanking = MEMBERS.map(m=>({ ...m, pts:(scores[m.id]?.history||[]).filter(h=>h.date>=weekStart).reduce((s,h)=>s+h.pts,0) })).sort((a,b)=>b.pts-a.pts);
        const leader = weekRanking[0];
        return (
          <div onClick={()=>onNavigate("game")} style={{ background:T.sandDark, borderRadius:20, padding:"14px 16px", marginBottom:16, border:"2px solid rgba(212,168,67,0.35)", cursor:"pointer", boxShadow:`0 1px 4px ${T.shadow}` }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:18 }}>🏆</span>
                <span style={{ fontSize:13, fontWeight:800, color:T.text }}>Classement de la semaine</span>
              </div>
              <span style={{ fontSize:11, color:"#B8901F", fontWeight:700 }}>Voir tout →</span>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {weekRanking.map((m,i)=>(
                <div key={m.id} style={{ flex:1, background:i===0?"rgba(212,168,67,0.15)":T.sand, borderRadius:10, padding:"10px 6px", textAlign:"center", border:`1px solid ${i===0?"rgba(212,168,67,0.4)":T.cardBorder}` }}>
                  <div style={{ fontSize:16 }}>{["🥇","🥈","🥉"][i]||"·"}</div>
                  <Avatar member={m.id} size={28} />
                  <div style={{ fontSize:10, fontWeight:700, color:T.text, marginTop:3 }}>{m.name}</div>
                  <div style={{ fontSize:13, fontWeight:900, color:i===0?"#D4A843":T.textMid, marginTop:2 }}>{m.pts}<span style={{ fontSize:9, fontWeight:400, color:T.textSoft }}> pts</span></div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Sections */}
      {SECTIONS.map(section=>(
        <div key={section.title} style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>{section.title}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {section.items.map(item=>(
              <button key={item.id} onClick={()=>onNavigate(item.id)}
                style={{ background:`linear-gradient(135deg, ${item.color}, ${item.color}CC)`, border:"none", borderRadius:14, padding:"16px", cursor:"pointer", textAlign:"center", color:"white" }}>
                <div style={{ fontSize:28 }}>{item.icon}</div>
                <div style={{ fontSize:13, fontWeight:800, marginTop:4 }}>{item.label}</div>
                <div style={{ fontSize:11, opacity:0.85, marginTop:2 }}>{item.desc}</div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* SOS shortcut */}
      <button onClick={()=>document.querySelector('[title="Je souffle"]')?.click()}
        style={{ width:"100%", padding:"14px", background:"rgba(231,76,60,0.1)", border:"2px solid rgba(231,76,60,0.3)", borderRadius:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:8 }}>
        <span style={{ fontSize:20 }}>🌊</span>
        <span style={{ fontSize:14, fontWeight:700, color:"#3498DB" }}>Je souffle</span>
      </button>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 👶 VERSION ENFANT
// ══════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════
// 😊 HUMEUR ENFANT — 3 dimensions, sans sélecteur de membre
// ══════════════════════════════════════════════════════════════

function KidsMoodView({ moods, onAdd, member }) {
  const MOOD_DIMS = [
    { key:"mood",    label:"Humeur",   emojis:["😔","😕","😐","🙂","😄"], color:"#D4A843" },
    { key:"energy",  label:"Énergie",  emojis:["🪫","😓","😑","⚡","🚀"], color:"#5BBFAD" },
    { key:"anxiety", label:"Anxiété",  emojis:["😰","😟","😐","😌","😎"], color:"#E8735A" },
  ];

  const [showForm, setShowForm] = useState(false);
  const [slot, setSlot] = useState("matin");
  const [form, setForm] = useState({ mood:3, energy:3, anxiety:3, note:"" });

  const todayStr = todayISO();
  const todayMoods = moods.filter(m=>m.member===member && m.date===todayStr);
  const hasMatin = todayMoods.some(m=>m.slot==="matin");
  const hasSoir  = todayMoods.some(m=>m.slot==="soir");

  const save = () => {
    // Empêche les doublons si l'évaluation de ce créneau existe déjà pour aujourd'hui
    if (moods.some(m=>m.member===member && m.date===todayStr && m.slot===slot)) { setShowForm(false); return; }
    onAdd({ id:Date.now(), member, date:todayStr, slot, ...form, createdAt:new Date().toISOString() });
    setShowForm(false);
    setForm({ mood:3, energy:3, anxiety:3, note:"" });
  };

  const lastEntries = moods.filter(m=>m.member===member).sort((a,b)=>b.date.localeCompare(a.date)||b.slot.localeCompare(a.slot)).slice(0,6);

  return (
    <div style={{ padding:"0 16px" }}>
      <div style={{ fontSize:18, fontWeight:800, color:"white", marginBottom:16 }}>😊 Comment je vais ?</div>

      {/* Today status */}
      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        {["matin","soir"].map(s=>{
          const done = s==="matin"?hasMatin:hasSoir;
          const entry = todayMoods.find(mo=>mo.slot===s);
          return (
            <button key={s} onClick={()=>{ if(!done){ setSlot(s); setShowForm(true); }}} style={{ flex:1, padding:"12px", borderRadius:12, border:`2px solid ${done?"#27AE60":"rgba(255,255,255,0.15)"}`, background:done?"rgba(39,174,96,0.15)":"rgba(255,255,255,0.05)", cursor:done?"default":"pointer", textAlign:"center" }}>
              <div style={{ fontSize:20 }}>{s==="matin"?"🌅":"🌙"}</div>
              <div style={{ fontSize:12, fontWeight:700, color:"white", marginTop:4, textTransform:"capitalize" }}>{s}</div>
              <div style={{ fontSize:11, color:done?"#27AE60":"rgba(255,255,255,0.4)", marginTop:2 }}>{done?"✓ Évalué":"Pas encore"}</div>
              {done && entry?.createdAt && (
                <div style={{ fontSize:9, color:entry.inWindow===false?"#E8735A":"rgba(255,255,255,0.35)", marginTop:2 }}>
                  {new Date(entry.createdAt).toTimeString().slice(0,5)}{entry.inWindow===false && " · hors plage"}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:16, padding:"16px", marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:800, color:"white", marginBottom:14, textAlign:"center" }}>
            {slot==="matin"?"🌅 Ce matin, je me sens...":"🌙 Ce soir, je me sens..."}
          </div>
          {MOOD_DIMS.map(dim=>(
            <div key={dim.key} style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.6)", marginBottom:8 }}>{dim.label}</div>
              <div style={{ display:"flex", justifyContent:"space-between", gap:4 }}>
                {dim.emojis.map((e,i)=>(
                  <button key={i} onClick={()=>setForm(f=>({...f,[dim.key]:i+1}))} style={{ flex:1, padding:"10px 4px", borderRadius:10, border:`2px solid ${form[dim.key]===i+1?dim.color:"rgba(255,255,255,0.1)"}`, background:form[dim.key]===i+1?`${dim.color}33`:"transparent", cursor:"pointer", fontSize:20, textAlign:"center" }}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <textarea placeholder="Un mot sur ta journée ? (optionnel)" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} style={{ width:"100%", boxSizing:"border-box", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, padding:"10px", fontSize:13, color:"white", outline:"none", resize:"none", minHeight:60, fontFamily:"inherit", marginBottom:12 }} />
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save} style={{ flex:2, padding:"12px", background:"#27AE60", border:"none", borderRadius:10, fontSize:14, fontWeight:800, color:"white", cursor:"pointer" }}>Valider 💙</button>
            <button onClick={()=>setShowForm(false)} style={{ flex:1, padding:"12px", background:"rgba(255,255,255,0.08)", border:"none", borderRadius:10, fontSize:14, color:"rgba(255,255,255,0.5)", cursor:"pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      {/* Recent history */}
      {lastEntries.length > 0 && (
        <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:16, padding:"14px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>Mes dernières évaluations</div>
          {lastEntries.map((e,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:i<lastEntries.length-1?"1px solid rgba(255,255,255,0.06)":"none" }}>
              <span style={{ fontSize:16 }}>{e.slot==="matin"?"🌅":"🌙"}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:e.inWindow===false?"#E8735A":"rgba(255,255,255,0.4)" }}>
                  {e.date} · {e.slot}{e.createdAt && " · "+new Date(e.createdAt).toTimeString().slice(0,5)}{e.inWindow===false && " · hors plage"}
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {MOOD_DIMS.map(dim=>(
                  <span key={dim.key} style={{ fontSize:16 }}>{dim.emojis[(e[dim.key]||3)-1]}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🍽️ REPAS ENFANT — lecture seule, sans édition
// ══════════════════════════════════════════════════════════════

function KidsMealsReadOnly({ recipes, menu }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const weekKey = getWeekKey(weekOffset);
  const currentMenu = menu[weekKey] || {};

  const getWeekLabel = () => {
    if (weekOffset===0) return "Cette semaine";
    if (weekOffset===1) return "Semaine prochaine";
    if (weekOffset===-1) return "Semaine dernière";
    const d = new Date(); const dow=d.getDay()===0?6:d.getDay()-1;
    d.setDate(d.getDate()-dow+weekOffset*7);
    return d.toLocaleDateString("fr-FR",{day:"numeric",month:"long"});
  };

  return (
    <div>
      {/* Week navigator */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(255,255,255,0.06)", borderRadius:10, border:"1px solid rgba(255,255,255,0.1)", padding:"8px 12px", marginBottom:14 }}>
        <button onClick={()=>setWeekOffset(o=>o-1)} style={{ background:"transparent",border:"none",cursor:"pointer",fontSize:20,color:"white",padding:"0 6px" }}>{"<"}</button>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:13,fontWeight:700,color:"white" }}>{getWeekLabel()}</div>
          {weekOffset!==0 && <button onClick={()=>setWeekOffset(0)} style={{ fontSize:11,color:"#E07B39",background:"transparent",border:"1px solid #E07B39",borderRadius:6,cursor:"pointer",padding:"2px 8px",fontWeight:700,marginTop:3 }}>Auj.</button>}
        </div>
        <button onClick={()=>setWeekOffset(o=>o+1)} style={{ background:"transparent",border:"none",cursor:"pointer",fontSize:20,color:"white",padding:"0 6px" }}>{">"}</button>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {DAYS.map(day=>(
          <div key={day} style={{ background:"rgba(255,255,255,0.05)", borderRadius:10, border:"1px solid rgba(255,255,255,0.1)", overflow:"hidden" }}>
            <div style={{ background:"rgba(255,255,255,0.08)", padding:"6px 12px" }}>
              <span style={{ fontSize:12, fontWeight:800, color:"white" }}>{day}</span>
            </div>
            <div style={{ padding:"6px 8px", display:"flex", flexDirection:"column", gap:4 }}>
              {MEAL_SLOTS.map(slot=>{
                const key = `${day}-${slot}`;
                const recipeId = currentMenu[key];
                const recipe = recipes.find(r=>r.id===recipeId);
                const isExt = recipeId === "ext";
                const label = isExt ? "🍕 Autre" : (recipe ? recipe.title : "—");
                return (
                  <div key={key} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", background:(recipe||isExt)?"rgba(224,123,57,0.12)":"transparent", borderRadius:7, border:`1px solid ${(recipe||isExt)?"#E07B39":"rgba(255,255,255,0.08)"}` }}>
                    <div style={{ fontSize:16, flexShrink:0 }}>{MEAL_SLOT_ICONS[slot]}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:"#E07B39", textTransform:"uppercase", letterSpacing:"0.04em", width:52, flexShrink:0 }}>{slot}</div>
                    <div style={{ fontSize:12, color:(recipe||isExt)?"white":"rgba(255,255,255,0.3)", flex:1 }}>{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KidsView({ member, tasks, events, appts, moods, scores, missions, claims, recipes=[], menu={}, shopping=[], onUpdateMenu, onUpdateShopping, onAddRecipe, onUpdateRecipe, onDeleteRecipe, onToggleTask, onAddMood, onCompleteClaim, onAddClaim, onDeleteClaim, onScoreUpdate, onExit, personalMissions=[], addPersonalMission, deletePersonalMission, votePersonalMission, validatePersonalMission, onOpenSas, bandeauCount=0, medications=[], medLogs=[], onLogTaken, onUnlogTaken }) {
  const [tab, setTab] = useState("home");
  const [selectedMission, setSelectedMission] = useState(null);
  const [kidsMissionSection, setKidsMissionSection] = useState(null);
  const m = memberOf(member);
  const todayStr = todayISO();
  const todayDow = new Date().getDay()===0?6:new Date().getDay()-1;

  const myTasks = tasks.filter(t=>!t.done && normalizeMembers(t.members||t.member).includes(member) && t.date===todayStr);
  const myEvents = events.filter(e=>normalizeMembers(e.members||e.member).includes(member) && e.day===todayDow);
  const myAppts = appts.filter(a=>normalizeMembers(a.members||a.member).includes(member) && a.date===todayStr);
  const todayClaims = claims.filter(c=>c.memberId===member && c.date===todayStr);
  const todayMood = moods.find(mo=>mo.member===member && mo.date===todayStr && mo.slot==="matin");
  const monthCutoff = new Date(); monthCutoff.setDate(1);
  const monthPts = (scores[member]?.history||[]).filter(h=>h.date>=toLocalISO(monthCutoff)).reduce((s,h)=>s+h.pts,0);
  const level = getLevel(monthPts);

  const KIDS_TABS = [
    { id:"home",     icon:"🏠", label:"Accueil" },
    { id:"planning", icon:"📅", label:"Ma journée" },
    { id:"repas",    icon:"🍽️", label:"Repas" },
    { id:"humeur",   icon:"😊", label:"Humeur" },
    { id:"defis",    icon:"🏆", label:"Défis" },
  ];

  const MOOD_EMOJIS = ["😔","😕","😐","🙂","😄"];

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg, #1E2A3A 0%, #2C3E52 100%)", fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif" }}>
      {/* Kids header */}
      <div style={{ background:"rgba(255,255,255,0.08)", padding:"16px 20px", paddingTop:"max(16px,env(safe-area-inset-top))", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:"white" }}>{m.avatar}</div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:"white" }}>Bonjour {m.name} ! 👋</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>{level.icon} {level.label} · {monthPts} pts ce mois</div>
          </div>
        </div>
        <button onClick={onExit} style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontSize:11, color:"rgba(255,255,255,0.6)" }}>Quitter</button>
      </div>

      {onOpenSas && (
        <button onClick={onOpenSas} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", padding:"10px 20px", background:"rgba(255,255,255,0.04)", border:"none", borderBottom:"1px solid rgba(255,255,255,0.08)", cursor:"pointer" }}>
          <span style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:14 }}>📋</span>
            <span style={{ fontSize:13, fontWeight:700, color:"white" }}>Points importants du jour</span>
          </span>
          {bandeauCount > 0 && (
            <span style={{ background:"#E8735A", color:"white", borderRadius:20, minWidth:20, height:20, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 6px" }}>{bandeauCount}</span>
          )}
        </button>
      )}

      {/* Content */}
      <div style={{ padding:"16px 16px 100px" }}>

        {/* HOME */}
        {tab==="home" && (
          <div>
            {/* Médicaments à prendre */}
            {(() => {
              const todayStr = todayISO();
              const medsToday = medications.filter(m=>m.member===member && m.active!==false && !medLogs.some(l=>l.medicationId===m.id && l.date===todayStr));
              if (medsToday.length===0) return null;
              return (
                <div style={{ background:"rgba(212,168,67,0.12)", border:"1px solid rgba(212,168,67,0.3)", borderRadius:16, padding:"14px 16px", marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#D4A843", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>💊 À prendre</div>
                  {medsToday.map(med=>(
                    <div key={med.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0" }}>
                      <button onClick={()=>onLogTaken && onLogTaken(med.id, member, todayStr)} style={{ width:24,height:24,borderRadius:"50%",border:"2px solid #D4A843",background:"transparent",cursor:"pointer",flexShrink:0 }} />
                      <div style={{ fontSize:14, fontWeight:700, color:"white" }}>{med.name}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>{MED_SLOT_ICONS[med.slot]} {med.time}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Today's schedule */}
            <div style={{ background:"rgba(255,255,255,0.08)", borderRadius:16, padding:"16px", marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:"rgba(255,255,255,0.6)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:12 }}>📅 Ma journée</div>
              {myEvents.length===0 && myAppts.length===0 && myTasks.length===0 && (
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textAlign:"center", padding:"10px 0" }}>Journée libre ! 🎉</div>
              )}
              {[...myAppts, ...myEvents].map((ev,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
                  <div style={{ fontSize:22 }}>{ev.specialist?"🏥":"📅"}</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:"white" }}>{ev.title}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>{ev.time||ev.date}</div>
                  </div>
                </div>
              ))}
              {myTasks.map(t=>(
                <div key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
                  <button onClick={()=>onToggleTask(t.id)} style={{ width:26,height:26,borderRadius:6,border:"2px solid rgba(255,255,255,0.3)",background:"transparent",cursor:"pointer",flexShrink:0 }} />
                  <div style={{ fontSize:14, fontWeight:600, color:"white" }}>{t.title}</div>
                </div>
              ))}
            </div>

            {/* 🏆 Classement de la semaine — même bloc que côté adulte */}
            {(() => {
              const weekStart = (() => { const d=new Date(); d.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1)); d.setHours(0,0,0,0); return toLocalISO(d); })();
              const weekRanking = MEMBERS.map(mb=>({ ...mb, pts:(scores[mb.id]?.history||[]).filter(h=>h.date>=weekStart).reduce((s,h)=>s+h.pts,0) })).sort((a,b)=>b.pts-a.pts);
              return (
                <div onClick={()=>setTab("defis")} style={{ background:"linear-gradient(135deg,#1E2A3A,#2C3E52)", borderRadius:16, padding:"14px 16px", marginBottom:16, border:"2px solid rgba(212,168,67,0.35)", cursor:"pointer" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:18 }}>🏆</span>
                      <span style={{ fontSize:13, fontWeight:800, color:"white" }}>Classement de la semaine</span>
                    </div>
                    <span style={{ fontSize:11, color:"rgba(212,168,67,0.8)", fontWeight:700 }}>Voir tout →</span>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    {weekRanking.map((mb,i)=>(
                      <div key={mb.id} style={{ flex:1, background:i===0?"rgba(212,168,67,0.15)":"rgba(255,255,255,0.04)", borderRadius:10, padding:"10px 6px", textAlign:"center", border:`1px solid ${i===0?"rgba(212,168,67,0.4)":"rgba(255,255,255,0.06)"}` }}>
                        <div style={{ fontSize:16 }}>{["🥇","🥈","🥉"][i]||"·"}</div>
                        <Avatar member={mb.id} size={28} />
                        <div style={{ fontSize:10, fontWeight:700, color:"white", marginTop:3 }}>{mb.name}</div>
                        <div style={{ fontSize:13, fontWeight:900, color:i===0?"#D4A843":"rgba(255,255,255,0.7)", marginTop:2 }}>{mb.pts}<span style={{ fontSize:9, fontWeight:400, color:"rgba(255,255,255,0.4)" }}> pts</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Mood check — redirige vers onglet Humeur */}
            {!todayMood && (
              <button onClick={()=>setTab("humeur")} style={{ width:"100%", background:"rgba(39,174,96,0.12)", border:"2px solid rgba(39,174,96,0.3)", borderRadius:16, padding:"14px 16px", marginBottom:16, cursor:"pointer", display:"flex", alignItems:"center", gap:12, textAlign:"left" }}>
                <span style={{ fontSize:28 }}>😊</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:800, color:"white" }}>Comment tu vas ce matin ?</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:2 }}>Tap pour évaluer ton humeur, ton énergie et ton anxiété →</div>
                </div>
              </button>
            )}

            {/* Tous les raccourcis */}
            <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>Accès rapide</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <button onClick={()=>setTab("planning")} style={{ background:"linear-gradient(135deg,#3D5A8A,#5B7FB5)", border:"none", borderRadius:14, padding:"16px", cursor:"pointer", textAlign:"center", color:"white" }}>
                <div style={{ fontSize:28 }}>📅</div>
                <div style={{ fontSize:13, fontWeight:800, marginTop:4 }}>Ma journée</div>
                <div style={{ fontSize:11, opacity:0.8 }}>{[...myAppts,...myEvents,...myTasks].length} élément(s)</div>
              </button>
              <button onClick={()=>setTab("repas")} style={{ background:"linear-gradient(135deg,#E07B39,#D4A843)", border:"none", borderRadius:14, padding:"16px", cursor:"pointer", textAlign:"center", color:"white" }}>
                <div style={{ fontSize:28 }}>🍽️</div>
                <div style={{ fontSize:13, fontWeight:800, marginTop:4 }}>Repas</div>
                <div style={{ fontSize:11, opacity:0.8 }}>Cette semaine</div>
              </button>
              <button onClick={()=>setTab("humeur")} style={{ background:"linear-gradient(135deg,#27AE60,#5BBFAD)", border:"none", borderRadius:14, padding:"16px", cursor:"pointer", textAlign:"center", color:"white" }}>
                <div style={{ fontSize:28 }}>😊</div>
                <div style={{ fontSize:13, fontWeight:800, marginTop:4 }}>Humeur</div>
                <div style={{ fontSize:11, opacity:0.8 }}>{todayMood?"Évalué ✓":"À faire"}</div>
              </button>
              <button onClick={()=>setTab("defis")} style={{ background:"linear-gradient(135deg,#D4A843,#E07B39)", border:"none", borderRadius:14, padding:"16px", cursor:"pointer", textAlign:"center", color:"white" }}>
                <div style={{ fontSize:28 }}>{level.icon}</div>
                <div style={{ fontSize:13, fontWeight:800, marginTop:4 }}>Défis</div>
                <div style={{ fontSize:11, opacity:0.8 }}>{monthPts} pts · {todayClaims.length} prise(s)</div>
              </button>
            </div>
          </div>
        )}

        {/* MISSIONS */}
        {/* PLANNING */}
        {tab==="planning" && (
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:"white", marginBottom:16 }}>📅 Ma journée</div>
            {[...myAppts, ...myEvents, ...myTasks].length===0 && (
              <div style={{ textAlign:"center", padding:"40px 0", color:"rgba(255,255,255,0.4)" }}>
                <div style={{ fontSize:48 }}>🎉</div>
                <div style={{ fontSize:16, marginTop:8 }}>Journée libre !</div>
              </div>
            )}
            {myAppts.map((a,i)=>(
              <div key={i} style={{ background:"rgba(91,191,173,0.2)", border:"2px solid #5BBFAD", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", gap:12 }}>
                <span style={{ fontSize:24 }}>🏥</span>
                <div><div style={{ fontSize:15, fontWeight:800, color:"white" }}>{a.title}</div><div style={{ fontSize:12, color:"#5BBFAD" }}>{a.time} · {a.specialist}</div></div>
              </div>
            ))}
            {myEvents.map((e,i)=>(
              <div key={i} style={{ background:"rgba(61,90,138,0.4)", border:"2px solid rgba(255,255,255,0.15)", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", gap:12 }}>
                <span style={{ fontSize:24 }}>📅</span>
                <div><div style={{ fontSize:15, fontWeight:800, color:"white" }}>{e.title}</div><div style={{ fontSize:12, color:"rgba(255,255,255,0.5)" }}>{e.time}</div></div>
              </div>
            ))}
            {myTasks.map(t=>(
              <div key={t.id} style={{ background:"rgba(123,94,167,0.2)", border:"2px solid rgba(123,94,167,0.5)", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:12 }}>
                <button onClick={()=>onToggleTask(t.id)} style={{ width:28,height:28,borderRadius:7,border:"2px solid rgba(255,255,255,0.3)",background:"transparent",cursor:"pointer",flexShrink:0,fontSize:16 }}>✓</button>
                <div style={{ fontSize:15, fontWeight:700, color:"white" }}>{t.title}</div>
              </div>
            ))}
          </div>
        )}

        {/* HUMEUR — même composant que la version adulte */}
        {tab==="humeur" && (
          <div style={{ padding:"4px 0 120px" }}>
            <KidsMoodView moods={moods} onAdd={onAddMood} member={member} />
          </div>
        )}

        {/* DEFIS */}
        {tab==="defis" && !kidsMissionSection && (
          <div>
            <div style={{ background:`linear-gradient(135deg,${level.color},${level.color}cc)`, borderRadius:16, padding:"20px 16px", marginBottom:16, textAlign:"center", color:"white" }}>
              <div style={{ fontSize:48 }}>{level.icon}</div>
              <div style={{ fontSize:20, fontWeight:800 }}>{level.label}</div>
              <div style={{ fontSize:32, fontWeight:900, marginTop:4 }}>{monthPts} pts</div>
              <div style={{ fontSize:12, opacity:0.7 }}>ce mois</div>
            </div>
            <button onClick={()=>setKidsMissionSection("perso")} style={{ width:"100%", background:`linear-gradient(135deg,#9B59B6,#3D5A8A)`, border:"none", borderRadius:16, padding:"16px 14px", cursor:"pointer", textAlign:"center", color:"white", marginBottom:16, position:"relative" }}>
              {pendingPersonalCount(personalMissions, member) > 0 && (
                <span style={{ position:"absolute", top:8, right:8, background:"#E74C3C", color:"white", borderRadius:20, minWidth:20, height:20, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 5px" }}>{pendingPersonalCount(personalMissions, member)}</span>
              )}
              <div style={{ fontSize:28, marginBottom:6 }}>⭐</div>
              <div style={{ fontSize:14, fontWeight:800 }}>Mes défis perso</div>
              <div style={{ fontSize:11, opacity:0.85, marginTop:4 }}>Crée tes défis, un membre de la famille valide</div>
            </button>

            {/* Missions du jour (fusionnées depuis l'ancien onglet Missions) */}
            <div style={{ fontSize:14, fontWeight:800, color:"white", marginBottom:12 }}>🎯 Missions du jour</div>
            {todayClaims.length>0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.5)", marginBottom:8, textTransform:"uppercase" }}>Ma mission</div>
                {todayClaims.map(c=>{
                  const fullMission = missions.find(ms=>ms.id===c.missionId);
                  return (
                  <div key={c.id} style={{ background:c.done?"rgba(39,174,96,0.2)":"rgba(212,168,67,0.2)", border:`2px solid ${c.done?"#27AE60":"#D4A843"}`, borderRadius:14, padding:"14px 16px", marginBottom:8 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <span style={{ fontSize:28, cursor:fullMission?"pointer":"default" }} onClick={()=>fullMission && setSelectedMission(fullMission)}>{c.icon}</span>
                      <div style={{ flex:1, cursor:fullMission?"pointer":"default" }} onClick={()=>fullMission && setSelectedMission(fullMission)}>
                        <div style={{ fontSize:15, fontWeight:800, color:"white" }}>{c.title}</div>
                        <div style={{ fontSize:12, color:"#D4A843", fontWeight:700 }}>+{c.points} pts</div>
                      </div>
                      {!c.done && <button onClick={()=>{ onCompleteClaim(c.id); onScoreUpdate(prev=>{ const cur=prev[member]||{points:0,history:[],completedChallenges:[]}; return {...prev,[member]:{...cur,points:(cur.points||0)+c.points,history:[...(cur.history||[]),{title:c.title,pts:c.points,importance:2,date:todayStr,isDaily:true}]}}; }); }} style={{ background:"#D4A843", border:"none", borderRadius:10, padding:"8px 14px", cursor:"pointer", fontSize:14, fontWeight:800, color:"white" }}>✓ Fait !</button>}
                      {c.done && <span style={{ fontSize:24 }}>✅</span>}
                    </div>
                    {!c.done && (
                      <button onClick={()=>onDeleteClaim(c.id)} style={{ marginTop:8, width:"100%", background:"transparent", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"6px", fontSize:11, color:"rgba(255,255,255,0.4)", cursor:"pointer" }}>
                        ✕ Annuler ma prise
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
            <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,0.5)", marginBottom:8, textTransform:"uppercase" }}>Missions disponibles</div>
            {missions.filter(m=>m.active!==false && !claims.some(c=>c.missionId===m.id && c.date===todayStr)).map(mission=>(
              <div key={mission.id} style={{ background:"rgba(255,255,255,0.06)", border:"2px solid rgba(255,255,255,0.1)", borderRadius:14, padding:"14px 16px", marginBottom:8, display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:28, cursor:"pointer" }} onClick={()=>setSelectedMission(mission)} title="Voir la description">{mission.icon}</span>
                <div style={{ flex:1, cursor:"pointer" }} onClick={()=>setSelectedMission(mission)}>
                  <div style={{ fontSize:15, fontWeight:700, color:"white" }}>{mission.title}</div>
                  <div style={{ fontSize:12, color:"#D4A843", fontWeight:700 }}>+{mission.points} pts</div>
                </div>
                <button
                  onClick={()=>{ const claim={id:Date.now(),missionId:mission.id,memberId:member,date:todayStr,done:false,points:mission.points,title:mission.title,icon:mission.icon}; onAddClaim(claim); }}
                  style={{ background:"rgba(212,168,67,0.3)", border:"2px solid #D4A843", borderRadius:10, padding:"8px 12px", cursor:"pointer", fontSize:13, fontWeight:800, color:"#D4A843" }}>Je prends !</button>
              </div>
            ))}

            <div style={{ fontSize:14, fontWeight:800, color:"white", margin:"20px 0 12px" }}>📊 Mes points</div>
            <div style={{ background:"rgba(255,255,255,0.08)", borderRadius:16, padding:"16px" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.6)", marginBottom:10, textTransform:"uppercase" }}>Derniers points</div>
              {[...(scores[member]?.history||[])].reverse().slice(0,5).map((h,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
                  <span style={{ fontSize:16 }}>{EISENHOWER.find(e=>e.value===h.importance)?.icon||"⭐"}</span>
                  <div style={{ flex:1, fontSize:13, color:"white" }}>{h.title}</div>
                  <div style={{ fontSize:13, fontWeight:800, color:"#D4A843" }}>+{h.pts}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab==="defis" && kidsMissionSection==="perso" && (
          <PersonalMissionsPanel
            personalMissions={personalMissions}
            addPersonalMission={addPersonalMission}
            deletePersonalMission={deletePersonalMission}
            votePersonalMission={votePersonalMission}
            validatePersonalMission={validatePersonalMission}
            activeUser={member}
            onBack={()=>setKidsMissionSection(null)}
            dark={true}
          />
        )}
      </div>

      {/* Repas enfant */}
      {tab==="repas" && (
        <div style={{ padding:"20px 16px 120px" }}>
          <div style={{ fontSize:18, fontWeight:800, color:"white", marginBottom:16 }}>🍽️ Les repas</div>
          <KidsMealsReadOnly recipes={recipes} menu={menu} />
        </div>
      )}

      {/* Fiche descriptive mission — enfant */}
      {selectedMission && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={()=>setSelectedMission(null)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"#1E2A3A", borderRadius:20, padding:"24px 20px", width:"100%", maxWidth:360, border:"1px solid rgba(255,255,255,0.1)", textAlign:"center" }}>
            <span style={{ fontSize:52 }}>{selectedMission.icon}</span>
            <div style={{ fontSize:18, fontWeight:800, color:"white", marginTop:10, marginBottom:6 }}>{selectedMission.title}</div>
            <div style={{ display:"flex", gap:10, justifyContent:"center", marginBottom:14 }}>
              <span style={{ fontSize:13, fontWeight:800, color:"#D4A843" }}>+{selectedMission.points} pts</span>
              {selectedMission.duration && (
                <span style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.5)" }}>⏱ {selectedMission.duration} min</span>
              )}
            </div>
            {selectedMission.desc && (
              <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:12, padding:"12px 14px", marginBottom:14, textAlign:"left" }}>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.8)", lineHeight:1.6 }}>{selectedMission.desc}</div>
              </div>
            )}
            {selectedMission.days && selectedMission.days.length < 7 && (
              <div style={{ display:"flex", gap:4, marginBottom:16 }}>
                {["L","M","M","J","V","S","D"].map((d,i)=>(
                  <div key={i} style={{ flex:1, textAlign:"center", padding:"5px 2px", borderRadius:7, background:selectedMission.days.includes(i)?"#D4A84333":"rgba(255,255,255,0.04)", border:`1px solid ${selectedMission.days.includes(i)?"#D4A843":"rgba(255,255,255,0.08)"}`, fontSize:11, fontWeight:700, color:selectedMission.days.includes(i)?"#D4A843":"rgba(255,255,255,0.4)" }}>{d}</div>
                ))}
              </div>
            )}
            <button onClick={()=>setSelectedMission(null)} style={{ width:"100%", padding:"12px", background:"#D4A843", border:"none", borderRadius:12, fontSize:14, fontWeight:800, color:"white", cursor:"pointer" }}>Fermer</button>
          </div>
        </div>
      )}

      {/* Bouton harcèlement — toujours accessible */}
      <div style={{ position:"fixed", bottom:80, left:16, zIndex:200 }}>
        <a href="https://e-enfance.org/besoin-daide/" target="_blank" rel="noopener noreferrer"
          style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(155,89,182,0.9)", border:"1px solid rgba(155,89,182,0.5)", borderRadius:20, padding:"8px 14px", textDecoration:"none", boxShadow:"0 2px 12px rgba(0,0,0,0.3)" }}>
          <span style={{ fontSize:16 }}>🛡️</span>
          <span style={{ fontSize:11, fontWeight:800, color:"white" }}>Aide harcèlement</span>
        </a>
      </div>

      {/* Kids bottom nav */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(20,30,45,0.98)", borderTop:"1px solid rgba(255,255,255,0.08)", display:"flex", paddingBottom:"env(safe-area-inset-bottom)" }}>
        {KIDS_TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ flex:1, padding:"10px 4px 8px", background:"transparent", border:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
            <span style={{ fontSize:20 }}>{t.icon}</span>
            <span style={{ fontSize:9, fontWeight:700, color:tab===t.id?"#D4A843":"rgba(255,255,255,0.4)" }}>{t.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 👤 ÉCRAN DE SÉLECTION DE PROFIL
// ══════════════════════════════════════════════════════════════

function ProfileSelectScreen({ onSelect, memberProfiles, onCreateFirstMember }) {
  const [pinTarget, setPinTarget] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState(false);
  const [firstName, setFirstName] = useState("");

  const handlePinSubmit = () => {
    const profile = memberProfiles[pinTarget.id];
    if (pinInput === profile?.pin) {
      setPinTarget(null); setPinInput(""); setPinError(false);
      onSelect(pinTarget.id, pinTarget.isKid);
    } else {
      setPinError(true);
      setPinInput("");
      setTimeout(()=>setPinError(false), 1500);
    }
  };

  if (pinTarget) {
    const m = MEMBERS.find(x=>x.id===pinTarget.id);
    return (
      <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#F5EDE4 0%,#FDF8F3 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 24px", fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif" }}>
        <div style={{ width:64, height:64, borderRadius:"50%", background:m?.color||"#E8A87C", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, marginBottom:16 }}>{m?.avatar||"😊"}</div>
        <div style={{ fontSize:20, fontWeight:800, color:"#3D2C3E", marginBottom:4 }}>{m?.name}</div>
        <div style={{ fontSize:13, color:"#8E8494", marginBottom:32 }}>🔒 Entrez votre code PIN</div>
        {/* PIN dots */}
        <div style={{ display:"flex", gap:12, marginBottom:24 }}>
          {[0,1,2,3].map(i=>(
            <div key={i} style={{ width:16, height:16, borderRadius:"50%", background:i<pinInput.length?(pinError?"#C97B6B":"#9CB8A0"):"#EDE7E0" }} />
          ))}
        </div>
        {/* Keypad */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, width:240, marginBottom:16 }}>
          {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k,i)=>(
            <button key={i} onClick={()=>{
              if(k==="⌫") setPinInput(p=>p.slice(0,-1));
              else if(k!==""&&pinInput.length<4) { const next=pinInput+k; setPinInput(next); if(next.length===4) setTimeout(()=>{ const prof=memberProfiles[pinTarget.id]; if(next===prof?.pin){setPinTarget(null);setPinInput("");setPinError(false);onSelect(pinTarget.id,pinTarget.isKid);}else{setPinError(true);setPinInput("");setTimeout(()=>setPinError(false),1500);}},100); }
            }} style={{ height:60, background:"#FFFFFF", border:"1px solid #EDE7E0", borderRadius:12, fontSize:20, fontWeight:700, color:k===""?"transparent":"#3D2C3E", cursor:k===""?"default":"pointer" }} disabled={k===""}>
              {k}
            </button>
          ))}
        </div>
        {pinError && <div style={{ fontSize:13, color:"#C97B6B", marginBottom:12 }}>Code incorrect ✗</div>}
        <button onClick={()=>{setPinTarget(null);setPinInput("");}} style={{ fontSize:12, color:"#B0A5B3", background:"transparent", border:"none", cursor:"pointer" }}>← Retour</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg, #F5EDE4 0%, #FDF8F3 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 24px", fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif" }}>
      {/* Logo */}
      <div style={{ textAlign:"center", marginBottom:48 }}>
        <div style={{ fontSize:56, marginBottom:12 }}>🏠</div>
        <div style={{ fontSize:32, fontWeight:900, fontFamily:"'Nunito',system-ui,sans-serif", color:"#3D2C3E", letterSpacing:"-0.02em" }}>Alfy</div>
        <div style={{ fontSize:14, color:"#8E8494", marginTop:4 }}>Votre assistant familial</div>
      </div>

      {/* Who are you? */}
      <div style={{ width:"100%", maxWidth:320 }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#5C5466", textAlign:"center", marginBottom:20 }}>Qui est là ?</div>
        {MEMBERS.length === 0 ? (
          <div style={{ background:"#FFFFFF", border:"1px solid #EDE7E0", borderRadius:16, padding:"20px" }}>
            <div style={{ fontSize:14, color:"#5C5466", marginBottom:12, textAlign:"center" }}>👋 Bienvenue ! Crée ton profil pour commencer.</div>
            <input placeholder="Ton prénom" value={firstName} onChange={e=>setFirstName(e.target.value)} style={{ width:"100%", boxSizing:"border-box", background:"#FDF8F3", border:"1px solid #EDE7E0", borderRadius:8, padding:"12px 14px", fontSize:15, color:"#3D2C3E", marginBottom:12, outline:"none" }} />
            <button onClick={()=>{
              if (!firstName.trim()) return;
              const id = onCreateFirstMember({ name: firstName, isKid: false });
              setFirstName("");
              onSelect(id, false);
            }} style={{ width:"100%", padding:"14px", background:"#E8A87C", border:"none", borderRadius:12, fontSize:15, fontWeight:800, color:"white", cursor:"pointer" }}>Créer mon profil →</button>
          </div>
        ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {MEMBERS.map(m => {
            const isKid = memberProfiles[m.id]?.isKid;
            return (
              <button key={m.id} onClick={()=>{
                const profile = memberProfiles[m.id];
                const hasPin = profile?.pin && profile.pin.length===4;
                if (!isKid && hasPin) { setPinTarget({id:m.id,isKid}); }
                else { onSelect(m.id, isKid); }
              }}
                style={{ display:"flex", alignItems:"center", gap:16, padding:"16px 20px", background:"#FFFFFF", border:`2px solid ${m.color}33`, borderRadius:16, cursor:"pointer", textAlign:"left" }}>
                <div style={{ width:48, height:48, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:800, color:"white", flexShrink:0 }}>{m.avatar}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:18, fontWeight:800, color:"#3D2C3E" }}>{m.name}</div>
                  <div style={{ fontSize:12, color:"#8E8494", marginTop:2 }}>{isKid ? "🧒 Profil enfant" : `👤 Profil parent${memberProfiles[m.id]?.pin?"  🔒":""}`}</div>
                </div>
                <span style={{ fontSize:20, color:m.color }}>›</span>
              </button>
            );
          })}
        </div>
        )}

        {/* Bouton réinitialiser — utile pour les testeurs et nouvelle installation */}
        <div style={{ textAlign:"center", marginTop:32 }}>
          {confirmReset ? (
            <div>
              <div style={{ fontSize:12, color:"#8E8494", marginBottom:8 }}>Réinitialiser Alfy ? Toutes les données seront effacées.</div>
              <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                <button onClick={()=>{ try { localStorage.clear(); localStorage.setItem("ff_members", "[]"); } catch {} window.location.reload(); }} style={{ fontSize:12, background:"#C97B6B", color:"white", border:"none", borderRadius:6, padding:"6px 14px", cursor:"pointer", fontWeight:700 }}>Confirmer</button>
                <button onClick={()=>setConfirmReset(false)} style={{ fontSize:12, background:"transparent", border:"1px solid #EDE7E0", color:"#5C5466", borderRadius:6, padding:"6px 14px", cursor:"pointer" }}>Annuler</button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setConfirmReset(true)} style={{ fontSize:12, color:"#B0A5B3", background:"transparent", border:"none", cursor:"pointer", textDecoration:"underline" }}>
              Réinitialiser l'application
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



// ══════════════════════════════════════════════════════════════
// 🎉 ONBOARDING — PREMIER LANCEMENT
// ══════════════════════════════════════════════════════════════

const ONBOARDING_SCREENS = [
  {
    id: "welcome",
    emoji: "🏠",
    title: "Bienvenue dans Alfy",
    subtitle: "Votre assistant familial",
    desc: "Alfy est conçu pour les familles qui jonglent au quotidien — agendas, rendez-vous, tâches, bien-être. Tout au même endroit, pour toute la famille.",
    color: "#E8735A",
    tip: null,
  },
  {
    id: "neurodiversity",
    emoji: "🧠",
    title: "Pensé pour les familles TND",
    subtitle: "Neuroatypique, c'est notre quotidien",
    desc: "Alfy a été créé par et pour les familles vivant avec le TDAH, la dyspraxie, le HPI, l'autisme ou d'autres profils neurodéveloppementaux. Pas de jugement, juste du soutien.",
    color: "#9B59B6",
    tip: "TND = Troubles du Neurodéveloppement",
  },
  {
    id: "planning",
    emoji: "📅",
    title: "Le planning",
    subtitle: "Tout le monde sur la même longueur d'onde",
    desc: "Visualisez en un coup d'œil les événements, RDV et tâches de toute la famille. Filtrez par membre, ajoutez des récurrences, et recevez des rappels avant chaque rendez-vous.",
    color: "#3D5A8A",
    tip: "💡 Astuce : tapez sur un événement pour le modifier rapidement.",
  },
  {
    id: "missions",
    emoji: "🎯",
    title: "Les missions du quotidien",
    subtitle: "Le ménage devient un jeu",
    desc: "Créez des missions (vaisselle, aspirateur, courses…) et laissez chaque membre s'attribuer celle qu'il prend en charge. Des points sont gagnés à chaque mission validée.",
    color: "#D4A843",
    tip: "💡 Astuce : tapez sur l'emoji d'une mission pour voir sa fiche descriptive.",
  },
  {
    id: "defis",
    emoji: "🏆",
    title: "Défis & Classement",
    subtitle: "La motivation par le jeu",
    desc: "Chaque membre progresse à travers 5 niveaux (🌱 Débutant → 👑 Légende). Des bonus hebdomadaires récompensent la régularité. Le classement famille se renouvelle chaque semaine.",
    color: "#27AE60",
    tip: "💡 Les niveaux se basent sur les points du mois en cours — tout le monde repart à zéro le 1er !",
  },
  {
    id: "humeur",
    emoji: "😊",
    title: "Suivi de l'humeur",
    subtitle: "Prendre soin de soi chaque jour",
    desc: "Chaque membre évalue son humeur, son énergie et son anxiété le matin et le soir. Alfy vous rappelle discrètement si vous oubliez. Le graphique montre l'évolution dans le temps.",
    color: "#5BBFAD",
    tip: "💡 Astuce : valider les 2 évaluations de la journée rapporte un bonus de 30 pts !",
  },
  {
    id: "sos",
    emoji: "🌊",
    title: "Je souffle",
    subtitle: "Prends un instant pour toi",
    desc: "Le bouton 🌊 Je souffle ouvre des exercices guidés pour retrouver son calme en quelques minutes, à n'importe quel moment.",
    color: "#E74C3C",
    tip: "💡 Ce bouton est toujours accessible, quelle que soit la page où vous êtes.",
  },
  {
    id: "kids",
    emoji: "🧒",
    title: "La version enfant",
    subtitle: "Une interface adaptée aux plus jeunes",
    desc: "Chaque enfant a son propre espace simplifié : ses missions du jour, son planning, son humeur et ses points. L'accès se configure dans les paramètres.",
    color: "#E07B39",
    tip: "💡 Astuce : configurez le type de profil (Parent/Enfant) lors de la création ou dans ⚙️ Paramètres.",
  },
  {
    id: "ready",
    emoji: "🚀",
    title: "Alfy est prêt !",
    subtitle: "Commençons par créer vos profils",
    desc: "Il ne reste plus qu'à créer les profils de votre famille. Vous pourrez ajouter autant de membres que nécessaire, choisir une couleur, un emoji et définir un code PIN pour les parents.",
    color: "#5BBFAD",
    tip: null,
  },
];

function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(0);
  const screen = ONBOARDING_SCREENS[step];
  const isLast = step === ONBOARDING_SCREENS.length - 1;
  const progress = ((step + 1) / ONBOARDING_SCREENS.length) * 100;

  return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(160deg,${screen.color}CC 0%,${screen.color}88 100%)`, fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif", display:"flex", flexDirection:"column", padding:"env(safe-area-inset-top) 0 env(safe-area-inset-bottom)" }}>

      {/* Progress bar */}
      <div style={{ padding:"16px 20px 0" }}>
        <div style={{ height:4, background:"rgba(255,255,255,0.2)", borderRadius:2 }}>
          <div style={{ height:4, background:"white", borderRadius:2, width:`${progress}%`, transition:"width 0.4s ease" }} />
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.6)" }}>{step+1} / {ONBOARDING_SCREENS.length}</span>
          <button onClick={onComplete} style={{ fontSize:11, color:"rgba(255,255,255,0.6)", background:"transparent", border:"none", cursor:"pointer" }}>Passer →</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 28px", textAlign:"center" }}>
        <div style={{ fontSize:80, marginBottom:24, lineHeight:1 }}>{screen.emoji}</div>
        <div style={{ fontSize:24, fontWeight:900, color:"white", marginBottom:6, lineHeight:1.2 }}>{screen.title}</div>
        <div style={{ fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.7)", marginBottom:24 }}>{screen.subtitle}</div>
        <div style={{ fontSize:15, color:"rgba(255,255,255,0.9)", lineHeight:1.7, marginBottom:24, maxWidth:340 }}>{screen.desc}</div>
        {screen.tip && (
          <div style={{ background:"rgba(255,255,255,0.15)", borderRadius:12, padding:"10px 16px", fontSize:13, color:"white", lineHeight:1.5, maxWidth:320 }}>
            {screen.tip}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ padding:"0 20px 32px", display:"flex", gap:10 }}>
        {step > 0 && (
          <button onClick={()=>setStep(s=>s-1)} style={{ flex:1, padding:"14px", background:"rgba(255,255,255,0.15)", border:"none", borderRadius:14, fontSize:15, fontWeight:700, color:"white", cursor:"pointer" }}>
            ← Retour
          </button>
        )}
        <button onClick={()=>{ if(isLast) onComplete(); else setStep(s=>s+1); }} style={{ flex:2, padding:"14px", background:"white", border:"none", borderRadius:14, fontSize:15, fontWeight:800, color:screen.color, cursor:"pointer", boxShadow:"0 4px 16px rgba(0,0,0,0.15)" }}>
          {isLast ? "Créer mes profils →" : "Suivant →"}
        </button>
      </div>

      {/* Dots indicator */}
      <div style={{ display:"flex", justifyContent:"center", gap:6, paddingBottom:16 }}>
        {ONBOARDING_SCREENS.map((_,i)=>(
          <div key={i} onClick={()=>setStep(i)} style={{ width:i===step?20:6, height:6, borderRadius:3, background:i===step?"white":"rgba(255,255,255,0.35)", cursor:"pointer", transition:"all 0.3s" }} />
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 🎉 PREMIER LANCEMENT — CRÉATION DES PROFILS
// ══════════════════════════════════════════════════════════════

const DEFAULT_COLORS = ["#E8735A","#5BBFAD","#D4A843","#9B59B6","#27AE60","#3D5A8A","#E07B39"];
const DEFAULT_AVATARS = ["😊","🌟","🦁","🐼","🦊","🐬","🌈"];

function SetupScreen({ onComplete }) {
  const [step, setStep] = useState(0); // 0=welcome, 1=add members, 2=done
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name:"", isKid:false, color:DEFAULT_COLORS[0], avatar:"😊" });
  const [showForm, setShowForm] = useState(false);

  const addMember = () => {
    if (!form.name.trim()) return;
    const idx = members.length;
    setMembers(prev => [...prev, {
      id: form.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g,"_") + "_" + Date.now(),
      name: form.name.trim(),
      isKid: form.isKid,
      color: form.color,
      avatar: form.avatar,
      pin: form.isKid ? "" : (form.pin||""),
    }]);
    setForm({ name:"", isKid:false, color:DEFAULT_COLORS[(idx+1)%DEFAULT_COLORS.length], avatar:DEFAULT_AVATARS[(idx+1)%DEFAULT_AVATARS.length] });
    setShowForm(false);
  };

  const handleComplete = () => {
    if (members.length === 0) return;
    onComplete(members);
  };

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#F5EDE4 0%,#FDF8F3 100%)", fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif", display:"flex", flexDirection:"column", padding:"env(safe-area-inset-top) 0 0" }}>

      {/* Step 0 — Welcome */}
      {step===0 && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 24px", textAlign:"center" }}>
          <div style={{ fontSize:72, marginBottom:20 }}>🏠</div>
          <div style={{ fontSize:32, fontWeight:900, fontFamily:"'Nunito',system-ui,sans-serif", color:"#3D2C3E", marginBottom:8, letterSpacing:"-0.02em" }}>Bienvenue dans Alfy</div>
          <div style={{ fontSize:15, color:"#5C5466", lineHeight:1.6, marginBottom:48, maxWidth:300 }}>
            Votre assistant familial pour simplifier le quotidien et prendre soin de chacun.
          </div>
          <button onClick={()=>setStep(1)} style={{ background:"#E8A87C", border:"none", borderRadius:14, padding:"16px 40px", fontSize:16, fontWeight:800, color:"white", cursor:"pointer", width:"100%", maxWidth:300 }}>
            Créer les profils →
          </button>
        </div>
      )}

      {/* Step 1 — Add members */}
      {step===1 && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"24px 20px" }}>
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:22, fontWeight:800, color:"#3D2C3E", marginBottom:4 }}>Qui fait partie de la famille ?</div>
            <div style={{ fontSize:13, color:"#8E8494" }}>Ajoutez chaque membre — vous pourrez modifier ça plus tard.</div>
          </div>

          {/* Members list */}
          {members.map((m,i)=>(
            <div key={m.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#FFFFFF", borderRadius:12, marginBottom:8, border:"1px solid #EDE7E0" }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{m.avatar}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15, fontWeight:700, color:"#3D2C3E" }}>{m.name}</div>
                <div style={{ fontSize:11, color:"#8E8494" }}>{m.isKid?"🧒 Profil enfant":"👤 Profil parent"}</div>
              </div>
              <button onClick={()=>setMembers(prev=>prev.filter((_,j)=>j!==i))} style={{ background:"transparent", border:"none", color:"#B0A5B3", fontSize:18, cursor:"pointer" }}>×</button>
            </div>
          ))}

          {/* Add form */}
          {showForm ? (
            <div style={{ background:"#FFFFFF", borderRadius:14, padding:"16px", marginBottom:12, border:"1px solid #EDE7E0" }}>
              <input
                placeholder="Prénom"
                value={form.name}
                onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                autoFocus
                style={{ background:"#FDF8F3", border:"1px solid #EDE7E0", borderRadius:8, padding:"10px 12px", fontSize:15, color:"#3D2C3E", outline:"none", width:"100%", boxSizing:"border-box", marginBottom:12 }}
              />
              {/* Avatar picker */}
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:11, color:"#8E8494", marginBottom:6 }}>Émoji</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {DEFAULT_AVATARS.map(a=>(
                    <button key={a} onClick={()=>setForm(f=>({...f,avatar:a}))} style={{ fontSize:22, background:form.avatar===a?"#F0E5EE":"transparent", border:`2px solid ${form.avatar===a?"#B8A9C9":"#EDE7E0"}`, borderRadius:8, padding:"4px 8px", cursor:"pointer" }}>{a}</button>
                  ))}
                </div>
              </div>
              {/* Color picker */}
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, color:"#8E8494", marginBottom:6 }}>Couleur</div>
                <div style={{ display:"flex", gap:6 }}>
                  {DEFAULT_COLORS.map(c=>(
                    <button key={c} onClick={()=>setForm(f=>({...f,color:c}))} style={{ width:30, height:30, borderRadius:"50%", background:c, border:`3px solid ${form.color===c?"#3D2C3E":"transparent"}`, cursor:"pointer" }} />
                  ))}
                </div>
              </div>
              {/* Profile type */}
              <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                <button onClick={()=>setForm(f=>({...f,isKid:false}))} style={{ flex:1, padding:"8px", borderRadius:8, border:`2px solid ${!form.isKid?"#9CB8A0":"#EDE7E0"}`, background:!form.isKid?"rgba(156,184,160,0.15)":"transparent", cursor:"pointer", fontSize:12, fontWeight:700, color:!form.isKid?"#6E8A72":"#B0A5B3" }}>👤 Parent</button>
                <button onClick={()=>setForm(f=>({...f,isKid:true,pin:""}))} style={{ flex:1, padding:"8px", borderRadius:8, border:`2px solid ${form.isKid?"#B8A9C9":"#EDE7E0"}`, background:form.isKid?"rgba(184,169,201,0.15)":"transparent", cursor:"pointer", fontSize:12, fontWeight:700, color:form.isKid?"#8A749A":"#B0A5B3" }}>🧒 Enfant</button>
              </div>
              {/* PIN for parents */}
              {!form.isKid && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, color:"#8E8494", marginBottom:6 }}>Code PIN (4 chiffres)</div>
                  <input
                    type="number"
                    placeholder="1234"
                    value={form.pin||""}
                    onChange={e=>setForm(f=>({...f,pin:e.target.value.slice(0,4)}))}
                    style={{ background:"#FDF8F3", border:"1px solid #EDE7E0", borderRadius:8, padding:"10px 12px", fontSize:20, color:"#3D2C3E", outline:"none", width:"100%", boxSizing:"border-box", letterSpacing:"0.3em", textAlign:"center" }}
                  />
                  <div style={{ fontSize:10, color:"#B0A5B3", marginTop:4 }}>Optionnel — protège votre profil</div>
                </div>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <button type="button" onClick={addMember} style={{ flex:2, background:"#E8A87C", border:"none", borderRadius:10, padding:"10px", fontSize:14, fontWeight:700, color:"white", cursor:"pointer" }}>Ajouter</button>
                <button type="button" onClick={()=>setShowForm(false)} style={{ flex:1, background:"#FDF8F3", border:"1px solid #EDE7E0", borderRadius:10, padding:"10px", fontSize:14, color:"#8E8494", cursor:"pointer" }}>Annuler</button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setShowForm(true)} style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", background:"transparent", border:"2px dashed #DDD1D9", borderRadius:12, marginBottom:8, cursor:"pointer", width:"100%", boxSizing:"border-box" }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:"#F5EDE4", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:"#B0A5B3" }}>+</div>
              <span style={{ fontSize:14, fontWeight:600, color:"#8E8494" }}>Ajouter un membre</span>
            </button>
          )}

          {members.length >= 1 && !showForm && (
            <button onClick={handleComplete} style={{ marginTop:"auto", background:"#E8A87C", border:"none", borderRadius:14, padding:"16px", fontSize:16, fontWeight:800, color:"white", cursor:"pointer", width:"100%" }}>
              C'est parti ! →
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🎙️ SECTION MÉDIAS
// ══════════════════════════════════════════════════════════════

const MEDIA_TYPES = [
  { id:"webinaire", label:"Webinaire",  icon:"🎥" },
  { id:"podcast",   label:"Podcast",    icon:"🎙️" },
  { id:"article",   label:"Article",    icon:"📰" },
  { id:"video",     label:"Vidéo",      icon:"▶️" },
  { id:"livre",     label:"Livre",      icon:"📚" },
];

const TND_THEMES = [
  { id:"all",    label:"Tous",          color:"#8896A5" },
  { id:"tdah",   label:"TDAH",          color:"#E8735A" },
  { id:"dys",    label:"DYS",           color:"#5BBFAD" },
  { id:"hpi",    label:"HPI",           color:"#D4A843" },
  { id:"autisme",label:"Autisme",       color:"#9B59B6" },
  { id:"tsa",    label:"TSA",           color:"#3D5A8A" },
  { id:"anxiete",label:"Anxiété",       color:"#27AE60" },
  { id:"parents",label:"Pour parents",  color:"#E07B39" },
  { id:"general",label:"Général",       color:"#8896A5" },
];

const INITIAL_MEDIAS = [];

function MediaView({ medias, onAdd, onDelete, isAdmin }) {
  const [filterType, setFilterType] = useState("all");
  const [filterTheme, setFilterTheme] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ title:"", type:"webinaire", theme:"tdah", url:"", notes:"", author:"", date: todayISO() });

  const filtered = medias.filter(m => {
    if (filterType !== "all" && m.type !== filterType) return false;
    if (filterTheme !== "all" && m.theme !== filterTheme) return false;
    if (search && !m.title.toLowerCase().includes(search.toLowerCase()) && !m.notes?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleAdd = () => {
    if (!form.title.trim() || !form.url.trim()) return;
    onAdd({ ...form, id: Date.now() });
    setForm({ title:"", type:"webinaire", theme:"tdah", url:"", notes:"", author:"", date: todayISO() });
    setShowAdd(false);
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>🎙️ Médias</h2>
        {isAdmin && <button onClick={()=>setShowAdd(s=>!s)} style={btnStyle(T.coral,{fontSize:12,padding:"6px 14px"})}>+ Ajouter</button>}
      </div>

      {/* Add form */}
      {showAdd && isAdmin && (
        <Modal title="➕ Nouveau média" onClose={()=>setShowAdd(false)}>
          <input placeholder="Titre *" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:8}} />
          <input placeholder="URL du lien *" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:8}} />
          <input placeholder="Auteur / Source" value={form.author} onChange={e=>setForm(f=>({...f,author:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:8}} />
          <div style={{ display:"flex", gap:8, marginBottom:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>Type</div>
              <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={{...inputStyle,width:"100%"}}>
                {MEDIA_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
              </select>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>Thème TND</div>
              <select value={form.theme} onChange={e=>setForm(f=>({...f,theme:e.target.value}))} style={{...inputStyle,width:"100%"}}>
                {TND_THEMES.filter(t=>t.id!=="all").map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <textarea placeholder="Notes / Description" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:12,minHeight:70,resize:"vertical"}} />
          <button onClick={handleAdd} style={btnStyle(T.coral,{width:"100%"})}>Publier le média</button>
        </Modal>
      )}

      {/* Search */}
      <input placeholder="🔍 Rechercher..." value={search} onChange={e=>setSearch(e.target.value)} style={{...inputStyle,width:"100%",boxSizing:"border-box",marginBottom:12}} />

      {/* Type filters */}
      <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:4, marginBottom:10 }}>
        <button onClick={()=>setFilterType("all")} style={{ flexShrink:0, padding:"5px 12px", borderRadius:20, fontSize:11, fontWeight:700, cursor:"pointer", border:"none", background:filterType==="all"?T.coral:T.sandDark, color:filterType==="all"?"white":T.textSoft }}>Tous</button>
        {MEDIA_TYPES.map(t=>(
          <button key={t.id} onClick={()=>setFilterType(filterType===t.id?"all":t.id)} style={{ flexShrink:0, padding:"5px 12px", borderRadius:20, fontSize:11, fontWeight:700, cursor:"pointer", border:"none", background:filterType===t.id?T.coral:T.sandDark, color:filterType===t.id?"white":T.textSoft }}>{t.icon} {t.label}</button>
        ))}
      </div>

      {/* Theme filters */}
      <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:8, marginBottom:14 }}>
        {TND_THEMES.map(t=>(
          <button key={t.id} onClick={()=>setFilterTheme(t.id)} style={{ flexShrink:0, padding:"4px 10px", borderRadius:20, fontSize:10, fontWeight:700, cursor:"pointer", border:`2px solid ${filterTheme===t.id?t.color:"transparent"}`, background:filterTheme===t.id?t.color+"22":T.sandDark, color:filterTheme===t.id?t.color:T.textSoft }}>{t.label}</button>
        ))}
      </div>

      {/* Count */}
      <div style={{ fontSize:11, color:T.textSoft, marginBottom:10 }}>{filtered.length} ressource{filtered.length>1?"s":""}</div>

      {/* Media cards */}
      {filtered.length===0 && (
        <div style={{ textAlign:"center", padding:"40px 0", color:T.textSoft }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🔍</div>
          <div>Aucun média trouvé</div>
        </div>
      )}
      {filtered.map(media=>{
        const mType = MEDIA_TYPES.find(t=>t.id===media.type);
        const mTheme = TND_THEMES.find(t=>t.id===media.theme);
        return (
          <div key={media.id} style={{ ...cardStyle(), marginBottom:10, borderLeft:`3px solid ${mTheme?.color||T.coral}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:T.sandDark, color:T.textSoft }}>{mType?.icon} {mType?.label}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:mTheme?.color+"22", color:mTheme?.color }}>{mTheme?.label}</span>
              </div>
              {isAdmin && <button onClick={()=>onDelete(media.id)} style={{ background:"transparent", border:"none", color:T.textSoft, cursor:"pointer", fontSize:16 }}>×</button>}
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:4 }}>{media.title}</div>
            {media.author && <div style={{ fontSize:11, color:T.textSoft, marginBottom:4 }}>par {media.author} · {media.date}</div>}
            {media.notes && <div style={{ fontSize:12, color:T.textMid, marginBottom:10, lineHeight:1.5 }}>{media.notes}</div>}
            <a href={media.url} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", background:T.coral, borderRadius:8, color:"white", fontSize:12, fontWeight:700, textDecoration:"none" }}>
              Accéder →
            </a>
          </div>
        );
      })}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 🎨 THÈMES & PERSONNALISATION
// ══════════════════════════════════════════════════════════════

const PRESET_THEMES = [
  { id:"cocon",    name:"Cocon",     preview:"#F5EDE4", bg:"#F5EDE4",  bgGrad:"linear-gradient(160deg,#F5EDE4 0%,#FDF8F3 100%)", card:"#FFFFFF", cardBorder:"#EDE7E0", text:"#3D2C3E", textSoft:"#8E8494", textMid:"#5C5466", accent:"#E8A87C", nav:"rgba(253,248,243,0.97)" },
  { id:"dark",     name:"Nuit",      preview:"#232F42", bg:"#232F42",  bgGrad:"linear-gradient(160deg,#232F42 0%,#2E3D54 100%)", card:"rgba(255,255,255,0.09)", cardBorder:"rgba(255,255,255,0.13)", text:"#F5F3EF", textSoft:"#8496A8", textMid:"#C2CDD8", accent:"#E8735A", nav:"rgba(24,32,46,0.97)" },
  { id:"ocean",    name:"Océan",     preview:"#0D2137", bg:"#0D2137",  bgGrad:"linear-gradient(160deg,#0D2137 0%,#0A3D5C 100%)", card:"rgba(255,255,255,0.07)", cardBorder:"rgba(100,200,255,0.12)", text:"#E8F4F8", textSoft:"#5B8FA8", textMid:"#9DC4D8", accent:"#5BBFAD", nav:"rgba(8,18,32,0.97)" },
  { id:"forest",   name:"Forêt",     preview:"#0D2117", bg:"#0D2117",  bgGrad:"linear-gradient(160deg,#0D2117 0%,#1A3B25 100%)", card:"rgba(255,255,255,0.06)", cardBorder:"rgba(100,200,100,0.12)", text:"#EAF2E8", textSoft:"#5B8A6B", textMid:"#9DC4A5", accent:"#27AE60", nav:"rgba(8,18,10,0.97)" },
  { id:"sunset",   name:"Coucher",   preview:"#2D1B0E", bg:"#2D1B0E",  bgGrad:"linear-gradient(160deg,#2D1B0E 0%,#3D2A18 100%)", card:"rgba(255,255,255,0.07)", cardBorder:"rgba(255,180,80,0.12)",  text:"#FAF0E6", textSoft:"#A07850", textMid:"#D4A875", accent:"#E07B39", nav:"rgba(20,12,5,0.97)" },
  { id:"purple",   name:"Lavande",   preview:"#1E1030", bg:"#1E1030",  bgGrad:"linear-gradient(160deg,#1E1030 0%,#2D1B4E 100%)", card:"rgba(255,255,255,0.07)", cardBorder:"rgba(180,120,255,0.12)", text:"#F2EEF8", textSoft:"#7B5EA7", textMid:"#B89ED4", accent:"#9B59B6", nav:"rgba(14,8,22,0.97)" },
  { id:"rose",     name:"Rosée",     preview:"#2A121F", bg:"#2A121F",  bgGrad:"linear-gradient(160deg,#2A121F 0%,#3D1B2E 100%)", card:"rgba(255,255,255,0.07)", cardBorder:"rgba(255,120,180,0.12)", text:"#F8E8EF", textSoft:"#A0607D", textMid:"#D8A0BC", accent:"#E85D9C", nav:"rgba(20,8,15,0.97)" },
  { id:"slate",    name:"Ardoise",   preview:"#1A1E24", bg:"#1A1E24",  bgGrad:"linear-gradient(160deg,#1A1E24 0%,#252B33 100%)", card:"rgba(255,255,255,0.06)", cardBorder:"rgba(150,170,190,0.12)", text:"#EAEDF0", textSoft:"#66707C", textMid:"#A8B4BE", accent:"#4FA5A8", nav:"rgba(12,14,18,0.97)" },
];

function applyTheme(theme) {
  const T_new = {
    navy:"#1E2A3A", navyMid:"#2C3E52",
    sand: theme.bg, sandDark: theme.card,
    coral:"#E8735A", coralSoft:"#F2A090",
    mint:"#5BBFAD", mintSoft:"#A8DDD5",
    gold:"#D4A843",
    text: theme.text, textMid: theme.textMid, textSoft: theme.textSoft,
    white: theme.card, danger:"#E74C3C",
    card: theme.card, cardBorder: theme.cardBorder,
    accent: theme.accent,
  };
  return T_new;
}

function ThemeSelector({ currentTheme, customColors, onSelectTheme, onCustomColor, memberId }) {
  const [tab, setTab] = useState("presets");

  return (
    <div>
      {/* Tab switcher */}
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        <button onClick={()=>setTab("presets")} style={{ flex:1, padding:"7px", borderRadius:999, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", background:tab==="presets"?T.coral:T.sandDark, color:tab==="presets"?"white":T.textSoft }}>Thèmes</button>
        <button onClick={()=>setTab("custom")} style={{ flex:1, padding:"7px", borderRadius:999, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", background:tab==="custom"?T.coral:T.sandDark, color:tab==="custom"?"white":T.textSoft }}>Personnalisé</button>
      </div>

      {/* Preset themes */}
      {tab==="presets" && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
          {PRESET_THEMES.map(t=>(
            <button key={t.id} onClick={()=>onSelectTheme(t.id)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, padding:"10px", background:T.sandDark, border:`2px solid ${currentTheme===t.id?T.coral:T.cardBorder}`, borderRadius:12, cursor:"pointer", width:"calc(33% - 7px)", boxSizing:"border-box" }}>
              <div style={{ width:40, height:40, borderRadius:8, background:t.bgGrad, border:`2px solid ${currentTheme===t.id?T.coral:T.cardBorder}` }} />
              <span style={{ fontSize:11, fontWeight:700, color:currentTheme===t.id?T.coral:T.textSoft }}>{t.name}</span>
              {currentTheme===t.id && <span style={{ fontSize:9, color:T.coral }}>✓ Actif</span>}
            </button>
          ))}
        </div>
      )}

      {/* Custom colors */}
      {tab==="custom" && (
        <div>
          {[
            { key:"bg",      label:"Fond",          default:"#F5EDE4" },
            { key:"text",    label:"Texte",          default:"#3D2C3E" },
            { key:"card",    label:"Vignettes",      default:"#FFFFFF" },
            { key:"accent",  label:"Couleur accent", default:"#E8A87C" },
            { key:"textSoft",label:"Texte secondaire",default:"#8E8494" },
          ].map(c=>(
            <div key={c.key} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <span style={{ fontSize:12, color:T.textSoft, flex:1 }}>{c.label}</span>
              <input type="color" value={customColors?.[c.key]||c.default} onChange={e=>onCustomColor(c.key, e.target.value)}
                style={{ width:40, height:32, borderRadius:6, border:"none", cursor:"pointer", background:"transparent" }} />
              <span style={{ fontSize:11, color:T.textSoft, fontFamily:"monospace" }}>{customColors?.[c.key]||c.default}</span>
            </div>
          ))}
          <button onClick={()=>onSelectTheme(currentTheme)} style={{ marginTop:10, width:"100%", background:"transparent", border:`1px solid ${T.cardBorder}`, borderRadius:999, padding:"7px", fontSize:12, color:T.textSoft, cursor:"pointer" }}>
            ↺ Revenir au thème prédéfini
          </button>
        </div>
      )}
    </div>
  );
}


const TABS = [
  { id:"home",    label:"Accueil",  icon:"🏠" },
  { id:"planning",label:"Planning", icon:"📅" },
  { id:"tasks",   label:"Tâches",   icon:"✓" },
  { id:"appts",   label:"RDV",      icon:"🏥" },
  { id:"fiches",  label:"Fiches",   icon:"📋" },
  { id:"repas",   label:"Repas",    icon:"🍽️" },
  { id:"docs",    label:"Docs",     icon:"📁" },
  { id:"rappels", label:"Rappels",  icon:"⏰" },
  { id:"humeur",  label:"Humeur",   icon:"😊" },
  { id:"medicaments", label:"Médicaments", icon:"💊" },
  { id:"game",    label:"Défis",    icon:"🏆" },
  { id:"messages",label:"Messages", icon:"✉️" },
  { id:"medias",  label:"Médias",   icon:"🎙️" },
  { id:"chat",    label:"Assistant",icon:"💬" },
];

export default function Alfy() {
  const [tab, setTab]     = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [themes, setThemes] = useState(()=>loadThemes());

  // Charge les polices de la charte V2 (Nunito pour les titres, Quicksand pour le texte courant)
  useEffect(() => {
    if (document.getElementById("alfy-v2-fonts")) return;
    const link = document.createElement("link");
    link.id = "alfy-v2-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Quicksand:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
  const [moodSlots, setMoodSlotsRaw] = useState(()=>loadMoodSlots());
  const setMoodSlots = (fn) => setMoodSlotsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; saveMoodSlots(next); return next; });
  const addTheme = (t) => { const next=[...themes,t]; setThemes(next); saveThemes(next); };
  const [activeUser, setActiveUser] = useState(null);
  const [onboardingDone, setOnboardingDone] = useState(() => {
    try { return !!localStorage.getItem("ff_onboarding_done"); } catch { return false; }
  });
  const [setupDone, setSetupDone] = useState(() => {
    try { return !!localStorage.getItem("ff_setup_done"); } catch { return false; }
  });
  const [customMembers, setCustomMembers] = useState(() => {
    try { const v=localStorage.getItem("ff_custom_members"); return v?JSON.parse(v):null; } catch { return null; }
  });
  const [currentUser, setCurrentUser] = useState(null); // null = show profile select
  const [membersState, setMembersState] = useState(() => loadMembersFromStorage());
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMemberName, setNewMemberName] = useState("");
  const [newMemberIsKid, setNewMemberIsKid] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  MEMBERS = membersState; // synchronise la liste globale utilisée dans tout le fichier avec l'état persistant
  const addMember = ({ name, isKid }) => {
    if (!name || !name.trim()) return;
    const id = slugifyMemberId(name, membersState);
    const color = MEMBER_COLOR_PALETTE[membersState.length % MEMBER_COLOR_PALETTE.length];
    const avatar = name.trim()[0].toUpperCase();
    const next = [...membersState, { id, name: name.trim(), color, avatar }];
    saveMembersToStorage(next);
    setMembersState(next);
    updateMemberProfile(id, { isKid: !!isKid });
    return id;
  };
  const deleteMember = (id) => {
    if (membersState.length <= 1) return { error: "Impossible de supprimer le dernier profil." };
    const remainingParents = membersState.filter(m=>m.id!==id && !memberProfiles[m.id]?.isKid);
    if (remainingParents.length === 0) return { error: "Il doit rester au moins un profil parent." };
    const next = membersState.filter(m=>m.id!==id);
    saveMembersToStorage(next);
    setMembersState(next);
    if (activeUser===id) setActiveUser(null);
    return { ok: true };
  };
  const [kidsMode, setKidsMode] = useState(null); // member id in kids mode
  const [showDaySas, setShowDaySas] = useState(false); // sas journée
  const [memberProfiles, setMemberProfilesRaw] = useState(() => {
    try {
      const v=localStorage.getItem("ff_member_profiles");
      const parsed = v?JSON.parse(v):{olivier:{isKid:false},stephanie:{isKid:false},cleo:{isKid:true}};
      syncMembersFromProfiles(parsed);
      return parsed;
    } catch { return {olivier:{isKid:false},stephanie:{isKid:false},cleo:{isKid:true}}; }
  });
  const updateMemberProfile = (id, data) => setMemberProfilesRaw(prev => {
    const next = {...prev,[id]:{...prev[id],...data}};
    try { localStorage.setItem("ff_member_profiles",JSON.stringify(next)); } catch {}
    return next;
  }); // null = vue famille
  const [presence, setPresence] = useState(() => {
    try { const v = localStorage.getItem("ff_presence"); return v ? JSON.parse(v) : {}; } catch { return {}; }
  });
  const [presenceModal, setPresenceModal] = useState(null); // member id
  const [showPresencePanel, setShowPresencePanel] = useState(false);
  const [showSOS, setShowSOS] = useState(false);
  const updatePresence = (p) => { setPresence(p); try { localStorage.setItem("ff_presence", JSON.stringify(p)); } catch {} };
  const [reminderTarget, setReminderTarget] = useState(null);
  const loadPrefs = (userId) => {
    const key = userId ? `ff_notif_prefs_${userId}` : "ff_notif_prefs";
    try { const v = localStorage.getItem(key); if (!v) return DEFAULT_NOTIF_PREFS; const saved = JSON.parse(v); const merged = {...DEFAULT_NOTIF_PREFS}; Object.keys(saved).forEach(k=>{ if(saved[k]!==undefined) merged[k]=saved[k]; }); return merged; } catch { return DEFAULT_NOTIF_PREFS; }
  };
  const [allNotifPrefs, setAllNotifPrefs] = useState(() => ({
    global: loadPrefs(null),
    olivier: loadPrefs("olivier"),
    stephanie: loadPrefs("stephanie"),
    cleo: loadPrefs("cleo"),
  }));
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [memberThemes, setMemberThemesRaw] = useState(() => {
    try {
      // v2 = brighter themes — clear old cache
      const ver = localStorage.getItem("ff_themes_ver");
      if (ver !== "v2") {
        localStorage.removeItem("ff_member_themes");
        localStorage.setItem("ff_themes_ver", "v2");
        return {};
      }
      const v = localStorage.getItem("ff_member_themes");
      return v ? JSON.parse(v) : {};
    } catch { return {}; }
  });
  const updateMemberTheme = (memberId, themeId, customColors) => {
    setMemberThemesRaw(prev => {
      // If customColors is explicitly {}, clear all custom colors (reset to preset)
      const prevCustom = prev[memberId]?.customColors || {};
      const newCustom = Object.keys(customColors||{}).length === 0 ? {} : {...prevCustom, ...(customColors||{})};
      const next = {...prev, [memberId]: { themeId, customColors: newCustom }};
      try { localStorage.setItem("ff_member_themes", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [showNotifSection, setShowNotifSection] = useState(false);
  const [showThemeSection, setShowThemeSection] = useState(false);

  // Active prefs = current user's prefs or global
  const notifPrefs = activeUser ? (allNotifPrefs[activeUser] || DEFAULT_NOTIF_PREFS) : allNotifPrefs.global;

  const updateNotifPref = (key, val) => {
    const userId = activeUser || "global";
    const storageKey = activeUser ? `ff_notif_prefs_${activeUser}` : "ff_notif_prefs";
    setAllNotifPrefs(prev => {
      const next = {...prev, [userId]: {...(prev[userId]||DEFAULT_NOTIF_PREFS), [key]: val}};
      try { localStorage.setItem(storageKey, JSON.stringify(next[userId])); } catch {}
      return next;
    });
  };
  useEffect(()=>{ if(tab!=="appts") setTargetAppt(null); }, [tab]);
  const load = (key, fallback) => {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
  };
  const save = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

  const [events, setEventsRaw] = useState(() => load("ff_events", INITIAL_EVENTS));
  const [tasks,  setTasksRaw]  = useState(() => load("ff_tasks",  INITIAL_TASKS));
  const [appts,  setApptsRaw]  = useState(() => load("ff_appts",  INITIAL_APPTS));

  const setEvents = (fn) => setEventsRaw(prev => { const next = typeof fn==="function" ? fn(prev) : fn; save("ff_events", next); return next; });
  const setTasks  = (fn) => setTasksRaw(prev  => { const next = typeof fn==="function" ? fn(prev) : fn; save("ff_tasks",  next); return next; });
  const setAppts  = (fn) => setApptsRaw(prev  => { const next = typeof fn==="function" ? fn(prev) : fn; save("ff_appts",  next); return next; });

  const [reminders, setRemindersRaw] = useState(() => load("ff_reminders", INITIAL_REMINDERS));
  const setReminders = (fn) => setRemindersRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_reminders",next); return next; });
  const deleteReminder = (id) => setReminders(rs=>rs.filter(x=>x.id!==id));
  const toggleReminder = (id) => setReminders(rs=>rs.map(r=>{
    if (r.id!==id) return r;
    // Un rappel annuel ne se termine jamais : on le décale d'un an et on le laisse actif
    if (!r.done && r.recurrence==="yearly") {
      const next = new Date(r.targetDate+"T00:00:00");
      next.setFullYear(next.getFullYear()+1);
      return { ...r, targetDate: toLocalISO(next), triggered:false, done:false };
    }
    return {...r,done:!r.done};
  }));
  const triggerReminder = (id) => setReminders(rs=>rs.map(r=>r.id===id?{...r,triggered:true}:r));
  const addReminder    = (r) => { setRemindersRaw(prev=>{ const next=[r,...prev]; save("ff_reminders",next); return next; }); };
  const addEvent    = (e) => {
    setEvents(ev=>[...ev,e]);
  };
  const updateEvent = (e) => setEvents(ev=>ev.map(x=>x.id===e.id?e:x));
  const deleteEvent = (id) => setEvents(ev=>ev.filter(x=>x.id!==id));

  const addTask    = (t) => {
    setTasks(ts=>[...ts,t]);
  };
  const updateTask = (t) => setTasks(ts=>ts.map(x=>x.id===t.id?t:x));
  const deleteTask = (id) => setTasks(ts=>ts.filter(x=>x.id!==id));
  const toggleTask = (id) => setTasks(ts=>ts.map(t=>t.id===id?{...t,done:!t.done}:t));

  const [fiches, setFichesRaw] = useState(() => load("ff_fiches", INITIAL_FICHES));
  const [notes, setNotesRaw]   = useState(() => load("ff_notes",  INITIAL_NOTES));
  const [scores, setScoresRaw] = useState(() => load("ff_scores", INITIAL_SCORES));
  const setScores = (fn) => {
    setScoresRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_scores",next); return next; });
  };
  const [missions, setMissionsRaw] = useState(() => {
    const loaded = load("ff_missions", INITIAL_MISSIONS);
    // Migration: ensure all missions have active field
    return loaded.map(m => ({ active:true, days:[0,1,2,3,4,5,6], ...m }));
  });
  const setMissions = (fn) => setMissionsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_missions",next); return next; });
  const [medias, setMediasRaw] = useState(() => load("ff_medias", INITIAL_MEDIAS));
  const setMedias = (fn) => setMediasRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_medias",next); return next; });
  const addMedia    = (m) => setMedias(ms=>[m,...ms]);
  const deleteMedia = (id) => setMedias(ms=>ms.filter(m=>m.id!==id));

  const addMission    = (m) => setMissions(ms=>[...ms,m]);
  const deleteMission = (id) => setMissions(ms=>ms.filter(m=>m.id!==id));

  // Missions perso — créées par chaque membre, validées par les autres
  const [personalMissions, setPersonalMissionsRaw] = useState(() => load("ff_personal_missions", []));
  const setPersonalMissions = (fn) => setPersonalMissionsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_personal_missions",next); return next; });
  const addPersonalMission    = (m) => setPersonalMissions(ms=>[...ms,m]);
  const deletePersonalMission = (id) => setPersonalMissions(ms=>ms.filter(m=>m.id!==id));
  const votePersonalMission   = (id, voterId, pts) => setPersonalMissions(ms=>ms.map(m=>{ if(m.id!==id) return m; const votes={...(m.votes||{}), [voterId]:pts}; const vals=Object.values(votes); const avg=Math.round(vals.reduce((s,v)=>s+v,0)/vals.length); return {...m, votes, avgPoints:avg}; }));
  const validatePersonalMission = (id, validatorId) => {
    setPersonalMissions(ms=>ms.map(m=>{ if(m.id!==id) return m; const validated=[...(m.validated||[]), validatorId]; return {...m, validated}; }));
    // Award average points to mission creator
    const mission = personalMissions.find(m=>m.id===id);
    if (mission) {
      const pts = mission.avgPoints || 50;
      setScores(prev=>{ const cur=prev[mission.creator]||{points:0,history:[],completedChallenges:[]}; return {...prev,[mission.creator]:{...cur,points:(cur.points||0)+pts,history:[...(cur.history||[]),{title:mission.title,pts,importance:3,date:todayISO(),isPersonal:true}]}}; });
    }
  };
  const [claims, setClaimsRaw] = useState(() => load("ff_claims", INITIAL_CLAIMS));
  const setClaims = (fn) => setClaimsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_claims",next); return next; });
  const addClaim      = (c) => setClaims(cs=>[...cs,c]);
  const completeClaim = (id) => setClaims(cs=>cs.map(c=>c.id===id?{...c,done:true}:c));
  const deleteClaim   = (id) => setClaims(cs=>cs.filter(c=>c.id!==id));
  const [moods, setMoodsRaw] = useState(() => load("ff_moods", INITIAL_MOODS));
  const [medications, setMedicationsRaw] = useState(() => load("ff_medications", INITIAL_MEDICATIONS));
  const setMedications = (fn) => setMedicationsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_medications",next); return next; });
  const addMedication = (m) => setMedications(ms=>[...ms,m]);
  const updateMedication = (m) => setMedications(ms=>ms.map(x=>x.id===m.id?m:x));
  const deleteMedication = (id) => setMedications(ms=>ms.filter(x=>x.id!==id));
  const [medLogs, setMedLogsRaw] = useState(() => load("ff_med_logs", INITIAL_MED_LOGS));
  const setMedLogs = (fn) => setMedLogsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_med_logs",next); return next; });
  const logMedTaken = (medicationId, member, date) => setMedLogs(ls=>[...ls, { id:Date.now(), medicationId, member, date, takenAt:new Date().toISOString() }]);
  const unlogMedTaken = (medicationId, date) => setMedLogs(ls=>ls.filter(l=>!(l.medicationId===medicationId && l.date===date)));
  const [moodAlerts, setMoodAlertsRaw] = useState(() => load("ff_mood_alerts", []));
  const setMoodAlerts = (fn) => setMoodAlertsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_mood_alerts",next); return next; });
  const setMoods = (fn) => setMoodsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_moods",next); return next; });
  const addMood = (m) => {
    const entry = { ...m, inWindow: isWithinMoodSlot(moodSlots[m.slot], m.createdAt || new Date()) };
    // Alerte parent — humeur basse (1, 2 ou 3 sur 5) chez un profil enfant, tous canaux
    if (memberProfiles[entry.member]?.isKid && entry.mood <= 3) {
      const alert = { id: Date.now()+1, member: entry.member, date: entry.date, slot: entry.slot, moodValue: entry.mood, createdAt: new Date().toISOString(), seen:false };
      setMoodAlerts(prev => [...prev, alert]);
      if (notifGranted) {
        try { new Notification(`⚠️ Alfy — Humeur`, { body: `${memberOf(entry.member).name} a signalé une humeur difficile (${entry.slot})`, icon:"/favicon.ico" }); } catch {}
      }
    }
    setMoods(ms => {
      const next = [...ms, entry];
      // Check mood bonus — seules les évaluations faites dans la bonne plage horaire comptent
      const todayStr = todayISO();
      const todayMoods = next.filter(mo=>mo.member===entry.member && mo.date===todayStr);
      const hasMatin = todayMoods.some(mo=>mo.slot==="matin" && mo.inWindow!==false);
      const hasSoir  = todayMoods.some(mo=>mo.slot==="soir" && mo.inWindow!==false);
      if (hasMatin && hasSoir) {
        setScores(prev => {
          const { newChallenges, weekKey, dayKey } = checkChallenges(prev, tasks, entry.member, missions, next);
          const moodCh = newChallenges.filter(ch=>ch.id==="mood_day"||ch.id==="mood_week");
          if (moodCh.length === 0) return prev;
          const cur = prev[entry.member] || { points:0, history:[], completedChallenges:[] };
          const bonusPts = moodCh.reduce((s,ch)=>(WEEKLY_CHALLENGES.find(c=>c.id===ch.id)?.points||0)+s, 0);
          const completedChallenges = [...(cur.completedChallenges||[]), ...moodCh.map(ch=>({id:ch.id,week:ch.key}))];
          const history = [...(cur.history||[]), ...moodCh.map(ch=>({title:WEEKLY_CHALLENGES.find(c=>c.id===ch.id)?.label||"Bonus", pts:WEEKLY_CHALLENGES.find(c=>c.id===ch.id)?.points||0, importance:2, date:todayStr, isDaily:false}))];
          return {...prev, [entry.member]: {...cur, points:(cur.points||0)+bonusPts, history, completedChallenges}};
        });
      }
      save("ff_moods", next);
      return next;
    });
  };

  const [messages, setMessagesRaw] = useState(() => load("ff_messages", INITIAL_MESSAGES));
  const setMessages = (fn) => setMessagesRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_messages",next); return next; });
  const [msgCurrentUser, setMsgCurrentUser] = useState("olivier");
  const sendMessage = (msg) => setMessages(ms=>[...ms, msg]);
  const markRead = (convId, userId) => {
    if (convId==="switch") { setMsgCurrentUser(userId); return; }
    setMessages(ms=>ms.map(m=>m.convId===convId && m.to===userId ? {...m,read:true} : m));
  };


  const [documents, setDocsRaw] = useState(() => load("ff_docs", INITIAL_DOCUMENTS));
  const setDocs = (fn) => setDocsRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_docs",next); return next; });
  const addDoc    = (d) => setDocs(ds=>[...ds,d]);
  const updateDoc = (d) => setDocs(ds=>ds.map(x=>x.id===d.id?d:x));
  const deleteDoc = (id) => setDocs(ds=>ds.filter(x=>x.id!==id));
  const [recipes, setRecipesRaw] = useState(() => load("ff_recipes", INITIAL_RECIPES));
  const [menu,    setMenuRaw]    = useState(() => load("ff_menu",    INITIAL_MENU));
  const [shopping,setShoppingRaw]= useState(() => load("ff_shopping",INITIAL_SHOPPING));
  const setRecipes = (fn) => setRecipesRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_recipes",next); return next; });
  const setMenu    = (fn) => setMenuRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_menu",next); return next; });
  const setShopping= (fn) => setShoppingRaw(prev=>{ const next=typeof fn==="function"?fn(prev):fn; save("ff_shopping",next); return next; });
  const addRecipe    = (r) => setRecipes(rs=>[...rs,r]);
  const updateRecipe = (r) => setRecipes(rs=>rs.map(x=>x.id===r.id?r:x));
  const deleteRecipe = (id)=> setRecipes(rs=>rs.filter(x=>x.id!==id));
  const setNotes = (fn) => setNotesRaw(prev => { const next = typeof fn==="function" ? fn(prev) : fn; save("ff_notes", next); return next; });
  const addNote    = (n) => setNotes(ns=>[n,...ns]);
  const updateNote = (n) => setNotes(ns=>ns.map(x=>x.id===n.id?n:x));
  const deleteNote = (id) => setNotes(ns=>ns.filter(x=>x.id!==id));
  const addAppt    = (a) => {
    setAppts(as=>[...as,a]);
  };
  const convertNote = (type, note, extra={}) => {
    if (type==="task")  { addTask({ id:Date.now(), title:note.text, member:extra.member||"olivier", date:extra.date||"", time:extra.time||"", importance:extra.importance||2, recurrence:"none", done:false }); }
    if (type==="appt")  { addAppt({ id:Date.now(), title:note.text, date:extra.date||"", time:extra.time||"09:00", specialist:extra.specialist||"", member:extra.member||"cleo", notes:extra.notes||"", recurrence:"none" }); }
    if (type==="reminder") { addReminder({ id:Date.now(), title:note.text, notes:"", member:extra.member||"olivier", members:extra.members||[extra.member||"olivier"], targetDate:extra.date||todayISO(), targetTime:extra.time||"09:00", done:false, triggered:false, createdAt:new Date().toISOString() }); }
    if (type==="event") {
      const day = extra.day !== undefined ? extra.day : (() => { const dow = new Date().getDay(); return dow===0?6:dow-1; })();
      addEvent({ id:Date.now(), title:note.text, day, time:extra.time||"09:00", member:extra.member||"olivier", type:extra.eventType||"work", recurrence:"none" });
    }
    deleteNote(note.id);
  };
  const setFiches = (fn) => setFichesRaw(prev => { const next = typeof fn==="function" ? fn(prev) : fn; save("ff_fiches", next); return next; });
  const [activeFiche, setActiveFiche] = useState(null);
  const [targetAppt, setTargetAppt]   = useState(null);
  const { bandeau, setBandeau, notifGranted } = useRappels({ events, tasks, appts, menu, recipes, notifPrefs, moods, personalMissions, currentUser });
  useRemindersAlarm({ reminders, onTrigger: triggerReminder, notifGranted });
  const requestNotif = () => {
    if ("Notification" in window) {
      Notification.requestPermission().then(p => { if (p === "granted") window.location.reload(); });
    }
  };
  const addFiche    = (f) => setFiches(fs=>[...fs,f]);
  const updateFiche = (f) => setFiches(fs=>fs.map(x=>x.id===f.id?f:x));
  const deleteFiche = (id) => setFiches(fs=>fs.filter(x=>x.id!==id));


  const updateAppt = (a) => setAppts(as=>as.map(x=>x.id===a.id?a:x));
  const deleteAppt  = (id) => setAppts(as=>as.filter(x=>x.id!==id));
  const cancelApptOccurrence = (occKey) => {
    const baseId = occKey.substring(0, occKey.lastIndexOf("_"));
    const base = appts.find(a=>String(a.id)===baseId);
    if (!base) return;
    if (!base.recurrence || base.recurrence==="none") { deleteAppt(base.id); return; }
    updateAppt({ ...base, cancelledOccurrences:[...(base.cancelledOccurrences||[]), occKey] });
  };
  const togglePaid  = (id) => setAppts(as=>as.map(a=>a.id===id?{...a,paid:!a.paid}:a));

  const today = new Date().toLocaleDateString("fr-FR",{ weekday:"long",day:"numeric",month:"long",year:"numeric" });

  // Compute meal events for planning
  const planningMealEvents = useMemo(() => {
    const DAYS_FR_ROOT = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
    const MEAL_TIMES = { "Petit-déj":"07:30", "Midi":"12:00", "Goûter":"16:30", "Soir":"19:00" };
    const MEAL_ICONS_P = { "Petit-déj":"🌅", "Midi":"☀️", "Goûter":"🍎", "Soir":"🌙" };
    const wk = getWeekKey(0);
    const weekMenu = menu[wk] || {};
    const evs = [];
    DAYS_FR_ROOT.forEach((dayFR, di) => {
      ["Petit-déj","Midi","Goûter","Soir"].forEach(slot => {
        const key = `${dayFR}-${slot}`;
        const recipeId = weekMenu[key];
        if (!recipeId || recipeId==="ext") return;
        const recipe = recipes.find(r=>r.id===+recipeId);
        if (!recipe) return;
        const cookId = weekMenu[key+"_cook"];
        evs.push({ id:`meal_${di}_${slot}`, day:di, time:MEAL_TIMES[slot], title:`${MEAL_ICONS_P[slot]} ${recipe.title}`, member:cookId||"olivier", members:cookId?[cookId]:[], type:"food", isMeal:true, slot, recipe });
      });
    });
    return evs;
  }, [menu, recipes]);

  // Onboarding
  if (!onboardingDone) {
    return <OnboardingScreen onComplete={()=>{
      try { localStorage.setItem("ff_onboarding_done","1"); } catch {}
      setOnboardingDone(true);
    }} />;
  }

  // First launch setup
  if (!setupDone) {
    return <SetupScreen onComplete={(members) => {
      try {
        localStorage.setItem("ff_setup_done", "1");
        localStorage.setItem("ff_custom_members", JSON.stringify(members));
        // Save member profiles
        const profiles = {};
        // Map to fixed member IDs by position
        const fixedIds = ["olivier","stephanie","cleo"];
        members.forEach((m,i) => {
          const id = fixedIds[i] || m.id;
          profiles[id] = { isKid: m.isKid, color: m.color, avatar: m.avatar, name: m.name, pin: m.pin||"" };
        });
        localStorage.setItem("ff_member_profiles", JSON.stringify(profiles));
        setMemberProfilesRaw(profiles);
        syncMembersFromProfiles(profiles);
      } catch {}
      setCustomMembers(members);
      setSetupDone(true);
    }} />;
  }

  // Compute active theme for current user
  const activeThemeId = memberThemes[currentUser]?.themeId || "cocon";
  const activeCustom = memberThemes[currentUser]?.customColors || {};
  const activePreset = PRESET_THEMES.find(t=>t.id===activeThemeId) || PRESET_THEMES[0];
  const activeBgGrad = activeCustom.bg
    ? `linear-gradient(160deg,${activeCustom.bg} 0%,${activeCustom.bg}CC 100%)`
    : activePreset.bgGrad;
  // Applique les couleurs du thème actif à l'objet de couleurs partagé T, en mutant ses propriétés
  // (et non en le réassignant) pour que tous les composants qui lisent T.xxx à chaque rendu
  // reflètent automatiquement le thème choisi, sans avoir à faire transiter le thème en props partout.
  T.text     = activeCustom.text     || activePreset.text;
  T.textMid  = activePreset.textMid;
  T.textSoft = activeCustom.textSoft || activePreset.textSoft;
  T.sand     = activeCustom.bg       || activePreset.bg;
  T.sandDark = activeCustom.card     || activePreset.card;
  T.cardBorder = activePreset.cardBorder || "rgba(255,255,255,0.08)";
  // Profile selection screen
  if (!currentUser) {
    return <ProfileSelectScreen memberProfiles={memberProfiles} onCreateFirstMember={addMember} onSelect={(memberId, isKid) => {
      setCurrentUser(memberId);
      setActiveUser(null);
      setShowDaySas(true);
      if (isKid) { setKidsMode(memberId); }
    }} />;
  }

  // Sas journée — points importants du jour
  if (showDaySas) {
    const { apptsToday: todayApptsSas, tasksToday: todayTasksSas, claimsToday: todayClaimsSas, remindersToday: todayRemindersSas, hasMatin, unread: unreadSas, pendingPersonal: pendingPersonalSas, medsToday: medsTodaySas } = computeDayPriorities(currentUser, { appts, tasks, claims, moods, reminders, messages, personalMissions, moodAlerts, medications, medLogs });
    const unseenMoodAlertsSas = moodAlerts.filter(a=>!a.seen && a.member!==currentUser);
    const member = MEMBERS.find(m=>m.id===currentUser);

    return (
      <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#F5EDE4 0%,#FDF8F3 100%)", fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif", display:"flex", flexDirection:"column" }}>
        {/* Header */}
        <div style={{ background:T.sandDark, padding:"20px 20px 16px", paddingTop:"max(20px,env(safe-area-inset-top))", borderBottom:`1px solid ${T.cardBorder}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:"50%", background:member?.color||"#E8735A", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:800, color:"white" }}>{member?.avatar||"?"}</div>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:"white" }}>Bonjour {member?.name} 👋</div>
              <div style={{ fontSize:12, color:T.textSoft, marginTop:2 }}>{new Date().toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 100px" }}>
          <div style={{ fontSize:13, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:14 }}>Points importants du jour</div>

          {/* RDV */}
          {todayApptsSas.length > 0 && (
            <div onClick={()=>{ setShowDaySas(false); setTab("appts"); }} style={{ background:"rgba(91,191,173,0.12)", border:"1px solid rgba(91,191,173,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#5BBFAD", textTransform:"uppercase" }}>🏥 RDV du jour</div>
                <span style={{ fontSize:12, color:T.textSoft }}>→</span>
              </div>
              {todayApptsSas.map(a=>(
                <div key={a.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0", borderBottom:`1px solid ${T.cardBorder}` }}>
                  <span style={{ fontSize:14, color:T.textSoft }}>{a.time}</span>
                  <span style={{ fontSize:14, color:"white", fontWeight:600 }}>{a.title}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tâches urgentes */}
          {todayTasksSas.length > 0 && (
            <div onClick={()=>{ setShowDaySas(false); setTab("tasks"); }} style={{ background:"rgba(232,115,90,0.12)", border:"1px solid rgba(232,115,90,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#E8735A", textTransform:"uppercase" }}>🔴 Tâches urgentes</div>
                <span style={{ fontSize:12, color:T.textSoft }}>→</span>
              </div>
              {todayTasksSas.map(t=>(
                <div key={t.id} style={{ fontSize:14, color:"white", fontWeight:600, padding:"4px 0" }}>• {t.title}</div>
              ))}
            </div>
          )}

          {/* Missions en cours */}
          {todayClaimsSas.length > 0 && (
            <div onClick={()=>{ setShowDaySas(false); setTab("game"); }} style={{ background:"rgba(212,168,67,0.12)", border:"1px solid rgba(212,168,67,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#D4A843", textTransform:"uppercase" }}>🎯 Missions en cours</div>
                <span style={{ fontSize:12, color:T.textSoft }}>→</span>
              </div>
              {todayClaimsSas.map(c=>(
                <div key={c.id} style={{ fontSize:14, color:"white", fontWeight:600, padding:"4px 0" }}>{c.icon} {c.title}</div>
              ))}
            </div>
          )}

          {/* Rappels */}
          {todayRemindersSas.length > 0 && (
            <div onClick={()=>{ setShowDaySas(false); setTab("reminders"); }} style={{ background:"rgba(155,89,182,0.12)", border:"1px solid rgba(155,89,182,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#9B59B6", textTransform:"uppercase" }}>⏰ Rappels du jour</div>
                <span style={{ fontSize:12, color:T.textSoft }}>→</span>
              </div>
              {todayRemindersSas.map(r=>(
                <div key={r.id} style={{ fontSize:14, color:"white", fontWeight:600, padding:"4px 0" }}>• {r.title}</div>
              ))}
            </div>
          )}

          {/* Humeur non évaluée */}
          {!hasMatin && (
            <div onClick={()=>{ setShowDaySas(false); setTab("humeur"); }} style={{ background:"rgba(39,174,96,0.12)", border:"1px solid rgba(39,174,96,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:12, cursor:"pointer" }}>
              <span style={{ fontSize:24 }}>😊</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#27AE60" }}>Humeur non évaluée</div>
                <div style={{ fontSize:11, color:T.textSoft }}>Tu n'as pas encore évalué ton humeur ce matin</div>
              </div>
              <span style={{ fontSize:12, color:T.textSoft }}>→</span>
            </div>
          )}

          {/* Défis perso à voter/valider */}
          {pendingPersonalSas > 0 && (
            <div onClick={()=>{ setShowDaySas(false); setTab("game"); }} style={{ background:"rgba(155,89,182,0.12)", border:"1px solid rgba(155,89,182,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:12, cursor:"pointer" }}>
              <span style={{ fontSize:24 }}>🗳️</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#9B59B6" }}>{pendingPersonalSas} défi{pendingPersonalSas>1?"s":""} perso à voter/valider</div>
                <div style={{ fontSize:11, color:T.textSoft }}>D'autres membres attendent ton avis</div>
              </div>
              <span style={{ fontSize:12, color:T.textSoft }}>→</span>
            </div>
          )}

          {/* Alerte humeur basse enfant */}
          {unseenMoodAlertsSas.length > 0 && (
            <div style={{ background:"rgba(232,115,90,0.15)", border:"1px solid #E8735A", borderRadius:14, padding:"14px 16px", marginBottom:10 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#E8735A", textTransform:"uppercase", marginBottom:6 }}>⚠️ Alerte humeur</div>
              {unseenMoodAlertsSas.map(a=>(
                <div key={a.id} style={{ fontSize:13, color:"white", fontWeight:600, padding:"3px 0" }}>{memberOf(a.member).name} — humeur difficile ce {a.slot}</div>
              ))}
            </div>
          )}

          {/* Messages non lus */}
          {unreadSas > 0 && (
            <div onClick={()=>{ setShowDaySas(false); setTab("messages"); }} style={{ background:"rgba(61,90,138,0.2)", border:"1px solid rgba(61,90,138,0.4)", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:12, cursor:"pointer" }}>
              <span style={{ fontSize:24 }}>✉️</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#5B7FB5" }}>{unreadSas} message{unreadSas>1?"s":""} non lu{unreadSas>1?"s":""}</div>
                <div style={{ fontSize:11, color:T.textSoft }}>Tu as des messages en attente</div>
              </div>
              <span style={{ fontSize:12, color:T.textSoft }}>→</span>
            </div>
          )}

          {/* Rien à signaler */}
          {/* Médicaments à prendre */}
          {medsTodaySas.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#D4A843", textTransform:"uppercase", marginBottom:6 }}>💊 À prendre aujourd'hui</div>
              {medsTodaySas.map(med=>(
                <div key={med.id} style={{ background:"rgba(212,168,67,0.12)", border:"1px solid rgba(212,168,67,0.3)", borderRadius:12, padding:"10px 14px", marginBottom:6, display:"flex", alignItems:"center", gap:10 }}>
                  <button onClick={()=>logMedTaken(med.id, currentUser, todayISO())} style={{ width:26,height:26,borderRadius:"50%",border:"2px solid #D4A843",background:"transparent",cursor:"pointer",flexShrink:0 }} title="Marquer comme pris" />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"white" }}>{med.name}</div>
                    <div style={{ fontSize:11, color:T.textSoft }}>{MED_SLOT_ICONS[med.slot]} {med.slot} · {med.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {todayApptsSas.length===0 && todayTasksSas.length===0 && todayClaimsSas.length===0 && todayRemindersSas.length===0 && hasMatin && unreadSas===0 && pendingPersonalSas===0 && unseenMoodAlertsSas.length===0 && medsTodaySas.length===0 && (
            <div style={{ textAlign:"center", padding:"40px 20px" }}>
              <div style={{ fontSize:48 }}>✨</div>
              <div style={{ fontSize:16, fontWeight:800, color:"white", marginTop:12 }}>Journée libre !</div>
              <div style={{ fontSize:13, color:T.textSoft, marginTop:6 }}>Rien d'urgent aujourd'hui — profites-en !</div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div style={{ position:"fixed", bottom:0, left:0, right:0, padding:"16px 20px", paddingBottom:"max(16px,env(safe-area-inset-bottom))", background:T.sandDark, borderTop:`1px solid ${T.cardBorder}` }}>
          <button onClick={()=>setShowDaySas(false)} style={{ width:"100%", padding:"16px", background:"#E8735A", border:"none", borderRadius:14, fontSize:16, fontWeight:800, color:"white", cursor:"pointer" }}>
            Commencer ma journée →
          </button>
        </div>
      </div>
    );
  }

  // Kids mode
  if (kidsMode) {
    return <KidsView member={kidsMode} tasks={tasks} events={events} appts={appts} moods={moods} scores={scores} missions={missions} claims={claims} recipes={recipes} menu={menu} shopping={shopping} onUpdateMenu={setMenu} onUpdateShopping={setShopping} onAddRecipe={addRecipe} onUpdateRecipe={updateRecipe} onDeleteRecipe={deleteRecipe} onToggleTask={toggleTask} onAddMood={addMood} onCompleteClaim={completeClaim} onAddClaim={addClaim} onDeleteClaim={deleteClaim} onScoreUpdate={setScores} onExit={()=>{ setKidsMode(null); setCurrentUser(null); }} personalMissions={personalMissions} addPersonalMission={addPersonalMission} deletePersonalMission={deletePersonalMission} votePersonalMission={votePersonalMission} validatePersonalMission={validatePersonalMission} onOpenSas={()=>setShowDaySas(true)} bandeauCount={computeDayPriorities(kidsMode, { appts, tasks, claims, moods, reminders, messages, personalMissions, moodAlerts, medications, medLogs }).count} medications={medications} medLogs={medLogs} onLogTaken={logMedTaken} onUnlogTaken={unlogMedTaken} />;
  }

  return (
    <div key={activeThemeId+(activeCustom.bg||"")} style={{ minHeight:"100vh", background: currentUser ? activeBgGrad : "linear-gradient(160deg,#F5EDE4 0%,#FDF8F3 100%)", fontFamily:"'Quicksand',system-ui,-apple-system,sans-serif", paddingLeft:"env(safe-area-inset-left)", paddingRight:"env(safe-area-inset-right)" }}>
      <div style={{ background:T.sandDark, borderBottom:`1px solid ${T.cardBorder}`, padding:"16px 20px", paddingTop:"max(16px, env(safe-area-inset-top))", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", flexDirection:"column" }}>
          <div onClick={()=>setTab("home")} style={{ cursor:"pointer", fontSize:18,fontWeight:800,color:T.text,letterSpacing:"-0.02em" }}>🏠 Alfy</div>
          <div onClick={()=>{ setCurrentUser(null); setActiveUser(null); setKidsMode(null); }} style={{ fontSize:11,color:T.textSoft,marginTop:1,cursor:"pointer" }}>← Changer de profil</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={()=>setShowSOS(true)} style={{ background:"rgba(52,152,219,0.2)", border:"1px solid rgba(52,152,219,0.5)", borderRadius:10, cursor:"pointer", fontSize:16, width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", color:"#3498DB" }} title="Je souffle">🌊</button>
          <button onClick={()=>setShowPresencePanel(p=>!p)} style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:10, cursor:"pointer", fontSize:16, width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center" }} title="Voir où est la famille">📍</button>
          <button onClick={()=>setShowNotifSettings(true)} style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:10, cursor:"pointer", fontSize:16, width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center" }} title="Paramètres">⚙️</button>
          {currentUser && (
            <div style={{ position:"relative" }}>
              <Avatar member={currentUser} size={30} />
              {(() => { const unread = messages.filter(msg=>msg.to===currentUser&&!msg.read).length; return unread>0 ? <span style={{ position:"absolute", top:-2, left:-2, background:T.danger, borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:800, color:"white" }}>{unread}</span> : null; })()}
            </div>
          )}
        </div>
      </div>
      {/* Hamburger nav */}
      <div style={{ background:T.sand, borderBottom:`1px solid ${T.cardBorder}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 16px" }}>
        {/* Current section label */}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>{TABS.find(t=>t.id===tab)?.icon}</span>
          <span style={{ fontSize:14, fontWeight:700, color:T.coral }}>{TABS.find(t=>t.id===tab)?.label}</span>
        </div>
        {/* Hamburger button */}
        <button onClick={()=>setMenuOpen(m=>!m)} style={{ background:"transparent", border:"none", cursor:"pointer", color:T.text, fontSize:22, padding:"10px 0", lineHeight:1 }}>
          {menuOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Slide-down menu */}
      {menuOpen && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:300, display:"flex" }}>
          {/* Backdrop */}
          <div onClick={()=>setMenuOpen(false)} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.4)" }} />
          {/* Menu panel */}
          <div style={{ position:"absolute", top:0, right:0, width:260, height:"100%", background:T.sandDark, boxShadow:"-4px 0 20px rgba(61,44,62,0.15)", display:"flex", flexDirection:"column", zIndex:1 }}>
            <div style={{ padding:"20px 20px 12px", borderBottom:"1px solid "+T.cardBorder }}>
              <div style={{ fontSize:18, fontWeight:800, fontFamily:"'Nunito',system-ui,sans-serif", color:T.text }}>Alfy</div>
              <div style={{ fontSize:11, color:T.textSoft, marginTop:2 }}>Menu principal</div>
            </div>
            <div style={{ flex:1, overflowY:"auto", padding:"8px 0" }}>
              {TABS.map(t=>(
                <button key={t.id} onClick={()=>{ setTab(t.id); setMenuOpen(false); }} style={{ display:"flex", alignItems:"center", gap:14, width:"100%", padding:"14px 20px", background:tab===t.id?T.sand:"transparent", border:"none", cursor:"pointer", textAlign:"left", borderLeft: tab===t.id ? `3px solid ${T.coral}` : "3px solid transparent" }}>
                  <span style={{ fontSize:20, minWidth:28 }}>{t.icon}</span>
                  <span style={{ fontSize:15, fontWeight:tab===t.id?700:400, color:tab===t.id?T.coral:T.textSoft }}>{t.label}</span>
                </button>
              ))}
            </div>
            <div style={{ padding:"16px 20px", borderTop:"1px solid "+T.cardBorder }}>
              <div style={{ fontSize:11, color:T.textSoft }}>Alfy V1 — famille Morvan</div>
            </div>
          </div>
        </div>
      )}
      <MoodAlertBanner alerts={moodAlerts} onDismiss={(id)=>setMoodAlerts(prev=>prev.map(a=>a.id===id?{...a,seen:true}:a))} />
      <Bandeau count={computeDayPriorities(currentUser, { appts, tasks, claims, moods, reminders, messages, personalMissions, moodAlerts, medications, medLogs }).count} notifGranted={notifGranted} onRequestNotif={requestNotif} onOpenSas={()=>setShowDaySas(true)} />
      {/* Présence panel */}
      {showPresencePanel && (
        <div style={{ background:T.sandDark, borderBottom:`1px solid ${T.cardBorder}` }}>
          <div style={{ padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${T.cardBorder}` }}>
            <span style={{ fontSize:12, fontWeight:800, color:T.text }}>📍 Où est la famille ?</span>
            <button onClick={()=>setShowPresencePanel(false)} style={{ background:"transparent", border:"none", color:T.textSoft, fontSize:16, cursor:"pointer" }}>✕</button>
          </div>
          {MEMBERS.map(m => {
            const p = presence[m.id];
            const s = p ? PRESENCE_STATUSES.find(x=>x.id===p.status) : null;
            return (
              <div key={m.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", borderBottom:`1px solid ${T.cardBorder}` }}>
                <Avatar member={m.id} size={32} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{memberOf(m.id).name}</div>
                  {s ? (
                    <div style={{ fontSize:12, color:s.color, fontWeight:600 }}>{s.icon} {s.label}{p.note ? ` — ${p.note}` : ""} <span style={{ color:T.textSoft, fontWeight:400 }}>· {p.updatedAt}</span></div>
                  ) : (
                    <div style={{ fontSize:12, color:T.textSoft }}>Position non renseignée</div>
                  )}
                </div>
                <button onClick={()=>{ setPresenceModal(m.id); setShowPresencePanel(false); }} style={{ background:s?s.color+"22":T.sandDark, border:`1px solid ${s?s.color:T.cardBorder}`, borderRadius:7, padding:"5px 10px", cursor:"pointer", fontSize:11, fontWeight:700, color:s?s.color:T.textSoft }}>
                  {s ? "Modifier" : "Indiquer"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ padding:"12px 16px", maxWidth:960, margin:"0 auto", boxSizing:"border-box", width:"100%" }}>
        {tab==="home"     && <HomeView onNavigate={setTab} tasks={tasks} appts={appts} events={events} reminders={reminders} moods={moods} scores={scores} messages={messages} activeUser={activeUser} currentUser={currentUser} medications={medications} medLogs={medLogs} />}
        {tab==="planning" && <PlanningView events={events} onAdd={addEvent} onUpdate={updateEvent} onDelete={deleteEvent} appts={appts} fiches={fiches} onOpenFiche={setActiveFiche} onUpdateFiche={updateFiche} tasks={tasks} onToggleTask={toggleTask} onAddTask={addTask} onAddAppt={addAppt} onUpdateTask={updateTask} onAddCompanion={null} onSwitchTab={(t,appt)=>{ setTab(t); if(appt) setTargetAppt(appt); }} activeUser={activeUser} addReminder={addReminder} deleteReminder={deleteReminder} onSetReminderTarget={setReminderTarget} themes={themes} onAddTheme={addTheme} mealEvents={planningMealEvents} claims={claims} onCompleteClaim={completeClaim} onScoreUpdate={setScores} reminders={reminders} />}
        {tab==="tasks"    && <TasksView tasks={tasks} onToggle={toggleTask} onAdd={addTask} onUpdate={updateTask} onDelete={deleteTask} activeUser={activeUser} themes={themes} onAddTheme={addTheme} addReminder={addReminder} deleteReminder={deleteReminder} />}
        {tab==="appts"    && <ApptsView appts={appts} onAdd={addAppt} onUpdate={updateAppt} onDelete={deleteAppt} fiches={fiches} onOpenFiche={setActiveFiche} onTogglePaid={togglePaid} initialEditAppt={targetAppt} activeUser={activeUser} themes={themes} onAddTheme={addTheme} addReminder={addReminder} deleteReminder={deleteReminder} />}
        {tab==="fiches"   && <FichesView fiches={fiches} onAdd={addFiche} onUpdate={updateFiche} onDelete={deleteFiche} appts={appts} onAddAppt={addAppt} onCancelOccurrence={cancelApptOccurrence} activeUser={activeUser} themes={themes} onAddTheme={addTheme} />}
        {tab==="repas"    && <RepasView recipes={recipes} menu={menu} shopping={shopping} onUpdateMenu={setMenu} onUpdateShopping={setShopping} onAddRecipe={addRecipe} onUpdateRecipe={updateRecipe} onDeleteRecipe={deleteRecipe} />}
        {tab==="rappels"  && <RemindersView reminders={reminders} onAdd={addReminder} onDelete={deleteReminder} onToggle={toggleReminder} activeUser={activeUser} themes={themes} onAddTheme={addTheme} />}
        {tab==="docs"     && <DocumentsView documents={documents} fiches={fiches} onAdd={addDoc} onUpdate={updateDoc} onDelete={deleteDoc} activeUser={activeUser} />}
        {tab==="game"     && <GameView scores={scores} tasks={tasks} onScoreUpdate={setScores} activeUser={activeUser} currentUser={currentUser} missions={missions} claims={claims} addMission={addMission} deleteMission={deleteMission} addClaim={addClaim} completeClaim={completeClaim} moods={moods} personalMissions={personalMissions} addPersonalMission={addPersonalMission} deletePersonalMission={deletePersonalMission} votePersonalMission={votePersonalMission} validatePersonalMission={validatePersonalMission} />}
        {tab==="humeur"   && <MoodView moods={moods} onAdd={addMood} activeUser={activeUser} currentUser={currentUser} />}
        {tab==="medicaments" && <MedicationsView medications={medications} medLogs={medLogs} onAdd={addMedication} onUpdate={updateMedication} onDelete={deleteMedication} onLogTaken={logMedTaken} onUnlogTaken={unlogMedTaken} activeUser={activeUser} />}
        {tab==="messages" && <MessagingView messages={messages} onSend={sendMessage} onMarkRead={markRead} activeUser={activeUser} currentUser={msgCurrentUser} />}
        {tab==="medias"   && <MediaView medias={medias} onAdd={addMedia} onDelete={deleteMedia} isAdmin={!memberProfiles[currentUser]?.isKid} />}
        {tab==="chat"     && <ChatView events={events} tasks={tasks} appts={appts} onAddEvent={addEvent} onAddTask={addTask} onAddAppt={addAppt} onToggleTask={toggleTask} />}
      </div>


      {/* Notification settings modal */}
      {showNotifSettings && (
        <Modal title="⚙️ Paramètres" onClose={()=>setShowNotifSettings(false)}>
          {/* ── Section : Gestion des profils ── */}
          <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>👤 Gestion des profils</div>
          <div style={{ display:"flex", gap:6, marginBottom:8, padding:"10px 0", borderBottom:`1px solid ${T.cardBorder}` }}>
            <button onClick={()=>setActiveUser(null)} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(activeUser===null?T.navy:T.sandDark), background:activeUser===null?T.navy:"transparent", color:activeUser===null?T.white:T.textMid }}>👥 Famille</button>
            {MEMBERS.map(m=>(
              <button key={m.id} onClick={()=>setActiveUser(m.id)} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(activeUser===m.id?m.color:T.sandDark), background:activeUser===m.id?m.color:"transparent", color:activeUser===m.id?T.white:T.textMid }}>
                {m.avatar}
              </button>
            ))}
          </div>

          {!showAddMember ? (
            <button onClick={()=>setShowAddMember(true)} style={{ width:"100%", background:"transparent", border:"1px dashed "+T.sandDark, borderRadius:8, padding:"8px", fontSize:12, fontWeight:700, color:T.textSoft, cursor:"pointer", marginBottom:12 }}>+ Ajouter un profil</button>
          ) : (
            <div style={{ marginBottom:12, padding:"10px 12px", background:T.sandDark, borderRadius:10 }}>
              <input placeholder="Prénom" value={newMemberName} onChange={e=>setNewMemberName(e.target.value)} style={{...inputStyle,width:"100%",marginBottom:8,boxSizing:"border-box"}} />
              <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                <button onClick={()=>setNewMemberIsKid(false)} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(!newMemberIsKid?T.navy:T.sandDark), background:!newMemberIsKid?T.navy:"transparent", color:!newMemberIsKid?T.white:T.textMid }}>👤 Parent</button>
                <button onClick={()=>setNewMemberIsKid(true)} style={{ flex:1, padding:"7px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(newMemberIsKid?"#D4A843":T.sandDark), background:newMemberIsKid?"#D4A84322":"transparent", color:newMemberIsKid?"#D4A843":T.textMid }}>🧒 Enfant</button>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>{ if(newMemberName.trim()){ addMember({name:newMemberName,isKid:newMemberIsKid}); setNewMemberName(""); setNewMemberIsKid(false); setShowAddMember(false); } }} style={btnStyle(T.coral,{flex:1,fontSize:12})}>Créer</button>
                <button onClick={()=>{ setShowAddMember(false); setNewMemberName(""); setNewMemberIsKid(false); }} style={btnStyle(T.textSoft,{flex:1,fontSize:12})}>Annuler</button>
              </div>
            </div>
          )}

          {activeUser && (
            <div style={{ marginBottom:16, padding:"12px 14px", background:T.sandDark, borderRadius:10 }}>
              <div style={{ fontSize:12, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Type de profil</div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>updateMemberProfile(activeUser,{isKid:false})} style={{ flex:1, padding:"8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(!(memberProfiles[activeUser]?.isKid)?T.navy:T.sandDark), background:!(memberProfiles[activeUser]?.isKid)?T.navy:"transparent", color:!(memberProfiles[activeUser]?.isKid)?T.white:T.textMid }}>👤 Parent</button>
                <button onClick={()=>updateMemberProfile(activeUser,{isKid:true})} style={{ flex:1, padding:"8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:"2px solid "+(memberProfiles[activeUser]?.isKid?"#D4A843":T.sandDark), background:memberProfiles[activeUser]?.isKid?"#D4A84322":"transparent", color:memberProfiles[activeUser]?.isKid?"#D4A843":T.textMid }}>🧒 Enfant</button>
              </div>
              {!memberProfiles[activeUser]?.isKid && (
                <div style={{ marginTop:8 }}>
                  <div style={{ fontSize:11, color:T.textSoft, marginBottom:5 }}>
                    Code PIN {memberProfiles[activeUser]?.pin ? "🔒 Défini" : "Non défini"}
                  </div>
                  <input type="number" placeholder="Nouveau PIN (4 chiffres)" maxLength={4}
                    onChange={e=>{ const v=e.target.value.slice(0,4); if(v.length===4||v.length===0) updateMemberProfile(activeUser,{pin:v}); }}
                    style={{ background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:8, padding:"8px 12px", fontSize:16, color:T.text, outline:"none", width:"100%", boxSizing:"border-box", letterSpacing:"0.3em", textAlign:"center", marginBottom:6 }} />
                  {memberProfiles[activeUser]?.pin && (
                    <button onClick={()=>updateMemberProfile(activeUser,{pin:""})} style={{ width:"100%", background:"transparent", border:"1px solid rgba(231,76,60,0.4)", borderRadius:8, padding:"7px", fontSize:12, fontWeight:700, color:"#E74C3C", cursor:"pointer" }}>
                      🔓 Supprimer le code PIN
                    </button>
                  )}
                </div>
              )}
              {memberProfiles[activeUser]?.isKid && (
                <button onClick={()=>{ setKidsMode(activeUser); setShowNotifSettings(false); }} style={{ ...btnStyle("#D4A843",{width:"100%",marginTop:8,fontSize:12}) }}>
                  Basculer vers la version enfant →
                </button>
              )}
              <div style={{ marginTop:12, paddingTop:10, borderTop:`1px solid ${T.cardBorder}` }}>
                {deleteConfirmId === activeUser ? (
                  <div>
                    <div style={{ fontSize:11, color:"#E74C3C", marginBottom:8, lineHeight:1.4 }}>
                      Supprimer {memberOf(activeUser).name} ? Ses données existantes (tâches, RDV, humeurs...) resteront en base mais n'apparaîtront plus.
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={()=>{
                        const res = deleteMember(activeUser);
                        if(res?.error) setDeleteError(res.error);
                        setDeleteConfirmId(null);
                      }} style={{ flex:1, background:"#E74C3C", color:"white", border:"none", borderRadius:8, padding:"7px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Confirmer</button>
                      <button onClick={()=>setDeleteConfirmId(null)} style={{ flex:1, background:"transparent", border:"1px solid "+T.textSoft, color:T.textSoft, borderRadius:8, padding:"7px", fontSize:12, cursor:"pointer" }}>Annuler</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={()=>{ setDeleteError(null); setDeleteConfirmId(activeUser); }} style={{ width:"100%", background:"transparent", border:"1px solid rgba(231,76,60,0.4)", borderRadius:8, padding:"7px", fontSize:12, fontWeight:700, color:"#E74C3C", cursor:"pointer" }}>
                    🗑️ Supprimer ce profil
                  </button>
                )}
                {deleteError && <div style={{ fontSize:11, color:"#E74C3C", marginTop:6 }}>⚠️ {deleteError}</div>}
              </div>
            </div>
          )}
          {/* ── Section : Plages horaires humeur ── */}
          <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, marginTop:16 }}>😊 Plages horaires humeur</div>
          <div style={{ fontSize:11, color:T.textSoft, marginBottom:10, lineHeight:1.4 }}>
            En dehors de ces plages, l'humeur reste enregistrable mais ne rapporte pas de points de défi.
          </div>
          <div style={{ display:"flex", gap:10, marginBottom:16 }}>
            {["matin","soir"].map(s=>(
              <div key={s} style={{ flex:1, background:T.sandDark, borderRadius:10, padding:"10px 12px" }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.textMid, marginBottom:6, textTransform:"capitalize" }}>{s==="matin"?"🌅 Matin":"🌙 Soir"}</div>
                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                  <input type="time" value={moodSlots[s].start} onChange={e=>setMoodSlots(prev=>({...prev,[s]:{...prev[s],start:e.target.value}}))} style={{...inputStyle,fontSize:12,padding:"6px 8px",flex:1}} />
                  <span style={{ fontSize:11, color:T.textSoft }}>→</span>
                  <input type="time" value={moodSlots[s].end} onChange={e=>setMoodSlots(prev=>({...prev,[s]:{...prev[s],end:e.target.value}}))} style={{...inputStyle,fontSize:12,padding:"6px 8px",flex:1}} />
                </div>
              </div>
            ))}
          </div>
          {/* ── Section : Notifications ── */}
          <button onClick={()=>setShowNotifSection(s=>!s)} style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:"10px 14px", cursor:"pointer", marginBottom: showNotifSection?0:16, marginTop:8 }}>
            <span style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em" }}>🔔 Notifications</span>
            <span style={{ color:T.textSoft, fontSize:14 }}>{showNotifSection?"▲":"▼"}</span>
          </button>
          {showNotifSection && <div style={{ background:T.sandDark, borderRadius:10, padding:"12px", marginBottom:16, border:`1px solid ${T.cardBorder}` }}>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:16, marginBottom:6, paddingRight:2 }}>
            <span style={{ fontSize:10, color:T.textSoft, fontWeight:700, width:34, textAlign:"center" }}>🔔 Push</span>
            <span style={{ fontSize:10, color:T.textSoft, fontWeight:700, width:34, textAlign:"center" }}>📋 Bandeau</span>
          </div>

          {[
            { key:"rdv",    bandeauKey:"bandeau_rdv",    label:"RDV",                icon:"🏥", timing:"before" },
            { key:"tasks",  bandeauKey:"bandeau_tasks",  label:"Tâches",             icon:"✓",  timing:"before" },
            { key:"events", bandeauKey:"bandeau_events", label:"Événements",         icon:"📅", timing:"before" },
            { key:"repas",  bandeauKey:"bandeau_repas",  label:"Repas du soir",      icon:"🍽️", timing:"time",   defaultTime:"17:30" },
            { key:"menu",   bandeauKey:"bandeau_menu",   label:"Menu semaine (dim.)",icon:"📋", timing:"time",   defaultTime:"20:00" },
          ].map(({key,bandeauKey,label,icon,timing,defaultTime})=>(
            <div key={key} style={{ padding:"9px 0", borderBottom:"1px solid "+T.sandDark }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>{icon}</span>
                  <span style={{ fontSize:13, color:T.text }}>{label}</span>
                </div>
                <div style={{ display:"flex", gap:16 }}>
                  <input type="checkbox" checked={notifPrefs[key]!==false} onChange={e=>updateNotifPref(key,e.target.checked)} style={{ width:18,height:18,accentColor:T.coral,cursor:"pointer" }} />
                  <input type="checkbox" checked={notifPrefs[bandeauKey]!==false} onChange={e=>updateNotifPref(bandeauKey,e.target.checked)} style={{ width:18,height:18,accentColor:T.mint,cursor:"pointer" }} />
                </div>
              </div>
              {notifPrefs[key]!==false && (
                <div style={{ display:"flex", alignItems:"center", gap:8, paddingLeft:28, marginTop:6 }}>
                  {timing==="before" ? (
                    <>
                      <span style={{ fontSize:12, color:T.textSoft }}>Rappel</span>
                      <select value={notifPrefs[key+"_before"]??(key==="tasks"?0:15)} onChange={e=>updateNotifPref(key+"_before",+e.target.value)} style={{...inputStyle,fontSize:12,padding:"4px 8px"}}>
                        <option value={0}>À l'heure</option>
                        {TIMING_OPTIONS.filter(t=>t>0).map(t=><option key={t} value={t}>{t} min avant</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize:12, color:T.textSoft }}>Heure</span>
                      <TimePicker value={notifPrefs[key+"_time"]||defaultTime} onChange={v=>updateNotifPref(key+"_time",v)} style={{width:130}} />
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Humeur — matin et soir gardent des horaires distincts, partagent un seul toggle bandeau */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 0 4px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:16 }}>😊</span>
              <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Humeur</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <span style={{ width:18 }} />
              <input type="checkbox" checked={notifPrefs.bandeau_mood!==false} onChange={e=>updateNotifPref("bandeau_mood",e.target.checked)} style={{ width:18,height:18,accentColor:T.mint,cursor:"pointer" }} title="Bandeau — couvre matin et soir" />
            </div>
          </div>
          <div style={{ fontSize:11, color:T.textSoft, paddingLeft:26, marginBottom:4 }}>Une seule case bandeau ci-dessus couvre matin + soir</div>
          {[
            { key:"mood_matin", label:"Humeur — matin", icon:"🌅", defaultTime:"08:00" },
            { key:"mood_soir",  label:"Humeur — soir",  icon:"🌙", defaultTime:"21:00" },
          ].map(({key,label,icon,defaultTime},i)=>(
            <div key={key} style={{ padding:"9px 0", borderBottom:i===0?"1px solid "+T.sandDark:"none" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>{icon}</span>
                  <span style={{ fontSize:13, color:T.text }}>{label}</span>
                </div>
                <div style={{ display:"flex", gap:16 }}>
                  <input type="checkbox" checked={notifPrefs[key]!==false} onChange={e=>updateNotifPref(key,e.target.checked)} style={{ width:18,height:18,accentColor:T.coral,cursor:"pointer" }} />
                  <span style={{ width:18 }} />
                </div>
              </div>
              {notifPrefs[key]!==false && (
                <div style={{ display:"flex", alignItems:"center", gap:8, paddingLeft:28, marginTop:6 }}>
                  <span style={{ fontSize:12, color:T.textSoft }}>Heure</span>
                  <TimePicker value={notifPrefs[key+"_time"]||defaultTime} onChange={v=>updateNotifPref(key+"_time",v)} style={{width:130}} />
                </div>
              )}
            </div>
          ))}
          </div>}
          {/* ── Section : Apparence ── */}
          {/* Apparence — uses activeUser or currentUser */}
          {(() => { const themeFor = activeUser || currentUser; return themeFor ? (
            <>
              <button onClick={()=>setShowThemeSection(s=>!s)} style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", background:T.sandDark, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:"10px 14px", cursor:"pointer", marginBottom: showThemeSection?0:8, marginTop:4 }}>
                <span style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em" }}>🎨 Apparence — {memberOf(themeFor).name}</span>
                <span style={{ color:T.textSoft, fontSize:14 }}>{showThemeSection?"▲":"▼"}</span>
              </button>
              {showThemeSection && (
                <div style={{ background:T.sandDark, borderRadius:10, padding:"12px", marginBottom:12, border:`1px solid ${T.cardBorder}` }}>
                  <ThemeSelector
                    key={themeFor+"-"+(memberThemes[themeFor]?.themeId||"dark")}
                    currentTheme={memberThemes[themeFor]?.themeId||"dark"}
                    customColors={memberThemes[themeFor]?.customColors||{}}
                    onSelectTheme={(themeId)=>{updateMemberTheme(themeFor, themeId, {});}}
                    onCustomColor={(key,val)=>updateMemberTheme(themeFor, memberThemes[themeFor]?.themeId||"dark", {...(memberThemes[themeFor]?.customColors||{}), [key]:val})}
                    memberId={themeFor}
                  />
                </div>
              )}
            </>
          ) : null; })()}
          {/* ── Section : Permissions ── */}
          <div style={{ fontSize:11, fontWeight:800, color:T.textSoft, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, marginTop:8 }}>🛡️ Permissions</div>
          <div style={{ ...cardStyle(), marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:4 }}>Notifications du navigateur</div>
            <div style={{ fontSize:11, color:T.textSoft, marginBottom:10 }}>Nécessaire pour les alertes en temps réel</div>
            <button onClick={()=>{ try { if(typeof Notification!=="undefined"&&Notification.permission==="default") Notification.requestPermission(); } catch(e){} }} style={{ ...btnStyle(typeof Notification!=="undefined"&&Notification.permission==="granted"?T.mint:T.coral,{fontSize:12,padding:"7px 14px"}) }}>
              {typeof Notification!=="undefined"&&Notification.permission==="granted"?"✓ Autorisées":"Autoriser les notifications"}
            </button>
          </div>
          <button onClick={()=>setShowNotifSettings(false)} style={btnStyle(T.coral,{width:"100%"})}>Fermer</button>
        </Modal>
      )}

      {showSOS && <SOSModal onClose={()=>setShowSOS(false)} activeUser={activeUser} />}

      {presenceModal && (
        <PresenceModal presence={presence} member={presenceModal} onUpdate={updatePresence} onClose={()=>setPresenceModal(null)} />
      )}

      {reminderTarget && (
        <Modal title={`⏰ Rappel — ${reminderTarget.item?.title||""}`} onClose={()=>setReminderTarget(null)}>
          <div style={{ fontSize:12, color:T.textSoft, marginBottom:12 }}>
            {reminderTarget.date} {reminderTarget.time ? `à ${reminderTarget.time}` : ""}
          </div>
          <ReminderAttacher
            reminders={[]}
            onChange={(newReminders) => {
              newReminders.forEach(lr => addReminder({
                id: Date.now()+Math.random(),
                title: reminderTarget.item?.title||"Rappel",
                notes: reminderTarget.type==="appt" ? `RDV — ${reminderTarget.item?.specialist||""}` : reminderTarget.type==="task" ? "Tâche" : "Événement",
                member: normalizeMembers(reminderTarget.item?.members||reminderTarget.item?.member||"olivier")[0],
                members: normalizeMembers(reminderTarget.item?.members||reminderTarget.item?.member||"olivier"),
                targetDate: lr.targetDate,
                targetTime: lr.targetTime,
                done: false, triggered: false,
                createdAt: new Date().toISOString()
              }));
              setReminderTarget(null);
            }}
            itemDate={reminderTarget.date}
            itemTime={reminderTarget.time}
            itemTitle={reminderTarget.item?.title}
          />
          <button onClick={()=>setReminderTarget(null)} style={btnStyle(T.textSoft,{width:"100%",marginTop:8})}>Annuler</button>
        </Modal>
      )}

      <PenseBete notes={notes} onAdd={addNote} onUpdate={updateNote} onDelete={deleteNote} onConvert={convertNote} fiches={fiches} />

      {activeFiche && (
        <FicheDetail
          themes={themes} onAddTheme={addTheme}
          fiche={activeFiche}
          appts={appts}
          onUpdate={(f)=>{ updateFiche(f); setActiveFiche(f); }}
          onDelete={(id)=>{ deleteFiche(id); setActiveFiche(null); }}
          onClose={()=>setActiveFiche(null)}
          onAddAppt={addAppt}
          onCancelOccurrence={cancelApptOccurrence}
        />
      )}
    </div>
  );
}
